import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Switch,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  Checkbox,
  Link,
  Slider,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import OutboxIcon from '@mui/icons-material/Outbox';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import LanguageIcon from '@mui/icons-material/Language';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { THEME } from './genesisData';
import {
  useGtmResource,
  useGtmModels,
  useReplies,
  setReplyRead,
  bulkReplyRead,
  fetchReplyThread,
  sendThreadReply,
  regenerateOpener,
  approveContact,
  rejectContact,
  editContactOpener,
  createCampaign,
  fetchCampaign,
  updateCampaign,
  previewCampaign,
  importContacts,
  fetchCampaignPrompts,
  updateCampaignPrompt,
  addCampaignPromptStep,
  deleteCampaignPromptStep,
  regenerateCampaignDrafts,
  fetchInstantlyCampaigns,
  wireInstantlyCampaign,
  unlinkInstantlyCampaign,
  provisionInstantlyCampaign,
  syncInstantlyCampaigns,
  activateInstantlyCampaign,
  pauseInstantlyCampaign,
  approveReply,
  rejectReply,
  submitReplyFeedback,
  updateSequence,
  reviseStepTemplate,
  fetchPromptVersions,
  savePrompt,
  rollbackPrompt,
  previewSequence,
  createSequence,
  routePreviewSequence,
  testTarget,
  getTestTargetRun,
  testSend,
  previewContactsFile,
  importContactsFile,
  fetchApolloLists,
  previewApolloList,
  importApolloList,
  useGtmContacts,
  createLinkAsset,
  uploadLinkAsset,
  replaceLinkAssetFile,
  updateLinkAsset,
  deleteLinkAsset,
  createSuppression,
  deleteSuppression,
  patchSuppression,
  createCompetitor,
  deleteCompetitor,
  syncMailboxesNow,
  setMailboxAutopilot,
  setMailboxPin,
  fetchFleetPlan,
  confirmFleetOrder,
  verifyUpload,
  verifyRemapEmail,
  startVerification,
  fetchVerifyJob,
  promoteVerifiedContacts,
  refreshVerifyCredits,
  downloadVerifyCsv,
  setGtmControls,
  mineProofsNow,
  dedupeProofsNow,
  saveProof,
  retireProof,
  putRotationOrder,
  syncDailyStats,
} from '../../hooks/useGtm';
// GTM-REACH-01. recharts is already a dependency (package.json ^2.12.7) and is already
// used by Financials.js and ProfitLossModal.js, so the reach chart adds no new package
// and no new way for the Docker build to fail.
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import useContactsStream from '../../hooks/useContactsStream';
import { useAuth } from '../../contexts/AuthContext';
import CreditPoolWidget from './CreditPoolWidget';
import InfoTooltip from '../../components/common/InfoTooltip';
// Live data now comes from /api/gtm/* (see useGtm). Only the presentation maps
// and the New-request NAICS seed list still come from the fixtures module.
import {
  STATUS_TONE,
  STATUS_LABEL,
  STATUS_HELP,
  NAICS_OPTIONS,
} from '../../data/outboundFixtures';

// ----- shared style tokens (mirrors the Genesis page) -----

const scrollSx = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(18,192,153,0.4)', borderRadius: '10px' },
};

const outlinedBtnSx = {
  color: THEME.textSecondary,
  borderColor: THEME.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  '&:hover': { borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint },
};

const mintBtnSx = {
  background: THEME.mint,
  color: '#06281f',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  boxShadow: 'none',
  '&:hover': { background: '#0fa884', boxShadow: 'none' },
};

const dangerBtnSx = {
  color: THEME.pink,
  borderColor: 'rgba(198,61,87,0.5)',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  '&:hover': { borderColor: THEME.pink, background: 'rgba(198,61,87,0.1)' },
};

// Filled danger button, used for destructive confirm actions in modals.
const dangerSolidBtnSx = {
  background: THEME.pink,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  boxShadow: 'none',
  '&:hover': { background: '#a83048', boxShadow: 'none' },
};

// ----- info tooltip (MUI InfoOutlined in a Tooltip), dark-themed -----

const tipSlotProps = {
  componentsProps: {
    tooltip: {
      sx: {
        background: THEME.cardRaised,
        color: THEME.textSecondary,
        border: `1px solid ${THEME.borderStrong}`,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 1.5,
        maxWidth: 260,
        px: 1.25,
        py: 0.75,
        boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
      },
    },
    arrow: { sx: { color: THEME.cardRaised, '&::before': { border: `1px solid ${THEME.borderStrong}` } } },
  },
};

const InfoTip = ({ text, size = 14, sx = {} }) => (
  <Tooltip title={text} arrow placement="top" {...tipSlotProps}>
    <InfoOutlinedIcon
      sx={{
        fontSize: size,
        color: THEME.textMuted,
        cursor: 'help',
        verticalAlign: 'middle',
        flexShrink: 0,
        '&:hover': { color: THEME.mint },
        ...sx,
      }}
    />
  </Tooltip>
);

// Status badge whose color follows STATUS_TONE.
//   active=mint, pending_approval=amber, held=pink, paused=gray, done=muted
const TONE_MAP = {
  mint: { bg: THEME.mintDim, color: THEME.mint },
  amber: { bg: THEME.amberDim, color: THEME.amber },
  pink: { bg: 'rgba(198,61,87,0.15)', color: THEME.pink },
  gray: { bg: 'rgba(255,255,255,0.06)', color: THEME.textSecondary },
  muted: { bg: 'rgba(255,255,255,0.04)', color: THEME.textMuted },
  // Grey-green: sending-eligible but unconfirmed (e.g. ready-on-flag, no warmup score yet).
  sage: { bg: 'rgba(143,185,167,0.12)', color: '#8fb9a7' },
};

const Badge = ({ tone = 'muted', children }) => {
  const t = TONE_MAP[tone] || TONE_MAP.muted;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: t.bg,
        color: t.color,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        px: '8px',
        py: '3px',
        borderRadius: '4px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

// Status badge wrapped in a tooltip explaining what the status means.
const StatusBadge = ({ status }) => (
  <Tooltip title={STATUS_HELP[status] || ''} arrow placement="top" {...tipSlotProps}>
    <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
      <Badge tone={STATUS_TONE[status] || 'muted'}>{STATUS_LABEL[status] || status}</Badge>
    </Box>
  </Tooltip>
);

// Count badge shown on tab labels (Approvals / Review).
const CountBadge = ({ count, tone = 'amber' }) => {
  const t = TONE_MAP[tone] || TONE_MAP.amber;
  if (!count) return null;
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        minWidth: 18,
        height: 18,
        px: '5px',
        borderRadius: '9px',
        background: t.bg,
        color: t.color,
        fontSize: '11px',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
      }}
    >
      {count}
    </Box>
  );
};

const fieldLabelSx = {
  fontSize: '10px',
  color: THEME.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  mb: 0.75,
};

// Field label with an inline info tooltip.
const FieldLabel = ({ children, tip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
    <Typography sx={{ ...fieldLabelSx, mb: 0 }}>{children}</Typography>
    {tip && <InfoTip text={tip} size={13} />}
  </Box>
);

// Tab-content explainer line with an info tooltip.
const Explainer = ({ children, tip, sx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, ...sx }}>
    <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>{children}</Typography>
    {tip && <InfoTip text={tip} />}
  </Box>
);

const inputProps = {
  sx: { fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } },
};

// Selectable chip used in the ICP form (target titles, company size).
const SelectChip = ({ label, selected, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      border: `1px solid ${selected ? THEME.mint : THEME.borderStrong}`,
      background: selected ? THEME.mintFaint : 'transparent',
      color: selected ? THEME.mint : THEME.textSecondary,
      borderRadius: '16px',
      px: 1.5,
      py: 0.6,
      fontSize: '12px',
      fontWeight: 600,
      '&:hover': { borderColor: THEME.mint },
    }}
  >
    {label}
  </Box>
);

// ----- confirm modal (MUI Dialog) -----
// Driven by a config object: { title, body, confirmLabel, tone, onConfirm }.

// USD formatter for the cost estimate. Null-safe: renders a placeholder rather
// than "$NaN" when a number is missing.
const usd = (n) =>
  n == null || Number.isNaN(Number(n))
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Responsive breakpoint (the spec's 768px). Used in sx media queries throughout.
const MOBILE_MQ = '@media (max-width:768px)';
// Under 768px, modals become full-height sheets with internal scroll.
const sheetPaperSx = {
  [MOBILE_MQ]: { m: 0, width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100%', borderRadius: 0 },
};

const ConfirmModal = ({ config, onClose }) => (
  <Dialog
    open={!!config}
    onClose={onClose}
    PaperProps={{
      sx: {
        background: THEME.card,
        color: THEME.textPrimary,
        border: `1px solid ${THEME.borderStrong}`,
        borderRadius: '12px',
        maxWidth: 440,
        fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
        ...sheetPaperSx,
      },
    }}
  >
    {config && (
      <>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary, pb: 1 }}>
          {config.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
            {config.body}
          </Typography>
          {/* Cost estimator (Chunk 4 Step 7): both numbers from the campaign read. */}
          {(config.costLoading || config.costEstimate) && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
              {config.costLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={13} sx={{ color: THEME.mint }} />
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Estimating run cost…</Typography>
                </Box>
              ) : config.costEstimate && config.costEstimate.available !== false ? (
                <>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Expected (after suppression)
                      </Typography>
                      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: THEME.mint }}>
                        ~{usd(config.costEstimate.expectedCost)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Max (ceiling)
                      </Typography>
                      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: THEME.textPrimary }}>
                        {usd(config.costEstimate.ceilingCost)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.75 }}>
                    Expected assumes a {Math.round((config.costEstimate.suppressionRate || 0) * 100)}% suppression rate.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Cost estimate unavailable.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={config.tone === 'danger' ? dangerSolidBtnSx : mintBtnSx}
            onClick={() => {
              if (config.onConfirm) config.onConfirm();
              onClose();
            }}
          >
            {config.confirmLabel}
          </Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);

// ----- per-tab loading / empty / error states -----

const LoadingState = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: THEME.textMuted }}>
    <CircularProgress size={16} sx={{ color: THEME.mint }} />
    <Typography sx={{ fontSize: '13px' }}>{label}</Typography>
  </Box>
);

const EmptyState = ({ label }) => (
  <Typography sx={{ fontSize: '13px', color: THEME.textMuted, py: 1 }}>{label}</Typography>
);

// Readable error block. A 401/403 message (token not attached) lands here, in
// brand-pink, instead of leaving the tab blank.
const ErrorState = ({ message, onRetry }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-start',
      p: 2,
      borderRadius: '10px',
      background: 'rgba(198,61,87,0.12)',
      border: `1px solid ${THEME.pink}`,
    }}
  >
    <WarningAmberIcon sx={{ color: THEME.pink, fontSize: 18, flexShrink: 0, mt: '1px' }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '13px', color: THEME.pink, fontWeight: 700 }}>
        Couldn't load this
      </Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mt: 0.25, lineHeight: 1.5 }}>
        {message}
      </Typography>
    </Box>
    {onRetry && (
      <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={onRetry}>
        Retry
      </Button>
    )}
  </Box>
);

// Renders the right state for a fetched resource, else the children (the list).
const Resource = ({ loading, error, empty, loadingLabel, emptyLabel, onRetry, children }) => {
  if (loading) return <LoadingState label={loadingLabel} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (empty) return <EmptyState label={emptyLabel} />;
  return children;
};

// ===== CSV import (Chunk: purchased-list import) =====
// No PapaParse in this app, so a small quote-aware CSV parser. Handles quoted
// fields, escaped "" quotes, and embedded commas/newlines. Returns a 2D array.
function parseCsv(text) {
  const s = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully-empty rows (trailing newline, blank lines).
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
}

// Canonical import fields and the header aliases that map onto them.
const IMPORT_FIELDS = ['firstName', 'lastName', 'email', 'title', 'companyName', 'companyDomain', 'linkedinUrl'];
const HEADER_ALIASES = {
  firstName: ['firstname', 'first name', 'first', 'fname', 'given name'],
  lastName: ['lastname', 'last name', 'last', 'lname', 'surname', 'family name'],
  email: ['email', 'email address', 'e-mail', 'work email', 'emailaddress'],
  title: ['title', 'job title', 'position', 'role'],
  companyName: ['company', 'company name', 'organization', 'organisation', 'employer', 'account name'],
  companyDomain: ['domain', 'company domain', 'website', 'company website', 'url'],
  linkedinUrl: ['linkedin', 'linkedin url', 'linkedin profile', 'linkedinurl', 'li url'],
};
function mapHeaders(headerRow) {
  const map = {}; // canonicalField -> column index
  (headerRow || []).forEach((h, idx) => {
    const norm = String(h || '').trim().toLowerCase();
    for (const field of IMPORT_FIELDS) {
      if (map[field] !== undefined) continue;
      if (HEADER_ALIASES[field].includes(norm)) {
        map[field] = idx;
        break;
      }
    }
  });
  return map;
}
function rowsToContacts(dataRows, headerMap) {
  return (dataRows || []).map((r) => {
    const obj = {};
    IMPORT_FIELDS.forEach((f) => {
      const idx = headerMap[f];
      obj[f] = idx !== undefined ? String(r[idx] ?? '').trim() : '';
    });
    return obj;
  });
}

const IMPORT_BATCH = 500; // matches the backend per-request cap

// Import a CSV list of purchased/existing contacts into a campaign. Parses client
// side, shows the detected column mapping + first 5 rows, then POSTs in batches of
// 500 and renders the per-row rejected list (Rule 3: nothing fails silently).
const ImportContactsDialog = ({ campaign, onClose }) => {
  const [fileName, setFileName] = useState('');
  const [headerRow, setHeaderRow] = useState(null);
  const [dataRows, setDataRows] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [result, setResult] = useState(null); // { total, imported, suppressedCustomers, rejected }

  const contacts = useMemo(() => rowsToContacts(dataRows, headerMap), [dataRows, headerMap]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setParseError(null);
    setImportError(null);
    setResult(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result || ''));
        if (rows.length < 2) {
          setParseError('CSV needs a header row and at least one data row.');
          setHeaderRow(null);
          setDataRows([]);
          setHeaderMap({});
          return;
        }
        const header = rows[0];
        const map = mapHeaders(header);
        setParseError(map.email === undefined ? 'No email column detected. A column mapping to email is required.' : null);
        setHeaderRow(header);
        setDataRows(rows.slice(1));
        setHeaderMap(map);
      } catch (err) {
        setParseError('Could not parse this file as CSV.');
      }
    };
    reader.onerror = () => setParseError('Could not read the file.');
    reader.readAsText(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  const runImport = async () => {
    setImporting(true);
    setImportError(null);
    const acc = { total: contacts.length, imported: 0, suppressedCustomers: 0, rejected: [] };
    try {
      for (let i = 0; i < contacts.length; i += IMPORT_BATCH) {
        const batch = contacts.slice(i, i + IMPORT_BATCH);
        const res = await importContacts(campaign.id, batch);
        acc.imported += res.imported || 0;
        acc.suppressedCustomers += res.suppressedCustomers || 0;
        (res.rejected || []).forEach((rj) =>
          acc.rejected.push({ ...rj, index: rj.index != null ? rj.index + i : null })
        );
      }
      setResult(acc);
    } catch (err) {
      setImportError(friendlyActionError('import', err));
    } finally {
      setImporting(false);
    }
  };

  const canImport = contacts.length > 0 && headerMap.email !== undefined && !importing;

  return (
    <Dialog
      open
      onClose={() => !importing && onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Import list into {campaign.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2 }}>
          Upload a CSV of purchased or existing contacts. Imported contacts are verified before any send.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button component="label" variant="outlined" size="small" sx={outlinedBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}>
            {fileName ? 'Choose a different CSV' : 'Choose CSV'}
            <input hidden type="file" accept=".csv,text/csv" onChange={onFile} />
          </Button>
          {fileName && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{fileName}</Typography>}
        </Box>

        {parseError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{parseError}</Typography>
          </Box>
        )}

        {headerRow && !result && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
              Detected columns
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {IMPORT_FIELDS.map((f) => {
                const mapped = headerMap[f] !== undefined;
                return (
                  <Box
                    key={f}
                    component="span"
                    sx={{
                      fontSize: '11px',
                      px: '8px',
                      py: '3px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.border}`,
                      background: mapped ? THEME.mintFaint : 'rgba(255,255,255,0.03)',
                      color: mapped ? THEME.mint : THEME.textMuted,
                    }}
                  >
                    {f}
                    {mapped ? ` = ${headerRow[headerMap[f]]}` : ' (unmapped)'}
                  </Box>
                );
              })}
            </Box>

            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
              Preview (first {Math.min(5, contacts.length)} of {contacts.length.toLocaleString('en-US')} rows)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {contacts.slice(0, 5).map((c, i) => (
                <Box key={i} sx={{ p: 1, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
                  <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-word' }}>
                    {[[c.firstName, c.lastName].filter(Boolean).join(' '), c.title, c.companyName, c.email].filter(Boolean).join('  ·  ') || '(empty row)'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {importError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{importError}</Typography>
          </Box>
        )}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontWeight: 600, mb: 1 }}>
              Imported {result.imported.toLocaleString('en-US')} of {result.total.toLocaleString('en-US')}
              {result.suppressedCustomers ? `  ·  ${result.suppressedCustomers.toLocaleString('en-US')} suppressed (existing customers)` : ''}
              {result.rejected.length ? `  ·  ${result.rejected.length.toLocaleString('en-US')} rejected` : ''}
            </Typography>
            {result.rejected.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
                  Rejected rows
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 240, overflowY: 'auto', ...scrollSx }}>
                  {result.rejected.map((rj, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, p: 0.75, borderRadius: '6px', background: 'rgba(198,61,87,0.08)', border: `1px solid ${THEME.border}` }}>
                      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, minWidth: 56, flexShrink: 0 }}>
                        {rj.index != null ? `row ${rj.index + 1}` : 'row ?'}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                        {rj.email || '(no email)'}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: THEME.pink, flexShrink: 0 }}>{rj.reason}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} disabled={importing} onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            sx={mintBtnSx}
            disabled={!canImport}
            onClick={runImport}
            startIcon={importing ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
          >
            {importing ? 'Importing…' : `Import ${contacts.length.toLocaleString('en-US')} contact${contacts.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ===== Contacts: browse + generic import (file + saved Apollo lists) =====

// The { imported, queued, suppressedAtIntake } summary shared by both import paths.
const ImportResult = ({ result }) => (
  <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', background: THEME.mintFaint, border: `1px solid ${THEME.mint}` }}>
    <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontWeight: 600 }}>
      Imported {Number(result.imported || 0).toLocaleString('en-US')}
      {' · '}
      {Number(result.queued || 0).toLocaleString('en-US')} queued
      {result.suppressedAtIntake != null ? ` · ${Number(result.suppressedAtIntake).toLocaleString('en-US')} suppressed at intake` : ''}
    </Typography>
  </Box>
);

// One line of a preview table, from a loosely-typed contact-ish row.
const previewLine = (r) =>
  [
    [r.firstName, r.lastName].filter(Boolean).join(' ') || r.name,
    r.title,
    r.companyName || r.company,
    r.email,
  ]
    .filter(Boolean)
    .join('  ·  ') || '(empty row)';

// A browse card for one pipeline contact. Pipeline state is read defensively (the
// list shape may carry held/heldReason, deferred/deferredReason, verified). Stacks
// under 768px with the badges dropping to a full-width row.
// VERIFY-LANES-01: lane / verification status badges for a contact, shown on contact and held
// surfaces. Both fields are read defensively and render nothing unless actually present (the
// contact serializers do not emit lane/verification today), so every surface is unchanged until
// the verify backend ships. lane 'risk' is an unresolved catch-all held off the main mailboxes;
// pending verification is still being checked, with an expected ready date when known.
const LaneBadges = ({ contact }) => {
  const c = contact || {};
  const lane = String(c.lane || '').toLowerCase();
  const status = String(c.status || '').toLowerCase();
  const pv = (c.pendingVerification && typeof c.pendingVerification === 'object') ? c.pendingVerification : {};
  const isRisk = lane === 'risk';
  const isPending = !isRisk && (status === 'pending_verification' || c.verificationPending === true || !!(pv.expectedReadyAt || pv.readyBy));
  const readyBy = fmtDate(c.readyBy || c.expectedReadyAt || pv.expectedReadyAt || pv.readyBy);
  if (!isRisk && !isPending) return null;
  return (
    <>
      {isRisk && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Badge tone="pink">catch-all, unresolved</Badge>
          <InfoTooltip title="Catch-all, unresolved">Held until a dedicated catch-all sending lane exists. These never send from your main mailboxes.</InfoTooltip>
        </Box>
      )}
      {isPending && <Badge tone="amber">{readyBy ? `checking, ready by ${readyBy}` : 'checking'}</Badge>}
    </>
  );
};

// GTM-HOLD-REASON-01. Hold reasons reached the operator as raw codes: a card simply
// printed "no_persona_matched" in pink. Every code the pipeline actually sets is mapped
// here to what happened and what to do about it, because the actions genuinely differ.
// The two B5c persona holds are the sharpest example: one needs a human to decide who
// this person is, the other needs an operator to switch a sequence on.
//
// `fix` marks the reasons an operator can clear themselves. An unmapped code still
// renders, as its raw value, rather than being swallowed.
const HOLD_REASONS = {
  no_persona_matched: {
    label: 'No persona matched',
    tone: 'amber',
    meaning: 'The router could not decide which audience this person belongs to, so nothing was drafted.',
    action: 'Needs a human read. Check the title and company, then either route them by hand or remove them.',
    fix: null,
  },
  persona_not_live: {
    label: 'Sequence not live',
    tone: 'amber',
    meaning: 'The router matched this person to a sequence that is not switched on, so nothing was drafted.',
    action: 'Activate that sequence and this contact resumes on its own.',
    fix: 'sequences',
  },
  fit_mismatch: {
    label: 'Not a fit',
    tone: 'gray',
    meaning: 'The fit check decided this person does not match the audience the campaign targets.',
    action: 'Read-only. Nothing sends to them.',
    fix: null,
  },
  fit_check_failed: {
    label: 'Fit check failed',
    tone: 'pink',
    meaning: 'The fit check could not complete, so the contact was held rather than mailed unchecked.',
    action: 'Usually transient. Regenerate the contact to re-run the check.',
    fix: null,
  },
  competitor_blocked: {
    label: 'Competitor',
    tone: 'pink',
    meaning: 'Their company is on the full-block competitor list.',
    action: 'Read-only. Edit the list in Settings if this is wrong.',
    fix: 'competitors',
  },
  suppressed: {
    label: 'Suppressed',
    tone: 'gray',
    meaning: 'This address or domain is on the we-know-them suppression list.',
    action: 'Read-only. Edit the list in Settings if this is wrong.',
    fix: 'suppressions',
  },
  already_contacted_instantly: {
    label: 'Already contacted',
    tone: 'gray',
    meaning: 'Instantly already has a send to this person, so a second one would be a duplicate.',
    action: 'Read-only. No action needed.',
    fix: null,
  },
  email_unverified: {
    label: 'Email unverified',
    tone: 'amber',
    meaning: 'The address never passed verification, so sending would risk the domain.',
    action: 'Run it through Verify a list, or remove the contact.',
    fix: 'verify',
  },
  email_catchall_unresolved: {
    label: 'Catch-all unresolved',
    tone: 'amber',
    meaning: 'The domain accepts everything, so the deep check could not confirm the address exists.',
    action: 'Waits on the slower deep-verification lane, which can take up to three days.',
    fix: null,
  },
  verification_failed: {
    label: 'Verification failed',
    tone: 'pink',
    meaning: 'The verification provider errored, so the address was never confirmed.',
    action: 'Usually transient. Re-verify the list.',
    fix: 'verify',
  },
  step_grounding_failed: {
    label: 'Draft not grounded',
    tone: 'pink',
    meaning: 'The composer could not ground a step in real evidence, so it refused to write it.',
    action: 'Regenerate the contact, or fix the evidence the step depends on.',
    fix: null,
  },
  template_vars_incomplete: {
    label: 'Missing template fields',
    tone: 'pink',
    meaning: 'The sequence needs fields this contact does not have, so a merge field would have gone out blank.',
    action: 'Fill the missing fields on the contact, or edit the sequence to stop requiring them.',
    fix: 'sequences',
  },
  deferred: {
    label: 'Deferred',
    tone: 'gray',
    meaning: 'Company governance parked this contact for now.',
    action: 'Read-only. It resumes on its own.',
    fix: null,
  },
};

// One hold reason, stated plainly, with its own action and an optional way to go fix it.
const HoldReason = ({ reason, onNavigate, dense = false }) => {
  if (!reason) return null;
  const meta = HOLD_REASONS[reason];
  if (!meta) {
    // Unmapped code: show it rather than hide it, and say plainly that it is unmapped.
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
        <Badge tone="pink">Held</Badge>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, fontFamily: 'monospace' }}>{reason}</Typography>
      </Box>
    );
  }
  const target = meta.fix === 'sequences' ? { group: 'emails', sub: 'sequences' }
    : meta.fix === 'competitors' ? { group: 'settings', sub: 'competitors' }
      : meta.fix === 'suppressions' ? { group: 'settings', sub: 'suppressions' }
        : meta.fix === 'verify' ? { group: 'find', sub: 'verify' }
          : null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Badge tone={meta.tone}>{meta.label}</Badge>
        {!dense && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>{meta.meaning}</Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.5 }}>{meta.action}</Typography>
        {target && onNavigate && (
          <Button
            size="small"
            variant="text"
            onClick={() => onNavigate(target)}
            sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 600, minWidth: 0, px: 0.75, color: THEME.mint, '&:hover': { background: 'rgba(255,255,255,0.04)' } }}
          >
            Go there
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ContactCard = ({ c, onNavigate }) => {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.name || '(no name)';
  const held = c.held === true || c.status === 'held' || !!c.holdReason || !!c.heldReason;
  const holdReason = c.holdReason || c.heldReason || (c.status === 'held' ? c.reason : '');
  const deferred = c.deferred === true || c.status === 'deferred' || !!c.deferredReason;
  const deferReason = c.deferredReason || c.deferReason;
  const verified = c.verified === true || c.emailStatus === 'verified' || c.emailVerified === true;
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 2, [MOBILE_MQ]: { flexDirection: 'column', gap: 1 } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-word' }}>{name}</Typography>
        {(c.title || c.companyName || c.company) && (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, wordBreak: 'break-word' }}>
            {[c.title, c.companyName || c.company].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {c.email && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-all' }}>{c.email}</Typography>}
        {held && holdReason && <HoldReason reason={holdReason} onNavigate={onNavigate} />}
        {deferred && deferReason && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>Deferred: {deferReason}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0, [MOBILE_MQ]: { width: '100%' } }}>
        {c.status && <StatusBadge status={c.status} />}
        {verified ? <Badge tone="mint">Verified</Badge> : <Badge tone="muted">Unverified</Badge>}
        {held && <Badge tone="pink">Held</Badge>}
        {deferred && <Badge tone="gray">Deferred</Badge>}
        <LaneBadges contact={c} />
      </Box>
    </Box>
  );
};

// The generic import dialog: a real upload -> preview -> import flow, plus a
// "From Apollo" source for lists already paid for. Both paths land the same
// { imported, queued, suppressedAtIntake } result.
// ── Import wizard step header: Source → Preview → Confirm, with the current step
// highlighted and finished steps checked. Plain three-dot progress, no library.
const IMPORT_STEPS = ['Source', 'Preview', 'Confirm'];
const ImportStepHeader = ({ step }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
    {IMPORT_STEPS.map((label, i) => {
      const n = i + 1;
      const active = n === step;
      const done = n < step;
      return (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700,
                background: active || done ? THEME.mint : 'transparent',
                color: active || done ? '#06281f' : THEME.textMuted,
                border: `1px solid ${active || done ? THEME.mint : THEME.border}`,
              }}
            >
              {done ? '✓' : n}
            </Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: active ? THEME.textPrimary : THEME.textMuted }}>{label}</Typography>
          </Box>
          {i < IMPORT_STEPS.length - 1 && <Box sx={{ width: 20, height: 1, background: THEME.border }} />}
        </React.Fragment>
      );
    })}
  </Box>
);

// The three ways to bring people in, shown as big pick-one cards on step 1.
const IMPORT_SOURCES = [
  { key: 'apollo', title: 'From an Apollo list', desc: 'Pull in a saved Apollo list you already revealed. No extra charge.' },
  { key: 'file', title: 'Upload a file', desc: 'Import a CSV or spreadsheet of contacts from your computer.' },
  { key: 'test', title: 'Test one person', desc: 'Not ready to import? Try the whole pipeline on a single prospect first.' },
];

// Rule 3: a refused import says what to DO about it, not just that it failed. Every 4xx
// the two import endpoints can return gets a recovery step; anything unrecognized still
// gets a generic one rather than leaving the operator staring at a bare code.
function importRecoveryHint(err) {
  const code = (err && err.code) || '';
  const status = (err && err.httpStatus) || 0;
  if (code === 'source_label_required') return 'Add a source label above, then import again.';
  if (code === 'xlsx_unsupported') return 'Open the file in your spreadsheet app and save it as CSV, then upload that.';
  if (code === 'empty_or_unparseable') return 'The file had no readable header row. Check it opens as a normal spreadsheet, with column names on the first line.';
  if (code === 'no_input') return 'The file did not reach the server. Pick it again and retry.';
  if (code === 'list_id_required') return 'Go back and pick the Apollo list again.';
  if (status === 413) return 'The file is too large. Split it into smaller files and import them one at a time.';
  if (status === 401 || status === 403) return 'Sign in again with a staff account that has united_ai admin access.';
  if (status >= 400 && status < 500) return 'Nothing was imported. Fix the problem above and try again.';
  if (status >= 500) return 'Nothing was imported. This one is on the server; retry in a moment.';
  return 'Nothing was imported. Try again.';
}

const ImportContactsFlow = ({ onClose, onImported }) => {
  const [step, setStep] = useState(1); // 1 Source · 2 Preview · 3 Confirm
  const [source, setSource] = useState(null); // 'file' | 'apollo' | 'test'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // ── file path ──
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, total, columnMapping, missingFields, note }

  // ── apollo path ──
  const [lists, setLists] = useState(null);
  const [listsLoading, setListsLoading] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [apolloPreview, setApolloPreview] = useState(null); // { contacts, total }
  const [errorHint, setErrorHint] = useState(null);

  // GTM-IMPORT-SOURCE-LABEL-01. Both import endpoints require a non-empty sourceLabel
  // and 400 without one. It is stored as the NAME of the list these contacts belong to,
  // so it has to be something the operator recognizes later, which is why it is asked
  // for rather than invented. Defaulted from the filename or the Apollo list name so the
  // common case is one keystroke; the operator can overwrite it.
  const [sourceLabel, setSourceLabel] = useState('');
  const labelReady = sourceLabel.trim().length > 0;

  const reset = () => {
    setError(null);
    setErrorHint(null);
    setResult(null);
    setFile(null);
    setPreview(null);
    setSourceLabel('');
    setSelectedList(null);
    setApolloPreview(null);
  };
  // Step 1 → pick a source. File/Apollo advance to Preview; Test one person hands off
  // to the dry-run tester (rendered in place of this dialog via the early return below).
  const chooseSource = (s) => {
    reset();
    setSource(s);
    if (s === 'apollo') { if (lists == null) loadLists(); setStep(2); }
    else if (s === 'file') { setStep(2); }
  };
  const backToSource = () => { reset(); setSource(null); setStep(1); };
  // Continue from Preview to Confirm is allowed once there is something to import.
  const canConfirm =
    (source === 'file' && !!preview && !(preview.missingFields && preview.missingFields.length > 0)) ||
    (source === 'apollo' && !!selectedList);

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!f) return;
    setFile(f);
    // Default the label to the filename without its extension: "chro-list.csv" reads as
    // "chro-list". Only ever overwrites the untouched default, never the operator's text.
    setSourceLabel((cur) => (cur.trim() ? cur : f.name.replace(/\.[^.]+$/, '')));
    setPreview(null);
    setResult(null);
    setError(null);
    setErrorHint(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const data = await previewContactsFile(form);
      setPreview({
        rows: data.rows || data.preview || data.sample || [],
        total: data.total != null ? data.total : (Array.isArray(data.rows) ? data.rows.length : 0),
        columnMapping: data.columnMapping || data.mapping || {},
        missingFields: data.missingFields || data.missing || [],
        note: data.note || '',
      });
    } catch (err) {
      setError(friendlyActionError('preview', err));
      setErrorHint(importRecoveryHint(err));
    } finally {
      setBusy(false);
    }
  };

  const runFileImport = async () => {
    if (!preview || !file) return;
    setBusy(true);
    setError(null);
    setErrorHint(null);
    try {
      // /contacts/preview keeps no server-side staging, so import re-posts the file
      // itself plus the filename as the list label.
      const form = new FormData();
      form.append('file', file);
      const res = await importContactsFile({ form, sourceLabel: sourceLabel.trim() });
      setResult(res || {});
      onImported();
    } catch (err) {
      setError(friendlyActionError('import', err));
      setErrorHint(importRecoveryHint(err));
    } finally {
      setBusy(false);
    }
  };

  const loadLists = async () => {
    setListsLoading(true);
    setError(null);
    setErrorHint(null);
    try {
      const data = await fetchApolloLists();
      setLists(data);
    } catch (err) {
      setLists([]);
      setError(friendlyActionError('load', err));
      setErrorHint(importRecoveryHint(err));
    } finally {
      setListsLoading(false);
    }
  };

  const pickList = async (list) => {
    setSelectedList(list);
    setSourceLabel((cur) => (cur.trim() ? cur : (list.name || '')));
    setApolloPreview(null);
    setResult(null);
    setError(null);
    setErrorHint(null);
    setBusy(true);
    try {
      const data = await previewApolloList(list.id || list._id, 1);
      setApolloPreview({
        contacts: data.contacts || data.rows || [],
        total: data.total != null ? data.total : (list.count != null ? list.count : null),
      });
    } catch (err) {
      setError(friendlyActionError('preview', err));
      setErrorHint(importRecoveryHint(err));
    } finally {
      setBusy(false);
    }
  };

  const runApolloImport = async () => {
    if (!selectedList) return;
    setBusy(true);
    setError(null);
    setErrorHint(null);
    try {
      const res = await importApolloList(selectedList.id || selectedList._id, sourceLabel.trim());
      setResult(res || {});
      onImported();
    } catch (err) {
      setError(friendlyActionError('import', err));
      setErrorHint(importRecoveryHint(err));
    } finally {
      setBusy(false);
    }
  };

  const columnEntries = preview ? Object.entries(preview.columnMapping || {}) : [];

  // "Test one person" hands straight off to the dry-run tester in place of the wizard.
  if (source === 'test') return <TestTargetModal onClose={onClose} />;

  // Count + label for the confirm step, derived from whichever source is chosen.
  const importCount =
    source === 'file'
      ? (preview ? Number(preview.total || preview.rows.length) : null)
      : (apolloPreview && apolloPreview.total != null ? Number(apolloPreview.total) : (selectedList && selectedList.count != null ? Number(selectedList.count) : null));
  const runImport = source === 'file' ? runFileImport : runApolloImport;
  const importing = busy && (step === 3);

  return (
    <Dialog
      open
      onClose={() => !busy && onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Import contacts</DialogTitle>
      <DialogContent>
        <ImportStepHeader step={result ? 3 : step} />

        {/* STEP 1 — pick a source */}
        {step === 1 && !result && (
          <Box>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mb: 2, lineHeight: 1.6 }}>
              Where should these people come from? Pick one — nothing is added until you preview and confirm.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {IMPORT_SOURCES.map((s) => (
                <Box
                  key={s.key}
                  onClick={() => chooseSource(s.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); chooseSource(s.key); } }}
                  sx={{
                    px: 2, py: 1.75, borderRadius: '12px', cursor: 'pointer',
                    border: `1px solid ${THEME.border}`, background: THEME.card,
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': { borderColor: THEME.mint, background: 'rgba(255,255,255,0.02)' },
                    [MOBILE_MQ]: { minHeight: 44 },
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25, lineHeight: 1.5 }}>{s.desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* STEP 2 — preview the chosen source */}
        {step === 2 && !result && source === 'file' && (
          <Box>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mb: 2, lineHeight: 1.6 }}>
              Choose a CSV or spreadsheet. We'll read it and show you the first rows so you can check it looks right. Nothing is imported yet.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component="label" variant="outlined" size="small" sx={outlinedBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}>
                {file ? 'Choose a different file' : 'Choose CSV or XLSX'}
                <input hidden type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
              </Button>
              {file && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{file.name}</Typography>}
              {busy && !preview && <CircularProgress size={14} sx={{ color: THEME.mint }} />}
            </Box>

            {preview && (
              <Box sx={{ mt: 2 }}>
                {/* column mapping */}
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                  Column mapping
                </Typography>
                {columnEntries.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {columnEntries.map(([field, col]) => (
                      <Box key={field} component="span" sx={{ fontSize: '11px', px: '8px', py: '3px', borderRadius: '4px', border: `1px solid ${THEME.border}`, background: THEME.mintFaint, color: THEME.mint }}>
                        {field} = {String(col)}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 1.5 }}>Columns auto-detected.</Typography>
                )}

                {/* missing-field note */}
                {preview.missingFields && preview.missingFields.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 15, color: THEME.amber, mt: '1px' }} />
                    <Typography sx={{ fontSize: '12px', color: THEME.amber }}>
                      Missing column{preview.missingFields.length === 1 ? '' : 's'}: {preview.missingFields.join(', ')}. Add {preview.missingFields.length === 1 ? 'it' : 'them'} and re-upload to continue.
                    </Typography>
                  </Box>
                )}
                {preview.note && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mb: 1.5 }}>{preview.note}</Typography>}

                {/* 25-row preview */}
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                  Preview (first {Math.min(25, preview.rows.length)} of {Number(preview.total || preview.rows.length).toLocaleString('en-US')} rows)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 320, overflowY: 'auto', ...scrollSx }}>
                  {preview.rows.slice(0, 25).map((r, i) => (
                    <Box key={i} sx={{ p: 1, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
                      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-word' }}>{previewLine(r)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {step === 2 && !result && source === 'apollo' && (
          <Box>
            <Typography sx={{ fontSize: '12px', color: THEME.mint, fontWeight: 600, mb: 0.5 }}>
              Contacts you already paid for, no extra charge.
            </Typography>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mb: 2, lineHeight: 1.6 }}>
              Pick one of your saved Apollo lists. We'll show you a preview of who's in it before anything is imported.
            </Typography>

            {listsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: THEME.textMuted }}>
                <CircularProgress size={16} sx={{ color: THEME.mint }} />
                <Typography sx={{ fontSize: '13px' }}>Loading your Apollo lists…</Typography>
              </Box>
            ) : lists && lists.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>No saved Apollo lists found. Create one in Apollo, then come back here.</Typography>
                <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, mt: 1 }} onClick={loadLists}>Retry</Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(lists || []).map((l) => {
                  const id = l.id || l._id;
                  const selected = selectedList && (selectedList.id || selectedList._id) === id;
                  return (
                    <Box
                      key={id}
                      onClick={() => pickList(l)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderRadius: '10px', cursor: 'pointer', border: `1px solid ${selected ? THEME.mint : THEME.border}`, background: selected ? THEME.mintFaint : THEME.card, '&:hover': { borderColor: THEME.borderStrong }, [MOBILE_MQ]: { minHeight: 44 } }}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{l.name || '(unnamed list)'}</Typography>
                      {l.count != null && <Badge tone="gray">{Number(l.count).toLocaleString('en-US')} contacts</Badge>}
                    </Box>
                  );
                })}
              </Box>
            )}

            {busy && selectedList && !apolloPreview && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: THEME.textMuted }}>
                <CircularProgress size={16} sx={{ color: THEME.mint }} />
                <Typography sx={{ fontSize: '13px' }}>Loading a preview of {selectedList.name}…</Typography>
              </Box>
            )}

            {apolloPreview && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                  Preview (first {Math.min(25, apolloPreview.contacts.length)}{apolloPreview.total != null ? ` of ${Number(apolloPreview.total).toLocaleString('en-US')}` : ''})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 320, overflowY: 'auto', ...scrollSx }}>
                  {apolloPreview.contacts.slice(0, 25).map((r, i) => (
                    <Box key={i} sx={{ p: 1, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
                      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-word' }}>{previewLine(r)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* STEP 3 — confirm */}
        {step === 3 && !result && (
          <Box>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mb: 2, lineHeight: 1.6 }}>
              {source === 'file'
                ? <>You're about to import{importCount != null ? <> <strong>{importCount.toLocaleString('en-US')}</strong></> : ''} contact{importCount === 1 ? '' : 's'} from <strong>{file ? file.name : 'your file'}</strong>.</>
                : <>You're about to import <strong>{selectedList ? selectedList.name : 'this list'}</strong>{importCount != null ? <> ({importCount.toLocaleString('en-US')} contact{importCount === 1 ? '' : 's'})</> : ''}. These are already revealed in Apollo, so there's no extra charge.</>}
            </Typography>
            {/* Required by both import endpoints. Asked for rather than invented,
                because it becomes the name of the list these contacts live under. */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textSecondary }}>Source label</Typography>
                <Typography sx={{ fontSize: '12px', color: THEME.pink }}>*</Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={sourceLabel}
                onChange={(e) => { setSourceLabel(e.target.value); if (error) setError(null); }}
                placeholder="Apollo CHRO/CPO list, Aug 2026"
                InputProps={inputProps}
                disabled={importing}
              />
              <Typography sx={{ fontSize: '11px', color: labelReady ? THEME.textMuted : THEME.amber, mt: 0.5, lineHeight: 1.5 }}>
                {labelReady
                  ? 'Names where these contacts came from, so you can tell this batch apart later. For example "Apollo CHRO/CPO list, Aug 2026".'
                  : 'A source label is required. Name where these contacts came from, for example "Apollo CHRO/CPO list, Aug 2026".'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, p: 2, borderRadius: '10px', background: THEME.mintFaint, border: `1px solid ${THEME.mintDim}` }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: THEME.mint, flexShrink: 0, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
                Every contact is checked and verified before the system drafts anything, and no email ever sends without your approval. You can pause the whole system at any time.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Rule 3: the server's own message, plus what to do about it. A refusal that
            only prints a code leaves the operator with nowhere to go. */}
        {error && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 2, p: 1.5, borderRadius: '10px', border: '1px solid rgba(198,61,87,0.35)', background: 'rgba(198,61,87,0.08)' }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5, wordBreak: 'break-word' }}>{error}</Typography>
              {errorHint && (
                <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5, mt: 0.5 }}>{errorHint}</Typography>
              )}
            </Box>
          </Box>
        )}

        {result && <ImportResult result={result} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {result ? (
          <Button variant="contained" sx={mintBtnSx} onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outlined" sx={outlinedBtnSx} disabled={busy} onClick={step === 1 ? onClose : (step === 2 ? backToSource : () => setStep(2))}>
              {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            {step === 2 && (
              <Button variant="contained" sx={mintBtnSx} disabled={!canConfirm} onClick={() => setStep(3)}>
                Continue
              </Button>
            )}
            {step === 3 && (
              <Button
                variant="contained"
                sx={mintBtnSx}
                disabled={importing || !canConfirm || !labelReady}
                onClick={runImport}
                startIcon={importing ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
              >
                {importing ? 'Importing…' : `Import ${importCount != null ? importCount.toLocaleString('en-US') + ' ' : ''}contact${importCount === 1 ? '' : 's'}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Status filters the server understands (?status=). "All" sends nothing, which is the
// unfiltered list. Held is first among the narrowing filters because finding a held
// contact is the reason this control exists.
const CONTACT_STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'held', label: 'Held' },
  { key: 'pending_approval', label: 'Pending approval' },
  { key: 'sourced', label: 'Sourced' },
  { key: 'new', label: 'Approved' },
  { key: 'rejected', label: 'Removed' },
];

const ContactsTab = ({ onNavigate }) => {
  const res = useGtmContacts();
  const [importing, setImporting] = useState(false);
  const contacts = res.contacts || [];
  const firstOnPage = res.pageSize ? (res.page - 1) * res.pageSize + 1 : 1;
  const lastOnPage = res.pageSize ? (res.page - 1) * res.pageSize + contacts.length : contacts.length;
  return (
    <Box sx={{ maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Explainer tip="Every contact the system sourced from Apollo or you imported, with its current pipeline state: verified, held (with the reason), or deferred by governance.">
          Every sourced and imported contact, with where it sits in the pipeline.
        </Explainer>
        <Button variant="contained" sx={{ ...mintBtnSx, flexShrink: 0, [MOBILE_MQ]: { minHeight: 44 } }} onClick={() => setImporting(true)}>
          Import contacts
        </Button>
      </Box>

      {/* Server-side status filter. Held is here because paging alone could not reach a
          contact that had aged past the first page. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {CONTACT_STATUS_FILTERS.map((f) => (
          <PresetButton
            key={f.key || 'all'}
            label={f.label}
            selected={res.status === f.key}
            onClick={() => res.setStatusFilter(f.key)}
          />
        ))}
      </Box>
      <Resource
        loading={res.loading && contacts.length === 0}
        error={res.error}
        empty={!res.loading && contacts.length === 0}
        loadingLabel="Loading contacts…"
        emptyLabel="No contacts yet. Import a list to begin."
        onRetry={res.reload}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contacts.map((c) => (
            <ContactCard key={c.id || c._id || c.email} c={c} onNavigate={onNavigate} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
            {res.total != null
              ? `${firstOnPage.toLocaleString('en-US')}–${lastOnPage.toLocaleString('en-US')} of ${res.total.toLocaleString('en-US')}${res.status ? ` ${(CONTACT_STATUS_FILTERS.find((f) => f.key === res.status) || {}).label || res.status}` : ''}`
              : `Showing ${contacts.length.toLocaleString('en-US')}`}
            {res.totalPages != null && res.totalPages > 1 ? ` · page ${res.page} of ${res.totalPages}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }} disabled={res.page <= 1 || res.loading} onClick={res.prev}>Previous</Button>
            <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }} disabled={res.loading || !res.hasNext} onClick={res.next}>Next</Button>
          </Box>
        </Box>
      </Resource>
      {importing && <ImportContactsFlow onClose={() => setImporting(false)} onImported={() => res.reload()} />}
    </Box>
  );
};

// ===== Edit prompts (per-campaign drafting prompts) =====
// Opened from a campaign row. Fetches the campaign's editable prompt(s) on open,
// renders ONE section per entry (label → textarea → updatedAt), and saves each
// section independently via PATCH { key, prompt } with inline success/error. A
// separate, confirmed "Regenerate pending drafts" recomposes drafts only for
// contacts still awaiting approval. Reuses the app's MUI Dialog + ConfirmModal.

// The per-step drafting models an admin may set. MUST match the backend's
// ALLOWED_PROMPT_MODELS allowlist in gtmController.
const PROMPT_MODEL_OPTIONS = ['claude-haiku-4-5', 'claude-sonnet-4-6', 'claude-sonnet-5', 'claude-opus-4-8'];
// Fallback model for the AI-revise call when a step has no model set (the revise endpoint
// requires an allowlisted model). Mirrors the backend GTM_DRAFT_MODEL default.
const DEFAULT_REVISE_MODEL = 'claude-sonnet-4-6';

// Per-step research providers. Keys MUST match the backend RESEARCH_SOURCE_ALLOWLIST
// (gtmDraftPrompts.js): brightdata / exa / serper.
const RESEARCH_SOURCES = [
  { key: 'brightdata', label: 'Bright Data' },
  { key: 'exa', label: 'Exa' },
  { key: 'serper', label: 'Serper' },
];
const RESEARCH_SOURCE_KEYS = RESEARCH_SOURCES.map((s) => s.key);
const DEFAULT_FOLLOWUP_DAY = 3; // mirrors the backend default when a follow-up day is unset

// Extract the unique [bracketed] shortcodes present in a template (order preserved). These
// are the merge tokens that MUST survive an edit or an AI revision.
const SHORTCODE_RE = /\[[^\]]+\]/g;
function extractShortcodes(text) {
  const found = String(text || '').match(SHORTCODE_RE) || [];
  const seen = [];
  found.forEach((t) => {
    if (!seen.includes(t)) seen.push(t);
  });
  return seen;
}

// One shortcode token rendered as a small mono chip (mirrors TokenText's highlight style).
const ShortcodeChip = ({ token }) => (
  <Box
    component="span"
    sx={{ display: 'inline-block', px: '4px', py: '1px', mx: '1px', borderRadius: '4px', background: THEME.mintFaint, border: `1px solid ${THEME.mintDim}`, color: THEME.mint, fontSize: '10px', fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap' }}
  >
    {token}
  </Box>
);

// Shortcode legend: lists the tokens present in the template so the operator always knows
// what must survive an edit / AI revision. Sourced from the template text itself.
const ShortcodeLegend = ({ text }) => {
  const tokens = extractShortcodes(text);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
      <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>
        {tokens.length ? 'Shortcodes that must survive:' : 'No shortcodes in this step.'}
      </Typography>
      {tokens.map((t) => (
        <ShortcodeChip key={t} token={t} />
      ))}
    </Box>
  );
};

// The allowlist model dropdown, shared by the Edit Prompts + Sequence step editors. An
// out-of-allowlist stored model is shown as a disabled "(current)" option so the value maps.
const PromptModelSelect = ({ value, onChange, disabled }) => (
  <Select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    size="small"
    displayEmpty
    renderValue={(v) => (v ? v : 'Default model')}
    sx={{
      fontSize: '12px',
      color: THEME.textPrimary,
      minWidth: 170,
      background: THEME.card,
      '& fieldset': { borderColor: THEME.border },
      '&:hover fieldset': { borderColor: THEME.borderStrong },
      '& .MuiSvgIcon-root': { color: THEME.textMuted },
      '& .MuiSelect-select': { py: '6px' },
    }}
    MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, '& .MuiMenuItem-root': { fontSize: '12px' }, '& .Mui-selected': { background: `${THEME.mintFaint} !important` } } } }}
  >
    {value && !PROMPT_MODEL_OPTIONS.includes(value) && (
      <MenuItem value={value} disabled>
        {value} (current)
      </MenuItem>
    )}
    {PROMPT_MODEL_OPTIONS.map((m) => (
      <MenuItem key={m} value={m}>
        {m}
      </MenuItem>
    ))}
  </Select>
);

// How each source builds its actual query, so the operator can see the exact input that goes
// to Bright Data / Exa / Serper — and which of them the Research focus actually steers. Mirrors
// the backend (gtmEnrichmentService): only Exa blends searchPrompt into its query; Serper
// searches company news and Bright Data fetches the LinkedIn profile from the identity alone.
const RESEARCH_SOURCE_QUERY = {
  exa: { label: 'Exa', usesFocus: true, base: '[name] · [company] · [title]' },
  serper: { label: 'Serper', usesFocus: false, base: 'recent news for [company]' },
  brightdata: { label: 'Bright Data', usesFocus: false, base: 'the LinkedIn profile for [name] at [company]' },
};

// The research row: source toggle chips, a multi-line "Research focus" the operator can review
// and tweak, and a live preview of what each selected source will actually search. Shared by
// both step editors. When `disabled` (the sequence's Research toggle is Off), everything is
// muted and inert to make it obvious it will not fire — values are kept.
const StepResearchRow = ({ sources, onToggle, searchPrompt, onSearchPrompt, disabled }) => {
  const focus = (searchPrompt || '').trim();
  const selected = RESEARCH_SOURCES.filter((s) => sources.includes(s.key));
  return (
    <Box sx={{ mt: 1.5, opacity: disabled ? 0.5 : 1 }}>
      <FieldLabel tip="Which providers gather the evidence used to write this step.">Research sources</FieldLabel>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25, pointerEvents: disabled ? 'none' : 'auto' }}>
        {RESEARCH_SOURCES.map((s) => (
          <SelectChip key={s.key} label={s.label} selected={sources.includes(s.key)} onClick={() => !disabled && onToggle(s.key)} />
        ))}
      </Box>

      <FieldLabel tip="Text you add here is blended into the Exa search query for this step to steer what it looks for. Serper (company news) and Bright Data (LinkedIn profile) search from the contact's identity and do not use this. Leave blank for general research.">
        Research focus (steers Exa)
      </FieldLabel>
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={14}
        size="small"
        disabled={disabled}
        placeholder={'What should this step\'s research dig up? e.g.\nrecent funding rounds or layoffs\nnew HR / benefits / wellbeing programs\nexpansion, new offices, or hiring surges\nESG or CSR initiatives'}
        value={searchPrompt}
        onChange={(e) => onSearchPrompt(e.target.value)}
        InputProps={inputProps}
        sx={{ '& .MuiInputBase-input': { fontSize: 13, color: THEME.textPrimary, lineHeight: 1.6 } }}
      />

      {/* Live preview: the exact query input each SELECTED source will run for this step. */}
      {selected.length > 0 && (
        <Box sx={{ mt: 1, p: 1.25, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
          <Typography sx={{ fontSize: 10, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
            What each source will search
          </Typography>
          {selected.map((s) => {
            const q = RESEARCH_SOURCE_QUERY[s.key];
            if (!q) return null;
            return (
              <Typography key={s.key} sx={{ fontSize: 12, color: THEME.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ fontWeight: 700, color: THEME.textPrimary }}>{q.label}</Box>
                {': '}{q.base}
                {q.usesFocus && focus && (
                  <Box component="span" sx={{ color: THEME.mint }}> + “{focus}”</Box>
                )}
                {q.usesFocus && !focus && (
                  <Box component="span" sx={{ color: THEME.textMuted }}> — add a research focus above to steer it</Box>
                )}
                {!q.usesFocus && (
                  <Box component="span" sx={{ color: THEME.textMuted }}> — searches from the contact's identity; the research focus does not change this</Box>
                )}
              </Typography>
            );
          })}
          <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 0.75, lineHeight: 1.5 }}>
            [name] · [company] · [title] are filled per contact at send time. Everything the sources return is combined into the research brief the opener is written from.
          </Typography>
        </Box>
      )}

      {disabled && (
        <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 0.75 }}>
          Research is off for this sequence, so these will not run. Values are kept.
        </Typography>
      )}
    </Box>
  );
};

// Normalize the /prompts (and /add-step, /delete) responses into ordered unified step
// entries [{ key, stepNumber, subject, body, model, dayOffset, threadMode, researchConfig }].
// The backend returns { steps:[...] } (draftPrompts is a back-compat alias for the SAME
// resolved steps). The unified step has NO `prompt` field — the editable copy is the
// template subject + body (`prompt`/`text` are read only as legacy fallbacks).
function normalizePrompts(data) {
  const raw =
    (data && data.steps != null && data.steps) ||
    (data && data.draftPrompts != null && data.draftPrompts) ||
    (data && data.prompts != null && data.prompts) ||
    data;
  const readEntry = (o, fallbackKey, i) => {
    const stepNumber = o && Number.isFinite(Number(o.stepNumber)) && Number(o.stepNumber) > 0 ? Number(o.stepNumber) : i + 1;
    const rc = (o && o.researchConfig) || {};
    const sources = Array.isArray(rc.sources) ? rc.sources.filter((s) => RESEARCH_SOURCE_KEYS.includes(s)) : RESEARCH_SOURCE_KEYS.slice();
    return {
      key: (o && (o.key || o.id || o.name)) || fallbackKey,
      stepNumber,
      subject: o && typeof o.subject === 'string' ? o.subject : '',
      body: o == null ? '' : o.body != null ? o.body : o.prompt != null ? o.prompt : o.text != null ? o.text : o.value || '',
      model: (o && o.model) || '',
      dayOffset:
        o && Number.isFinite(Number(o.dayOffset)) && Number(o.dayOffset) >= 0
          ? Number(o.dayOffset)
          : stepNumber === 1
          ? 0
          : DEFAULT_FOLLOWUP_DAY,
      threadMode: (o && o.threadMode) || (stepNumber === 1 ? 'new_thread' : 'reply_on_thread'),
      researchConfig: { sources, searchPrompt: typeof rc.searchPrompt === 'string' ? rc.searchPrompt : '' },
    };
  };
  let list;
  if (Array.isArray(raw)) {
    list = raw.map((p, i) => readEntry(p, String(i), i));
  } else if (raw && typeof raw === 'object') {
    list = Object.entries(raw).map(([key, v], i) => readEntry(v && typeof v === 'object' ? v : { body: v }, key, i));
  } else {
    list = [];
  }
  return list.sort((a, b) => a.stepNumber - b.stepNumber);
}

// One unified sequence step: a step header (Step N + editable day offset + remove), a
// thread-mode toggle (locked to New thread on step 1), the template SUBJECT + BODY (with a
// shortcode legend), a model dropdown, and a research row. The step has no `prompt` — the
// editable copy is subject + body. Saves the full entry through the campaigns/:id/prompts
// PATCH (the same sequence doc the Sequences tab edits). Dirty-tracks every field.
const PromptSection = ({ campaignId, entry, onRemove }) => {
  const isStep1 = entry.stepNumber === 1;
  const initialSources =
    entry.researchConfig && Array.isArray(entry.researchConfig.sources)
      ? entry.researchConfig.sources
      : RESEARCH_SOURCE_KEYS.slice();
  const initialFocus = (entry.researchConfig && entry.researchConfig.searchPrompt) || '';

  const [subject, setSubject] = useState(entry.subject || '');
  const [baselineSubject, setBaselineSubject] = useState(entry.subject || '');
  const [body, setBody] = useState(entry.body || '');
  const [baselineBody, setBaselineBody] = useState(entry.body || '');
  const [model, setModel] = useState(entry.model || '');
  const [baselineModel, setBaselineModel] = useState(entry.model || '');
  const [dayOffset, setDayOffset] = useState(String(entry.dayOffset ?? 0));
  const [baselineDayOffset, setBaselineDayOffset] = useState(String(entry.dayOffset ?? 0));
  const [threadMode, setThreadMode] = useState(entry.threadMode || (isStep1 ? 'new_thread' : 'reply_on_thread'));
  const [baselineThreadMode, setBaselineThreadMode] = useState(entry.threadMode || (isStep1 ? 'new_thread' : 'reply_on_thread'));
  const [sources, setSources] = useState(initialSources);
  const [baselineSources, setBaselineSources] = useState(initialSources);
  const [searchPrompt, setSearchPrompt] = useState(initialFocus);
  const [baselineSearchPrompt, setBaselineSearchPrompt] = useState(initialFocus);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type:'success'|'error', message } | null

  const isReply = threadMode === 'reply_on_thread';
  const effectiveSubject = isReply ? '' : subject; // a reply carries no subject
  const clearStatus = () => setStatus((s) => (s ? null : s));
  const sourcesKey = (arr) => arr.slice().sort().join(',');
  const dirty =
    effectiveSubject !== baselineSubject ||
    body !== baselineBody ||
    model !== baselineModel ||
    dayOffset !== baselineDayOffset ||
    threadMode !== baselineThreadMode ||
    searchPrompt !== baselineSearchPrompt ||
    sourcesKey(sources) !== sourcesKey(baselineSources);

  const toggleSource = (k) => {
    setSources((prev) => (prev.includes(k) ? prev.filter((s) => s !== k) : [...prev, k]));
    clearStatus();
  };

  const save = async () => {
    const dayNum = parseInt(dayOffset, 10);
    if (!Number.isInteger(dayNum) || dayNum < 0) {
      setStatus({ type: 'error', message: 'Day offset must be a whole number ≥ 0.' });
      return;
    }
    if (!body.trim()) {
      setStatus({ type: 'error', message: 'The step body cannot be empty.' });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      await updateCampaignPrompt(campaignId, {
        key: entry.key,
        subject: isReply ? '' : subject,
        body,
        model: model || undefined,
        dayOffset: dayNum,
        threadMode,
        researchConfig: { sources, searchPrompt },
      });
      // Baselines move to the saved values so the section isn't "dirty" after a save.
      setBaselineSubject(isReply ? '' : subject);
      setBaselineBody(body);
      setBaselineModel(model);
      setDayOffset(String(dayNum));
      setBaselineDayOffset(String(dayNum));
      setBaselineThreadMode(threadMode);
      setBaselineSources(sources);
      setBaselineSearchPrompt(searchPrompt);
      setStatus({ type: 'success', message: 'Saved.' });
    } catch (err) {
      const msg =
        err && err.code === 'no_sequence_ref'
          ? 'This campaign has no sequence attached. Set the campaign\'s sequence before editing steps.'
          : (err && err.message) || 'Could not save this step.';
      setStatus({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ borderTop: `1px solid ${THEME.border}`, pt: 2, mt: 2, '&:first-of-type': { borderTop: 'none', pt: 0, mt: 0 } }}>
      {/* Step header: Step N · Day +X (editable) · remove */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>Step {entry.stepNumber}</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>·</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Day +</Typography>
          <TextField
            type="number"
            size="small"
            value={dayOffset}
            disabled={isStep1 || saving}
            onChange={(e) => {
              setDayOffset(e.target.value);
              clearStatus();
            }}
            InputProps={inputProps}
            inputProps={{ min: 0, step: 1, 'aria-label': `Day offset for step ${entry.stepNumber}` }}
            sx={{ width: 76, '& .MuiInputBase-input': { py: '6px', fontSize: 13, color: THEME.textPrimary } }}
          />
          {isStep1 && <InfoTip text="Step 1 is the opener; it sends on day 0." size={13} />}
        </Box>
        <Box sx={{ flex: 1 }} />
        {!isStep1 && onRemove && (
          <Tooltip title="Remove this step" arrow>
            <IconButton
              size="small"
              disabled={saving}
              onClick={onRemove}
              sx={{ color: THEME.textMuted, '&:hover': { color: THEME.pink, background: 'rgba(198,61,87,0.1)' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Thread mode: locked to New thread on step 1, a toggle otherwise. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Thread</Typography>
        {isStep1 ? (
          <Badge tone="gray">New thread</Badge>
        ) : (
          <>
            <SelectChip label="New thread" selected={threadMode === 'new_thread'} onClick={() => { setThreadMode('new_thread'); clearStatus(); }} />
            <SelectChip label="Reply on thread" selected={threadMode === 'reply_on_thread'} onClick={() => { setThreadMode('reply_on_thread'); clearStatus(); }} />
          </>
        )}
      </Box>

      {/* Subject (a reply carries none). */}
      <Box sx={{ mb: 1.25 }}>
        <FieldLabel tip="The email subject. A reply-on-thread step sends no subject.">Subject</FieldLabel>
        <TextField
          fullWidth
          size="small"
          disabled={isReply || saving}
          value={isReply ? '' : subject}
          placeholder={isReply ? 'Reply on the running thread, no subject' : 'Subject line'}
          onChange={(e) => {
            setSubject(e.target.value);
            clearStatus();
          }}
          InputProps={inputProps}
          sx={{ '& .MuiInputBase-input': { fontSize: 13, color: THEME.textPrimary } }}
        />
      </Box>

      {/* Body template + shortcode legend. */}
      <FieldLabel tip="The email body template. Keep every [bracketed] shortcode — they are merged per contact at send.">Body</FieldLabel>
      <TextField
        fullWidth
        multiline
        minRows={5}
        maxRows={20}
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          clearStatus();
        }}
        InputProps={inputProps}
        sx={{ '& .MuiInputBase-root': { fontSize: 13, color: THEME.textPrimary, lineHeight: 1.6 } }}
      />
      <ShortcodeLegend text={body} />

      {/* Model + research. */}
      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Model</Typography>
        <PromptModelSelect value={model} disabled={saving} onChange={(v) => { setModel(v); clearStatus(); }} />
      </Box>
      <StepResearchRow
        sources={sources}
        onToggle={toggleSource}
        searchPrompt={searchPrompt}
        onSearchPrompt={(v) => { setSearchPrompt(v); clearStatus(); }}
        disabled={saving}
      />

      {/* Save + inline status. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          sx={mintBtnSx}
          disabled={saving || !dirty}
          onClick={save}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {status && status.type === 'success' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: THEME.mint }} />
            <Typography sx={{ fontSize: '12px', color: THEME.mint }}>{status.message}</Typography>
          </Box>
        )}
        {status && status.type === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{status.message}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const EditPromptsDialog = ({ campaign, onClose }) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // ConfirmModal config (regenerate OR remove-step)
  const [regen, setRegen] = useState({ status: 'idle' }); // idle|running|done|error
  const [stepBusy, setStepBusy] = useState(false); // add/remove in flight
  const [stepError, setStepError] = useState(null);
  const [seqMeta, setSeqMeta] = useState({ sequenceId: null, sequenceStatus: null, source: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCampaignPrompts(campaign.id);
      setSeqMeta({
        sequenceId: (data && data.sequenceId) || null,
        sequenceStatus: (data && data.sequenceStatus) || null,
        source: (data && data.source) || null,
      });
      setEntries(normalizePrompts(data));
    } catch (err) {
      setError((err && err.message) || 'Could not load prompts.');
      setEntries(null);
    } finally {
      setLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Add-step / remove-step both return the full { draftPrompts:[...] } array. Re-normalizing
  // it re-renders every section; because each PromptSection is keyed by entry.key, existing
  // sections keep their unsaved local edits and only the added/removed one changes.
  // no_sequence_ref (409) means the campaign has no sequence attached; the add/delete
  // endpoints can't write. Surface it with a hint rather than a raw message.
  const stepErrMessage = (err, fallback) =>
    err && err.code === 'no_sequence_ref'
      ? 'This campaign has no sequence attached. Set the campaign\'s sequence before adding or removing steps.'
      : (err && err.message) || fallback;

  const addStep = async () => {
    setStepBusy(true);
    setStepError(null);
    try {
      const res = await addCampaignPromptStep(campaign.id);
      setEntries(normalizePrompts(res));
    } catch (err) {
      setStepError(stepErrMessage(err, 'Could not add a step.'));
    } finally {
      setStepBusy(false);
    }
  };

  const doRemoveStep = async (key) => {
    setStepBusy(true);
    setStepError(null);
    try {
      const res = await deleteCampaignPromptStep(campaign.id, key);
      setEntries(normalizePrompts(res));
    } catch (err) {
      setStepError(stepErrMessage(err, 'Could not remove this step.'));
    } finally {
      setStepBusy(false);
    }
  };

  const askRemoveStep = (entry) =>
    setConfirm({
      title: `Remove step ${entry.stepNumber}?`,
      body: `This deletes step ${entry.stepNumber}${entry.subject ? ` ("${entry.subject}")` : ''} from ${campaign.name}. Step 1 (the opener) always stays; only this follow-up is removed. This cannot be undone.`,
      confirmLabel: 'Remove step',
      tone: 'danger',
      onConfirm: () => doRemoveStep(entry.key),
    });

  const runRegenerate = async () => {
    setRegen({ status: 'running' });
    try {
      const res = await regenerateCampaignDrafts(campaign.id);
      const n = res && (res.regenerated != null ? res.regenerated : res.count != null ? res.count : res.updated);
      setRegen({ status: 'done', count: typeof n === 'number' ? n : null });
    } catch (err) {
      setRegen({ status: 'error', message: (err && err.message) || 'Could not regenerate drafts.' });
    }
  };

  const askRegenerate = () =>
    setConfirm({
      title: 'Regenerate pending drafts?',
      body: `This recomposes ALL sequence steps for contacts in ${campaign.name} that are still awaiting approval. Contacts you've already approved, rejected, or that have been sent are not affected.`,
      confirmLabel: 'Regenerate pending drafts',
      tone: 'mint',
      onConfirm: runRegenerate,
    });

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>Edit prompts · {campaign.name}</span>
        {seqMeta.sequenceStatus && (
          <Badge tone={SEQ_STATUS_META[seqMeta.sequenceStatus] ? SEQ_STATUS_META[seqMeta.sequenceStatus].tone : 'muted'}>
            {SEQ_STATUS_META[seqMeta.sequenceStatus] ? SEQ_STATUS_META[seqMeta.sequenceStatus].label : seqMeta.sequenceStatus}
          </Badge>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2 }}>
          Each step of this campaign's outbound sequence — the SAME steps the Sequences tab edits. Edit the copy, timing, threading, and research per step, and save each on its own.
        </Typography>

        <Resource
          loading={loading}
          error={error}
          empty={!loading && entries != null && entries.length === 0}
          loadingLabel="Loading prompts…"
          emptyLabel="No editable prompts for this campaign."
          onRetry={load}
        >
          <Box>
            {(entries || []).map((entry) => (
              <PromptSection
                key={entry.key}
                campaignId={campaign.id}
                entry={entry}
                onRemove={entry.stepNumber === 1 ? undefined : () => askRemoveStep(entry)}
              />
            ))}
          </Box>
        </Resource>

        {/* Add step. */}
        {entries != null && !error && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              sx={outlinedBtnSx}
              disabled={stepBusy || loading}
              onClick={addStep}
              startIcon={stepBusy ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <AddIcon sx={{ fontSize: 16 }} />}
            >
              {stepBusy ? 'Working…' : 'Add step'}
            </Button>
            {stepError && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
                <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{stepError}</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Regenerate pending drafts — separate, clearly labeled, confirmed. */}
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${THEME.borderStrong}` }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mb: 1, lineHeight: 1.5 }}>
            Apply the current prompts to contacts still awaiting approval. This re-drafts every step for those pending contacts only — approved and sent contacts are left untouched.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              sx={outlinedBtnSx}
              disabled={regen.status === 'running'}
              onClick={askRegenerate}
              startIcon={regen.status === 'running' ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            >
              {regen.status === 'running' ? 'Regenerating…' : 'Regenerate pending drafts'}
            </Button>
            {regen.status === 'done' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: THEME.mint }} />
                <Typography sx={{ fontSize: '12px', color: THEME.mint }}>
                  {regen.count != null ? `Regenerated ${regen.count.toLocaleString('en-US')} pending draft${regen.count === 1 ? '' : 's'}.` : 'Pending drafts regenerated.'}
                </Typography>
              </Box>
            )}
            {regen.status === 'error' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
                <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{regen.message}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Dialog>
  );
};

// ===== Instantly campaign picker (per-campaign wire field) =====
// The GTM campaign row wires to an Instantly campaign. The dropdown is populated from
// GET /api/gtm/instantly/campaigns; selecting one writes it through PATCH
// /campaigns/:id/instantly-campaign. When the Instantly key is unset (424) or the list
// fails, a "paste ID manually" free-text fallback covers the wiring.

// ─── GTM-OPERATOR-CLARITY-01: Instantly status translation layer ─────────────
// Every Instantly campaign state resolves to operator-facing copy (a plain badge label + a
// one-sentence explanation shown on hover) and, elsewhere, to exactly one action. Nothing
// renders as an unexplained raw string: an unknown status still shows its raw value in an
// amber badge (Rule 3).

// Classify an Instantly status (numeric v2 code OR string) into a stable kind. Includes the
// two health states T6 now reports: accounts_unhealthy and bounce_protect.
function instantlyStatusKind(status) {
  const s = String(status == null ? '' : status).toLowerCase().trim();
  if (s === '1' || s === 'active' || s === 'running') return 'active';
  if (s === '2' || s === 'paused') return 'paused';
  if (s === '0' || s === 'draft') return 'draft';
  if (s === '3' || s === 'completed' || s === 'complete') return 'completed';
  if (s === 'accounts_unhealthy') return 'accounts_unhealthy';
  if (s === 'bounce_protect') return 'bounce_protect';
  return 'other';
}

// Operator-facing badge per kind (tone + plain label). Unknown handled in instantlyStatusBadge.
const INSTANTLY_BADGE = {
  active: { tone: 'mint', label: 'Sending' },
  paused: { tone: 'amber', label: 'Paused' },
  draft: { tone: 'gray', label: 'Not started' },
  completed: { tone: 'muted', label: 'Finished' },
  accounts_unhealthy: { tone: 'pink', label: 'Mailbox problem' },
  bounce_protect: { tone: 'pink', label: 'Auto-paused: bounces' },
};

// One-sentence plain-English explanation per kind, shown on hover under each badge.
const INSTANTLY_STATUS_HELP = {
  active: 'This campaign is live and sending on its schedule.',
  paused: 'Sending is paused. Activate it to resume.',
  draft: 'This campaign has not started sending yet.',
  completed: 'This campaign has finished sending to its list.',
  accounts_unhealthy: 'The email accounts attached to this campaign have a deliverability issue (warmup incomplete, disconnected, or flagged). Sending is unreliable until fixed in Instantly.',
  bounce_protect: 'Instantly paused this campaign because too many emails bounced. Resuming without cleaning the list risks burning your sending domains.',
};

// { tone, label } for a status. Unknown -> the raw string in an amber badge, never invisible.
function instantlyStatusBadge(status) {
  const meta = INSTANTLY_BADGE[instantlyStatusKind(status)];
  if (meta) return meta;
  const raw = String(status == null ? '' : status).trim();
  return { tone: 'amber', label: raw || 'Unknown' };
}

// Plain-English line for a status; unknown gets an honest generic fallback (never a bare label).
function instantlyStatusHelp(status) {
  return INSTANTLY_STATUS_HELP[instantlyStatusKind(status)]
    || 'A status reported by Instantly we do not have a plain description for yet. Open Instantly to see the details.';
}

// General Instantly app URL. A per-campaign deep-link path is NOT verified against Instantly,
// so we open the app generally (correct beats specific).
const INSTANTLY_APP_URL = 'https://app.instantly.ai';
const openInstantly = () => window.open(INSTANTLY_APP_URL, '_blank', 'noopener,noreferrer');

// ─── GTM-HOME-SYNTHESIS-01: one synthesized status per campaign ──────────────
// synthesizeCampaignState folds the GTM pipeline state and the linked Instantly delivery
// state into ONE operator-facing status with at most one action. Priority-ordered, first
// match wins. Pure: no side effects, no fetches. `counts.pendingApprovals` may be null when
// a per-campaign count isn't available client-side (the sentence degrades gracefully).
// Returns { key, badge, tone, sentence, action } where action = { kind, label } | null.
function synthesizeCampaignState(gtmCampaign, linkedEntry, counts) {
  const c = gtmCampaign || {};
  const gtm = String(c.status || '').toLowerCase().trim();
  const pipeKind = linkedEntry ? instantlyStatusKind(statusOf(linkedEntry)) : null;
  const pipeName = (linkedEntry && linkedEntry.name) || 'Instantly';
  const pending = counts && counts.pendingApprovals != null ? Number(counts.pendingApprovals) : null;
  const activeish = gtm === 'active' || gtm === 'processing' || gtm === 'ready';

  // 1. GTM pending_approval with emails still waiting on the operator.
  if (gtm === 'pending_approval' && (pending == null || pending > 0)) {
    const sentence = pending != null
      ? `${pending.toLocaleString('en-US')} ${pending === 1 ? 'email needs' : 'emails need'} your approval.`
      : 'Emails need your approval.';
    return { key: 'waiting_approval', badge: 'Waiting on you', tone: 'amber', sentence, action: { kind: 'review', label: 'Review' } };
  }
  // 2. GTM held for review.
  if (gtm === 'held') {
    return { key: 'waiting_held', badge: 'Waiting on you', tone: 'amber', sentence: 'Contacts were held for review.', action: { kind: 'reviewHeld', label: 'Review held' } };
  }
  // 3. Delivery stopped by bounce protection.
  if (pipeKind === 'bounce_protect') {
    return { key: 'bounce_protect', badge: 'Stopped by bounces', tone: 'pink', sentence: 'Instantly paused delivery to protect your domains. Clean the list before resuming.', action: { kind: 'reviewInstantly', label: 'Review in Instantly' } };
  }
  // 4. Sending accounts unhealthy.
  if (pipeKind === 'accounts_unhealthy') {
    return { key: 'accounts_unhealthy', badge: 'Mailbox problem', tone: 'pink', sentence: 'The sending accounts on this pipe have issues. Fix in Instantly.', action: { kind: 'fixInstantly', label: 'Fix in Instantly' } };
  }
  // 5. Actively delivering (GTM go-state and a live pipe).
  if (activeish && pipeKind === 'active') {
    return { key: 'sending', badge: 'Sending', tone: 'mint', sentence: `Delivering via ${pipeName}.`, action: { kind: 'pause', label: 'Pause' } };
  }
  // 6. Ready/active but no delivery pipe linked yet.
  if (activeish && !linkedEntry) {
    return { key: 'needs_pipe', badge: 'Needs a delivery pipe', tone: 'amber', sentence: 'Approved emails have nowhere to send. Link an Instantly campaign.', action: { kind: 'linkPipe', label: 'Link a pipe' } };
  }
  // 7. Delivery pipe paused.
  if (pipeKind === 'paused') {
    return { key: 'paused', badge: 'Delivery paused', tone: 'gray', sentence: 'Delivery is paused. Resume when you are ready.', action: { kind: 'resume', label: 'Resume' } };
  }
  // 8. Finished.
  if (gtm === 'done' || gtm === 'completed' || pipeKind === 'completed') {
    return { key: 'finished', badge: 'Finished', tone: 'muted', sentence: 'This campaign has finished.', action: null };
  }
  // 9. Fallback: never blank. Show both raw states in an amber badge (Rule 3).
  const rawPipe = linkedEntry ? String(statusOf(linkedEntry)) : 'no pipe';
  return {
    key: 'unknown',
    badge: `${gtm || 'unknown'} / ${rawPipe}`,
    tone: 'amber',
    sentence: `Unrecognized state. Campaign: ${gtm || 'unknown'}; delivery: ${rawPipe}. Expand for details.`,
    action: null,
  };
}

// Section order for the Home campaigns list (Waiting on you -> red -> Sending -> Needs a
// pipe -> Paused -> Finished -> Other). A section renders only when it has campaigns.
const SYNTH_SECTIONS = [
  { label: 'Waiting on you', keys: ['waiting_approval', 'waiting_held'] },
  { label: 'Delivery problems', keys: ['bounce_protect', 'accounts_unhealthy'] },
  { label: 'Sending', keys: ['sending'] },
  { label: 'Needs a delivery pipe', keys: ['needs_pipe'] },
  { label: 'Paused', keys: ['paused'] },
  { label: 'Finished', keys: ['finished'] },
  { label: 'Other', keys: ['unknown'] },
];

// The linked Instantly entry for a GTM campaign (or null), given an id->entry map.
const linkedEntryOf = (c, byId) => (c && c.instantlyCampaignId && byId && byId[c.instantlyCampaignId]) || null;

// The canonical status for a T6 campaign-list entry: the string `statusLabel`
// ("active"/"paused"/"draft") when present, else the raw numeric v2 `status` code as a
// fallback. instantlyStatusKind handles both forms.
const statusOf = (entry) =>
  (entry && entry.statusLabel != null && String(entry.statusLabel).trim() !== '')
    ? entry.statusLabel
    : (entry && entry.status);

// Small colored status dot (uses a THEME token, no new styling system).
const StatusDot = ({ color }) => (
  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
);

// One option / rendered value: dot + name + translated status word (e.g. "CHRO Outreach,
// Sending"). The dot color and word both come from the operator-facing translation layer.
const InstantlyOption = ({ name, status }) => {
  const badge = instantlyStatusBadge(status);
  const dotColor = (TONE_MAP[badge.tone] || TONE_MAP.muted).color;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
      <StatusDot color={dotColor} />
      <Typography sx={{ fontSize: 13, color: THEME.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name || '(unnamed)'}
      </Typography>
      <Typography sx={{ fontSize: 12, color: THEME.textMuted, flexShrink: 0 }}>{badge.label}</Typography>
    </Box>
  );
};

// The desync case: a wired id that is NOT in the Instantly list (deleted on their side).
const UnknownWired = ({ id }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
    <StatusDot color={THEME.textMuted} />
    <Typography sx={{ fontSize: 13, color: THEME.amber, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      Unknown campaign ({id})
    </Typography>
  </Box>
);

const instantlySelectSx = {
  fontSize: '13px',
  color: THEME.textPrimary,
  background: THEME.card,
  '& fieldset': { borderColor: THEME.border },
  '&:hover fieldset': { borderColor: THEME.borderStrong },
  '& .MuiSvgIcon-root': { color: THEME.textMuted },
  '& .MuiSelect-select': { py: '7px' },
};
const instantlyMenuProps = {
  PaperProps: {
    sx: {
      background: THEME.cardRaised,
      color: THEME.textPrimary,
      border: `1px solid ${THEME.borderStrong}`,
      '& .MuiMenuItem-root': { fontSize: '13px' },
      '& .Mui-selected': { background: `${THEME.mintFaint} !important` },
    },
  },
};

// Per-campaign Instantly wire field. `instantly` is the shared list state fetched once
// by the tab: { list, loading, error, keyMissing, reload }.
const InstantlyWireField = ({ campaign, instantly }) => {
  const [wiredId, setWiredId] = useState(campaign.instantlyCampaignId || '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type:'success'|'error', message } | null
  const [manual, setManual] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [confirm, setConfirm] = useState(null); // ConfirmModal config for provision / live toggle
  const [provisioning, setProvisioning] = useState(false);
  const [provisionStatus, setProvisionStatus] = useState(null); // { type, message } | null
  const [liveBusy, setLiveBusy] = useState(false); // activate/pause in flight
  const [liveError, setLiveError] = useState(null); // verbatim API reason on failure
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState(null); // verbatim API reason on failure

  const { list, loading, error, keyMissing } = instantly;
  const idOf = (x) => x.id || x._id;
  const known = list.find((x) => idOf(x) === wiredId);
  // Desync: an id is wired, the list loaded fine, but the id isn't in it.
  const unknownWired = !!wiredId && !known && !loading && !keyMissing && !error;
  // When the list can't load (key unset, or a fetch error with nothing to pick), the
  // manual free-text field is the only path — force it open.
  const forceManual = keyMissing || (!!error && list.length === 0);
  const showManual = manual || forceManual;
  // Provision & activate: shown only when wired, in the list, and safe to activate. Never on
  // active, and never on the red health states (accounts_unhealthy / bounce_protect) — pushing
  // a campaign live from here past a mailbox issue or bounce protection is a footgun.
  const knownKind = known ? instantlyStatusKind(statusOf(known)) : null;
  const canProvision = !!known && !['active', 'accounts_unhealthy', 'bounce_protect'].includes(knownKind);
  // The API returns mailboxCount; 0 means nothing is attached to send through.
  const noMailboxes = !!known && Number(known.mailboxCount) === 0;

  const save = async (rawId) => {
    const value = String(rawId || '').trim();
    if (!value) {
      setStatus({ type: 'error', message: 'Enter an Instantly campaign id.' });
      return;
    }
    setSaving(true);
    setStatus(null);
    setProvisionStatus(null);
    try {
      await wireInstantlyCampaign(campaign.id, value);
      setWiredId(value);
      setManualValue('');
      setStatus({ type: 'success', message: 'Wired.' });
    } catch (err) {
      setStatus({ type: 'error', message: (err && err.message) || 'Could not wire this campaign.' });
    } finally {
      setSaving(false);
    }
  };

  const runProvision = async () => {
    setProvisioning(true);
    setProvisionStatus(null);
    try {
      await provisionInstantlyCampaign(wiredId);
      setProvisionStatus({ type: 'success', message: 'Provisioned and activated on Instantly.' });
      if (instantly.reload) instantly.reload(); // refresh so the status dot/word update
    } catch (err) {
      setProvisionStatus({ type: 'error', message: (err && err.message) || 'Could not provision this campaign.' });
    } finally {
      setProvisioning(false);
    }
  };

  const askProvision = () =>
    setConfirm({
      title: 'Provision & activate on Instantly?',
      body: `This sets the Instantly sequence steps from ${campaign.name}'s prompts, applies a weekday sending schedule, and activates the campaign on Instantly.`,
      confirmLabel: 'Provision & activate',
      tone: 'mint',
      onConfirm: runProvision,
    });

  // Live status control: the linked campaign's kind drives whether we offer Activate
  // (paused/draft) or Pause (active). Failures surface the API's verbatim reason inline.
  const liveKind = knownKind;
  const liveName = (known && known.name) || campaign.name;

  const runLive = async (action) => {
    setLiveBusy(true);
    setLiveError(null);
    try {
      if (action === 'activate') await activateInstantlyCampaign(wiredId);
      else await pauseInstantlyCampaign(wiredId);
      if (instantly.reload) instantly.reload(); // refresh so the badge/word update
    } catch (err) {
      setLiveError((err && err.message) || `Could not ${action} this campaign in Instantly.`);
    } finally {
      setLiveBusy(false);
    }
  };

  const askActivate = () =>
    setConfirm({
      title: 'Activate in Instantly?',
      body: `This sets "${liveName}" live in Instantly. It will begin sending on its schedule.`,
      confirmLabel: 'Activate',
      tone: 'mint',
      onConfirm: () => runLive('activate'),
    });
  const askPause = () =>
    setConfirm({
      title: 'Pause in Instantly?',
      body: `This pauses "${liveName}" in Instantly. Sending stops until you activate it again.`,
      confirmLabel: 'Pause',
      tone: 'mint',
      onConfirm: () => runLive('pause'),
    });

  // Unlink clears the campaign's Instantly link. Wired the same way as the linker; the current
  // backend rejects an empty id, so the reason surfaces verbatim (never a faked local clear).
  const runUnlink = async () => {
    setUnlinking(true);
    setUnlinkError(null);
    try {
      await unlinkInstantlyCampaign(campaign.id);
      setWiredId('');
      if (instantly.reload) instantly.reload();
    } catch (err) {
      setUnlinkError((err && err.message) || 'Could not unlink this delivery pipe.');
    } finally {
      setUnlinking(false);
    }
  };
  const askUnlink = () =>
    setConfirm({
      title: 'Unlink this delivery pipe?',
      body: 'Approved emails will hold until a new pipe is linked.',
      confirmLabel: 'Unlink',
      tone: 'danger',
      onConfirm: runUnlink,
    });

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${THEME.border}` }}>
      <FieldLabel tip="The Instantly campaign this list sends through. Pick it from your Instantly account, or paste its id if the list can't load.">
        Instantly campaign
      </FieldLabel>

      {/* Live status of the LINKED campaign as a badge, with lastSyncedAt as a subtle caption
          when the payload carries it. Shown only once a campaign is wired and in the list. */}
      {known && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Tooltip title={instantlyStatusHelp(statusOf(known))} arrow placement="top" {...tipSlotProps}>
            <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
              <Badge tone={instantlyStatusBadge(statusOf(known)).tone}>{instantlyStatusBadge(statusOf(known)).label}</Badge>
            </Box>
          </Tooltip>
          {known.lastSyncedAt && (
            <Typography sx={{ fontSize: 11, color: THEME.textMuted }}>Synced {fmtWhen(known.lastSyncedAt)}</Typography>
          )}
        </Box>
      )}

      {/* Dropdown (hidden when manual entry is forced by a missing key / failed list). */}
      {!forceManual &&
        (loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <CircularProgress size={14} sx={{ color: THEME.mint }} />
            <Typography sx={{ fontSize: 12, color: THEME.textMuted }}>Loading campaigns…</Typography>
          </Box>
        ) : (
          <Select
            value={wiredId && (known || unknownWired) ? wiredId : ''}
            onChange={(e) => save(e.target.value)}
            disabled={saving}
            size="small"
            displayEmpty
            fullWidth
            renderValue={(val) => {
              if (!val) return <Typography sx={{ fontSize: 13, color: THEME.textMuted }}>Select an Instantly campaign</Typography>;
              const item = list.find((x) => idOf(x) === val);
              return item ? <InstantlyOption name={item.name} status={statusOf(item)} /> : <UnknownWired id={val} />;
            }}
            sx={instantlySelectSx}
            MenuProps={instantlyMenuProps}
          >
            {list.map((x) => (
              <MenuItem key={idOf(x)} value={idOf(x)}>
                <InstantlyOption name={x.name} status={statusOf(x)} />
              </MenuItem>
            ))}
            {/* Keep the wired-but-missing id selectable so MUI's value maps (no warning). */}
            {unknownWired && (
              <MenuItem value={wiredId} disabled>
                <UnknownWired id={wiredId} />
              </MenuItem>
            )}
          </Select>
        ))}

      {/* Desync warning. */}
      {unknownWired && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.amber, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: THEME.amber, lineHeight: 1.4 }}>
            This id isn't in your Instantly account (it may have been deleted there). Pick another campaign or paste a new id.
          </Typography>
        </Box>
      )}

      {/* Non-424 list error. */}
      {!keyMissing && error && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{error} — paste the id manually below.</Typography>
        </Box>
      )}

      {/* 424: key unset. */}
      {keyMissing && (
        <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 0.25 }}>Set the Instantly key to load campaigns.</Typography>
      )}

      {/* Provision & activate — shown when the wired campaign isn't already Active. */}
      {canProvision && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            sx={outlinedBtnSx}
            disabled={provisioning || saving}
            onClick={askProvision}
            startIcon={provisioning ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          >
            {provisioning ? 'Provisioning…' : 'Provision & activate'}
          </Button>
          {provisionStatus && provisionStatus.type === 'success' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 14, color: THEME.mint }} />
              <Typography sx={{ fontSize: 11, color: THEME.mint }}>{provisionStatus.message}</Typography>
            </Box>
          )}
          {provisionStatus && provisionStatus.type === 'error' && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{provisionStatus.message}</Typography>
            </Box>
          )}
        </Box>
      )}
      {/* Post-provision success when the button has since hidden (status became Active). */}
      {!canProvision && provisionStatus && provisionStatus.type === 'success' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: THEME.mint }} />
          <Typography sx={{ fontSize: 11, color: THEME.mint }}>{provisionStatus.message}</Typography>
        </Box>
      )}

      {/* One action per live state, honest about where it lives. Active -> Pause. Paused/Not
          started -> Activate (disabled with a reason when no mailboxes are attached). The red
          health states do NOT offer Activate; they link out to Instantly, where the fix lives.
          Both Pause/Activate are behind a confirm; a failure reason shows verbatim. */}
      {known && liveKind === 'active' && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            sx={outlinedBtnSx}
            disabled={liveBusy}
            onClick={askPause}
            startIcon={liveBusy ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
          >
            {liveBusy ? 'Pausing…' : 'Pause'}
          </Button>
          {liveError && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{liveError}</Typography>
            </Box>
          )}
        </Box>
      )}
      {known && (liveKind === 'paused' || liveKind === 'draft') && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            sx={outlinedBtnSx}
            disabled={liveBusy || noMailboxes}
            onClick={askActivate}
            startIcon={liveBusy ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
          >
            {liveBusy ? 'Activating…' : 'Activate in Instantly'}
          </Button>
          {noMailboxes && (
            <Typography sx={{ fontSize: 11, color: THEME.textMuted, lineHeight: 1.4 }}>
              No mailboxes attached. Attach in Instantly first.
            </Typography>
          )}
          {liveError && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{liveError}</Typography>
            </Box>
          )}
        </Box>
      )}
      {known && liveKind === 'accounts_unhealthy' && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.5, mb: 0.75 }}>{INSTANTLY_STATUS_HELP.accounts_unhealthy}</Typography>
          <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={openInstantly} startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}>
            Fix mailboxes in Instantly
          </Button>
        </Box>
      )}
      {known && liveKind === 'bounce_protect' && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.5, mb: 0.75 }}>{INSTANTLY_STATUS_HELP.bounce_protect}</Typography>
          <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={openInstantly} startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}>
            Review in Instantly
          </Button>
        </Box>
      )}

      {/* Manual-paste toggle (only meaningful when the dropdown is available). */}
      {!forceManual && (
        <Box
          component="button"
          type="button"
          onClick={() => setManual((m) => !m)}
          sx={{ mt: 0.75, background: 'none', border: 'none', p: 0, cursor: 'pointer', color: THEME.textMuted, fontSize: 11, textDecoration: 'underline', '&:hover': { color: THEME.mint } }}
        >
          {showManual ? 'Hide manual entry' : 'Paste ID manually'}
        </Box>
      )}

      {/* Free-text fallback. */}
      {showManual && (
        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Instantly campaign id (UUID)"
            value={manualValue}
            onChange={(e) => {
              setManualValue(e.target.value);
              if (status) setStatus(null);
            }}
            InputProps={inputProps}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="outlined"
            size="small"
            sx={outlinedBtnSx}
            disabled={saving || !manualValue.trim()}
            onClick={() => save(manualValue)}
            startIcon={saving ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : null}
          >
            {saving ? 'Saving…' : 'Save id'}
          </Button>
        </Box>
      )}

      {/* The current wired id, for reference, plus Unlink (hidden while manual entry shows). */}
      {wiredId && !showManual && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 11, color: THEME.textMuted, wordBreak: 'break-all', flex: '1 1 auto', minWidth: 0 }}>Wired id: {wiredId}</Typography>
          <Box
            component="button"
            type="button"
            onClick={askUnlink}
            disabled={unlinking}
            sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', color: THEME.textMuted, fontSize: 11, textDecoration: 'underline', flexShrink: 0, '&:hover': { color: THEME.pink } }}
          >
            {unlinking ? 'Unlinking…' : 'Unlink'}
          </Box>
        </Box>
      )}
      {unlinkError && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{unlinkError}</Typography>
        </Box>
      )}

      {/* Inline success / error. */}
      {status && status.type === 'success' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: THEME.mint }} />
          <Typography sx={{ fontSize: 11, color: THEME.mint }}>{status.message}</Typography>
        </Box>
      )}
      {status && status.type === 'error' && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{status.message}</Typography>
        </Box>
      )}

      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

// ===== Tab: Campaigns =====
// One dense row per campaign carrying the SINGLE synthesized status + one action. Everything
// else (the Instantly picker, live controls, auto-approve, raw states) is demoted to expand.

const rowIconBtnSx = { color: THEME.textSecondary, '&:hover': { color: THEME.mint, background: THEME.mintFaint }, '&.Mui-disabled': { color: THEME.border } };

const CampaignRow = ({ campaign: c, synth, linkedEntry, instantly, loadInstantly, onNavigate, onTogglePause, autoApproveOf, autoApproveErrors, onToggleAutoApprove, onImport, onPrompts }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const wiredId = c.instantlyCampaignId;
  const isPausedGtm = c.status === 'paused';
  const canToggleGtm = c.status === 'active' || c.status === 'paused';

  // The one action drives the delivery pipe (real Instantly endpoints); GTM pause is a
  // separate control demoted into the expanded row.
  const runPipe = async (which) => {
    setBusy(true);
    setActionError(null);
    try {
      if (which === 'pause') await pauseInstantlyCampaign(wiredId);
      else await activateInstantlyCampaign(wiredId);
      if (loadInstantly) await loadInstantly();
    } catch (err) {
      setActionError((err && err.message) || `Could not ${which} delivery.`);
    } finally {
      setBusy(false);
    }
  };

  const doAction = () => {
    const a = synth.action;
    if (!a) return;
    if (a.kind === 'review') { onNavigate({ group: 'review', chip: 'approvals' }); return; }
    if (a.kind === 'reviewHeld') { onNavigate({ group: 'review', chip: 'review' }); return; }
    if (a.kind === 'reviewInstantly' || a.kind === 'fixInstantly') { openInstantly(); return; }
    if (a.kind === 'linkPipe') { setExpanded(true); return; }
    if (a.kind === 'pause') {
      setConfirm({ title: 'Pause delivery?', body: `Sending stops for ${(linkedEntry && linkedEntry.name) || c.name} until you resume.`, confirmLabel: 'Pause', tone: 'mint', onConfirm: () => runPipe('pause') });
      return;
    }
    if (a.kind === 'resume') {
      setConfirm({ title: 'Resume delivery?', body: `Sending resumes for ${(linkedEntry && linkedEntry.name) || c.name}.`, confirmLabel: 'Resume', tone: 'mint', onConfirm: () => runPipe('resume') });
    }
  };

  const total = Number(c.prospectCount ?? c.total);
  const sent = Number(c.sentCount ?? c.sent ?? c.emailsSent);
  const hasProgress = Number.isFinite(sent) && Number.isFinite(total) && total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((sent / total) * 100)) : 0;

  return (
    <Box id={`gtm-campaign-${c.id}`} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, overflow: 'hidden' }}>
      {/* Dense row: name + prospects, the ONE synthesized badge + sentence, the ONE action, expand. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 190px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: THEME.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Typography>
          <Typography sx={{ fontSize: 11, color: THEME.textMuted }}>{(c.prospectCount ?? 0).toLocaleString('en-US')} prospects</Typography>
        </Box>
        <Badge tone={synth.tone}>{synth.badge}</Badge>
        <Typography title={synth.sentence} sx={{ flex: '2 1 220px', minWidth: 0, fontSize: 12, color: THEME.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{synth.sentence}</Typography>
        {synth.action && (
          <Button variant="outlined" size="small" sx={outlinedBtnSx} disabled={busy} onClick={doAction} startIcon={busy ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : null}>
            {synth.action.label}
          </Button>
        )}
        <IconButton size="small" onClick={() => setExpanded((v) => !v)} title={expanded ? 'Hide details' : 'Show details'} sx={rowIconBtnSx}>
          <ExpandMoreIcon sx={{ fontSize: 20, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        </IconButton>
      </Box>
      {actionError && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, px: 2, pb: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: THEME.pink }}>{actionError}</Typography>
        </Box>
      )}

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.75, pt: 0.5, borderTop: `1px solid ${THEME.border}` }}>
          {/* Meta + demoted controls (edit prompts, import, GTM pause). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            <Typography sx={{ fontSize: 12, color: THEME.textMuted }}>{[c.source, c.persona].filter(Boolean).join(' · ')}</Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Edit this campaign's drafting prompts" arrow>
              <IconButton size="small" onClick={() => onPrompts(c)} sx={rowIconBtnSx}><EditOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Import a CSV list into this campaign" arrow>
              <IconButton size="small" onClick={() => onImport(c)} sx={rowIconBtnSx}><UploadFileOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <IconButton
              size="small"
              disabled={!canToggleGtm}
              onClick={() => onTogglePause(c)}
              title={canToggleGtm ? (isPausedGtm ? 'Resume campaign (GTM pipeline)' : 'Pause campaign (GTM pipeline)') : 'Not running'}
              sx={{ ...rowIconBtnSx, color: isPausedGtm ? THEME.mint : THEME.textSecondary }}
            >
              {isPausedGtm ? <PlayCircleOutlineIcon fontSize="small" /> : <PauseCircleOutlineIcon fontSize="small" />}
            </IconButton>
          </Box>

          {hasProgress && (
            <Box sx={{ mt: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sent</Typography>
                <Typography sx={{ fontSize: 11, color: THEME.textSecondary }}>{sent.toLocaleString('en-US')} of {total.toLocaleString('en-US')}</Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', background: THEME.mint }} />
              </Box>
            </Box>
          )}

          {/* The full Instantly plumbing: picker, statuses, wired id + Unlink, Pause/Activate. */}
          <InstantlyWireField campaign={c} instantly={{ ...instantly, reload: loadInstantly }} />

          {/* Auto-approve: compact footer switch; long explainer lives in the (i) tooltip. */}
          {onToggleAutoApprove && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Switch
                checked={autoApproveOf ? autoApproveOf(c) : false}
                onChange={(e) => onToggleAutoApprove(c, e.target.checked)}
                size="small"
                sx={{ flexShrink: 0, '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.mint }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.mint } }}
              />
              <Typography sx={{ fontSize: 12, color: THEME.textSecondary }}>Auto-approve contacts that pass all gates</Typography>
              <InfoTooltip title="Auto-approve" size={13}>
                When on, a contact is approved and sent automatically once it clears every check: a verified email address, recent enough research, and a confident hook. Anything that fails a check still waits for you in Review and approve. Off by default.
              </InfoTooltip>
              {autoApproveErrors && autoApproveErrors[c.id] && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexBasis: '100%', mt: 0.25 }}>
                  <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
                  <Typography sx={{ fontSize: 11, color: THEME.pink }}>{autoApproveErrors[c.id]}</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Details (for debugging): the two raw states the synthesis folded together. */}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${THEME.border}` }}>
            <Typography sx={{ fontSize: 10, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Details</Typography>
            <Typography sx={{ fontSize: 11, color: THEME.textMuted, lineHeight: 1.6 }}>
              GTM pipeline: {STATUS_LABEL[c.status] || c.status || 'unknown'} ({c.status || 'none'}) · Delivery: {linkedEntry ? `${instantlyStatusBadge(statusOf(linkedEntry)).label} (${String(statusOf(linkedEntry))})` : 'no pipe linked'}
            </Typography>
          </Box>
        </Box>
      </Collapse>
      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

const CampaignsTab = ({ campaigns, loading, error, onRetry, onTogglePause, autoApproveOf, autoApproveErrors, onToggleAutoApprove, instantly, loadInstantly, onNavigate, pendingByCampaign }) => {
  const [importCampaign, setImportCampaign] = useState(null);
  const [promptsCampaign, setPromptsCampaign] = useState(null);

  // The Instantly campaign list is fetched ONCE by HomeTab and passed in, so the Delivery
  // issues attention card and these cards read the exact same live status (no drift). A 424
  // surfaces as keyMissing so rows fall back to manual id entry instead of erroring.

  // Sync from Instantly: pull the latest campaign list + live status, then refresh the shared
  // list so every card's badge/dropdown updates. Mirrors the Mailboxes tab's sync-now pattern.
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const syncCampaigns = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await syncInstantlyCampaigns();
      setSyncedAt(new Date());
      await loadInstantly();
    } catch (err) {
      setSyncError((err && err.message) || 'Could not sync campaigns from Instantly.');
    } finally {
      setSyncing(false);
    }
  };

  return (
  <Box sx={{ maxWidth: 920 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Explainer tip="This tab lists every prospect list you've asked for, with the stage it's currently in.">
        Every list you've requested, and where it is in the pipeline.
      </Explainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        <Button
          variant="contained"
          size="small"
          sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
          disabled={syncing}
          onClick={syncCampaigns}
          startIcon={syncing ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : <SyncIcon sx={{ fontSize: 16 }} />}
        >
          {syncing ? 'Syncing…' : 'Sync from Instantly'}
        </Button>
        {syncedAt && !syncError && (
          <Typography sx={{ fontSize: 10, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Synced {fmtWhen(syncedAt)}</Typography>
        )}
      </Box>
    </Box>
    {syncError && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
        <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11, color: THEME.pink, lineHeight: 1.4 }}>{syncError}</Typography>
      </Box>
    )}
    <Resource
      loading={loading}
      error={error}
      empty={!campaigns || campaigns.length === 0}
      loadingLabel="Loading campaigns…"
      emptyLabel="No campaigns yet. Use “Find new people” above to ask the system for your first list."
      onRetry={onRetry}
    >
    {(() => {
      // Synthesize one status per campaign, then group + order into sections. A section renders
      // only when non-empty. Same synthesis the Home attention card and the "sending" chip use.
      const instantlyById = {};
      (instantly.list || []).forEach((x) => { const id = x.id || x._id; if (id) instantlyById[id] = x; });
      const rows = (campaigns || []).map((c) => {
        const linkedEntry = linkedEntryOf(c, instantlyById);
        const synth = synthesizeCampaignState(c, linkedEntry, { pendingApprovals: pendingByCampaign ? pendingByCampaign[c.id] : undefined });
        return { c, linkedEntry, synth };
      });
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {SYNTH_SECTIONS.map((section) => {
            const inSection = rows.filter((r) => section.keys.includes(r.synth.key));
            if (!inSection.length) return null;
            return (
              <Box key={section.label}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>{section.label}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {inSection.map((r) => (
                    <CampaignRow
                      key={r.c.id}
                      campaign={r.c}
                      synth={r.synth}
                      linkedEntry={r.linkedEntry}
                      instantly={instantly}
                      loadInstantly={loadInstantly}
                      onNavigate={onNavigate}
                      onTogglePause={onTogglePause}
                      autoApproveOf={autoApproveOf}
                      autoApproveErrors={autoApproveErrors}
                      onToggleAutoApprove={onToggleAutoApprove}
                      onImport={setImportCampaign}
                      onPrompts={setPromptsCampaign}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    })()}
    </Resource>
    {importCampaign && (
      <ImportContactsDialog campaign={importCampaign} onClose={() => setImportCampaign(null)} />
    )}
    {promptsCampaign && (
      <EditPromptsDialog campaign={promptsCampaign} onClose={() => setPromptsCampaign(null)} />
    )}
  </Box>
  );
};

// ===== Tab: New request (ICP form) =====

const TITLE_OPTIONS = [
  'Chief HR Officer',
  'VP People',
  'Head of Total Rewards',
  'Director of Wellbeing',
  'VP Talent',
  'Benefits Lead',
];
const SIZE_OPTIONS = ['51–200', '201–500', '501–1k', '1k–5k', '5k–10k', '10k+'];

// Live "Routes to: [sequence]" line under the audience field. Debounced POST to
// /sequences/route-preview as the operator types, so the automatic send-time routing
// is visible while composing. Read-only; tolerates the response shape.
const RoutePreviewLine = ({ audience }) => {
  const [state, setState] = useState({ status: 'idle' }); // idle|loading|done|empty|error

  useEffect(() => {
    const q = (audience || '').trim();
    if (q.length < 3) {
      setState({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    const timer = setTimeout(async () => {
      try {
        const data = await routePreviewSequence(q);
        if (cancelled) return;
        // Contract: { matchedKey, matchedName, confidence, reasoning }. matchedName
        // is the badge, reasoning the why — read those first, keep the older-shape
        // fallbacks defensive.
        const matched = (data && (data.matched || data.sequence || data.route)) || null;
        const name =
          (data && (data.matchedName || data.matchedKey)) ||
          (matched && (matched.name || matched.key)) ||
          (data && data.sequenceName) ||
          null;
        const why = (data && (data.reasoning || data.why || data.reason || data.rationale)) || '';
        setState(name ? { status: 'done', name, why } : { status: 'empty' });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', message: friendlyActionError('preview', err) });
      }
    }, 450); // debounce keystrokes

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [audience]);

  if (state.status === 'idle') return null;
  return (
    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Routes to:</Typography>
      {state.status === 'loading' && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>checking…</Typography>}
      {state.status === 'empty' && <Typography sx={{ fontSize: '11px', color: THEME.textSecondary }}>no matching sequence yet</Typography>}
      {state.status === 'error' && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{state.message}</Typography>}
      {state.status === 'done' && (
        <>
          <Badge tone="mint">{state.name}</Badge>
          {state.why && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{state.why}</Typography>}
        </>
      )}
    </Box>
  );
};

// ─── Test as a target (dry-run one prospect) + send-to-my-inbox ──────────────
// The two backing endpoints (POST /api/gtm/test-target, /api/gtm/test-send) ship
// in the T6 chunk running in parallel and may not be deployed yet — so everything
// here fails SOFT: a 404/501 renders "not available yet" inline in the affected
// section, never a blank page.

const TEST_STAGES = [
  { key: 'evidence', title: 'Evidence gathered' },
  { key: 'email', title: 'Email discovery' },
  { key: 'suppression', title: 'Suppression check' },
  { key: 'fit', title: 'Fit check' },
  { key: 'routing', title: 'Routed sequence' },
  { key: 'assembled', title: 'Assembled email' },
];

// Map the poll's live `stage` value onto a TEST_STAGES index. Accepts the stage keys
// plus common aliases the backend may emit (email discovery, fit check, compose, ...).
// Returns -1 when unknown so the UI falls back to the first stage.
const TEST_STAGE_ALIASES = {
  evidence: 'evidence',
  email: 'email', email_discovery: 'email', discovery: 'email',
  suppression: 'suppression', suppress: 'suppression',
  fit: 'fit', fit_check: 'fit',
  routing: 'routing', route: 'routing', routed: 'routing', routed_sequence: 'routing',
  compose: 'assembled', assemble: 'assembled', assembled: 'assembled', assembled_email: 'assembled',
};
function testStageIndex(stageKey) {
  if (!stageKey) return -1;
  const norm = String(stageKey).toLowerCase().trim();
  const canon = TEST_STAGE_ALIASES[norm] || norm;
  return TEST_STAGES.findIndex((s) => s.key === canon);
}

// A parallel-chunk endpoint is "not available yet" when it 404s / 501s.
const isNotDeployed = (err) => !!err && (err.httpStatus === 404 || err.httpStatus === 501);
const NOT_READY_MSG =
  "This runs against a backend endpoint that isn't deployed yet — it ships with the test chunk running in parallel. It'll work here once that lands.";

// Turn a coded hold/failure into a human line, special-casing the ones we know;
// everything else degrades to the raw code spelled out (never swallowed).
function humanizeTestReason(code) {
  const map = {
    link_asset_no_default:
      'This sequence uses [link] but no default link asset is set. Set a default in the Links library, then send.',
    pool_empty: 'No email could be found for this person from the available sources yet.',
    send_disabled: 'Sending is disabled on the server (GTM_SEND_ENABLED is off).',
    no_email: 'There is no email to send to for this person.',
  };
  if (!code) return '';
  if (map[code]) return map[code];
  const s = String(code).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Flat-contract readers: each field may arrive as a scalar or an object that also
// carries its own reason/reasoning — read both shapes defensively.
const stageText = (v) => (v == null ? '' : typeof v === 'string' ? v : v.text || v.value || v.raw || '');
const stageReason = (v) =>
  v && typeof v === 'object' ? v.reason || v.reasoning || v.why || v.detail || '' : '';

// Verdict word -> badge tone.
const fitTone = (verdict) => {
  const v = String(verdict || '').toLowerCase();
  if (/pass|fit|match|good|yes|true/.test(v)) return 'mint';
  if (/fail|mismatch|no\b|reject|bad|false/.test(v)) return 'pink';
  return 'amber';
};

// One stage row in the staged results card: a header (title + status) over its body.
const TestStageBox = ({ title, status, children }) => {
  const statusMeta =
    status === 'done'
      ? { label: null }
      : status === 'running'
      ? { label: 'working…', color: THEME.mint }
      : { label: 'pending', color: THEME.textMuted };
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: THEME.card, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: children ? `1px solid ${THEME.border}` : 'none' }}>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '11px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </Typography>
        {status === 'running' && <CircularProgress size={12} sx={{ color: THEME.mint }} />}
        {statusMeta.label && (
          <Typography sx={{ fontSize: '10px', color: statusMeta.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {statusMeta.label}
          </Typography>
        )}
      </Box>
      {children && <Box sx={{ px: 1.5, py: 1.25 }}>{children}</Box>}
    </Box>
  );
};

// Collapsible raw-evidence block: default open on desktop, collapsed on the mobile
// sheet so the card stays scannable (mirrors PreviewStep).
const EvidenceBlock = ({ text }) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [open, setOpen] = useState(!isMobile);
  if (!text) return <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No evidence returned.</Typography>;
  return (
    <Box>
      <Box onClick={() => setOpen((o) => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
        <Typography sx={{ flex: 1, fontSize: '12px', color: THEME.textSecondary }}>{open ? 'Hide raw evidence' : 'Show raw evidence'}</Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: THEME.textMuted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Typography sx={{ mt: 0.75, fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Typography>
      </Collapse>
    </Box>
  );
};

// "Send this to my inbox": an email field (defaulting to the authed user's address)
// + a send button. Reused by the target-test result card and the sequence Preview
// sheet. Owns its own send state; a HOLD (e.g. link_asset_no_default) renders inline,
// never as a toast; a real send fires the success toast. `buildPayload()` supplies
// the { evidence?/sampleContact?/sequenceKey/step } specifics; this adds `email`.
const SendToInboxButton = ({ buildPayload, disabled, hint }) => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [hold, setHold] = useState(null); // inline hold / error / not-ready message
  const [sent, setSent] = useState(false); // success toast

  // Default to the authed user's address once auth resolves; leave it editable.
  // Empty (required) if the app never exposes one.
  useEffect(() => {
    if (currentUser && currentUser.email) setEmail((e) => e || currentUser.email);
  }, [currentUser]);

  const send = async () => {
    const to = email.trim();
    if (!to) {
      setHold('Enter an email address to send the test to.');
      return;
    }
    setSending(true);
    setHold(null);
    try {
      const res = await testSend({ ...buildPayload(), email: to });
      const heldCode =
        res && (res.hold || res.held || res.holdReason || (res.ok === false && (res.reason || res.error)));
      if (heldCode) setHold(humanizeTestReason(heldCode));
      else setSent(true);
    } catch (err) {
      if (isNotDeployed(err)) setHold(NOT_READY_MSG);
      else if (err && err.code) setHold(humanizeTestReason(err.code));
      else setHold((err && err.message) || 'Could not send the test email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {hint && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{hint}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputProps={inputProps}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={outlinedBtnSx}
          disabled={disabled || sending}
          onClick={send}
          startIcon={sending ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <SendOutlinedIcon sx={{ fontSize: 15 }} />}
        >
          {sending ? 'Sending…' : 'Send this to my inbox'}
        </Button>
      </Box>
      {hold && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: THEME.amber, mt: '1px' }} />
          <Typography sx={{ fontSize: '12px', color: THEME.amber, lineHeight: 1.5 }}>{hold}</Typography>
        </Box>
      )}
      <Snackbar
        open={sent}
        autoHideDuration={4000}
        onClose={() => setSent(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSent(false)} sx={{ width: '100%' }}>
          Test email sent, check your inbox.
        </Alert>
      </Snackbar>
    </Box>
  );
};

// GTM-PROVENANCE-01: plain-language source registry tags for a cited capability, so an
// invented-sounding product name traces to a real registry entry in one glance.
const PROVENANCE_REGISTRY_LABEL = {
  agent_registry: 'from agent registry',
  artifact_registry: 'from artifact registry',
  tool_library: 'from tool library',
};
// The compose tier in operator language.
const PROVENANCE_TIER_LINE = {
  client_proof: 'Led with named client proof',
  capability: 'Led with a matched capability (no client proof fit)',
  immutable_facts: 'Immutable facts only',
};

// GTM-WHY-THIS-DRAFT-01: shared bits for the decision-trail section. Every value rendered is read
// straight off the provenance payload; nothing (counts, claim text, reasons) is inferred here.
const trailLabelSx = { fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 };
const provStr = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
const provScore = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n)) return '';
  return n <= 1 ? n.toFixed(2) : String(Math.round(n * 100) / 100);
};

// "7 · Why this draft" — the real decision trail behind the assembled email, in operator language.
// Collapsible, muted, same idiom as the other stages. Five rows (what it used, what it considered,
// why this angle, what it left out, grounding), each read from the provenance payload and each
// hidden entirely when its fields are absent, so a partial or failed draft never prints placeholders.
// Null provenance (pre-provenance payloads) renders nothing.
const WhyThisDraft = ({ provenance }) => {
  const [open, setOpen] = useState(false);
  const [considerOpen, setConsiderOpen] = useState(false);
  const p = provenance;
  if (!p || typeof p !== 'object') return null;

  const obj = (v) => (v && typeof v === 'object' ? v : null);
  const tier = p.tierUsed || '';
  const tierText = PROVENANCE_TIER_LINE[tier] || (p.tierReason ? 'Draft did not complete' : 'Compose tier not recorded');

  // 1. WHAT IT USED — the cited proof (its claim text, client as a secondary label) and the used capability.
  const proofUsed = obj(p.proofUsed) || obj(p.usedProof);
  const usedClaim = proofUsed ? provStr(proofUsed.claim || proofUsed.claimText || proofUsed.whatWeDo || proofUsed.text) : '';
  const usedClient = proofUsed ? provStr(proofUsed.client || proofUsed.clientName || proofUsed.company) : '';
  const capsUsed = Array.isArray(p.capabilitiesUsed) ? p.capabilitiesUsed
    : (obj(p.capabilityUsed) ? [p.capabilityUsed] : (Array.isArray(p.citedCapabilities) ? p.citedCapabilities : []));

  // 2. WHAT IT CONSIDERED — other offered proofs (by claim text) and other matched capabilities (with scores).
  const proofsConsidered = Array.isArray(p.proofsConsidered) ? p.proofsConsidered : (Array.isArray(p.otherProofsOffered) ? p.otherProofsOffered : []);
  const capsConsidered = Array.isArray(p.capabilitiesConsidered) ? p.capabilitiesConsidered : (Array.isArray(p.otherMatchedCapabilities) ? p.otherMatchedCapabilities : []);

  // 3. WHY THIS ANGLE — the composer's own one-sentence reason and the evidence items it drew from.
  const angleReason = provStr(p.angleReason || p.hookReason || p.personalizationReason || (obj(p.hook) && p.hook.reason));
  const evidenceUsed = Array.isArray(p.evidenceUsed) ? p.evidenceUsed : (Array.isArray(p.hookEvidence) ? p.hookEvidence : []);

  // 4. WHAT IT LEFT OUT — capabilities stripped by competitor protection, and proofs not offered, with reasons.
  const strippedCaps = Array.isArray(p.strippedCapabilities) ? p.strippedCapabilities : (Array.isArray(p.competitorStripped) ? p.competitorStripped : []);
  const proofsNotOffered = Array.isArray(p.proofsNotOffered) ? p.proofsNotOffered : (Array.isArray(p.withheldProofs) ? p.withheldProofs : []);

  // 5. GROUNDING — evidence counts by source and brief length, compact.
  const briefLen = firstFiniteNumber(p.researchBriefLength, obj(p.grounding) && p.grounding.briefLength) || 0;
  const evidenceBySource = Array.isArray(p.evidenceBySource) ? p.evidenceBySource : (obj(p.grounding) && Array.isArray(p.grounding.sources) ? p.grounding.sources : []);

  const pv = p.promptVersions && typeof p.promptVersions === 'object' ? p.promptVersions : {};
  const pvReasons = [
    pv.openerReason ? `opener: ${pv.openerReason}` : '',
    pv.fitCheckReason ? `fit check: ${pv.fitCheckReason}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: THEME.card, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer', userSelect: 'none' }}
      >
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '11px', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          7 · Why this draft
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 16, color: THEME.textMuted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {/* Tier (the backend's compose-tier decision, context for the trail) */}
          <Box>
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, fontWeight: 600 }}>{tierText}</Typography>
            {p.tierReason && <Typography sx={{ fontSize: '11px', color: THEME.amber, mt: 0.25, lineHeight: 1.5 }}>{p.tierReason}</Typography>}
          </Box>

          {/* 1. WHAT IT USED */}
          {(usedClaim || capsUsed.length > 0) && (
            <Box>
              <Typography sx={trailLabelSx}>What it used</Typography>
              {usedClaim && (
                <Box sx={{ border: `1px solid ${THEME.mintDim}`, borderRadius: '6px', background: THEME.mintFaint, p: 1 }}>
                  <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, lineHeight: 1.5 }}>{usedClaim}</Typography>
                  {usedClient && <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>{usedClient}</Typography>}
                </Box>
              )}
              {capsUsed.map((cap, i) => {
                const nm = provStr(cap.capabilityName || cap.name);
                if (!nm) return null;
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Capability used:</Typography>
                    <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: THEME.textPrimary, wordBreak: 'break-word' }}>{nm}</Typography>
                    {PROVENANCE_REGISTRY_LABEL[cap.sourceRegistry] && <Badge tone="gray">{PROVENANCE_REGISTRY_LABEL[cap.sourceRegistry]}</Badge>}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* 2. WHAT IT CONSIDERED (collapsed by default) */}
          {(proofsConsidered.length > 0 || capsConsidered.length > 0) && (
            <Box>
              <Box
                onClick={() => setConsiderOpen((o) => !o)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setConsiderOpen((o) => !o); } }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
              >
                <Typography sx={{ ...trailLabelSx, mb: 0 }}>What it considered</Typography>
                <ExpandMoreIcon sx={{ fontSize: 14, color: THEME.textMuted, transform: considerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
              </Box>
              <Collapse in={considerOpen} unmountOnExit>
                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {proofsConsidered.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mb: 0.35 }}>Other proofs offered</Typography>
                      {proofsConsidered.map((pr, i) => {
                        const claim = provStr(pr.claim || pr.claimText || pr.whatWeDo || pr.text);
                        if (!claim) return null;
                        const client = provStr(pr.client || pr.clientName || pr.company);
                        const sc = provScore(pr.score);
                        return (
                          <Box key={i} sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.5 }}>
                              {claim}{sc ? <Box component="span" sx={{ color: THEME.textMuted }}> · {sc}</Box> : null}
                            </Typography>
                            {client && <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>{client}</Typography>}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {capsConsidered.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mb: 0.35 }}>Other matched capabilities</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {capsConsidered.map((cap, i) => {
                          const nm = provStr(cap.capabilityName || cap.name);
                          if (!nm) return null;
                          const sc = provScore(cap.score);
                          return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: THEME.textSecondary, wordBreak: 'break-word' }}>{nm}</Typography>
                              {sc && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, ...numeralSx }}>{sc}</Typography>}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* 3. WHY THIS ANGLE */}
          {(angleReason || evidenceUsed.length > 0) && (
            <Box>
              <Typography sx={trailLabelSx}>Why this angle</Typography>
              {angleReason && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{angleReason}</Typography>}
              {evidenceUsed.length > 0 && (
                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  {evidenceUsed.map((ev, i) => {
                    const label = provStr(ev.label || ev.title || ev.snippet || ev.text || ev.source);
                    const url = provStr(ev.url || ev.link || ev.source);
                    const isUrl = /^https?:\/\//i.test(url);
                    if (!label && !isUrl) return null;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', background: THEME.textMuted, flexShrink: 0 }} />
                        {isUrl ? (
                          <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '11px', color: THEME.mint, wordBreak: 'break-word' }}>
                            {label || url}<OpenInNewIcon sx={{ fontSize: 11, ml: 0.25, verticalAlign: 'middle' }} />
                          </Link>
                        ) : (
                          <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, wordBreak: 'break-word' }}>{label}</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* 4. WHAT IT LEFT OUT */}
          {(strippedCaps.length > 0 || proofsNotOffered.length > 0) && (
            <Box>
              <Typography sx={trailLabelSx}>What it left out</Typography>
              {strippedCaps.map((cap, i) => {
                const nm = provStr(cap.capabilityName || cap.name);
                if (!nm) return null;
                const reason = provStr(cap.reason);
                return (
                  <Box key={`s${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.4 }}>
                    <Badge tone="pink">stripped</Badge>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: THEME.textSecondary, wordBreak: 'break-word' }}>{nm}</Typography>
                      {reason && <Typography sx={{ fontSize: '10px', color: THEME.textMuted, lineHeight: 1.5 }}>{reason}</Typography>}
                    </Box>
                  </Box>
                );
              })}
              {proofsNotOffered.map((pr, i) => {
                const claim = provStr(pr.claim || pr.claimText || pr.whatWeDo || pr.text);
                if (!claim) return null;
                const client = provStr(pr.client || pr.clientName || pr.company);
                const reason = provStr(pr.reason);
                return (
                  <Box key={`n${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.4 }}>
                    <Badge tone="muted">not offered</Badge>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.5, wordBreak: 'break-word' }}>{claim}</Typography>
                      {client && <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>{client}</Typography>}
                      {reason && <Typography sx={{ fontSize: '10px', color: THEME.textMuted, lineHeight: 1.5 }}>{reason}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* 5. GROUNDING (compact) */}
          {(briefLen > 0 || evidenceBySource.length > 0) && (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
              Grounding: {[
                ...evidenceBySource.map((s) => {
                  const src = provStr(s.source || s.type || s.name);
                  const cnt = firstFiniteNumber(s.count, s.n);
                  return src ? (cnt != null ? `${src} ${fmtCount(cnt)}` : src) : '';
                }).filter(Boolean),
                briefLen > 0 ? `${briefLen.toLocaleString('en-US')}-char brief` : '',
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}

          {/* Prompt versions footer */}
          <Box sx={{ borderTop: `1px solid ${THEME.border}`, pt: 0.75 }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
              Composed by opener v{pv.opener != null ? pv.opener : '?'} · fit check v{pv.fitCheck != null ? pv.fitCheck : '?'}
            </Typography>
            {pvReasons && <Typography sx={{ fontSize: '11px', color: THEME.amber, mt: 0.25, lineHeight: 1.5 }}>{pvReasons}</Typography>}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// The staged results card: one section per stage, in order, filled from the flat
// test-target response. Per-stage hold/failure reasons render inline in-section.
const TestTargetResult = ({ data, sequenceKey }) => {
  const emailFound = data.emailFound === true || (!!data.email && data.emailFound !== false);
  const emailStr = typeof data.email === 'string' ? data.email : (data.email && data.email.value) || '';
  const emailConf = data.emailConfidence != null ? data.emailConfidence : data.email && data.email.confidence;
  const emailReason = stageReason(data.email) || data.emailReason || data.poolReason || '';
  // Verification source (e.g. "NeverBounce"), rendered as a "VERIFIED · {source}" chip ONLY when
  // the payload exposes it. The current test-target/enrichment result does not carry it (see the
  // T6 follow-up in the commit), so nothing is faked — the label appears the moment the field ships.
  const verifSource = data.verificationSource || (data.email && data.email.verificationSource) || '';

  const sup = data.suppression;
  const suppressed =
    sup === true || (sup && (sup.suppressed === true || sup.matched === true)) || (typeof sup === 'string' && /suppress/i.test(sup));
  const supReason = stageReason(sup) || (typeof sup === 'string' ? sup : '');

  const fitLabel = typeof data.fitVerdict === 'string' ? data.fitVerdict : (data.fitVerdict && (data.fitVerdict.verdict || data.fitVerdict.label)) || '';
  const fitReason = stageReason(data.fitVerdict);

  const routed = data.routedSequence;
  const routedName = typeof routed === 'string' ? routed : (routed && (routed.name || routed.key)) || '';
  const routedKey = (routed && (routed.key || routed.matchedKey)) || (typeof routed === 'string' ? routed : '') || sequenceKey || '';
  const routedWhy = stageReason(routed);
  // The Evidence stage states what actually ran, from researchApplied + researchSource. Falls
  // back to the routed sequence's researchMode for pre-researchApplied payloads.
  const researchApplied = data.researchApplied != null ? String(data.researchApplied).toLowerCase() : '';
  const researchSource = String(data.researchSource || '').toLowerCase();
  const routedMode = routed && typeof routed === 'object' ? String(routed.researchMode || '').toLowerCase() : '';
  const researchRan = researchApplied ? researchApplied === 'on' : routedMode !== 'off';
  // The one honest warning: research off because THE SEQUENCE itself skips it, so real campaigns
  // on this sequence also skip research (not just this test).
  const researchWarn = researchApplied === 'off' && researchSource === 'sequence_setting';
  const researchMsg = researchApplied === 'on'
    ? (researchSource === 'override' ? 'Research ran (forced on for this test)' : 'Research ran (sequence setting)')
    : researchApplied === 'off'
      ? (researchSource === 'override'
        ? 'Research off (this test only)'
        : 'Research off (sequence setting: real campaigns for this sequence also skip research)')
      : (researchRan ? '' : 'Research is off for this sequence. The opener leans on client proof instead of researched hooks.');

  const asm = data.assembledEmail;
  const asmSubject = asm && typeof asm === 'object' ? asm.subject || '' : '';
  const asmBody = asm && typeof asm === 'object' ? asm.body || asm.text || '' : stageText(asm);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <TestStageBox title="1 · Evidence gathered" status="done">
        {researchMsg && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: researchRan ? 1 : 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: researchWarn ? THEME.amber : (researchRan ? THEME.mint : THEME.textMuted), mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: researchWarn ? THEME.amber : THEME.textSecondary, lineHeight: 1.5 }}>
              {researchMsg}
            </Typography>
          </Box>
        )}
        {researchRan && (
          <>
            {stageReason(data.evidence) && (
              <Typography sx={{ fontSize: '12px', color: THEME.amber, mb: 0.75 }}>{stageReason(data.evidence)}</Typography>
            )}
            <EvidenceBlock text={stageText(data.evidence)} />
          </>
        )}
      </TestStageBox>

      <TestStageBox title="2 · Email discovery" status="done">
        {emailFound && emailStr ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontWeight: 600, wordBreak: 'break-all' }}>{emailStr}</Typography>
            {verifSource && <Badge tone="mint">VERIFIED · {verifSource}</Badge>}
            {emailConf != null && <Badge tone="gray">{typeof emailConf === 'number' ? `${Math.round(emailConf * (emailConf <= 1 ? 100 : 1))}% confidence` : String(emailConf)}</Badge>}
          </Box>
        ) : (
          // pool_empty etc. is guidance, not an error.
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: THEME.textMuted, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>
              {humanizeTestReason(emailReason) || 'No email was found for this person from the available sources yet.'}
            </Typography>
          </Box>
        )}
      </TestStageBox>

      <TestStageBox title="3 · Suppression check" status="done">
        {suppressed ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
            <Badge tone="pink">Would be suppressed</Badge>
            {supReason && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>{supReason}</Typography>}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Badge tone="mint">Clear</Badge>
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Not on the suppression list.</Typography>
          </Box>
        )}
      </TestStageBox>

      <TestStageBox title="4 · Fit check" status="done">
        {fitLabel ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
            <Badge tone={fitTone(fitLabel)}>{fitLabel}</Badge>
            {fitReason && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>{fitReason}</Typography>}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No fit verdict returned.</Typography>
        )}
      </TestStageBox>

      <TestStageBox title="5 · Routed sequence" status="done">
        {routedName || routedKey ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
            <Badge tone="mint">{routedName || routedKey}</Badge>
            {routedWhy && <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.5 }}>{routedWhy}</Typography>}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No sequence routed.</Typography>
        )}
      </TestStageBox>

      <TestStageBox title="6 · Assembled email" status="done">
        {asmBody || asmSubject ? (
          <Box>
            {asmSubject && (
              <>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textSecondary, mb: 0.75 }}>{asmSubject}</Typography>
              </>
            )}
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Body</Typography>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{asmBody || '(empty)'}</Typography>
            {stageReason(asm) && <Typography sx={{ fontSize: '11px', color: THEME.amber, mt: 0.75 }}>{stageReason(asm)}</Typography>}
            {/* Send-to-my-inbox: the run's evidence + the routed/chosen sequence, step 1. */}
            <SendToInboxButton
              disabled={!asmBody && !asmSubject}
              buildPayload={() => ({ evidence: stageText(data.evidence), sequenceKey: routedKey || undefined, step: 1 })}
            />
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No email assembled (an earlier stage held).</Typography>
        )}
      </TestStageBox>

      {/* 7 · Why this draft — provenance of the compose (renders on hold/fail too). */}
      <WhyThisDraft provenance={data.provenance} />
    </Box>
  );
};

// The full "Test as a target" modal: identify one prospect, run them through the
// whole pipeline (no write, no send), and see each stage's result — then optionally
// mail the assembled email to your own inbox.
const TestTargetModal = ({ onClose }) => {
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [sequenceKey, setSequenceKey] = useState(''); // '' = Auto-route
  const [researchOverride, setResearchOverride] = useState(''); // '' = follow sequence | 'on' | 'off'
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null); // hard error (surfaced inline)
  const [failed, setFailed] = useState(null); // run reached status 'failed'; reason surfaced
  const [notReady, setNotReady] = useState(false); // fail-soft "not available yet"
  const [runId, setRunId] = useState(null); // async run handle from the 202 POST
  const [stageKey, setStageKey] = useState(null); // live stage reported by the poll

  const seqRes = useGtmResource('/api/gtm/sequences', (d) => (Array.isArray(d) ? d : (d && d.sequences) || []));
  const sequences = seqRes.data || [];

  // The run is async: POST returns 202 { runId }, then we poll GET /test-target/:runId
  // every 2s until it reports done or failed. The live `stage` drives the pipeline
  // display; done fills the result, failed surfaces the reason (never a silent stall).
  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const res = await getTestTargetRun(runId);
        if (cancelled) return;
        const status = res && res.status;
        const liveStage = res && (res.stage || res.currentStage || res.step);
        if (liveStage) setStageKey(liveStage);
        if (status === 'done') {
          setResult((res && (res.result || res.data)) || res || {});
          setRunning(false);
          return; // terminal: stop polling
        }
        if (status === 'failed' || status === 'error') {
          const reason = res && (res.error || res.reason || res.message);
          const code = reason && typeof reason === 'object' ? reason.code : reason;
          setFailed(humanizeTestReason(code) || (typeof reason === 'string' ? reason : '') || 'The test run failed.');
          setRunning(false);
          return; // terminal: stop polling
        }
        timer = setTimeout(tick, 2000); // still running: poll again in 2s
      } catch (err) {
        if (cancelled) return;
        if (isNotDeployed(err)) setNotReady(true);
        else setError(friendlyActionError('run', err));
        setRunning(false);
      }
    };
    tick(); // first poll immediately, then every 2s
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [runId]);

  const run = async () => {
    if (!linkedinUrl.trim()) {
      setError('Enter a LinkedIn profile URL to test.');
      return;
    }
    setRunning(true);
    setError(null);
    setFailed(null);
    setNotReady(false);
    setResult(null);
    setStageKey(null);
    setRunId(null);
    try {
      const res = await testTarget({
        linkedinUrl: linkedinUrl.trim(),
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        title: title.trim() || undefined,
        sequenceKey: sequenceKey || undefined,
        // 'on' | 'off' only; omit for the default so the routed sequence's setting governs.
        researchOverride: researchOverride === 'on' || researchOverride === 'off' ? researchOverride : undefined,
      });
      if (res && res.runId) {
        // 202 accepted: the poll effect takes over and keeps `running` true.
        setRunId(res.runId);
      } else if (res) {
        // Back-compat: an older backend returned the finished result inline.
        setResult(res);
        setRunning(false);
      } else {
        setRunning(false);
      }
    } catch (err) {
      if (isNotDeployed(err)) setNotReady(true);
      else setError(friendlyActionError('run', err));
      setRunning(false);
    }
  };

  return (
    <Dialog
      open
      onClose={() => !running && onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceOutlinedIcon sx={{ fontSize: 20, color: THEME.mint }} />
        Test as a target
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2, lineHeight: 1.6 }}>
          Run one real person through the whole pipeline — evidence, email discovery, suppression, fit, routing, and the
          assembled email — without writing or sending anything. Then optionally mail the result to your own inbox.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <FieldLabel tip="The prospect's LinkedIn profile URL. This is the primary signal the pipeline works from.">
              LinkedIn URL
            </FieldLabel>
            <TextField
              fullWidth
              size="small"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/…"
              InputProps={inputProps}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <FieldLabel>Name (optional)</FieldLabel>
              <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} InputProps={inputProps} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <FieldLabel>Company (optional)</FieldLabel>
              <TextField fullWidth size="small" value={company} onChange={(e) => setCompany(e.target.value)} InputProps={inputProps} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <FieldLabel>Title (optional)</FieldLabel>
              <TextField fullWidth size="small" value={title} onChange={(e) => setTitle(e.target.value)} InputProps={inputProps} />
            </Box>
          </Box>

          <Box sx={{ maxWidth: 320 }}>
            <FieldLabel tip="Force a specific sequence, or leave on Auto-route to let send-time routing pick.">
              Sequence
            </FieldLabel>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={sequenceKey}
              onChange={(e) => setSequenceKey(e.target.value)}
              sx={{ fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border }, '& .MuiSvgIcon-root': { color: THEME.textMuted }, '& .MuiSelect-select': { py: '8px' } }}
              MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, '& .MuiMenuItem-root': { fontSize: 13 } } } }}
            >
              <MenuItem value="">Auto-route (recommended)</MenuItem>
              {sequences.map((s) => (
                <MenuItem key={s.key || s._id} value={s.key || ''}>
                  {s.name || s.key}{s.status && s.status !== 'live' ? ` (${s.status})` : ''}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ maxWidth: 420 }}>
            <FieldLabel>Research</FieldLabel>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {[{ v: '', label: 'Follow sequence setting' }, { v: 'on', label: 'On' }, { v: 'off', label: 'Off' }].map((o) => (
                <SelectChip key={o.v || 'follow'} label={o.label} selected={researchOverride === o.v} onClick={() => setResearchOverride(o.v)} />
              ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 0.5 }}>
              Only affects this test. The sequence's own setting governs real campaigns.
            </Typography>
          </Box>

          <Box>
            <Button
              variant="contained"
              sx={mintBtnSx}
              disabled={running}
              onClick={run}
              startIcon={running ? <CircularProgress size={14} sx={{ color: '#06281f' }} /> : <ScienceOutlinedIcon sx={{ fontSize: 17 }} />}
            >
              {running ? 'Running…' : 'Run test'}
            </Button>
          </Box>

          {error && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: THEME.pink, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{error}</Typography>
            </Box>
          )}

          {/* Run reached status 'failed': surface the reason inline, never a blank card. */}
          {failed && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: THEME.pink, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{failed}</Typography>
            </Box>
          )}

          {/* Fail-soft: endpoint not deployed yet. Inline, never a blank card. */}
          {notReady && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 1.5, borderRadius: '10px', background: THEME.amberDim, border: '1px solid rgba(251,191,36,0.3)' }}>
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 18, color: THEME.amber, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.amber, lineHeight: 1.6 }}>{NOT_READY_MSG}</Typography>
            </Box>
          )}

          {/* Live pipeline: the poll's stage drives which row is working; earlier rows
              read done, later rows pending. Before the first poll returns, stage 1 works. */}
          {running && !result && (() => {
            const li = testStageIndex(stageKey);
            const active = li >= 0 ? li : 0;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {TEST_STAGES.map((st, i) => (
                  <TestStageBox
                    key={st.key}
                    title={`${i + 1} · ${st.title}`}
                    status={i < active ? 'done' : i === active ? 'running' : 'pending'}
                  />
                ))}
              </Box>
            );
          })()}

          {result && <TestTargetResult data={result} sequenceKey={sequenceKey} />}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} disabled={running} onClick={onClose}>
          {result || failed ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===== Find people › Test one person (Pass 1 redesign) =====
// Relocates the "Test as a target" dry-run into its own Find-people sub-tab. The
// heavy lifting stays in TestTargetModal (unchanged, intact); this panel is just the
// instructive entry point that opens it, written for a non-technical operator.
const TestPersonPanel = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, mb: 2.5, borderRadius: '12px', background: THEME.mintFaint, border: `1px solid ${THEME.mintDim}` }}>
        <ScienceOutlinedIcon sx={{ fontSize: 20, color: THEME.mint, flexShrink: 0, mt: '1px' }} />
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          Try the whole pipeline on a single person before you commit to a list. Enter one prospect and the system
          researches them, checks fit, and drafts their email — without sending or saving anything. You can then mail
          the result to your own inbox to see exactly what they'd receive.
        </Typography>
      </Box>
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px', background: THEME.card, p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>Test one person</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
          Nothing here spends a send or writes to any list. It's a safe dry run.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ScienceOutlinedIcon />}
          sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }}
          onClick={() => setOpen(true)}
        >
          Test as a target
        </Button>
      </Box>
      {open && <TestTargetModal onClose={() => setOpen(false)} />}
    </Box>
  );
};

const NewRequestTab = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [audienceDescription, setAudienceDescription] = useState('');
  const [seed, setSeed] = useState('');
  const [industry, setIndustry] = useState(null); // { code, label } | null
  const [sizes, setSizes] = useState([]); // optional explicit override
  const [budget, setBudget] = useState('1'); // default $1; presets scale from here
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [testOpen, setTestOpen] = useState(false); // "Test as a target" modal

  // Live "~N contacts" blast-radius estimate next to Run budget: budget divided by
  // the live per-contact cost from GET /api/gtm/pricing. Data-backed, not a guess.
  const pricingRes = useGtmResource('/api/gtm/pricing', (d) => d || {});
  const perContact = pricingRes.data && Number(pricingRes.data.perContactTotal);
  const cleanBudgetNum = Number(String(budget).replace(/[^0-9.]/g, ''));
  const estimatedContacts =
    perContact > 0 && Number.isFinite(cleanBudgetNum) && cleanBudgetNum > 0
      ? Math.floor(cleanBudgetNum / perContact)
      : null;

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  // Quick-fill: a pill appends its text into the audience textarea (the source of
  // truth), skipping an exact duplicate so repeated clicks do not stack.
  const appendToAudience = (text) =>
    setAudienceDescription((prev) => {
      const base = prev.trim();
      if (!base) return text;
      if (base.toLowerCase().includes(text.toLowerCase())) return prev;
      return `${base}, ${text}`;
    });

  // Source the campaign for real: POST /api/gtm/campaigns (writes pending_approval,
  // sends nothing). audienceDescription is the primary free-text targeting field;
  // sizes/naics/lookalikeSeed ride along as optional overrides. Budget goes as a
  // clean NUMBER (the field holds a "$5,000"-style string; Number() of that is NaN
  // and would 400). The isSubmitting guard disables the button against a double-fire.
  const submit = async () => {
    const cleanBudget = Number(String(budget).replace(/[^0-9.]/g, ''));
    if (!name.trim()) {
      setSubmitError('Give this list a name first.');
      return;
    }
    if (!audienceDescription.trim()) {
      setSubmitError('Describe who you want to reach.');
      return;
    }
    if (!Number.isFinite(cleanBudget) || cleanBudget <= 0) {
      setSubmitError('Enter a run budget, a positive dollar amount.');
      return;
    }
    const payload = {
      name: name.trim(),
      audienceDescription: audienceDescription.trim(), // primary free-text targeting, the source of truth
      sizes, // optional explicit override (criteria.companySizeBands)
      naics: industry ? industry.code : '', // optional explicit override
      lookalikeSeed: seed,
      budget: cleanBudget, // NUMBER; backend coerces via Number(), NaN would 400
    };
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createCampaign(payload);
      if (onCreated) onCreated(); // route to Approvals so the run is visible
    } catch (err) {
      setSubmitError(friendlyActionError('create', err)); // Rule 3: never silent
    } finally {
      setIsSubmitting(false); // roll the button back to enabled
    }
  };

  // FREE preview: same targeting payload as submit, but no reveal and no charge.
  // The operator can preview as many times as they like before committing with Find.
  const handlePreview = async () => {
    if (!audienceDescription.trim() && sizes.length === 0 && !industry) {
      setPreviewError('Describe who you want to reach (or add a size or industry) to preview.');
      return;
    }
    const cleanBudget = Number(String(budget).replace(/[^0-9.]/g, ''));
    const payload = {
      name: name.trim(),
      audienceDescription: audienceDescription.trim(),
      sizes,
      naics: industry ? industry.code : '',
      lookalikeSeed: seed,
      budget: Number.isFinite(cleanBudget) ? cleanBudget : 0, // ignored by preview; kept for parity
    };
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await previewCampaign(payload);
      setPreview(res);
    } catch (err) {
      setPreview(null);
      setPreviewError(friendlyActionError('preview', err)); // Rule 3: surface inline
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      {/* What this tab does */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          p: 2,
          mb: 2.5,
          borderRadius: '12px',
          background: THEME.mintFaint,
          border: `1px solid ${THEME.mintDim}`,
        }}
      >
        <TravelExploreIcon sx={{ fontSize: 20, color: THEME.mint, flexShrink: 0, mt: '1px' }} />
        <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          This is where you ask the system to find people. Fill in who you want to reach, and it searches
          Apollo, finds verified contacts, and builds a list for your review. Nothing sends here.
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${THEME.border}`,
          borderRadius: '12px',
          background: THEME.card,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box>
          <FieldLabel tip="A label for you to recognize this list later. It doesn't affect who gets found.">
            Campaign name
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CHRO · West-coast Biotech"
            InputProps={inputProps}
          />
        </Box>

        {/* PRIMARY input: free-text audience. The source of truth for targeting. */}
        <Box>
          <FieldLabel tip="Describe the people you want to reach in your own words: titles, seniority, industry, region, anything. The system reads this to build the list.">
            Who do you want to reach?
          </FieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={audienceDescription}
            onChange={(e) => setAudienceDescription(e.target.value)}
            placeholder="e.g. Heads of People and total rewards leaders at mid-market biotech on the west coast"
            InputProps={inputProps}
          />
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>
            Describe the audience in plain language.
          </Typography>
          <RoutePreviewLine audience={audienceDescription} />
        </Box>

        {/* Optional quick-fills: clicking a pill appends its text into the box above. */}
        <Box>
          <FieldLabel tip="Optional shortcuts. Clicking a pill adds its text to the box above, it does not replace what you wrote. The box is the source of truth.">
            Quick fills (optional)
          </FieldLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['CHRO', 'WELLBEING'].map((s) => (
              <SelectChip key={s} label={s} selected={false} onClick={() => appendToAudience(s)} />
            ))}
            {TITLE_OPTIONS.map((t) => (
              <SelectChip key={t} label={t} selected={false} onClick={() => appendToAudience(t)} />
            ))}
          </Box>
        </Box>

        <Box>
          <FieldLabel tip="Name a company you already like; we find others like it.">
            Lookalike seed
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="e.g. our 3 best pharma logos, or a domain to clone"
            InputProps={inputProps}
          />
        </Box>

        <Box>
          <FieldLabel tip="Optional override. Pick a specific industry code. Leave blank to let the audience description decide. The full list comes from Apollo when this goes live.">
            Industry / NAICS (optional)
          </FieldLabel>
          <Autocomplete
            options={NAICS_OPTIONS}
            value={industry}
            onChange={(e, v) => setIndustry(v)}
            getOptionLabel={(o) => (o ? `${o.code} — ${o.label}` : '')}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            size="small"
            componentsProps={{
              paper: {
                sx: {
                  background: THEME.cardRaised,
                  color: THEME.textPrimary,
                  border: `1px solid ${THEME.borderStrong}`,
                  '& .MuiAutocomplete-option': {
                    fontSize: 13,
                    '&[aria-selected="true"]': { background: THEME.mintFaint },
                    '&.Mui-focused': { background: THEME.mintFaint },
                  },
                  '& .MuiAutocomplete-noOptions': { color: THEME.textMuted, fontSize: 13 },
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { color: THEME.textPrimary },
              '& fieldset': { borderColor: THEME.border },
              '& .MuiSvgIcon-root': { color: THEME.textMuted },
              '& .MuiInputBase-input::placeholder': { color: THEME.textMuted, opacity: 1 },
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Type to search, e.g. pharma" />
            )}
          />
        </Box>

        <Box>
          <FieldLabel tip="Optional override. Narrow to specific company headcount bands. Leave blank to let the audience description decide.">
            Company size (optional)
          </FieldLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SIZE_OPTIONS.map((s) => (
              <SelectChip
                key={s}
                label={s}
                selected={sizes.includes(s)}
                onClick={() => toggle(sizes, setSizes, s)}
              />
            ))}
          </Box>
        </Box>

        <Box>
          <FieldLabel tip="The most this run can spend. The system sizes the list to fit and never exceeds it.">
            Run budget
          </FieldLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {[['$1', '1'], ['$5', '5'], ['$25', '25']].map(([label, val]) => (
              <SelectChip key={val} label={label} selected={budget === val} onClick={() => setBudget(val)} />
            ))}
            <SelectChip label="Custom" selected={!['1', '5', '25'].includes(budget)} onClick={() => setBudget('')} />
          </Box>
          <TextField
            fullWidth
            size="small"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="$1"
            InputProps={inputProps}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>AI sizes the list to fit.</Typography>
            {estimatedContacts != null && (
              <Typography sx={{ fontSize: '11px', color: THEME.mint, fontWeight: 700 }}>
                ~{estimatedContacts.toLocaleString('en-US')} contacts
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={15} sx={{ color: '#06281f' }} /> : <TravelExploreIcon />}
            sx={mintBtnSx}
            onClick={submit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Finding…' : 'Find these people in Apollo'}
          </Button>
          <Button
            variant="outlined"
            startIcon={previewing ? <CircularProgress size={15} sx={{ color: THEME.mint }} /> : <VisibilityOutlinedIcon />}
            sx={outlinedBtnSx}
            onClick={handlePreview}
            disabled={previewing}
          >
            {previewing ? 'Previewing…' : 'Preview people (free)'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScienceOutlinedIcon />}
            sx={outlinedBtnSx}
            onClick={() => setTestOpen(true)}
          >
            Test as a target
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: THEME.textMuted }}>
            <LockOutlinedIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '11px' }}>
              Searches Apollo and builds a reviewable list. No emails go out.
            </Typography>
          </Box>
        </Box>
        {submitError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{submitError}</Typography>
          </Box>
        )}
      </Box>

      {/* PREVIEW (free) rendered below the form. Shows HOW the sentence was
          interpreted (composed targeting) and a sample of the matched people. */}
      {(previewError || preview || previewing) && (
        <Box sx={{ mt: 2.5, border: `1px solid ${THEME.border}`, borderRadius: '12px', background: THEME.card, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>Preview</Typography>
            <InfoTip text="Free targeting preview. Nothing is revealed, written, or charged." />
          </Box>

          {previewError && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: preview ? 1.5 : 0 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: THEME.pink, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.5 }}>{previewError}</Typography>
            </Box>
          )}

          {previewing && !preview && (
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Loading preview…</Typography>
          )}

          {preview && (
            <>
              <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                How this was interpreted
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: preview.targeting && preview.targeting.rationale ? 1 : 2 }}>
                {(preview.targeting?.titles || []).map((t, i) => <Badge key={`t${i}`} tone="mint">{t}</Badge>)}
                {(preview.targeting?.sizes || []).map((s, i) => <Badge key={`s${i}`} tone="gray">{s}</Badge>)}
                {preview.targeting?.naics && <Badge tone="gray">NAICS {preview.targeting.naics}</Badge>}
                {preview.targeting?.persona && <Badge tone="amber">{preview.targeting.persona}</Badge>}
                {(!preview.targeting ||
                  ((preview.targeting.titles || []).length === 0 &&
                    (preview.targeting.sizes || []).length === 0 &&
                    !preview.targeting.naics &&
                    !preview.targeting.persona)) && (
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No structured targeting resolved.</Typography>
                )}
              </Box>
              {preview.targeting?.rationale && (
                <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6, mb: 2, fontStyle: 'italic' }}>
                  {preview.targeting.rationale}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Sample people
                </Typography>
                {preview.total_entries != null && (
                  <Typography sx={{ fontSize: '11px', color: THEME.mint, fontWeight: 700 }}>
                    {Number(preview.total_entries).toLocaleString('en-US')} total matches
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(preview.people || []).length === 0 && (
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>No people matched this targeting.</Typography>
                )}
                {(preview.people || []).map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontWeight: 600 }}>
                        {[p.firstName, p.lastNameObfuscated].filter(Boolean).join(' ') || '(name hidden)'}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
                        {[p.title, p.companyName].filter(Boolean).join('  ·  ')}
                      </Typography>
                    </Box>
                    <Badge tone={p.hasEmail ? 'mint' : 'muted'}>{p.hasEmail ? 'email available' : 'no email'}</Badge>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 2 }}>
                Preview is free. Finding people spends credits only on reveal.
              </Typography>
            </>
          )}
        </Box>
      )}

      {testOpen && <TestTargetModal onClose={() => setTestOpen(false)} />}
    </Box>
  );
};

// ===== Tab: Approvals (Chunk 3.5 — editable, one card per contact) =====
// The queue is fed live by GET /api/gtm/stream/contacts (see useContactsStream):
// contacts appear progressively with their state and openers supersede in place
// when the deep-transcript recomposition arrives. Each card is ONE contact —
// approve / edit / reject / regenerate act on that contact only.

// ── evidence sources → chip/panel kind (LinkedIn / News / Talk) ──────────────
// Derived from enrichment.sources[].type; keyword-bucketed so new provider tags
// fall into the closest of the three display kinds rather than leaking raw types.
function sourceKind(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('linkedin') || t.includes('profile')) return { key: 'linkedin', label: 'LinkedIn', Icon: LinkedInIcon };
  if (
    t.includes('transcript') || t.includes('talk') || t.includes('podcast') ||
    t.includes('youtube') || t.includes('video') || t.includes('media') || t.includes('audio')
  ) return { key: 'talk', label: 'Talk', Icon: RecordVoiceOverOutlinedIcon };
  if (
    t.includes('news') || t.includes('article') || t.includes('press') ||
    t.includes('serper') || t.includes('exa') || t.includes('web') || t.includes('blog')
  ) return { key: 'news', label: 'News', Icon: ArticleOutlinedIcon };
  return { key: t || 'source', label: type || 'Source', Icon: ArticleOutlinedIcon };
}

// Unique source kinds, in first-seen order, for the collapsed chip row.
function uniqueChips(sources) {
  const seen = new Set();
  const out = [];
  (sources || []).forEach((s) => {
    const k = sourceKind(s && s.type);
    if (!seen.has(k.key)) {
      seen.add(k.key);
      out.push(k);
    }
  });
  return out;
}

const SourceChip = ({ kind }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      px: '8px',
      py: '3px',
      borderRadius: '4px',
      background: 'rgba(255,255,255,0.05)',
      border: `1px solid ${THEME.border}`,
      color: THEME.textSecondary,
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      lineHeight: 1,
    }}
  >
    <kind.Icon sx={{ fontSize: 12 }} />
    {kind.label}
  </Box>
);

// The evidence panel: each enrichment.sources entry as { type, url, snippet } so
// the operator sees the actual profile, article, or transcript the opener drew from.
// Confidence chip for the opener's hook. Accepts a 'high'|'medium'|'low' string or a
// 0..1 number, bucketing the number so a numeric confidence still lands on a tone.
const HOOK_CONF_META = {
  high: { label: 'High confidence', tone: 'mint' },
  medium: { label: 'Medium confidence', tone: 'amber' },
  low: { label: 'Low confidence', tone: 'pink' },
};
function hookConfMeta(c) {
  if (typeof c === 'number') {
    if (c >= 0.75) return HOOK_CONF_META.high;
    if (c >= 0.4) return HOOK_CONF_META.medium;
    return HOOK_CONF_META.low;
  }
  const key = String(c == null ? '' : c).toLowerCase().trim();
  return HOOK_CONF_META[key] || (c ? { label: `${c} confidence`, tone: 'muted' } : null);
}

// The hook evidence panel: the specific, timely reason the opener leads with (an
// event the enrichment pass found). Renders subject, date, specifics, source, and a
// confidence chip when a hook was found; otherwise a muted proof-led fallback line.
// Always renders one block so revealing it never shifts the layout of the panel.
const HookPanel = ({ hook }) => {
  const found = !!hook && hook.found !== false;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Hook
        </Typography>
        <InfoTip text="The specific, timely reason the opener leads with, such as a recent event. When none is found, the opener falls back to a proof-led angle." size={13} />
      </Box>
      {found ? (
        <Box sx={{ p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            {hook.subject && (
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>{hook.subject}</Typography>
            )}
            {hookConfMeta(hook.confidence) && <Badge tone={hookConfMeta(hook.confidence).tone}>{hookConfMeta(hook.confidence).label}</Badge>}
          </Box>
          {hook.eventDate && (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>{String(hook.eventDate)}</Typography>
          )}
          {hook.specifics && (
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mt: 0.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{hook.specifics}</Typography>
          )}
          {hook.source && (
            typeof hook.source === 'string' && /^https?:\/\//i.test(hook.source) ? (
              <Link href={hook.source} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '11px', color: THEME.mint, textDecoration: 'none', mt: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                source <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Link>
            ) : (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>Source: {String(hook.source)}</Typography>
            )
          )}
        </Box>
      ) : (
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
          No specific hook found, proof-led fallback used.
        </Typography>
      )}
    </Box>
  );
};

// GTM-EVIDENCE-PROVENANCE-01. What the opener ACTUALLY drew from, read only from
// composeProvenance.evidenceUsed (contract v21: items are { fact, signalId }).
//
// This panel used to be the raw enrichment.sources array under a label claiming the
// opener drew from it. The model itself calls that field "URLs used across the brief":
// it is everything the enrichment pass gathered, which is why 10+ items appeared under a
// cap of 3. The label was false, not merely imprecise.
//
// There is NO fallback to the raw arrays. Falling back is the bug: it is what made a
// gathered-evidence list look like a used-evidence list. Absent provenance says so.
const OpenerEvidencePanel = ({ contact }) => {
  const prov = (contact && (contact.composeProvenance || contact.provenance)) || null;
  // Tolerate both the flat and the hook-nested shapes the composer writes.
  const used = prov
    ? (Array.isArray(prov.evidenceUsed)
      ? prov.evidenceUsed
      : (prov.hook && Array.isArray(prov.hook.evidenceUsed) ? prov.hook.evidenceUsed : null))
    : null;

  const shell = (children) => (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          What the opener drew from
        </Typography>
        <InfoTip
          text="The specific facts the composer recorded as used when it wrote this opener, capped at 3. This is provenance, not the full research brief. Everything the enrichment pass gathered is listed separately below."
          size={13}
        />
      </Box>
      {children}
    </Box>
  );

  if (!Array.isArray(used)) {
    return shell(
      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
        <InfoOutlinedIcon sx={{ fontSize: 15, color: THEME.textMuted, flexShrink: 0, mt: '1px' }} />
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.5 }}>
          Provenance is not available for this draft, so which facts the opener used was not recorded. The evidence gathered for this contact is listed below, but it is not a record of what the opener actually used.
        </Typography>
      </Box>
    );
  }

  if (used.length === 0) {
    return shell(
      <Box sx={{ p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
          The composer recorded no evidence as used for this opener.
        </Typography>
      </Box>
    );
  }

  return shell(
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {used.map((e, i) => {
        const fact = typeof e === 'string' ? e : (e && e.fact) || '';
        const signalId = (e && e.signalId) || '';
        return (
          <Box key={signalId || i} sx={{ display: 'flex', gap: 1, p: 1.25, borderRadius: '8px', background: THEME.mintFaint, border: `1px solid ${THEME.mintDim}` }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: THEME.mint, flexShrink: 0, mt: '2px' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, lineHeight: 1.5, wordBreak: 'break-word' }}>
                {fact || '(no fact recorded)'}
              </Typography>
              {signalId && (
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {signalId}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const EvidencePanel = ({ sources }) => {
  if (!sources || sources.length === 0) {
    return (
      <Box sx={{ p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>
          No evidence sources on this contact yet.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          All evidence gathered
        </Typography>
        <InfoTip
          text="Everything the enrichment pass found for this contact, which is the whole research brief and NOT a record of what the opener used. The composer cites at most three of these; which three is shown above under What the opener drew from."
          size={13}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sources.map((s, i) => {
          const kind = sourceKind(s && s.type);
          return (
            <Box
              key={i}
              sx={{ display: 'flex', gap: 1, p: 1.25, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}
            >
              <kind.Icon sx={{ fontSize: 16, color: THEME.textFaint, flexShrink: 0, mt: '2px' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {(s && s.type) || kind.label}
                  </Typography>
                  {s && s.url && (
                    <Link
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '11px', color: THEME.mint, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      source <OpenInNewIcon sx={{ fontSize: 11 }} />
                    </Link>
                  )}
                </Box>
                {s && s.snippet && (
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25, lineHeight: 1.5 }}>
                    {s.snippet}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// State chip — the contact's live lifecycle position, reusing the Badge palette.
const STATE_META = {
  enriching: { label: 'Enriching', tone: 'gray' },
  drafting: { label: 'Drafting', tone: 'amber' },
  ready: { label: 'Ready', tone: 'mint' },
  deepening: { label: 'Deepening', tone: 'amber' },
  needs_opener: { label: 'Needs opener', tone: 'pink' },
  enrichment_failed: { label: 'Enrichment failed', tone: 'pink' },
  sourced: { label: 'Queued', tone: 'muted' },
};

// Prefer the SSE `stage` (the live transition) but treat 'snapshot'/'upgraded' as
// non-states and fall back to `status`. pending_approval renders as "Ready".
function deriveState(stage, status) {
  let raw = stage && stage !== 'snapshot' && stage !== 'upgraded' ? stage : status;
  if (raw === 'pending_approval') raw = 'ready';
  return STATE_META[raw] || { label: raw || 'Unknown', tone: 'muted' };
}

const StateChip = ({ stage, status }) => {
  const m = deriveState(stage, status);
  return <Badge tone={m.tone}>{m.label}</Badge>;
};

// The small "model: X" stamp on each draft, from opener.modelUsed.
const ModelStamp = ({ model }) =>
  model ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: THEME.textMuted, fontWeight: 600 }}>
      <AutoAwesomeIcon sx={{ fontSize: 11 }} />
      model: {model}
    </Box>
  ) : null;

// Dark-themed model dropdown, options ALWAYS from GET /api/gtm/models (never hardcoded).
const ModelSelect = ({ value, options, onChange, disabled }) => (
  <Select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    size="small"
    displayEmpty
    sx={{
      fontSize: '12px',
      color: THEME.textPrimary,
      minWidth: 160,
      background: THEME.card,
      '& fieldset': { borderColor: THEME.border },
      '&:hover fieldset': { borderColor: THEME.borderStrong },
      '& .MuiSvgIcon-root': { color: THEME.textMuted },
      '& .MuiSelect-select': { py: '6px' },
    }}
    MenuProps={{
      PaperProps: {
        sx: {
          background: THEME.cardRaised,
          color: THEME.textPrimary,
          border: `1px solid ${THEME.borderStrong}`,
          '& .MuiMenuItem-root': { fontSize: '12px' },
          '& .Mui-selected': { background: `${THEME.mintFaint} !important` },
        },
      },
    }}
  >
    {(!options || options.length === 0) && (
      <MenuItem value="" disabled>
        No models available
      </MenuItem>
    )}
    {(options || []).map((m) => (
      <MenuItem key={m} value={m}>
        {m}
      </MenuItem>
    ))}
  </Select>
);

// Human-readable message for a failed contact action, special-casing the
// contracts the card must not swallow (409 not-pending, 423 sourcing paused).
function friendlyActionError(action, err) {
  if (err && err.code === 'contact_not_pending_approval') {
    return `Can't ${action}, this contact is no longer pending approval${
      err.serverStatus ? ` (now "${err.serverStatus}")` : ''
    }. It may have already been approved or updated.`;
  }
  if (err && err.code === 'sourcing_paused') {
    return `Can't ${action}, GTM sourcing is paused. Resume it, then try again.`;
  }
  if (err && err.code === 'sequence_draft') {
    return `Can't ${action}, this campaign's sequence is still in draft. Publish it live (in the Sequences tab) before approving contacts.`;
  }
  if (err && err.code === 'contact_not_found') {
    return `Can't ${action}, this contact no longer exists.`;
  }
  if (err && err.code === 'send_disabled') {
    return `Can't ${action}, sending is disabled on the server (GTM_SEND_ENABLED is off).`;
  }
  if (err && err.code === 'no_draft') {
    return `Can't ${action}, there is no draft to send for this reply.`;
  }
  // Contact import refusals. Without these the bare server code was the whole message.
  if (err && err.code === 'source_label_required') {
    return 'A source label is required, so nothing was imported.';
  }
  if (err && err.code === 'empty_or_unparseable') {
    return 'That file could not be read as a spreadsheet, so nothing was imported.';
  }
  if (err && err.code === 'no_input') {
    return 'No file reached the server, so nothing was imported.';
  }
  if (err && err.code === 'list_id_required') {
    return 'No Apollo list was identified, so nothing was imported.';
  }
  // GTM-REPLY-REFUSAL-COPY-01: approveReply refuses a send for three different reasons
  // and each one asks something different of the operator, so each gets its own words
  // instead of one shrug. Only the third is fixable from here, and it names the alias.
  if (err && err.code === 'reply_thread_unknown') {
    return 'No Instantly thread recorded for this contact, so it cannot be answered from here.';
  }
  if (err && err.code === 'reply_mailbox_unknown') {
    return 'We could not determine which of our mailboxes this prospect wrote to.';
  }
  if (err && err.code === 'reply_mailbox_not_in_fleet') {
    const alias = (err.data && err.data.replyFromMailbox) || 'that mailbox';
    return `This prospect wrote to ${alias}, which is no longer connected in Instantly. Reconnect that mailbox or re-sync the fleet.`;
  }
  if (err && err.code === 'budget_too_low') {
    const per = err.data && err.data.perContactTotal;
    return `Raise the run budget. It is below the minimum needed to source even one contact${per ? ` (about $${per} per contact)` : ''}.`;
  }
  return (err && err.message) || `Could not ${action} this contact.`;
}

// One editable approval card. Holds only local UI state (expand / edit buffers /
// selected model / regenerating); all persistence flows up through callbacks.
const ContactApprovalCard = ({
  contact,
  opener,
  models,
  defaultModel,
  selectable,
  selected,
  actionError,
  onToggleSelect,
  onApprove,
  onReject,
  onSaveEdit,
  onRegenerate,
  focused,
  innerRef,
  autoApproved,
}) => {
  const [open, setOpen] = useState(false); // Context & flags disclosure
  // GTM-QUEUE-COMPACT-01: the email body is collapsed by default so a queue of
  // drafts is scannable. Subject and a one-line snippet stay visible; the body is
  // one click away, and editing forces it open.
  const [bodyOpen, setBodyOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(opener.text || '');
  const [draftSubject, setDraftSubject] = useState(opener.subject || '');
  const [model, setModel] = useState(opener.modelUsed || defaultModel || '');
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Seed the model selector once models/default resolve (they load after mount).
  useEffect(() => {
    if (!model && (opener.modelUsed || defaultModel)) setModel(opener.modelUsed || defaultModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultModel, opener.modelUsed]);

  const isReady = contact.status === 'pending_approval';
  const sources = Array.isArray(contact.sources) ? contact.sources : [];
  const chips = uniqueChips(sources);
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() || contact.email;
  const sub = [contact.title, contact.companyName].filter(Boolean).join(' · ');

  // Reasoning flags — fit / suppression / competitor context, shown as expandable
  // context when the contact carries it. Data-backed only; nothing is fabricated.
  const flagReasons = [
    { label: 'Fit', text: contact.fitReason || (contact.fit && contact.fit.reason) },
    { label: 'Suppression', text: contact.suppressionReason },
    { label: 'Competitor', text: contact.competitorReason },
  ].filter((f) => f.text);
  const contextCount = chips.length + flagReasons.length;

  // The full assembled Email 1. Prefer opener.body (the template-wrapped body the
  // backend assembles); fall back to opener.text for older contacts that predate
  // the body field so nothing breaks.
  const assembledBody = opener.body || opener.text || '';
  // Live preview while editing: the operator edits only the opener line, so splice
  // the draft into the assembled body where the original opener line sits. If we
  // have no body (or it does not contain the line), the opener line IS the preview.
  const previewBody =
    opener.body && opener.text && opener.body.includes(opener.text)
      ? opener.body.replace(opener.text, draftText)
      : draftText;

  // One-line gist of the email, for the collapsed state.
  const bodySnippet = assembledBody.replace(/\s+/g, ' ').trim().slice(0, 120);

  const beginEdit = () => {
    setDraftText(opener.text || '');
    setDraftSubject(opener.subject || '');
    setSaveError(null);
    setEditing(true);
    setOpen(true);
    setBodyOpen(true);
  };
  // Awaits the page's optimistic-then-reconciled save. On failure the page has
  // already rolled back the local edit; we keep the editor open and show why so
  // nothing reads as a false success.
  const saveEdit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveEdit(contact.id, { text: draftText, subject: draftSubject });
      setEditing(false);
    } catch (err) {
      setSaveError((err && err.message) || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setRegenError(null);
    try {
      await onRegenerate(contact.id, model);
    } catch (err) {
      setRegenError((err && err.message) || 'Regenerate failed.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Box
      ref={innerRef}
      sx={{
        border: `1px solid ${focused || selected ? THEME.mint : THEME.border}`,
        boxShadow: focused ? `0 0 0 3px ${THEME.mintFaint}` : 'none',
        borderRadius: '14px',
        background: THEME.card,
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      {/* header — who this is, plus bulk-select and live state */}
      <Box sx={{ px: 3, pt: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, [MOBILE_MQ]: { px: 2 } }}>
        {selectable && (
          <Checkbox
            checked={!!selected}
            onChange={() => onToggleSelect(contact.id)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.25, mt: '2px', color: THEME.border, '&.Mui-checked': { color: THEME.mint } }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: THEME.textPrimary }}>{name}</Typography>
            <StateChip stage={contact.stage} status={contact.status} />
            <LaneBadges contact={contact} />
            <ModelStamp model={opener.modelUsed} />
          </Box>
          {sub && <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25 }}>{sub}</Typography>}
        </Box>
      </Box>

      {/* THE EMAIL — the hero of the card: subject, then the full assembled body,
          the biggest thing on screen. Inline edit swaps in the editor in place. */}
      <Box sx={{ px: 3, pt: 2, pb: 2.5, [MOBILE_MQ]: { px: 2 } }}>
        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography sx={fieldLabelSx}>Subject</Typography>
              <TextField fullWidth size="small" value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} InputProps={inputProps} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Personalization opener</Typography>
                <InfoTip
                  text="You edit only the model-written opener line. The rest of the email template around it is fixed, and the system assembles the full body from this line."
                  size={13}
                />
              </Box>
              <TextField fullWidth multiline minRows={3} size="small" value={draftText} onChange={(e) => setDraftText(e.target.value)} InputProps={inputProps} />
            </Box>
            {/* live full-email preview, updating as the opener line is edited */}
            <Box>
              <Typography sx={fieldLabelSx}>Full email preview</Typography>
              <Box sx={{ p: 2, borderRadius: '10px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
                <Typography sx={{ fontSize: '15px', color: THEME.textPrimary, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                  {previewBody || '—'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                sx={mintBtnSx}
                disabled={saving}
                onClick={saveEdit}
                startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
              >
                {saving ? 'Saving…' : 'Save edits'}
              </Button>
              <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={saving} onClick={() => setEditing(false)}>
                Cancel
              </Button>
              {saveError && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{saveError}</Typography>}
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Subject
              </Typography>
              <InfoTip text="Every merge field resolved. This is the precise message the send loop will deliver on approval." size={13} />
              <Box sx={{ flex: 1 }} />
              {isReady && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ ...outlinedBtnSx, py: 0.25 }}
                  onClick={beginEdit}
                >
                  Edit
                </Button>
              )}
            </Box>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: THEME.textPrimary, mb: 1, lineHeight: 1.35 }}>
              {opener.subject || '—'}
            </Typography>

            {/* The draft itself, collapsed by default. Closed, you get the first line
                so the queue stays scannable; open, the exact message that will send. */}
            <Box
              onClick={() => setBodyOpen((v) => !v)}
              role="button"
              tabIndex={0}
              aria-expanded={bodyOpen}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBodyOpen((v) => !v); } }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                userSelect: 'none',
                color: THEME.textSecondary,
                '&:hover': { color: THEME.textPrimary },
                [MOBILE_MQ]: { minHeight: 44 },
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0, transform: bodyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                {bodyOpen ? 'Hide email' : 'Read email'}
              </Typography>
              {!bodyOpen && (
                <Typography
                  sx={{ fontSize: '12px', color: THEME.textMuted, ml: 0.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {bodySnippet || (isReady ? '—' : 'Draft in progress…')}
                </Typography>
              )}
            </Box>
            <Collapse in={bodyOpen} unmountOnExit>
              <Box sx={{ p: 2.5, mt: 1, borderRadius: '10px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}`, [MOBILE_MQ]: { p: 2 } }}>
                <Typography sx={{ fontSize: '15px', color: THEME.textPrimary, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                  {assembledBody || (isReady ? '—' : 'Draft in progress…')}
                </Typography>
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      {/* Context & flags — the "why": source chips, any fit/suppression/competitor
          reasoning, the evidence it drew from, and the per-contact regenerate. Kept
          out of the way so the email stays the focus; one tap reveals it. */}
      <Box sx={{ px: 3, pb: open ? 0 : 2, [MOBILE_MQ]: { px: 2 } }}>
        <Box
          onClick={() => setOpen((o) => !o)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            userSelect: 'none',
            color: THEME.textSecondary,
            '&:hover': { color: THEME.textPrimary },
            [MOBILE_MQ]: { minHeight: 44 },
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Context &amp; flags</Typography>
          {contextCount > 0 && <CountBadge count={contextCount} tone="gray" />}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 3, pb: 2, pt: 1.5, [MOBILE_MQ]: { px: 2 } }}>
          {(chips.length > 0 || contact.naics?.code) && (
            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
              {chips.map((ch) => (
                <SourceChip key={ch.key} kind={ch} />
              ))}
              {contact.naics?.code && (
                <Box
                  component="span"
                  title={`NAICS source: ${contact.naics.source}${contact.naics.confidence === 'weak' ? ' (low confidence)' : ''}`}
                  sx={{ display: 'inline-flex' }}
                >
                  <Badge tone={contact.naics.confidence === 'weak' ? 'amber' : 'gray'}>
                    {contact.naics.label ? `${contact.naics.label} (${contact.naics.code})` : contact.naics.code}
                  </Badge>
                </Box>
              )}
            </Box>
          )}
          {flagReasons.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              {flagReasons.map((f) => (
                <Box key={f.label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Badge tone="amber">{f.label}</Badge>
                  <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{f.text}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <HookPanel hook={opener && opener.hook} />
          <OpenerEvidencePanel contact={contact} />
          <EvidencePanel sources={sources} />

          {/* per-card regenerate — ONE-contact altitude */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Regenerate with</Typography>
            <ModelSelect value={model} options={models} onChange={setModel} disabled={regenerating} />
            <Button
              size="small"
              variant="outlined"
              onClick={regenerate}
              disabled={regenerating || !model}
              startIcon={regenerating ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              sx={outlinedBtnSx}
            >
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </Button>
            {regenError && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{regenError}</Typography>}
          </Box>
        </Box>
      </Collapse>

      {/* decision row — the two large actions, or the auto-approve badge when the
          campaign approves passing contacts on its own (no manual action needed). */}
      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.015)', [MOBILE_MQ]: { px: 2 } }}>
        {autoApproved ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Badge tone="mint">Auto-approved</Badge>
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, flex: 1, minWidth: 180 }}>
              This campaign approves contacts that pass every gate. No action needed.
            </Typography>
            {/* Autopilot still has to be interruptible: a test row in an auto-approving
                campaign needs a way out before the send loop claims it. */}
            <Tooltip arrow placement="top" {...tipSlotProps} title="Pull this person out before autopilot sends to them.">
              <Button
                size="small"
                variant="outlined"
                sx={dangerBtnSx}
                onClick={() => onReject(contact.id)}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              >
                Remove
              </Button>
            </Tooltip>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                sx={{ ...mintBtnSx, px: 3, py: 1.25, fontSize: '14px', flex: '1 1 auto', [MOBILE_MQ]: { minHeight: 48 } }}
                disabled={!isReady}
                onClick={() => onApprove(contact.id)}
                startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              >
                Approve
              </Button>
              {/* GTM-QUEUE-REMOVE-01: the terminal reject, named and iconed as the
                  throw-it-out action it has always been. Enabled at every status, so
                  a test row that never reached 'ready' can still be cleared. */}
              <Tooltip arrow placement="top" {...tipSlotProps} title="Takes this person out of the queue for good. Nothing sends to them. Use it for test rows.">
                <Button
                  variant="outlined"
                  sx={{ ...dangerBtnSx, px: 3, py: 1.25, fontSize: '14px', [MOBILE_MQ]: { minHeight: 48 } }}
                  onClick={() => onReject(contact.id)}
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                >
                  Remove
                </Button>
              </Tooltip>
            </Box>
            {!isReady && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1 }}>
                Approve unlocks when the draft is ready. Remove works at any point.
              </Typography>
            )}
          </>
        )}
        {/* Surface a failed approve/reject (e.g. 409 not-pending) instead of
            swallowing it — the optimistic removal has been rolled back. */}
        {actionError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>{actionError}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// GTM-QUEUE-AUTOPILOT-01: the auto-approve switches, surfaced where the approving
// actually happens instead of only on the Home campaign cards. Auto-approve is a
// per-campaign trust policy on the backend (campaign.autoApprove.enabled, PATCH
// /api/gtm/campaigns/:id), so this is a switch per campaign, not one global button.
// Collapsed by default: the summary line is the whole state at a glance.
//
// NOTE: this is a different thing from mailbox autopilot (Settings > Mailboxes),
// which manages warmup and sending capacity and never approves anything.
const ApprovalsAutopilotPanel = ({ campaigns, waitingByCampaign, autoApproveOf, autoApproveErrors, onToggleAutoApprove }) => {
  const [open, setOpen] = useState(false);
  const list = campaigns || [];
  if (!onToggleAutoApprove || list.length === 0) return null;

  const onCount = list.filter((c) => autoApproveOf(c)).length;
  const summary =
    onCount === 0
      ? 'Off. Every contact waits for your OK.'
      : `On for ${onCount} of ${list.length} ${list.length === 1 ? 'campaign' : 'campaigns'}.`;

  return (
    <Box sx={{ borderRadius: '10px', border: `1px solid ${onCount ? THEME.mintDim : THEME.border}`, background: THEME.card, mb: 2 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1.75, py: 1.25, cursor: 'pointer', userSelect: 'none' }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16, color: onCount ? THEME.mint : THEME.textMuted }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>Autopilot</Typography>
        {onCount > 0 && <Badge tone="mint">{`${onCount} on`}</Badge>}
        <InfoTooltip title="Autopilot (auto-approve)" size={13}>
          When on for a campaign, a contact is approved and sent automatically once it clears every check: a verified email address, recent enough research, and a confident hook. Anything that fails a check still waits for you here. Off by default, and set per campaign. This is separate from mailbox autopilot in Settings, which only manages warmup and sending capacity.
        </InfoTooltip>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, flex: 1, minWidth: 120 }}>{summary}</Typography>
        <ExpandMoreIcon sx={{ fontSize: 20, color: THEME.textMuted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.75, pb: 1.5, pt: 0.5, borderTop: `1px solid ${THEME.border}` }}>
          {list.map((c) => {
            const on = autoApproveOf(c);
            const waiting = waitingByCampaign[c.id] || 0;
            return (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.75 }}>
                <Switch
                  checked={on}
                  onChange={(e) => onToggleAutoApprove(c, e.target.checked)}
                  size="small"
                  sx={{ flexShrink: 0, '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.mint }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.mint } }}
                />
                <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, flex: 1, minWidth: 140 }}>{c.name || c.key || c.id}</Typography>
                {waiting > 0 && (
                  <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{`${waiting} waiting here`}</Typography>
                )}
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: on ? THEME.mint : THEME.textMuted, width: 74, textAlign: 'right' }}>
                  {on ? 'Sends itself' : 'Asks you'}
                </Typography>
                {autoApproveErrors && autoApproveErrors[c.id] && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexBasis: '100%' }}>
                    <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
                    <Typography sx={{ fontSize: 11, color: THEME.pink }}>{autoApproveErrors[c.id]}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

const EditableApprovalsTab = ({
  contacts,
  streamStatus,
  models,
  modelsLoading,
  modelsError,
  defaultModel,
  onChangeDefaultModel,
  selectedIds,
  onToggleSelect,
  onBulkApprove,
  onBulkReject,
  onClearSelection,
  onApprove,
  onReject,
  onSaveEdit,
  onRegenerate,
  effectiveOpener,
  actionErrors,
  autoApproveIds,
  anyBusy,
  onApproveAllPending,
  campaigns,
  autoApproveOf,
  autoApproveErrors,
  onToggleAutoApprove,
}) => {
  const isAuto = useCallback((c) => !!(autoApproveIds && c && autoApproveIds.has(c.campaignId)), [autoApproveIds]);
  // Pending rows a human still has to approve: pending_approval, minus the ones whose
  // campaign auto-approves (those need no manual action).
  const pendingIds = contacts.filter((c) => c.status === 'pending_approval' && !isAuto(c)).map((c) => c.id);
  const readyCount = pendingIds.length;
  // How many rows in this queue belong to each campaign, for the autopilot panel.
  const waitingByCampaign = useMemo(() => {
    const m = {};
    contacts.forEach((c) => { if (c.campaignId) m[c.campaignId] = (m[c.campaignId] || 0) + 1; });
    return m;
  }, [contacts]);
  const selectedCount = selectedIds.size;
  const streaming = streamStatus === 'live' || streamStatus === 'connecting';

  // Keyboard-driven review on desktop: j / k move a focus ring between cards, a
  // approves the focused card (only when its draft is ready). Disabled on touch and
  // ignored while typing in a field or holding a modifier, so it never hijacks input.
  const isDesktop = useMediaQuery('(min-width:769px)');
  const [focusIdx, setFocusIdx] = useState(0);
  const cardRefs = useRef([]);
  const fi = contacts.length ? Math.min(focusIdx, contacts.length - 1) : 0;

  useEffect(() => {
    if (!isDesktop || contacts.length === 0) return;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (e.key === 'j') {
        e.preventDefault();
        setFocusIdx((i) => Math.min(contacts.length - 1, Math.min(i, contacts.length - 1) + 1));
      } else if (e.key === 'k') {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, Math.min(i, contacts.length - 1) - 1));
      } else if (e.key === 'a') {
        const c = contacts[Math.min(focusIdx, contacts.length - 1)];
        if (c && c.status === 'pending_approval' && !isAuto(c)) {
          e.preventDefault();
          onApprove(c.id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, contacts, focusIdx, onApprove, isAuto]);

  // Keep the focused card in view as j/k move through the list.
  useEffect(() => {
    if (!isDesktop) return;
    const el = cardRefs.current[fi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [fi, isDesktop]);

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Each card is one person the system found and drafted for. Review the opener and the evidence it drew from, edit if needed, then approve to let it send.">
        Every person waiting for your OK — the exact email and the evidence behind it. Edit inline, regenerate with a different model, or approve.
      </Explainer>
      {isDesktop && contacts.length > 0 && (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mb: 1.5 }}>
          Tip: press <strong>j</strong> / <strong>k</strong> to move between cards, <strong>a</strong> to approve the focused one.
        </Typography>
      )}

      {/* Autopilot: who still has to ask you, and who sends on its own. */}
      <ApprovalsAutopilotPanel
        campaigns={campaigns}
        waitingByCampaign={waitingByCampaign}
        autoApproveOf={autoApproveOf}
        autoApproveErrors={autoApproveErrors}
        onToggleAutoApprove={onToggleAutoApprove}
      />

      {/* ENGINE-ALTITUDE control bar — acts on the whole drafting engine. Blue
          accent keeps it visually distinct from the per-contact (mint/pink)
          actions on each card. The campaign start/pause lives in the page header. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          p: 1.5,
          mb: 2,
          borderRadius: '10px',
          background: THEME.blueDim,
          border: '1px solid rgba(96,165,250,0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: THEME.blue }} />
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, fontWeight: 600 }}>Default drafting model</Typography>
          <InfoTip text="The model new drafts use across the engine. A card's own 'Regenerate with' overrides it for that one contact." size={13} />
        </Box>
        <ModelSelect value={defaultModel} options={models} onChange={onChangeDefaultModel} disabled={modelsLoading} />
        {modelsError && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{modelsError}</Typography>}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
          {selectedCount > 0 ? `${selectedCount} selected` : `${readyCount} ready`}
        </Typography>
        {selectedCount > 0 ? (
          <>
            <Button
              size="small"
              variant="contained"
              sx={mintBtnSx}
              disabled={anyBusy}
              onClick={() => onBulkApprove([...selectedIds])}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            >
              Approve selected
            </Button>
            {/* GTM-QUEUE-REMOVE-01: clear a whole batch of test rows in one pass. */}
            <Tooltip arrow placement="top" {...tipSlotProps} title="Takes every selected person out of the queue for good. Nothing sends to them.">
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={dangerBtnSx}
                  disabled={anyBusy}
                  onClick={() => onBulkReject([...selectedIds])}
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                >
                  Remove selected
                </Button>
              </Box>
            </Tooltip>
            <Button size="small" variant="outlined" sx={outlinedBtnSx} onClick={onClearSelection}>
              Clear
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            sx={mintBtnSx}
            disabled={anyBusy || pendingIds.length === 0}
            onClick={onApproveAllPending}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
          >
            Approve all pending{pendingIds.length ? ` (${pendingIds.length})` : ''}
          </Button>
        )}
      </Box>

      {streamStatus === 'error' && (
        <Box sx={{ mb: 2 }}>
          <ErrorState message="The live contact stream is unreachable. Cards will appear here once it reconnects." />
        </Box>
      )}

      {contacts.length === 0 ? (
        streaming ? <LoadingState label="Waiting for contacts…" /> : <EmptyState label="No contacts in the queue." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {contacts.map((c, i) => (
            <ContactApprovalCard
              key={c.id}
              contact={c}
              opener={effectiveOpener(c)}
              models={models}
              defaultModel={defaultModel}
              selectable={c.status === 'pending_approval' && !isAuto(c)}
              selected={selectedIds.has(c.id)}
              actionError={actionErrors[c.id]}
              onToggleSelect={onToggleSelect}
              onApprove={onApprove}
              onReject={onReject}
              onSaveEdit={onSaveEdit}
              onRegenerate={onRegenerate}
              focused={isDesktop && i === fi}
              innerRef={(el) => { cardRefs.current[i] = el; }}
              autoApproved={isAuto(c)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ===== Tab: Review =====
// Held contacts as rows. Nothing auto-resumes.

const ReviewTab = ({ contacts, loading, error, onRetry, onDrop, onHandle, onNavigate }) => (
  <Box sx={{ maxWidth: 920 }}>
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        p: 1.5,
        mb: 2,
        borderRadius: '8px',
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${THEME.border}`,
      }}
    >
      <WarningAmberIcon sx={{ fontSize: 16, color: THEME.amber }} />
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>
        The safety stop-gap caught these. Nothing auto-resumes.
      </Typography>
      <InfoTip text="Between emails, the system re-checks each person. If something sensitive shows up, it stops and waits for a human. These never resume automatically." size={15} />
    </Box>

    <Resource
      loading={loading}
      error={error}
      empty={!contacts || contacts.length === 0}
      loadingLabel="Loading held contacts…"
      emptyLabel="No held contacts."
      onRetry={onRetry}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {(contacts || []).map((ct, idx) => {
          const name = `${ct.firstName || ''} ${ct.lastName || ''}`.trim();
          // title is "" until contacts carry a job title — drop it (and its
          // separator) so the row never shows a stray dot or blank segment.
          const headline = [name, ct.title, ct.persona].filter(Boolean).join(' · ');
          const sub = [ct.companyName, ct.email].filter(Boolean).join(' · ');
          return (
            <Box
              key={ct.email || idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                border: `1px solid ${THEME.border}`,
                borderRadius: '10px',
                background: THEME.card,
                px: 2,
                py: 1.5,
                [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'stretch' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>
                  {headline}
                </Typography>
                {sub && (
                  <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25 }}>
                    {sub}
                  </Typography>
                )}
                {ct.heldReason && <HoldReason reason={ct.heldReason} onNavigate={onNavigate} />}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={() => onHandle(ct)}>
                  Handle personally
                </Button>
                <Button variant="outlined" size="small" sx={dangerBtnSx} onClick={() => onDrop(ct)}>
                  Drop
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Resource>
  </Box>
);

// Tab label with an info tooltip beside it.
const TabLabel = ({ children, tip, count, countTone }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    {children}
    {count != null && <CountBadge count={count} tone={countTone} />}
    {tip && <InfoTip text={tip} size={13} sx={{ ml: 0.5 }} />}
  </Box>
);

// ===== Navigation (Pass 1 redesign): six task-verb groups =====
// The IA is organized around the operator's job, not the data model. Each surface's
// original info tooltip is preserved verbatim and relocated with it: the Queue tips
// ride the Review & approve chips, the Library tips ride the Settings sub-tabs, and
// Sequences/Prompts tips ride the Emails sub-tabs.
//
// Old → new mapping (see LEGACY_GROUP redirect in OutboundPage):
//   campaigns → home (campaign management now lives on Home)
//   new       → find/new      contacts → find/import
//   queue     → review (chip preserved)
//   sequences → emails/sequences   library:prompts → emails/prompts
//   library   → settings (links/suppressions/competitors/mailboxes)
//   inbox     → replies
const NAV_GROUPS = [
  { key: 'home', label: 'Home', tip: 'Everything that needs you right now, and your campaigns at a glance.' },
  { key: 'find', label: 'Find people', tip: 'Bring new people into the pipeline: ask the system to find them, import a list, or test one person.' },
  { key: 'review', label: 'Review & approve', tip: 'Everything waiting on you: approvals first, plus the people the system skipped.' },
  { key: 'emails', label: 'Emails', tip: 'The email content the system sends: each sequence, and the system prompts that write the copy.' },
  { key: 'replies', label: 'Replies', tip: 'Prospects who replied, with a drafted response awaiting your approval. The badge counts the replies you have not opened yet.' },
  { key: 'settings', label: 'Settings', tip: 'Reference data and configuration: links, suppressions, competitors, and sending mailboxes.' },
];
const NAV_GROUP_KEYS = NAV_GROUPS.map((g) => g.key);

// Find people sub-tabs: source new people (New request), import your own list, or
// dry-run one prospect end to end.
const FIND_SUBS = [
  { key: 'new', label: 'New request', tip: 'Ask the system to find new people to reach.' },
  { key: 'import', label: 'Import', tip: 'Every sourced and imported contact with its pipeline state. Import more from a file or a saved Apollo list.' },
  { key: 'verify', label: 'Verify a list', tip: 'Upload a list of email addresses to verify through Scrubby before outreach. See what will be checked and what it costs, then clean it. Catch-all addresses go to a slower deep-verification lane.' },
  { key: 'test', label: 'Test one person', tip: 'Pick one prospect, run them through the whole pipeline without sending, and see every stage — then optionally mail it to your own inbox.' },
];
const FIND_KEYS = FIND_SUBS.map((s) => s.key);

// Emails sub-tabs: the per-sequence copy/cadence, and the system prompts behind it.
const EMAILS_SUBS = [
  { key: 'sequences', label: 'Sequences', tip: "Edit each sequence's copy, cadence, and link; preview it; set it live." },
  { key: 'prompts', label: 'Prompts', tip: 'The live GTM system prompts. View, edit to a new version, preview compose_opener output, and roll back.' },
  { key: 'proofs', label: 'Proofs', tip: 'The inventory of what we can claim: live proofs, claims proposed from calls, and demand signals.' },
];
const EMAILS_KEYS = EMAILS_SUBS.map((s) => s.key);

// Settings sub-tabs: the reference lists and sending configuration.
const SETTINGS_SUBS = [
  { key: 'links', label: 'Links', tip: 'The link-asset library that [link] resolves to. Register URLs or upload files.' },
  { key: 'suppressions', label: 'Suppressions', tip: "The 'we know them' send-suppression list (email or domain)." },
  { key: 'competitors', label: 'Competitors', tip: 'The manual competitor-protection list. Block competing capabilities from being cited, or hold a company entirely.' },
  { key: 'mailboxes', label: 'Mailboxes', tip: 'The sending mailboxes and their warmup state, plus the daily send/enrich capacity they add up to. Instantly is the source of truth; sync to pull the latest.' },
];
const SETTINGS_KEYS = SETTINGS_SUBS.map((s) => s.key);

// Review & approve: Approvals is the primary surface. The other four are the
// "skipped" queues, revealed behind a disclosure. Tips preserved verbatim.
const QUEUE_CHIPS = [
  { key: 'approvals', label: 'Approvals', tip: 'Lists waiting for your OK before anything sends.', countTone: 'amber' },
  { key: 'review', label: 'Review', tip: 'People the safety system paused for a human to check.', countTone: 'pink' },
  { key: 'mismatch', label: 'Mismatch', tip: 'Contacts the fit check held as not matching the audience. Read-only.', countTone: 'gray' },
  { key: 'deferred', label: 'Deferred', tip: 'Contacts deferred by company governance. Read-only.', countTone: 'gray' },
  { key: 'competitor', label: 'Competitors', tip: 'Contacts held because their company is a full-block competitor. Read-only.', countTone: 'pink' },
];
const QUEUE_KEYS = QUEUE_CHIPS.map((c) => c.key);
// The four "skipped" queues shown behind the Review & approve disclosure.
const SKIPPED_CHIPS = QUEUE_CHIPS.filter((c) => c.key !== 'approvals');

// A selectable nav chip: label + optional count badge + the relocated info tooltip.
// Used for the Queue filter chips and the Library sub-tabs. Wraps to a second line
// in a flex-wrap row rather than overflowing; 44px min height on mobile.
const NavChip = ({ label, tip, count, countTone, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      px: 1.25,
      py: 0.5,
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 600,
      border: `1px solid ${selected ? THEME.mint : THEME.border}`,
      color: selected ? THEME.mint : THEME.textSecondary,
      background: selected ? THEME.mintFaint : 'transparent',
      '&:hover': { borderColor: THEME.mint },
      [MOBILE_MQ]: { minHeight: 44 },
    }}
  >
    {label}
    {count != null && <CountBadge count={count} tone={countTone} />}
    {tip && <InfoTip text={tip} size={13} sx={{ ml: 0.5 }} />}
  </Box>
);

// ===== Tab: Sequences (Chunk 3.7 — compact scaffold reference) =====
// Static reference data: the fixed persona sequences fetched ONCE from
// GET /api/gtm/sequences (no per-contact calls, no message log). The model writes
// only the [personalization] opener; the cadence and template around it are fixed
// and shown here so the scaffold structure is legible.

// Highlight the merge tokens ([personalization], [First Name], [Company], [link],
// and any other [bracketed] token) so the scaffold structure reads clearly. split
// with a capturing group yields alternating text / token segments; each token
// segment matches ^[...]$ exactly.
const MERGE_TOKEN_SPLIT = /(\[[^\]]+\])/g;
const isMergeToken = (s) => /^\[[^\]]+\]$/.test(s);

const TokenText = ({ text }) => {
  const parts = String(text || '').split(MERGE_TOKEN_SPLIT);
  return (
    <>
      {parts.map((part, i) =>
        isMergeToken(part) ? (
          <Box
            key={i}
            component="span"
            sx={{
              display: 'inline',
              px: '4px',
              py: '1px',
              mx: '1px',
              borderRadius: '4px',
              background: THEME.mintFaint,
              border: `1px solid ${THEME.mintDim}`,
              color: THEME.mint,
              fontSize: '0.85em',
              fontWeight: 700,
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

// Status badge palette + a "Day 0-3-7-11-16" cadence label.
const SEQ_STATUS_META = {
  live: { label: 'Live', tone: 'mint' },
  draft: { label: 'Draft', tone: 'amber' },
};
function cadenceLabel(seq) {
  const days =
    Array.isArray(seq.cadence) && seq.cadence.length
      ? seq.cadence
      : Array.isArray(seq.steps)
      ? seq.steps.map((s) => s.dayOffset)
      : [];
  return days.length ? `Day ${days.join('-')}` : 'No steps';
}

// One sequence as a bordered list row. Click to open the editor.
const SequenceListCard = ({ seq, onOpen }) => {
  const st = SEQ_STATUS_META[seq.status] || { label: seq.status || 'unknown', tone: 'muted' };
  const stepCount = Array.isArray(seq.steps) ? seq.steps.length : 0;
  return (
    <Box
      onClick={onOpen}
      sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.5, cursor: 'pointer', '&:hover': { borderColor: THEME.borderStrong } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>{seq.name || seq.key || 'Untitled'}</Typography>
            {seq.variant && <Badge tone="gray">Variant {seq.variant}</Badge>}
            <Badge tone={st.tone}>{st.label}</Badge>
          </Box>
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25 }}>
            {cadenceLabel(seq)} · {stepCount} step{stepCount === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" sx={outlinedBtnSx}>Open</Button>
      </Box>
    </Box>
  );
};

// A stacked before/after template preview with shortcodes highlighted (reuses TokenText).
const TemplatePreviewPane = ({ label, subject, body, accent }) => (
  <Box sx={{ flex: 1, minWidth: 0, border: `1px solid ${accent || THEME.border}`, borderRadius: '6px', background: 'rgba(255,255,255,0.02)', p: 1 }}>
    <Typography sx={{ fontSize: '10px', color: accent || THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, mb: 0.5 }}>{label}</Typography>
    {subject != null && subject !== '' && (
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textSecondary, mb: 0.5, whiteSpace: 'pre-wrap' }}>
        <TokenText text={subject} />
      </Typography>
    )}
    <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
      <TokenText text={body || ''} />
    </Typography>
  </Box>
);

// One editable step inside the editor. Subject is disabled + cleared for a reply. Carries the
// unified per-step model + research config, a shortcode legend, and an AI-revise control whose
// preview must preserve every shortcode before it can be accepted.
// Constrained tone presets for the per-step Direction box. Each writes ONE plain-English
// line into the existing stepDirection field (no new field, no endpoint). Tapping swaps
// the current preset line in place rather than stacking, and leaves freeform text intact.
const TONE_PRESETS = [
  { key: 'warm', label: 'Warm', line: 'Write this step in a warm, personable tone.' },
  { key: 'direct', label: 'Direct', line: 'Keep this step direct and to the point.' },
  { key: 'concise', label: 'Concise', line: 'Keep this step short and concise.' },
  { key: 'formal', label: 'Formal', line: 'Use a formal, professional tone for this step.' },
  { key: 'friendly', label: 'Friendly', line: 'Use a friendly, approachable tone for this step.' },
  { key: 'urgent', label: 'Urgent', line: 'Give this step a clear sense of urgency.' },
];
const TONE_PRESET_LINES = TONE_PRESETS.map((t) => t.line);

const SequenceStepEditor = ({ step, index, error, onChange, sequenceId, researchOff }) => {
  const isReply = step.threadBehavior === 'reply_same_thread';
  const set = (patch) => onChange(index, patch);
  const setThread = (tb) => (tb === 'reply_same_thread' ? set({ threadBehavior: tb, subject: '' }) : set({ threadBehavior: tb }));

  const rc = step.researchConfig || { sources: RESEARCH_SOURCE_KEYS.slice(), searchPrompt: '' };
  const sources = Array.isArray(rc.sources) ? rc.sources : RESEARCH_SOURCE_KEYS.slice();
  const toggleSource = (k) =>
    set({ researchConfig: { ...rc, sources: sources.includes(k) ? sources.filter((s) => s !== k) : [...sources, k] } });

  // Simple/Advanced split: technical controls (model, research, AI revise) live behind an
  // Advanced Collapse, closed by default, so the daily view stays simple. Nothing removed.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Tone presets write a single plain-English tone line into stepDirection. activeToneKey
  // detects which preset (if any) is currently present, so tapping swaps the line rather
  // than stacking, and the operator's own freeform text is preserved on its own lines.
  const activeToneKey = (() => {
    const lines = (step.stepDirection || '').split('\n').map((l) => l.trim());
    const found = TONE_PRESETS.find((t) => lines.includes(t.line));
    return found ? found.key : null;
  })();
  const applyTone = (t) => {
    const kept = (step.stepDirection || '')
      .split('\n')
      .filter((l) => !TONE_PRESET_LINES.includes(l.trim()));
    const freeform = kept.join('\n').replace(/^\n+|\n+$/g, '');
    if (activeToneKey === t.key) {
      set({ stepDirection: freeform }); // tapping the active tone clears its line
    } else {
      set({ stepDirection: freeform ? `${t.line}\n${freeform}` : t.line });
    }
  };

  // AI revise: preview-only; Accept applies to the step (saved via the sequence Save path).
  const stepKey = step.key || String(step.order);
  const reviseModel = step.model && PROMPT_MODEL_OPTIONS.includes(step.model) ? step.model : DEFAULT_REVISE_MODEL;
  const [instruction, setInstruction] = useState('');
  const [revising, setRevising] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, body } | null
  const [violated, setViolated] = useState(null); // { missing:[], invented:[] } | null
  const [reviseError, setReviseError] = useState(null);

  const doRevise = async () => {
    if (!instruction.trim()) {
      setReviseError('Enter a revision instruction.');
      return;
    }
    setRevising(true);
    setReviseError(null);
    setViolated(null);
    setPreview(null);
    try {
      const res = await reviseStepTemplate(sequenceId, stepKey, { instruction: instruction.trim(), model: reviseModel });
      const p = (res && res.preview) || res || {};
      setPreview({ subject: p.subject != null ? p.subject : '', body: p.body != null ? p.body : '' });
    } catch (err) {
      if (err && err.code === 'shortcodes_violated') {
        setViolated({ missing: (err.data && err.data.missing) || [], invented: (err.data && err.data.invented) || [] });
      } else if (err && err.code === 'anthropic_key_missing') {
        setReviseError('AI revision is unavailable: the Anthropic key is not set on the server.');
      } else {
        setReviseError((err && err.message) || 'Revision failed.');
      }
    } finally {
      setRevising(false);
    }
  };

  const acceptRevision = () => {
    if (!preview) return;
    set({ subject: isReply ? '' : (preview.subject || ''), body: preview.body || '' });
    setPreview(null);
    setInstruction('');
  };
  const discardRevision = () => {
    setPreview(null);
    setViolated(null);
  };

  return (
    <Box sx={{ border: `1px solid ${error ? THEME.pink : THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Badge tone="mint">Step {step.order}</Badge>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Day</Typography>
          <TextField size="small" type="number" value={step.dayOffset} onChange={(e) => set({ dayOffset: e.target.value })} sx={{ width: 78 }} InputProps={inputProps} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[['new_thread', 'New thread'], ['reply_same_thread', 'Reply on thread']].map(([v, label]) => (
            <Box
              key={v}
              onClick={() => setThread(v)}
              sx={{ cursor: 'pointer', px: '8px', py: '4px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, userSelect: 'none', border: `1px solid ${step.threadBehavior === v ? THEME.mint : THEME.border}`, color: step.threadBehavior === v ? THEME.mint : THEME.textSecondary, background: step.threadBehavior === v ? THEME.mintFaint : 'transparent' }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography sx={fieldLabelSx}>Subject</Typography>
        <TextField
          fullWidth size="small" disabled={isReply}
          value={isReply ? '' : (step.subject || '')}
          placeholder={isReply ? 'Reply on the running thread, no subject' : 'Subject line'}
          onChange={(e) => set({ subject: e.target.value })}
          InputProps={inputProps}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Body</Typography>
        <TextField fullWidth multiline minRows={3} size="small" value={step.body || ''} onChange={(e) => set({ body: e.target.value })} InputProps={inputProps} />
        <ShortcodeLegend text={(step.subject || '') + '\n' + (step.body || '')} />
        {step.body ? (
          <Box sx={{ mt: 0.75, p: 1, borderRadius: '6px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              <TokenText text={step.body} />
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Direction for this step (Simple view): tone presets + free-text steer. Local
          state only (persisted with the rest on Save). Blank = use the global prompt. */}
      <Box sx={{ mt: 1.5 }}>
        <FieldLabel tip="A plain-language steer for how this step is written, layered on top of the global prompt. Leave blank to use the global prompt unchanged.">Direction for this step</FieldLabel>
        {/* Tone presets: each writes one plain-English tone line into stepDirection,
            swapping any prior preset line and preserving the operator's own text. */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          {TONE_PRESETS.map((t) => (
            <SelectChip key={t.key} label={t.label} selected={activeToneKey === t.key} onClick={() => applyTone(t)} />
          ))}
        </Box>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mb: 0.75 }}>
          Presets just fill in the direction below. Edit it however you like.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="e.g. lean into the cost-savings angle, sound a bit more urgent"
          value={step.stepDirection || ''}
          onChange={(e) => set({ stepDirection: e.target.value })}
          InputProps={inputProps}
          sx={{ '& .MuiInputBase-input': { fontSize: 13, color: THEME.textPrimary } }}
        />
        {/* Preview AI-composes only step 1's opener; steps 2+ are template assembly,
            so their direction applies at send but is not shown in the live preview. */}
        {index > 0 && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>
            Applies at send. Live preview shows step 1.
          </Typography>
        )}
      </Box>

      {/* Advanced: model, research, and AI revise, hidden by default so the daily view
          stays simple. Same Collapse idiom as the Preview section. Nothing here is
          removed or unwired; the controls are only tucked away until opened. */}
      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${THEME.border}` }}>
        <Box
          onClick={() => setAdvancedOpen((o) => !o)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdvancedOpen((o) => !o); } }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', color: THEME.textMuted, '&:hover': { color: THEME.textSecondary } }}
        >
          <ExpandMoreIcon sx={{ fontSize: 16, transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Advanced</Typography>
        </Box>
        <Collapse in={advancedOpen} unmountOnExit>
          {/* Per-step model + research. */}
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Model</Typography>
            <PromptModelSelect value={step.model || ''} onChange={(v) => set({ model: v })} />
          </Box>
          <StepResearchRow
            sources={sources}
            onToggle={toggleSource}
            searchPrompt={rc.searchPrompt || ''}
            onSearchPrompt={(v) => set({ researchConfig: { ...rc, searchPrompt: v } })}
            disabled={researchOff}
          />

          {/* Revise with AI. Preview-only; Accept applies to the step (persisted on Save). */}
          <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${THEME.border}` }}>
            <FieldLabel tip="AI rewrites this step's subject + body per your instruction, preserving every [shortcode]. You preview and accept before it changes anything.">Revise with AI</FieldLabel>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="e.g. make it shorter and more casual"
                value={instruction}
                onChange={(e) => { setInstruction(e.target.value); setReviseError(null); }}
                InputProps={inputProps}
                sx={{ flex: 1, minWidth: 220, '& .MuiInputBase-input': { fontSize: 13 } }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={outlinedBtnSx}
                disabled={revising || !instruction.trim()}
                onClick={doRevise}
                startIcon={revising ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              >
                {revising ? 'Revising…' : 'Revise'}
              </Button>
            </Box>

            {/* 422: shortcodes went missing or were invented. Template unchanged. */}
            {violated && (
              <Box sx={{ mt: 1, p: 1, borderRadius: '6px', background: 'rgba(198,61,87,0.08)', border: `1px solid ${THEME.pink}` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>
                    The revision broke shortcode integrity, so nothing changed. Try a different instruction.
                  </Typography>
                </Box>
                {violated.missing && violated.missing.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>Went missing:</Typography>
                    {violated.missing.map((t) => <ShortcodeChip key={t} token={t} />)}
                  </Box>
                )}
                {violated.invented && violated.invented.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>Invented:</Typography>
                    {violated.invented.map((t) => <ShortcodeChip key={t} token={t} />)}
                  </Box>
                )}
              </Box>
            )}

            {reviseError && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
                <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>{reviseError}</Typography>
              </Box>
            )}

            {/* Preview: current vs revised, shortcodes highlighted. Accept / Discard. */}
            {preview && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TemplatePreviewPane label="Current" subject={isReply ? '' : step.subject} body={step.body} />
                  <TemplatePreviewPane label="Revised (preview)" subject={isReply ? '' : preview.subject} body={preview.body} accent={THEME.mint} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" size="small" sx={mintBtnSx} onClick={acceptRevision} startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}>Accept</Button>
                  <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={discardRevision}>Discard</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      {step.note && (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, fontStyle: 'italic', mt: 1 }}>
          Authoring note: {step.note}
        </Typography>
      )}
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px' }} />
          <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

// Preview result: every assembled step for the sample contact.
// One preview step rendered as an accordion. On mobile it defaults collapsed
// (header only) so the full-screen preview sheet stays scannable; on desktop it
// defaults open. The whole header is the tap target.
const PreviewStep = ({ s, index }) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [open, setOpen] = useState(!isMobile);
  const replies = s.threadBehavior === 'reply_same_thread' || s.subject == null;
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: THEME.card, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, cursor: 'pointer', flexWrap: 'wrap' }}
      >
        <Badge tone="mint">Step {s.order != null ? s.order : index + 1}</Badge>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Day {s.dayOffset != null ? s.dayOffset : '?'}</Typography>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600, color: THEME.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {replies ? 'replies on thread' : (s.subject || '(none)')}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: THEME.textMuted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textSecondary, mb: 0.75 }}>
            {replies ? 'replies on thread' : (s.subject || '(none)')}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Body</Typography>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {s.assembledBody || s.body || ''}
          </Typography>
          {s.linkError && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: THEME.pink, mt: '1px' }} />
              <Typography sx={{ fontSize: '11px', color: THEME.pink }}>Link: {s.linkError}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const SequencePreview = ({ result }) => {
  if (!result) return null;
  return (
    <Box sx={{ mt: 2, border: `1px solid ${THEME.border}`, borderRadius: '10px', background: 'rgba(255,255,255,0.02)', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.textPrimary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assembled preview</Typography>
        {result.draft && <Badge tone="amber">Draft preview</Badge>}
        {result.composeNote && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>opener: {result.composeNote}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {(result.steps || []).map((s, i) => (
          <PreviewStep key={i} s={s} index={i} />
        ))}
      </Box>
    </Box>
  );
};

// The editor dialog for one sequence: sequence-level fields + per-step editors,
// save (422 invalid_step rendered inline; tokenWarnings -> keep/restore), preview.
const SequenceEditor = ({ sequence, linkAssets, onClose, onSaved }) => {
  const initialSteps = () =>
    (sequence.steps || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        order: s.order,
        key: s.key || null,
        dayOffset: s.dayOffset,
        threadBehavior: s.threadBehavior,
        subject: s.subject == null ? '' : s.subject,
        body: s.body || '',
        note: s.note || null,
        model: s.model || '',
        stepDirection: s.stepDirection || '',
        researchConfig: {
          sources: s.researchConfig && Array.isArray(s.researchConfig.sources)
            ? s.researchConfig.sources.filter((x) => RESEARCH_SOURCE_KEYS.includes(x))
            : RESEARCH_SOURCE_KEYS.slice(),
          searchPrompt: (s.researchConfig && s.researchConfig.searchPrompt) || '',
        },
      }));

  const [name, setName] = useState(sequence.name || '');
  const [audienceHint, setAudienceHint] = useState(sequence.audienceHint || '');
  const [linkAssetKey, setLinkAssetKey] = useState(sequence.linkAssetKey || '');
  const [status, setStatus] = useState(sequence.status || 'draft');
  // Sequence-wide research mode ('full' default | 'off'). Off skips the research step.
  const [researchMode, setResearchMode] = useState(sequence.researchMode === 'off' ? 'off' : 'full');
  const [steps, setSteps] = useState(initialSteps);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [stepErrors, setStepErrors] = useState({});
  const [goLiveConfirm, setGoLiveConfirm] = useState(false);
  const [tokenWarn, setTokenWarn] = useState(null); // { warnings:[{step, removedTokens}] }

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState({ name: '', company: '', title: '', evidence: '' });
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const updateStep = (index, patch) => setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const toggleStatus = () => {
    if (status === 'live') setStatus('draft');
    else setGoLiveConfirm(true); // confirm before flipping to live
  };

  const buildPayload = () => ({
    name: name.trim(),
    audienceHint,
    linkAssetKey: linkAssetKey || null, // Default asset when blank
    status,
    researchMode, // 'full' | 'off' — persisted through the same save path (PATCH sequence)
    steps: steps.map((s) => ({
      order: s.order,
      key: s.key || undefined,
      dayOffset: Number(s.dayOffset),
      threadBehavior: s.threadBehavior,
      subject: s.threadBehavior === 'reply_same_thread' ? null : (s.subject || ''),
      body: s.body,
      note: s.note || null,
      model: s.model || '',
      stepDirection: (s.stepDirection || ''),
      researchConfig: s.researchConfig || undefined,
    })),
  });

  // Gate-then-confirm: a plain save 409s (saving NOTHING) if it removes a tracked
  // merge token; confirming re-PATCHes the SAME payload with confirmTokenRemoval:true.
  const save = async (confirmTokenRemoval = false) => {
    setSaving(true);
    setSaveError(null);
    setStepErrors({});
    try {
      const payload = buildPayload();
      if (confirmTokenRemoval) payload.confirmTokenRemoval = true;
      await updateSequence(sequence._id, payload);
      onSaved();
      onClose();
    } catch (err) {
      if (err.code === 'token_removal_requires_confirm') {
        // 409: nothing saved. Name the removed tokens; confirm to re-submit.
        setTokenWarn({ warnings: (err.data && err.data.tokenWarnings) || [] });
      } else if (err.code === 'invalid_step' && err.data && err.data.step != null) {
        // 422 invalid_step -> render inline on the named step (Rule 3).
        setStepErrors({ [err.data.step]: err.data.detail || err.message });
        setSaveError(`Step ${err.data.step}: ${err.data.detail || err.message}`);
      } else {
        setSaveError(err.message || 'Save failed.');
      }
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      // Pass the in-flight steps as draftSteps so the preview reflects UNSAVED edits
      // (per-step directions, body, subject, etc.), not the last-saved sequence.
      const res = await previewSequence(
        sequence._id,
        {
          name: previewForm.name,
          company: previewForm.company,
          title: previewForm.title,
          evidence: previewForm.evidence || undefined,
        },
        buildPayload().steps,
      );
      setPreviewResult(res);
    } catch (err) {
      setPreviewResult(null);
      setPreviewError(err.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const tokenWarnBody = tokenWarn
    ? tokenWarn.warnings.map((w) => `Step ${w.step} no longer contains ${w.removedTokens.join(', ')}.`).join(' ')
    : '';

  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>Edit sequence: {sequence.name || sequence.key}{sequence.variant ? ` (Variant ${sequence.variant})` : ''}</span>
        <Badge tone={SEQ_STATUS_META[status] ? SEQ_STATUS_META[status].tone : 'muted'}>
          {SEQ_STATUS_META[status] ? SEQ_STATUS_META[status].label : status}
        </Badge>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Name</Typography>
            <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} InputProps={inputProps} />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Audience hint</Typography>
            <TextField fullWidth size="small" value={audienceHint} onChange={(e) => setAudienceHint(e.target.value)} placeholder="Free-text routing signal (Rule 1)" InputProps={inputProps} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box sx={{ minWidth: 240 }}>
              <Typography sx={fieldLabelSx}>Link asset for [link]</Typography>
              <Select
                fullWidth size="small" displayEmpty value={linkAssetKey}
                onChange={(e) => setLinkAssetKey(e.target.value)}
                sx={{ fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border }, '& .MuiSvgIcon-root': { color: THEME.textMuted }, '& .MuiSelect-select': { py: '8px' } }}
                MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, '& .MuiMenuItem-root': { fontSize: 13 } } } }}
              >
                <MenuItem value="">Default</MenuItem>
                {(linkAssets || []).map((la) => (
                  <MenuItem key={la.key} value={la.key}>{la.label} ({la.key})</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', color: status === 'live' ? THEME.mint : THEME.textSecondary, fontWeight: 600 }}>{status === 'live' ? 'Live' : 'Draft'}</Typography>
              <Switch checked={status === 'live'} onChange={toggleStatus} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.mint }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.mint } }} />
            </Box>
          </Box>
        </Box>

        {/* Sequence-wide Research toggle. Off skips the research step for this sequence; the
            per-step Research controls below mute so it is obvious they will not fire. */}
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${THEME.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Research</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '12px', color: researchMode === 'off' ? THEME.textMuted : THEME.mint, fontWeight: 600 }}>{researchMode === 'off' ? 'Off' : 'On'}</Typography>
            <Switch
              checked={researchMode !== 'off'}
              onChange={(e) => setResearchMode(e.target.checked ? 'full' : 'off')}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.mint }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.mint } }}
            />
          </Box>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5, lineHeight: 1.6, maxWidth: 560 }}>
            Off: skips the research step for people in this sequence. Emails open warm and lean on client proof instead of researched hooks. Faster and cheaper per contact. Applies to REAL campaigns using this sequence. To experiment without affecting campaigns, use the Research control inside Test as a target.
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, mt: 2 }}>Steps</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {steps.map((s, i) => (
            <SequenceStepEditor key={i} step={s} index={i} error={stepErrors[s.order]} onChange={updateStep} sequenceId={sequence._id} researchOff={researchMode === 'off'} />
          ))}
        </Box>

        {saveError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{saveError}</Typography>
          </Box>
        )}

        <Box sx={{ mt: 2, borderTop: `1px solid ${THEME.border}`, pt: 2 }}>
          <Button size="small" variant="outlined" sx={outlinedBtnSx} onClick={() => setPreviewOpen((o) => !o)}>
            {previewOpen ? 'Hide preview' : 'Preview with a sample contact'}
          </Button>
          <Collapse in={previewOpen} unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Name" value={previewForm.name} onChange={(e) => setPreviewForm((f) => ({ ...f, name: e.target.value }))} InputProps={inputProps} />
                <TextField size="small" placeholder="Company" value={previewForm.company} onChange={(e) => setPreviewForm((f) => ({ ...f, company: e.target.value }))} InputProps={inputProps} />
                <TextField size="small" placeholder="Title" value={previewForm.title} onChange={(e) => setPreviewForm((f) => ({ ...f, title: e.target.value }))} InputProps={inputProps} />
              </Box>
              <TextField size="small" fullWidth multiline minRows={2} placeholder="Evidence (optional): drives the real opener line" value={previewForm.evidence} onChange={(e) => setPreviewForm((f) => ({ ...f, evidence: e.target.value }))} InputProps={inputProps} />
              <Box>
                <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={previewing} onClick={runPreview}
                  startIcon={previewing ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}>
                  {previewing ? 'Assembling…' : 'Preview all steps'}
                </Button>
              </Box>
              {previewError && <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{previewError}</Typography>}
              <SequencePreview result={previewResult} />
              {/* Send-to-my-inbox: this sequence + the preview's sample contact, no evidence. */}
              {previewResult && (
                <SendToInboxButton
                  hint="Send this assembled preview to your own inbox to see it as a recipient would."
                  buildPayload={() => ({
                    sequenceKey: sequence.key || sequence._id,
                    step: 1,
                    sampleContact: {
                      name: previewForm.name,
                      company: previewForm.company,
                      title: previewForm.title,
                    },
                  })}
                />
              )}
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} disabled={saving} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={mintBtnSx} disabled={saving} onClick={() => save()} startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>

      {/* Confirm before flipping to live. */}
      <ConfirmModal
        config={goLiveConfirm ? { title: 'Set this sequence live?', body: 'Setting this live makes the sequence send-eligible. Once you Save, it can start sending emails to contacts. You can switch back to Draft at any time.', confirmLabel: 'Set live', tone: 'mint', onConfirm: () => setStatus('live') } : null}
        onClose={() => setGoLiveConfirm(false)}
      />

      {/* Token-removal confirm. The 409 saved NOTHING; confirm to re-submit. */}
      <Dialog open={!!tokenWarn} onClose={() => setTokenWarn(null)}
        PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx, maxWidth: 440 } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 600 }}>Merge token removed</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>{tokenWarnBody} Keep this change?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" sx={outlinedBtnSx} disabled={saving} onClick={() => setTokenWarn(null)}>Cancel</Button>
          <Button variant="contained" sx={mintBtnSx} disabled={saving} onClick={() => { setTokenWarn(null); save(true); }}>Keep this change</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// Create a new DRAFT sequence: name + audience hint -> POST /sequences. On success
// the caller opens the returned draft straight in the editor. Nothing sends.
const NewSequenceModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [audienceHint, setAudienceHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!name.trim()) {
      setError('Give the sequence a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const seq = await createSequence({ name: name.trim(), audienceHint: audienceHint.trim() });
      onCreated(seq);
    } catch (err) {
      setError(friendlyActionError('create', err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>New sequence</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2 }}>
          Creates a draft you can edit and preview before setting it live. Nothing sends.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={fieldLabelSx}>Name</Typography>
          <TextField fullWidth size="small" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. CHRO · Mid-market biotech" InputProps={inputProps} />
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>Audience</Typography>
          <TextField fullWidth size="small" value={audienceHint} onChange={(e) => setAudienceHint(e.target.value)} placeholder="Free-text routing signal: who this sequence is for" InputProps={inputProps} />
        </Box>
        {error && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{error}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} disabled={saving} onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          sx={mintBtnSx}
          disabled={saving || !name.trim()}
          onClick={submit}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
        >
          {saving ? 'Creating…' : 'Create draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Inline audience-hint editor for a sequence row (Item 4). A plain textarea saved
// through updateSequence, with a "steers research" helper and inline saved/error
// states so a failed write never reads as a success (Rule 3).
const AudienceHintEditor = ({ seq, onSaved }) => {
  const [value, setValue] = useState(seq.audienceHint || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const dirty = value !== (seq.audienceHint || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateSequence(seq._id, { audienceHint: value });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(friendlyActionError('save', err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 0.75, border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Audience hint</Typography>
        <InfoTip text="Steers research: a plain-language note on who this sequence is for. The router reads it to match contacts to this sequence, and research uses it to focus." size={13} />
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={value}
        onChange={(e) => { setValue(e.target.value); setSaved(false); }}
        placeholder="Who is this sequence for? For example: heads of RevOps at Series B to D SaaS companies."
        InputProps={inputProps}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
          disabled={saving || !dirty}
          onClick={save}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && <Typography sx={{ fontSize: '11px', color: THEME.mint }}>Saved.</Typography>}
        {error && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{error}</Typography>}
      </Box>
    </Box>
  );
};

// ─── Pitch rotation (persona rotation state, folded into the /capacity response) ─────
// Brad-facing: a "pitch" is one persona/sequence. United runs ONE pitch to brand-new
// people until it reaches the threshold, then moves to the next. This surface reads the
// rotation snapshot the capacity endpoint carries and, when the API marks which positions
// are locked, lets the operator reorder only the upcoming pitches. Everything fails soft:
// no rotation state in the response renders nothing at all.

const ROTATION_EXPLAINER =
  'United sends one pitch at a time to brand-new people, finishes the full test, then moves to the next. This keeps results readable, so you know which pitch actually works.';

// Humanize a persona key like "founder_led_saas" into "Founder Led Saas" as a readable
// fallback only when the API supplies no displayName. Never invents data.
function humanizePersonaKey(key) {
  const s = String(key == null ? '' : key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return 'this pitch';
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

// First argument that is a finite number, else null (so "unknown" stays unknown).
function firstFiniteNumber() {
  for (let i = 0; i < arguments.length; i += 1) {
    const v = arguments[i];
    if (v != null && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
}

// Count the leading rows the API marked locked (a contiguous prefix: finished pitches then
// the active one). Reordering is only ever offered on the rows AFTER this prefix.
function countLeadingLocked(rows) {
  let n = 0;
  for (const r of rows) { if (r && r.locked === true) n += 1; else break; }
  return n;
}

// Reduce the rotation snapshot to a stable view the panel + Home line render from. Prefers a
// rich `personas` array, else the thin `personaOrder` snapshot. Returns null when there is no
// usable rotation state (caller renders nothing). Locks are read VERBATIM from the API
// (per-persona `locked` or top-level `lockedPositions`); when the API gives no lock info,
// `reorderable` is false and no locks are shown or derived in the frontend.
function normalizeRotation(rotation) {
  if (!rotation || rotation.enabled === false || rotation.available === false) return null;
  const threshold = firstFiniteNumber(rotation.threshold) || 1000;
  const status = String(rotation.status || '');
  const holding = status === 'holding_for_refill';
  const activeIndex = firstFiniteNumber(rotation.activeIndex);
  // Lock source, in precedence order: the backend's explicit locks object
  // (locks.lockedKeys / locks.reorderableKeys, buildView's authoritative shape), then a
  // per-persona `locked` boolean, then the older top-level lockedPositions. Locks are read
  // verbatim; the frontend never derives which positions are locked.
  const locksObj = rotation.locks && typeof rotation.locks === 'object' ? rotation.locks : null;
  const lockedKeySet = locksObj && Array.isArray(locksObj.lockedKeys) ? new Set(locksObj.lockedKeys.map((v) => String(v))) : null;
  const reorderableKeySet = locksObj && Array.isArray(locksObj.reorderableKeys) ? new Set(locksObj.reorderableKeys.map((v) => String(v))) : null;
  const lockedPositions = Array.isArray(rotation.lockedPositions) ? rotation.lockedPositions : null;
  const lockedSet = lockedPositions ? new Set(lockedPositions.map((v) => String(v))) : null;

  const phaseFromIndex = (i) =>
    activeIndex == null ? 'upcoming' : i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'upcoming';

  // Turns true only when the API actually provided lock info; gates whether reordering is
  // offered at all (so the frontend never derives locks).
  let apiLocks = false;

  // Resolve one persona's lock from the API, never derived. Returns true/false when the API
  // says, else undefined. Also flips `apiLocks` so reordering only turns on with real locks.
  const resolveLock = (key, i, pLocked) => {
    if (lockedKeySet || reorderableKeySet) {
      apiLocks = true;
      if (lockedKeySet && lockedKeySet.has(key)) return true;
      if (reorderableKeySet && reorderableKeySet.has(key)) return false;
      if (typeof pLocked === 'boolean') return pLocked;
      return undefined;
    }
    if (typeof pLocked === 'boolean') { apiLocks = true; return pLocked; }
    if (lockedSet) { apiLocks = true; return lockedSet.has(key) || lockedSet.has(String(i)); }
    return undefined;
  };

  let rows = [];

  const rich = Array.isArray(rotation.personas) ? rotation.personas : null;
  if (rich && rich.length) {
    rows = rich.map((p, i) => {
      const key = String(p.key || p.personaKey || p.sequenceKey || `pitch-${i}`);
      const raw = String(p.status || p.phase || '').toLowerCase();
      let phase;
      if (raw === 'done' || raw === 'complete' || raw === 'completed') phase = 'done';
      else if (raw === 'active' || raw === 'sending' || raw === 'running' || raw === 'holding_for_refill') phase = 'active';
      else if (raw === 'upcoming' || raw === 'up_next' || raw === 'pending' || raw === 'queued') phase = 'upcoming';
      else phase = phaseFromIndex(i);
      const prog = p.progress && typeof p.progress === 'object' ? p.progress : null;
      return {
        key,
        displayName: p.displayName || p.name || humanizePersonaKey(key),
        phase,
        // buildView: per-persona firstTouchCount (achieved for completed, live for active);
        // active also carries progress.firstTouchCount. Older names kept as fallbacks.
        reached: firstFiniteNumber(prog && prog.firstTouchCount, p.firstTouchCount, p.reached, p.count, p.reachedCount),
        // buildView: thresholdApplied per persona (active also carries progress.threshold).
        threshold: firstFiniteNumber(p.thresholdApplied, prog && prog.threshold, p.threshold) || threshold,
        startedAt: p.startedAt || p.runStartedAt || null,
        // buildView: endedAt is the completion date; keep completedAt as a fallback.
        completedAt: p.endedAt || p.completedAt || null,
        sourcedBacklog: firstFiniteNumber(p.sourcedBacklog, p.sourced, p.backlog),
        locked: resolveLock(key, i, p.locked),
      };
    });
  } else {
    const order = Array.isArray(rotation.personaOrder) ? rotation.personaOrder : [];
    if (!order.length) return null;
    rows = order.map((key, i) => {
      const phase = phaseFromIndex(i);
      const k = String(key);
      return {
        key: k,
        displayName: humanizePersonaKey(key),
        phase,
        reached: phase === 'active' ? firstFiniteNumber(rotation.firstTouchCount) : phase === 'done' ? threshold : 0,
        threshold,
        startedAt: null,
        completedAt: null,
        // The thin snapshot exposes the active persona's sourced backlog only.
        sourcedBacklog: phase === 'active' ? firstFiniteNumber(rotation.sourcedBacklog) : null,
        locked: resolveLock(k, i, undefined),
      };
    });
  }

  if (!rows.length) return null;
  const activeRow = rows.find((r) => r.phase === 'active') || null;
  return { threshold, status, holding, rows, activeRow, reorderable: apiLocks };
}

// The lock tooltip: the running pitch explains the threshold gate verbatim; a finished
// pitch gets a truthful variant (the API's quoted "running now" copy is only correct for
// the active pitch, so a completed row must not claim it is running).
const rotationLockTooltip = (phase, threshold) =>
  phase === 'active'
    ? `This pitch is running now. The order can't change until it finishes reaching ${fmtCount(threshold)} people.`
    : 'This pitch already finished. Finished pitches stay in their place.';

const PITCH_STATUS_META = {
  done: { tone: 'gray', label: 'Done' },
  active: { tone: 'mint', label: 'Sending now' },
  upcoming: { tone: 'muted', label: 'Up next' },
};

// One pitch row: a drag handle (upcoming, reorderable) or a lock icon (finished/active),
// the display name, a status chip, the active progress bar, finished count and dates, and
// the sourced-backlog depth in plain words.
const PitchRow = ({ row, dragHandleProps, dragging }) => {
  const meta = PITCH_STATUS_META[row.phase] || PITCH_STATUS_META.upcoming;
  const th = row.threshold || 1000;
  const reached = row.reached;
  const pct = row.phase === 'active' && reached != null && th > 0
    ? Math.min(100, Math.max(0, Math.round((reached / th) * 100)))
    : null;
  const dates = row.phase === 'done' && row.startedAt && row.completedAt
    ? `${fmtDate(row.startedAt)} to ${fmtDate(row.completedAt)}`
    : '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, border: `1px solid ${THEME.border}`, borderRadius: '10px', background: dragging ? 'rgba(255,255,255,0.05)' : THEME.card, px: 1.5, py: 1.25, mb: 1 }}>
      <Box sx={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: '2px' }}>
        {dragHandleProps ? (
          <Box {...dragHandleProps} sx={{ display: 'inline-flex', cursor: 'grab', color: THEME.textMuted, '&:hover': { color: THEME.textSecondary } }}>
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </Box>
        ) : row.locked ? (
          <Tooltip title={rotationLockTooltip(row.phase, th)} arrow placement="top" {...tipSlotProps}>
            <Box component="span" sx={{ display: 'inline-flex', cursor: 'help', color: THEME.textMuted }}>
              <LockOutlinedIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        ) : null}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-word' }}>{row.displayName}</Typography>
          <Badge tone={meta.tone}>{meta.label}</Badge>
        </Box>
        {row.phase === 'active' && (
          <Box sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, mb: 0.4 }}>
              {fmtCount(reached)} of {fmtCount(th)} people reached
            </Typography>
            {pct != null && (
              <Box sx={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', background: THEME.mint }} />
              </Box>
            )}
          </Box>
        )}
        {row.phase === 'done' && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.4 }}>
            Reached {fmtCount(row.reached != null ? row.reached : th)} people{dates ? ` · ${dates}` : ''}
          </Typography>
        )}
        {row.sourcedBacklog != null && row.sourcedBacklog > 0 && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.4 }}>
            {fmtCount(row.sourcedBacklog)} people sourced for this pitch
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// The Pitch rotation panel. Reads its own light capacity snapshot so it fails soft on its
// own. Drag-and-drop is offered on upcoming rows only, and only when the API told us which
// positions are locked; Save sends the FULL ordered key array to PUT /api/gtm/rotation/order
// and any validation error is shown verbatim.
const PitchRotationPanel = () => {
  const capRes = useGtmResource('/api/gtm/capacity', (d) => d || {});
  const rot = normalizeRotation((capRes.data || {}).rotation);
  const [draftKeys, setDraftKeys] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  const rows = rot ? rot.rows : [];
  const serverSig = rows.map((r) => r.key).join(',');
  // Drop any local reorder when the server order changes underneath us.
  useEffect(() => { setDraftKeys(null); setSaveError(null); setSaved(false); }, [serverSig]);

  if (!rot) return null;

  const serverKeys = rows.map((r) => r.key);
  const reorderable = rot.reorderable;
  const lockedCount = countLeadingLocked(rows);
  const rowByKey = {};
  rows.forEach((r) => { rowByKey[r.key] = r; });
  const keys = draftKeys || serverKeys;
  const dirty = !!draftKeys && draftKeys.join(',') !== serverKeys.join(',');

  const onDragEnd = (result) => {
    if (!result || !result.destination) return;
    const from = result.source.index;
    let to = result.destination.index;
    if (from < lockedCount) return;          // a locked row cannot move
    if (to < lockedCount) to = lockedCount;  // and nothing can cross above the locked prefix
    if (from === to) return;
    const next = keys.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDraftKeys(next);
    setSaved(false);
  };

  const reset = () => { setDraftKeys(null); setSaveError(null); };

  const save = async () => {
    setSaving(true); setSaveError(null); setSaved(false);
    try {
      await putRotationOrder(keys);
      setSaved(true);
      setDraftKeys(null);
      capRes.reload();
    } catch (err) {
      setSaveError((err && err.message) || 'Could not save the new order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px', background: THEME.card, p: 2, mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>Pitch rotation</Typography>
        <InfoTooltip title="Pitch rotation" size={13}>{ROTATION_EXPLAINER}</InfoTooltip>
      </Box>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6, mb: 1.5, maxWidth: 640 }}>
        {ROTATION_EXPLAINER}
      </Typography>

      {reorderable ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="pitch-rotation">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {keys.map((key, i) => {
                  const row = rowByKey[key];
                  if (!row) return null;
                  return (
                    <Draggable key={key} draggableId={key} index={i} isDragDisabled={!!row.locked}>
                      {(dp, snap) => (
                        <Box ref={dp.innerRef} {...dp.draggableProps} style={dp.draggableProps.style}>
                          <PitchRow row={row} dragHandleProps={row.locked ? null : dp.dragHandleProps} dragging={snap.isDragging} />
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box>
          {rows.map((row) => (
            <PitchRow key={row.key} row={row} dragHandleProps={null} dragging={false} />
          ))}
        </Box>
      )}

      {reorderable && (
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.5 }}>
          Drag the upcoming pitches to change their order. Running and finished pitches are locked.
        </Typography>
      )}

      {dirty && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            sx={mintBtnSx}
            disabled={saving}
            onClick={save}
            startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
          >
            {saving ? 'Saving…' : 'Save new order'}
          </Button>
          <Button variant="text" size="small" disabled={saving} onClick={reset} sx={{ color: THEME.textMuted, textTransform: 'none', fontSize: '12px' }}>
            Cancel
          </Button>
        </Box>
      )}
      {saveError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{saveError}</Typography>}
      {saved && !dirty && <Typography sx={{ fontSize: '11px', color: THEME.mint, mt: 1 }}>New pitch order saved.</Typography>}
    </Box>
  );
};

// The Home one-liner: which pitch is running and how far along, or an amber hold note when
// paused for refill. Reads the same normalized rotation; renders nothing without state.
const HomeRotationLine = ({ rotation }) => {
  const rot = normalizeRotation(rotation);
  if (!rot) return null;
  if (rot.holding) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, mb: 3, borderRadius: '10px', background: THEME.amberDim, border: '1px solid rgba(251,191,36,0.3)' }}>
        <HealthDot color={THEME.amber} />
        <Typography sx={{ fontSize: '12px', color: THEME.amber }}>Paused while we source more people who fit this pitch</Typography>
      </Box>
    );
  }
  const active = rot.activeRow;
  if (!active) return null;
  const reached = active.reached != null ? active.reached : 0;
  const th = active.threshold || rot.threshold;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 3 }}>
      <HealthDot color={THEME.mint} />
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>
        Running the {active.displayName} pitch: {fmtCount(reached)} of {fmtCount(th)} reached
      </Typography>
    </Box>
  );
};

// ===== Tab: Proofs (the lifecycle of what we can claim) — aligned to INTEL-03 =====
// Everything mined from calls lands here with a status (asked_about -> resonated -> in_progress
// -> done), a reviewed flag (only reviewed done/in_progress entries are citable), attribution
// (company + speakerRole, null = unattributed and never guessed), and near-duplicate clustering.
// The header shows the lifecycle funnel + reviewed-citable counter from GET /proofs/intel.

const PROOF_STATUS_META = {
  asked_about: { tone: 'gray', label: 'Asked about' },
  resonated: { tone: 'blue', label: 'Resonated' },
  in_progress: { tone: 'amber', label: 'In progress' },
  done: { tone: 'mint', label: 'Done' },
  retired: { tone: 'muted', label: 'Retired' },
};
// Click-to-advance order (matches the backend STATUS_RANK). Advancing from 'done' cycles back.
const PROOF_STATUS_RANK = ['asked_about', 'resonated', 'in_progress', 'done'];
const nextProofStatus = (s) => {
  const i = PROOF_STATUS_RANK.indexOf(String(s || '').toLowerCase());
  return i < 0 ? PROOF_STATUS_RANK[0] : PROOF_STATUS_RANK[(i + 1) % PROOF_STATUS_RANK.length];
};
const proofStatusMeta = (s) => PROOF_STATUS_META[String(s || '').toLowerCase().trim()] || { tone: 'gray', label: String(s || 'unknown') };

// A left-ruled verbatim quote block (the evidence quote).
const QuoteBlock = ({ text, label }) => (text ? (
  <Box sx={{ mt: 0.5, pl: 1, borderLeft: `2px solid ${THEME.border}` }}>
    {label && <Typography sx={{ fontSize: 10, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>}
    <Typography sx={{ fontSize: 12, color: THEME.textSecondary, lineHeight: 1.6, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>“{text}”</Typography>
  </Box>
) : null);

// The lifecycle funnel + reviewed-citable line from /proofs/intel. Reads defensively (funnel
// counts under funnel|counts|byStatus; reviewedCitable a number or { reviewed, total }).
const ProofFunnelHeader = ({ intel, totalProofs }) => {
  const i = intel || {};
  const funnel = i.funnel || i.counts || i.byStatus || {};
  const num = (k) => firstFiniteNumber(funnel[k], (i.funnel && i.funnel[k]));
  const parts = [
    ['asked_about', 'asked about'],
    ['resonated', 'resonated'],
    ['in_progress', 'in progress'],
    ['done', 'done'],
  ].map(([k, label]) => { const n = num(k); return n != null ? `${fmtCount(n)} ${label}` : null; }).filter(Boolean);
  // reviewed-citable: a count, plus a denominator (its .total, else the funnel/total proof count).
  const rc = i.reviewedCitable;
  const reviewed = firstFiniteNumber(rc && rc.reviewed, rc && rc.count, typeof rc === 'number' ? rc : null);
  const denom = firstFiniteNumber(rc && rc.total, rc && rc.of, rc && rc.citable, totalProofs);
  return (
    <Box sx={{ mb: 2 }}>
      {parts.length > 0 && (
        <Typography sx={{ fontSize: 13, color: THEME.textSecondary }}>{parts.join(' · ')}</Typography>
      )}
      {reviewed != null && (
        <Typography sx={{ fontSize: 12, color: THEME.textMuted, mt: 0.25 }}>
          {fmtCount(reviewed)}{denom != null ? ` of ${fmtCount(denom)}` : ''} reviewed and citable
        </Typography>
      )}
    </Box>
  );
};

// Discoverable status-advance control: the status badge with a trailing step chevron, a
// tooltip naming the lifecycle order, and a hover lift. Same click behavior as before.
const StatusAdvanceBadge = ({ meta, disabled, onClick }) => (
  <Tooltip title="Click to advance: asked about → resonated → in progress → done" arrow placement="top" {...tipSlotProps}>
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25, background: 'none', border: 'none', p: '2px', borderRadius: '6px',
        cursor: disabled ? 'default' : 'pointer', transition: 'transform 120ms, box-shadow 120ms',
        '&:hover': disabled ? {} : { transform: 'translateY(-1px)', boxShadow: '0 2px 6px rgba(0,0,0,0.35)' },
      }}
    >
      <Badge tone={meta.tone}>{meta.label}</Badge>
      <ExpandMoreIcon sx={{ fontSize: 13, color: THEME.textMuted, transform: 'rotate(-90deg)', ml: '-2px' }} />
    </Box>
  </Tooltip>
);

// The solid-mint "Reviewed ✓" state chip, with a hover-revealed × to unreview.
const ReviewedChip = ({ disabled, onUnreview }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onUnreview}
    title="Click to unreview"
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.4, background: THEME.mint, color: '#06281f', border: 'none', borderRadius: '6px',
      px: '8px', py: '4px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', cursor: disabled ? 'default' : 'pointer',
      '& .unrev': { opacity: 0, transition: 'opacity 120ms' }, '&:hover .unrev': { opacity: 1 },
    }}
  >
    Reviewed ✓<Box component="span" className="unrev">×</Box>
  </Box>
);

// Inline company editor: the company chip (or a muted "no company" placeholder) with a pencil
// that swaps to a text input + save/cancel. The parent saves optimistically (revert-and-surface).
const CompanyInlineEdit = ({ company, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(company || '');
  const start = () => { setDraft(company || ''); setEditing(true); };
  const commit = () => { onSave(draft.trim()); setEditing(false); };
  if (editing) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <TextField
          autoFocus size="small" variant="standard" value={draft} placeholder="Company"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); } }}
          InputProps={{ sx: { fontSize: 12, color: THEME.textPrimary, '&:before': { borderColor: THEME.borderStrong }, '&:after': { borderColor: THEME.mint } } }}
          sx={{ width: 150 }}
        />
        <IconButton size="small" onClick={commit} title="Save company" sx={{ p: 0.25, color: THEME.mint }}><CheckCircleOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
        <Box component="button" type="button" onClick={() => setEditing(false)} title="Cancel" sx={{ background: 'none', border: 'none', p: '2px 4px', cursor: 'pointer', color: THEME.textMuted, fontSize: 16, lineHeight: 1, '&:hover': { color: THEME.textSecondary } }}>×</Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {company ? <Badge tone="gray">{company}</Badge> : <Badge tone="muted">no company</Badge>}
      <IconButton size="small" disabled={disabled} onClick={start} title="Edit company" sx={{ p: 0.25, color: THEME.textMuted, '&:hover': { color: THEME.mint } }}>
        <EditOutlinedIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
};

// Full proof edit in a compact modal: the fields the PATCH contract accepts. Same save path
// (saveProof); nothing clever — field, value, save.
const ProofEditModal = ({ proof, onClose, onSaved }) => {
  const [company, setCompany] = useState(proof.company || '');
  const [whatWeDo, setWhatWeDo] = useState(proof.whatWeDo || '');
  const [evidenceQuote, setEvidenceQuote] = useState(proof.evidenceQuote || '');
  const [speakerRole, setSpeakerRole] = useState(proof.speakerRole || '');
  const [aliases, setAliases] = useState(Array.isArray(proof.aliases) ? proof.aliases : []);
  const [aliasDraft, setAliasDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const addAlias = () => { const v = aliasDraft.trim().replace(/,+$/, '').trim(); if (v && !aliases.includes(v)) setAliases((a) => [...a, v]); setAliasDraft(''); };
  const removeAlias = (a) => setAliases((list) => list.filter((x) => x !== a));
  const fieldSx = { '& .MuiInputBase-input': { fontSize: 13, color: THEME.textPrimary } };
  const labelProps = { sx: { fontSize: 12, color: THEME.textMuted } };
  const save = async () => {
    setSaving(true); setError(null);
    try {
      await saveProof({ id: proof._id, company: company.trim(), whatWeDo: whatWeDo.trim(), evidenceQuote: evidenceQuote.trim(), speakerRole: speakerRole.trim() || null, aliases });
      onSaved(); onClose();
    } catch (err) { setError((err && err.message) || 'Could not save this proof.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm" PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', ...sheetPaperSx } }}>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary }}>Edit proof</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <TextField size="small" label="Company" value={company} onChange={(e) => setCompany(e.target.value)} InputProps={inputProps} InputLabelProps={labelProps} sx={fieldSx} />
        <TextField size="small" label="What we do" value={whatWeDo} onChange={(e) => setWhatWeDo(e.target.value)} multiline minRows={2} InputProps={inputProps} InputLabelProps={labelProps} sx={fieldSx} />
        <TextField size="small" label="Evidence quote (verbatim)" value={evidenceQuote} onChange={(e) => setEvidenceQuote(e.target.value)} multiline minRows={2} InputProps={inputProps} InputLabelProps={labelProps} sx={fieldSx} />
        <TextField size="small" label="Speaker role" value={speakerRole} onChange={(e) => setSpeakerRole(e.target.value)} InputProps={inputProps} InputLabelProps={labelProps} sx={fieldSx} />
        <Box>
          <Typography sx={{ fontSize: 12, color: THEME.textMuted, mb: 0.5 }}>Aliases</Typography>
          {aliases.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
              {aliases.map((a) => (
                <Box key={a} component="button" type="button" onClick={() => removeAlias(a)} title="Remove alias" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, background: 'rgba(255,255,255,0.06)', color: THEME.textSecondary, border: 'none', borderRadius: '6px', px: '8px', py: '4px', fontSize: 11, cursor: 'pointer', '&:hover': { color: THEME.pink } }}>
                  {a}<Box component="span">×</Box>
                </Box>
              ))}
            </Box>
          )}
          <TextField
            size="small" fullWidth placeholder="Add alias, press Enter" value={aliasDraft}
            onChange={(e) => setAliasDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addAlias(); } }}
            InputProps={inputProps} sx={fieldSx}
          />
        </Box>
        {error && <Typography sx={{ fontSize: 12, color: THEME.pink }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" sx={mintBtnSx} onClick={save} disabled={saving || !whatWeDo.trim()}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// One proof card. LABELS (non-interactive muted chips) stay inline with the content;
// ACTIONS live in a compact right-aligned row so they read as controls, not tags.
const ProofCard = ({ proof, citedByClient, onSaved, onAskRetire }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Optimistic company (undefined = mirror proof.company); reverts on failure. Setting a company
  // this way also clears any companyGuess (the reload reflects the backend promotion).
  const [companyOverride, setCompanyOverride] = useState(undefined);
  const displayCompany = companyOverride !== undefined ? companyOverride : proof.company;
  const status = String(proof.status || '').toLowerCase();
  const meta = proofStatusMeta(status);
  const reviewed = proof.reviewed === true;
  // Citable = the operator's two clicks (reviewed + a citable status). The composer gate
  // enforces the same server-side; this makes the cause/effect legible.
  const citable = reviewed && (status === 'done' || status === 'in_progress');
  const aliases = Array.isArray(proof.aliases) ? proof.aliases : [];
  const related = Array.isArray(proof.relatedTo) ? proof.relatedTo : [];
  const sources = Array.isArray(proof.sourceTranscriptIds) ? proof.sourceTranscriptIds : [];
  const speaker = proof.speakerRole; // null = unattributed, never guessed
  // CONFIRM-01: a guessed attribution ("Probably X") awaiting confirmation. Renders a real
  // Confirm button only when the field is present (reuses saveProof; no new endpoint).
  const speakerGuess = proof.speakerRoleGuess || proof.proposedSpeakerRole || '';
  const needsConfirm = !speaker && !!speakerGuess;
  const clientKey = proof.client || proof.company || proof.clientDomain || '';
  const cited = citedByClient ? firstFiniteNumber(citedByClient[clientKey], citedByClient[String(proof.clientDomain || '').toLowerCase()]) : null;

  const patch = async (body) => {
    setBusy(true); setError(null);
    try { await saveProof({ id: proof._id, ...body }); onSaved(); }
    catch (err) { setError((err && err.message) || 'Could not update this proof.'); }
    finally { setBusy(false); }
  };
  const advanceStatus = () => patch({ status: nextProofStatus(status) });
  const toggleReviewed = () => patch({ reviewed: !reviewed });
  const confirmGuess = () => patch({ speakerRole: speakerGuess });
  // Company pencil: optimistic (show the new value immediately), revert + surface on failure.
  const saveCompany = async (v) => {
    setCompanyOverride(v); setError(null);
    try { await saveProof({ id: proof._id, company: v }); onSaved(); }
    catch (err) { setCompanyOverride(undefined); setError((err && err.message) || 'Could not set company.'); }
  };

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {clientKey && <Typography sx={{ fontSize: 13, fontWeight: 700, color: THEME.textPrimary }}>{clientKey}</Typography>}
            {/* status: a discoverable advance control (badge + chevron + tooltip + hover lift) */}
            <StatusAdvanceBadge meta={meta} disabled={busy} onClick={advanceStatus} />
            {/* citability: the result of reviewed + status, made legible */}
            {citable ? <Badge tone="mint">Citable</Badge> : <Badge tone="amber">not yet citable</Badge>}
            {cited != null && <Typography sx={{ fontSize: 11, color: THEME.textMuted }}>cited in {fmtCount(cited)} drafts · 30d</Typography>}
          </Box>

          <Typography sx={{ fontSize: 13, color: THEME.textSecondary, mt: 0.4, lineHeight: 1.5 }}>{proof.whatWeDo}</Typography>
          <QuoteBlock text={proof.evidenceQuote} label="Evidence" />

          {/* LABELS (non-interactive) + the one inline control: an editable company (pencil). */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
            <CompanyInlineEdit company={displayCompany} disabled={busy} onSave={saveCompany} />
            {speaker ? <Badge tone="gray">{speaker}</Badge> : (needsConfirm ? <Badge tone="muted">Probably {speakerGuess}</Badge> : <Badge tone="muted">no attribution</Badge>)}
            {proof.sourceType && <Badge tone="muted">{String(proof.sourceType).toUpperCase()}</Badge>}
            {sources.length > 0 && <Badge tone="muted">{sources.length} source{sources.length === 1 ? '' : 's'}</Badge>}
            {related.length > 0 && <Badge tone="blue">similar to {related.length} other{related.length === 1 ? '' : 's'}</Badge>}
          </Box>

          {aliases.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {aliases.map((a, i) => <Badge key={i} tone="gray">{a}</Badge>)}
            </Box>
          )}
          {error && <Typography sx={{ fontSize: 11, color: THEME.pink, mt: 0.5 }}>{error}</Typography>}
        </Box>

        {/* ACTION ROW: real controls, right-aligned, compact. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {needsConfirm && (
            <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={busy} onClick={confirmGuess}>Confirm</Button>
          )}
          {reviewed ? (
            <ReviewedChip disabled={busy} onUnreview={toggleReviewed} />
          ) : (
            <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={busy} onClick={toggleReviewed}>Mark reviewed</Button>
          )}
          <Button size="small" variant="text" disabled={busy} onClick={() => setEditOpen(true)} sx={{ color: THEME.textSecondary, textTransform: 'none', minWidth: 0, '&:hover': { color: THEME.mint } }}>Edit</Button>
          <Button size="small" variant="text" disabled={busy} onClick={() => onAskRetire(proof)} sx={{ color: THEME.textMuted, textTransform: 'none', minWidth: 0, '&:hover': { color: THEME.pink } }}>Retire</Button>
        </Box>
      </Box>
      {editOpen && <ProofEditModal proof={proof} onClose={() => setEditOpen(false)} onSaved={onSaved} />}
    </Box>
  );
};

const PROOF_FILTERS = [
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'unreviewed', label: 'Unreviewed' },
  { key: 'all', label: 'All' },
];

const ProofsTab = () => {
  const proofsRes = useGtmResource('/api/gtm/proofs', (d) => (Array.isArray(d) ? d : (d && d.proofs) || []));
  const intelRes = useGtmResource('/api/gtm/proofs/intel', (d) => d || {});
  const [extracting, setExtracting] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [note, setNote] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [filter, setFilter] = useState('all');

  const allProofs = (proofsRes.data || []).filter((p) => !p.retired);
  const intel = intelRes.data || {};
  const citedByClient = intel.citedByClient || intel.perClient || intel.clientCited30d || intel.citedPerClient || null;
  const proofs = allProofs.filter((p) => (filter === 'reviewed' ? p.reviewed === true : filter === 'unreviewed' ? p.reviewed !== true : true));

  const reloadAll = () => { proofsRes.reload(); intelRes.reload(); };

  const extract = async () => {
    setExtracting(true); setNote(null);
    try {
      const r = await mineProofsNow();
      setNote(`Extracted: scanned ${fmtCount(r && r.transcriptsScanned)} transcripts, ${fmtCount(r && r.proofsUpserted)} proofs upserted.`);
      reloadAll();
    } catch (err) { setNote((err && err.message) || 'Could not extract from transcripts.'); }
    finally { setExtracting(false); }
  };
  const dedup = async () => {
    setDeduping(true); setNote(null);
    try {
      const r = await dedupeProofsNow();
      const merged = firstFiniteNumber(r && r.merged, r && r.mergedCount);
      const groups = firstFiniteNumber(r && r.groups, r && r.groupsMerged, r && r.clusters);
      setNote(`Dedup complete: ${merged != null ? `${fmtCount(merged)} merged` : 'ran'}${groups != null ? ` across ${fmtCount(groups)} groups` : ''}.`);
      reloadAll();
    } catch (err) { setNote((err && err.message) || 'Could not run dedup.'); }
    finally { setDeduping(false); }
  };
  const askRetire = (proof) => setConfirm({
    title: 'Retire this proof?',
    body: `"${proof.whatWeDo}" will no longer be offered to the composer. You can re-add it later.`,
    confirmLabel: 'Retire',
    tone: 'danger',
    onConfirm: async () => { try { await retireProof(proof._id); reloadAll(); } catch (e) { setNote((e && e.message) || 'Could not retire.'); } },
  });

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The composer can only cite reviewed entries with status done or in progress. Advance status by clicking the badge; mark an entry reviewed to make it citable.">
        Everything from your calls lands here: what clients asked for, what we're building, what we've shipped. Only entries you've reviewed with status done or in progress can be cited in outreach.
      </Explainer>

      <ProofFunnelHeader intel={intel} totalProofs={allProofs.length} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {PROOF_FILTERS.map((f) => <SelectChip key={f.key} label={f.label} selected={filter === f.key} onClick={() => setFilter(f.key)} />)}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={extracting} onClick={extract} startIcon={extracting ? <CircularProgress size={12} sx={{ color: THEME.mint }} /> : null}>{extracting ? 'Extracting…' : 'Extract from transcripts'}</Button>
        <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={deduping} onClick={dedup} startIcon={deduping ? <CircularProgress size={12} sx={{ color: THEME.mint }} /> : null}>{deduping ? 'Running…' : 'Run dedup'}</Button>
      </Box>

      {note && <Typography sx={{ fontSize: 11, color: THEME.textMuted, mb: 1 }}>{note}</Typography>}

      <Resource loading={proofsRes.loading} error={proofsRes.error} empty={proofs.length === 0} loadingLabel="Loading proofs…" emptyLabel="No proofs here yet. Extract them from the transcript library." onRetry={reloadAll}>
        {proofs.map((p) => <ProofCard key={p._id} proof={p} citedByClient={citedByClient} onSaved={reloadAll} onAskRetire={askRetire} />)}
      </Resource>
      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

const SequencesTab = () => {
  const seqRes = useGtmResource('/api/gtm/sequences', (d) => (Array.isArray(d) ? d : (d && d.sequences) || []));
  const linkRes = useGtmResource('/api/gtm/link-assets', (d) => (d && d.linkAssets) || []);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const sequences = seqRes.data || [];
  return (
    <Box sx={{ maxWidth: 860 }}>
      <PitchRotationPanel />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Explainer tip="Every outbound sequence: its copy, cadence, and link. Edit copy and structure here with no deploy; set a sequence live to make it send-eligible.">
          Your outbound sequences. Open one to edit its steps and copy, preview it against a sample contact, and set it live.
        </Explainer>
        <Button variant="contained" sx={{ ...mintBtnSx, flexShrink: 0, [MOBILE_MQ]: { minHeight: 44 } }} onClick={() => setCreating(true)}>
          New sequence
        </Button>
      </Box>
      <Resource
        loading={seqRes.loading}
        error={seqRes.error}
        empty={sequences.length === 0}
        loadingLabel="Loading sequences…"
        emptyLabel="No sequences configured yet."
        onRetry={seqRes.reload}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {sequences.map((seq) => (
            <Box key={seq._id}>
              <SequenceListCard seq={seq} onOpen={() => setEditing(seq)} />
              <AudienceHintEditor seq={seq} onSaved={seqRes.reload} />
            </Box>
          ))}
        </Box>
      </Resource>
      {editing && (
        <SequenceEditor sequence={editing} linkAssets={linkRes.data || []} onClose={() => setEditing(null)} onSaved={seqRes.reload} />
      )}
      {creating && (
        <NewSequenceModal
          onClose={() => setCreating(false)}
          onCreated={(seq) => {
            setCreating(false);
            seqRes.reload();
            if (seq && (seq._id || seq.id)) setEditing({ ...seq, _id: seq._id || seq.id });
          }}
        />
      )}
    </Box>
  );
};

// ===== Tab: Inbox (Chunk 4 — replies awaiting approval) =====
// Replied contacts with an AI-drafted response. Review the inbound reply, edit the
// draft inline, then Approve (sends via Instantly) or Reject. Nothing sends without
// a human click. Fetched paginated from GET /api/gtm/replies (no all-messages fetch).

// The AI-classified reply intent, colored via the Badge palette.
const INTENT_META = {
  interested: { label: 'Interested', tone: 'mint' },
  question: { label: 'Question', tone: 'gray' },
  referral: { label: 'Referral', tone: 'amber' },
  not_interested: { label: 'Not interested', tone: 'pink' },
  other: { label: 'Other', tone: 'muted' },
};

const IntentChip = ({ intent }) => {
  const m = INTENT_META[intent] || { label: intent || 'Unclassified', tone: 'muted' };
  return <Badge tone={m.tone}>{m.label}</Badge>;
};

// REPLY-NOISE-01: the reply classifier's verdict (`minedType`). The split that
// matters at a glance is book-the-meeting vs archive-it, so positive and negative
// get real colour and everything else stays quiet.
const MINED_META = {
  human_positive: { label: 'Positive', tone: 'mint' },
  human_negative: { label: 'Negative', tone: 'pink' },
  human_neutral: { label: 'Neutral', tone: 'gray' },
  auto_reply: { label: 'Out of office', tone: 'muted' },
  bounce_notice: { label: 'Bounced', tone: 'muted' },
  unsubscribe: { label: 'Unsubscribed', tone: 'muted' },
};

const MinedChip = ({ minedType }) => {
  const m = MINED_META[minedType];
  if (!m) return null;
  return <Badge tone={m.tone}>{m.label}</Badge>;
};

// The classifier's one-sentence gist. The most scannable thing on the row, so it sits
// directly under the name, above the verbatim message.
const MinedSummary = ({ text }) => {
  if (!text) return null;
  return (
    <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, fontStyle: 'italic', lineHeight: 1.5, mb: 1 }}>
      {text}
    </Typography>
  );
};

// Per-reply feedback into the learning log: thumbs (rating 5 or 1) plus an optional
// note and an optional corrected intent. Submits through onSubmit; surfaces its own
// success / error so a failed write never reads as a false success.
const FeedbackControl = ({ onSubmit }) => {
  const [rating, setRating] = useState(null); // 5 = up, 1 = down
  const [note, setNote] = useState('');
  const [correctedIntent, setCorrectedIntent] = useState('');
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const pick = (r) => {
    setRating(r);
    setOpen(true);
    setDone(false);
    setErr(null);
  };
  const send = async () => {
    setSending(true);
    setErr(null);
    try {
      await onSubmit({ rating, note: note.trim(), correctedIntent });
      setDone(true);
      setOpen(false);
    } catch (e) {
      setErr((e && e.message) || 'Feedback failed.');
    } finally {
      setSending(false);
    }
  };

  if (done) return <Typography sx={{ fontSize: '11px', color: THEME.mint }}>Thanks, feedback logged.</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: open ? 1 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mr: 0.5 }}>Draft quality</Typography>
        <IconButton size="small" onClick={() => pick(5)} sx={{ color: rating === 5 ? THEME.mint : THEME.textMuted }}>
          <ThumbUpOffAltIcon sx={{ fontSize: 15 }} />
        </IconButton>
        <IconButton size="small" onClick={() => pick(1)} sx={{ color: rating === 1 ? THEME.pink : THEME.textMuted }}>
          <ThumbDownOffAltIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          <TextField
            size="small"
            placeholder="Optional note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            InputProps={inputProps}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Correct intent</Typography>
            <Select
              size="small"
              displayEmpty
              value={correctedIntent}
              onChange={(e) => setCorrectedIntent(e.target.value)}
              sx={{
                fontSize: '12px',
                color: THEME.textPrimary,
                minWidth: 150,
                background: THEME.card,
                '& fieldset': { borderColor: THEME.border },
                '& .MuiSvgIcon-root': { color: THEME.textMuted },
                '& .MuiSelect-select': { py: '6px' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: THEME.cardRaised,
                    color: THEME.textPrimary,
                    border: `1px solid ${THEME.borderStrong}`,
                    '& .MuiMenuItem-root': { fontSize: '12px' },
                    '& .Mui-selected': { background: `${THEME.mintFaint} !important` },
                  },
                },
              }}
            >
              <MenuItem value="">(unchanged)</MenuItem>
              {Object.keys(INTENT_META).map((k) => (
                <MenuItem key={k} value={k}>
                  {INTENT_META[k].label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={sending} onClick={send}>
              {sending ? 'Sending…' : 'Send feedback'}
            </Button>
            {err && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{err}</Typography>}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// One reply row: prospect, inbound reply (verbatim), intent, editable draft, and
// the Approve / Reject actions. Approve sends whatever is currently in the draft
// field (edit-inline). Busy state disables the buttons so a click cannot double-fire.
// Presentation-only cleanup of a plain-text reply body. It NEVER mutates the stored
// bodyText — it returns a display copy. Rule 1 does not apply: this is display hygiene, not
// intent classification. Best-effort formatting only:
//   - collapse bracketed image-URL blocks ([https://...png?ver=...], an HTML-to-text
//     signature artifact) into a subtle [image] token;
//   - collapse runs of 3+ blank lines down to one;
//   - split off a trailing signature/footer at the FIRST conventional boundary line
//     (exactly "--" or "#end") so it can be folded. If no boundary is found, the full body
//     is shown as-is.
const IMG_URL_IN_BRACKETS = /\[[ \t]*https?:\/\/[^\]\s]+\.(?:png|jpe?g|gif|webp|svg|bmp|tiff?)(?:\?[^\]]*)?[ \t]*\]/gi;

function cleanReplyBody(text) {
  const raw = text == null ? '' : String(text);
  let cleaned = raw.replace(/\r\n?/g, '\n');    // normalize newlines to \n
  cleaned = cleaned.replace(/^[ \t]+$/gm, '');  // whitespace-only lines -> truly blank
  cleaned = cleaned.replace(IMG_URL_IN_BRACKETS, '[image]');
  cleaned = cleaned.replace(/\n{4,}/g, '\n\n');  // runs of 3+ blank lines -> one blank line

  // Signature/footer boundary: the first line that is exactly "--" or "#end". Fold from that
  // line down, but only when there is real message content above it (never hide everything).
  const lines = cleaned.split('\n');
  let boundary = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const t = lines[i].trim();
    if (t === '--' || t === '#end') { boundary = i; break; }
  }
  if (boundary > 0) {
    const head = lines.slice(0, boundary).join('\n').replace(/\n+$/, '');
    const tail = lines.slice(boundary).join('\n').replace(/\n+$/, '');
    if (head.trim()) return { body: head, signature: tail };
  }
  return { body: cleaned.replace(/\n+$/, ''), signature: '' };
}

// Renders a reply body with the display-time cleanup above, contained so it can never
// overflow its card: long unbroken strings (URLs, tracking links) wrap, and very long
// bodies scroll internally within ~400px instead of dominating the tab. A detected
// signature/footer is folded behind a toggle, default collapsed.
// `full` drops the internal scroll cap. In the tab's old flat list a 400px window kept
// one long reply from swallowing the page, but inside an opened conversation that same
// window reads as the message being cut off, which is exactly what it must not do:
// expanding a message shows the whole body, however long.
const ReplyBody = ({ text, empty = '(no message text captured)', full = false }) => {
  const [showSig, setShowSig] = useState(false);
  const hasText = text != null && String(text).trim() !== '';
  const { body, signature } = useMemo(() => cleanReplyBody(text), [text]);
  const bodyTextSx = {
    fontSize: '13px',
    color: THEME.textSecondary,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };
  if (!hasText) {
    return <Typography sx={bodyTextSx}>{empty}</Typography>;
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={full ? { overflowX: 'hidden' } : { maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
        <Typography sx={bodyTextSx}>{body || empty}</Typography>
        {signature && showSig && (
          <Typography sx={{ ...bodyTextSx, mt: 1, pt: 1, borderTop: `1px dashed ${THEME.border}`, color: THEME.textMuted }}>
            {signature}
          </Typography>
        )}
      </Box>
      {signature && (
        <Box
          onClick={() => setShowSig((v) => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSig((v) => !v); } }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, cursor: 'pointer', userSelect: 'none', color: THEME.textMuted, '&:hover': { color: THEME.textSecondary } }}
        >
          <ExpandMoreIcon sx={{ fontSize: 16, transform: showSig ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
            {showSig ? 'Hide signature and footer' : 'Show signature and footer'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ----- Read / unread marks (GTM-REPLY-READ-01) -----
// A reply you have looked at stops shouting for attention, and you can push it back
// to unread to come back to it later. Marking is DELIBERATE: opening a message or
// pressing the toggle. Scrolling past a message never marks it, so the count only
// ever moves because you did something. It never approves, rejects, or hides anything.

// Unread rows carry a mint left edge and a dot; read rows sit quiet.
const UnreadDot = () => (
  <Box
    component="span"
    aria-hidden
    sx={{ width: 8, height: 8, borderRadius: '50%', background: THEME.mint, flexShrink: 0, boxShadow: `0 0 0 3px ${THEME.mintFaint}` }}
  />
);

// The per-row toggle. Reads as a statement of the current state plus the action.
const ReadToggle = ({ read, onToggle, disabled }) => (
  <Tooltip
    arrow
    placement="top"
    {...tipSlotProps}
    title={read ? 'You already opened this one. Mark it unread to come back to it later.' : 'Mark this as opened so it stops counting as new.'}
  >
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={onToggle}
        startIcon={read ? <MarkEmailUnreadOutlinedIcon sx={{ fontSize: 15 }} /> : <MarkEmailReadOutlinedIcon sx={{ fontSize: 15 }} />}
        sx={{
          textTransform: 'none',
          fontSize: '11px',
          fontWeight: 600,
          minWidth: 0,
          px: 1,
          color: THEME.textMuted,
          '&:hover': { color: THEME.textPrimary, background: 'rgba(255,255,255,0.04)' },
        }}
      >
        {read ? 'Mark unread' : 'Mark read'}
      </Button>
    </Box>
  </Tooltip>
);

// The verbatim inbound text, whichever field carried it. Matched rows serialize it as
// `inboundReply`; the bare `inbound` fallback is for older shapes.
const inboundTextOf = (r) => (r && (r.inboundReply || r.bodyText || r.inbound)) || '';

// Which of our sending aliases the prospect actually wrote to. Worth stating on the
// thread: some prospects only ever talk to the address that first mailed them.
const RepliedToMailbox = ({ mailbox }) => {
  if (!mailbox) return null;
  return (
    <Tooltip arrow placement="top" {...tipSlotProps} title="The sending mailbox this prospect replied to. Your response goes back into this same thread.">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: THEME.textMuted, cursor: 'help' }}>
        <SendOutlinedIcon sx={{ fontSize: 13 }} />
        <Typography sx={{ fontSize: '11px', wordBreak: 'break-all' }}>{`to ${mailbox}`}</Typography>
      </Box>
    </Tooltip>
  );
};

// ONE inbound message inside a thread. The body is closed until you open it, and
// opening it is what marks it read: nothing is ever marked just by scrolling past.
// GTM-REPLY-COMPOSE-01. The tab is a mail client now: compact one-line conversations,
// a reading pane that opens in place, and a composer that answers from the alias the
// prospect wrote to.
//
// The unit of pagination is the CONVERSATION (?groupBy=thread), so Load more can never
// cut an exchange in half. That retires the client-side sender grouping this file used
// to do: threadId is real, it comes from the provider, and it survives a subject change.

// Why the composer is closed, in the operator's words. Only a stale alias is fixable
// from here, which is why only that one names an address and offers a way out.
function replyBlockCopy(reason, mailbox) {
  if (reason === 'no_inbound_message') {
    return 'This conversation has only our own messages, so there is nothing to reply to.';
  }
  if (reason === 'reply_thread_unknown') {
    return 'No Instantly thread recorded for this contact, so it cannot be answered from here.';
  }
  if (reason === 'reply_mailbox_unknown') {
    return 'We could not determine which of our mailboxes this prospect wrote to.';
  }
  if (reason === 'reply_mailbox_not_in_fleet') {
    return `This prospect wrote to ${mailbox || 'that mailbox'}, which is no longer connected in Instantly. Reconnect that mailbox or re-sync the fleet.`;
  }
  return 'This conversation cannot be answered from here.';
}

// Send failures. cannot_reply_to_own_message means we picked the wrong target, which is
// a bug on our side rather than anything the operator did, so it says so.
function sendReplyErrorCopy(err) {
  const code = (err && err.code) || '';
  const mailbox = (err && err.data && err.data.replyFromMailbox) || '';
  if (code === 'empty_body') return 'A reply needs a body.';
  if (code === 'reply_not_found') return 'That message no longer exists.';
  if (code === 'cannot_reply_to_own_message') {
    return 'That target is one of our own messages, so the reply was not sent. This is a bug on our side; reload the conversation and try again.';
  }
  if (code === 'send_disabled') return 'Sending is disabled on the server, so nothing was sent.';
  if (code === 'reply_send_failed') return 'Instantly refused the send. Nothing went out.';
  if (code === 'reply_thread_unknown' || code === 'reply_mailbox_unknown' || code === 'reply_mailbox_not_in_fleet') {
    return replyBlockCopy(code, mailbox);
  }
  return (err && err.message) || 'The reply could not be sent.';
}

// A short, single-line gist of a message for the collapsed row and the list.
function replySnippet(msg, max = 140) {
  const t = inboundTextOf(msg) || (msg && msg.bodyText) || '';
  return String(t).replace(/\s+/g, ' ').trim().slice(0, max);
}

// Who a message is from, in as few characters as read well.
function senderLabel(msg) {
  if (!msg) return '(unknown sender)';
  if (msg.outbound) return 'You';
  const name = [msg.firstName, msg.lastName].filter(Boolean).join(' ').trim();
  return name || msg.name || msg.fromEmail || msg.email || '(unknown sender)';
}

// ONE message inside the reading pane. Ours are tinted and right-labelled so a thread
// reads as a conversation rather than a list of arrivals. Collapsed shows one line;
// opening an inbound message is what marks it read.
const ThreadMessage = ({ msg, defaultOpen, onSetRead }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const outbound = !!msg.outbound;
  const read = !!msg.read;

  // Auto-open is still a deliberate act: the operator clicked into this conversation.
  useEffect(() => {
    if (defaultOpen && !outbound && !read && onSetRead && msg.replyId) onSetRead(msg.replyId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !outbound && !read && onSetRead && msg.replyId) onSetRead(msg.replyId, true);
  };

  const keyToggle = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } };

  // Collapsed, the WHOLE card is the button, padding included, so there is no thin strip
  // to hunt for. Open, only the header row toggles, which keeps the controls and the
  // selectable message text inside from fighting a card-wide click handler.
  const cardClick = open ? {} : { onClick: toggle, role: 'button', tabIndex: 0, onKeyDown: keyToggle };
  const headerClick = open ? { onClick: toggle, role: 'button', tabIndex: 0, onKeyDown: keyToggle } : {};

  return (
    <Box
      {...cardClick}
      aria-expanded={open}
      sx={{
        borderRadius: '10px',
        border: `1px solid ${outbound ? 'rgba(96,165,250,0.25)' : THEME.border}`,
        background: outbound ? 'rgba(96,165,250,0.05)' : 'rgba(255,255,255,0.02)',
        p: 1.25,
        ml: outbound ? { xs: 0, sm: 3 } : 0,
        cursor: open ? 'default' : 'pointer',
        userSelect: open ? 'auto' : 'none',
        transition: 'background 120ms, border-color 120ms',
        ...(open ? {} : {
          '&:hover': {
            background: outbound ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.05)',
            borderColor: outbound ? 'rgba(96,165,250,0.45)' : THEME.textMuted,
          },
          '&:hover .replyExpandCue': { color: THEME.textPrimary, background: 'rgba(255,255,255,0.08)' },
        }),
        [MOBILE_MQ]: { minHeight: 44 },
      }}
    >
      <Box {...headerClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 0 }}>
        {!outbound && !read && <UnreadDot />}
        <Typography sx={{ fontSize: '12px', fontWeight: !outbound && !read ? 700 : 600, color: outbound ? THEME.blue : THEME.textPrimary, flexShrink: 0 }}>
          {senderLabel(msg)}
        </Typography>
        {!open && (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {replySnippet(msg) || '(no message text captured)'}
          </Typography>
        )}
        <Box sx={{ flex: open ? 1 : 0, minWidth: 4 }} />
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, flexShrink: 0 }}>{fmtWhen(msg.receivedAt)}</Typography>
        {/* The cue reads as a control rather than decoration: a filled round target that
            lights up with the row, so the click area is not a guess. */}
        <Box
          className="replyExpandCue"
          aria-hidden
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            flexShrink: 0,
            color: THEME.textSecondary,
            background: 'rgba(255,255,255,0.04)',
            transition: 'color 120ms, background 120ms',
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        {!outbound && <MinedSummary text={msg.minedSummary} />}
        {!outbound && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <MinedChip minedType={msg.minedType} />
            <Box sx={{ flex: 1, minWidth: 8 }} />
            {onSetRead && msg.replyId && <ReadToggle read={read} onToggle={() => onSetRead(msg.replyId, !read)} />}
          </Box>
        )}
        {/* The whole body, uncapped, line breaks intact. inboundReply is the only body
            field the serializer emits, for our own outbound messages as well as theirs. */}
        <Box sx={{ mt: 0.5, minWidth: 0, overflow: 'hidden' }}>
          <ReplyBody full text={inboundTextOf(msg)} empty="(no message text captured)" />
        </Box>
      </Collapse>
    </Box>
  );
};

// The reply box. Always inline at the bottom of an open conversation, never a modal.
// Disabled with the real reason when the server says the thread cannot be answered.
const ThreadComposer = ({ thread, onSent, onGoToMailboxes }) => {
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [showSubject, setShowSubject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canReply = !!thread.canReply;
  const blocked = thread.blockedReason;

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const out = await sendThreadReply(thread.replyToReplyId, { body, subject });
      setBody('');
      setSubject('');
      setShowSubject(false);
      if (onSent) onSent(out);
    } catch (err) {
      setError(sendReplyErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  if (!canReply) {
    return (
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <LockOutlinedIcon sx={{ fontSize: 15, color: THEME.textMuted, flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>
            {replyBlockCopy(blocked, thread.replyFromMailbox)}
          </Typography>
        </Box>
        {blocked === 'reply_mailbox_not_in_fleet' && onGoToMailboxes && (
          <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, mt: 1 }} onClick={onGoToMailboxes} startIcon={<SyncIcon sx={{ fontSize: 15 }} />}>
            Open Mailboxes to re-sync
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
          {`Sends from ${thread.replyFromMailbox}`}
        </Typography>
        {thread.mailboxInFleet == null && (
          <Tooltip arrow placement="top" {...tipSlotProps} title="The mailbox fleet could not be read, so we could not confirm this alias is still connected in Instantly.">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Badge tone="amber">Fleet unverified</Badge>
            </Box>
          </Tooltip>
        )}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Button
          size="small"
          variant="text"
          onClick={() => setShowSubject((v) => !v)}
          sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 600, minWidth: 0, px: 1, color: THEME.textMuted, '&:hover': { color: THEME.textPrimary } }}
        >
          {showSubject ? 'Use the thread subject' : 'Change subject'}
        </Button>
      </Box>

      {showSubject && (
        <TextField
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={thread.subject ? `Re: ${thread.subject}` : 'Subject'}
          InputProps={inputProps}
        />
      )}

      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply"
        InputProps={inputProps}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          sx={mintBtnSx}
          disabled={busy || !body.trim()}
          onClick={send}
          startIcon={busy ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : <SendOutlinedIcon sx={{ fontSize: 16 }} />}
        >
          {busy ? 'Sending…' : 'Send reply'}
        </Button>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>This sends immediately.</Typography>
      </Box>

      {error && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

// The AI-drafted reply, when there is one. This is the OTHER send path: it posts to
// the contact-keyed approve endpoint, which can only reach replies matched to a
// gtm_contact. Almost none are today, so this block is usually absent; it renders only
// when the newest inbound message actually carries a draft, and it stays visibly
// separate from the human composer because the two send by different routes.
const AiDraftBlock = ({ msg, actionError, onApprove, onReject, onFeedback, onGoToMailboxes }) => {
  const [draft, setDraft] = useState(msg.draft || '');
  const [busy, setBusy] = useState(null);

  const run = async (which) => {
    setBusy(which);
    try {
      if (which === 'approve') await onApprove(msg.id, draft, senderLabel(msg));
      else await onReject(msg.id);
    } catch (e) {
      /* the page surfaces actionError */
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', border: `1px solid ${THEME.mintDim}`, background: THEME.mintFaint }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
        <AutoAwesomeIcon sx={{ fontSize: 15, color: THEME.mint }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          AI drafted response
        </Typography>
        <IntentChip intent={msg.intent} />
        {msg.draftModelUsed && <ModelStamp model={msg.draftModelUsed} />}
      </Box>
      <TextField fullWidth multiline minRows={3} size="small" value={draft} onChange={(e) => setDraft(e.target.value)} InputProps={inputProps} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          sx={mintBtnSx}
          disabled={busy != null || !draft.trim()}
          onClick={() => run('approve')}
          startIcon={busy === 'approve' ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
        >
          {busy === 'approve' ? 'Sending…' : 'Approve and send'}
        </Button>
        <Button size="small" variant="outlined" sx={dangerBtnSx} disabled={busy != null} onClick={() => run('reject')}>
          {busy === 'reject' ? 'Rejecting…' : 'Reject draft'}
        </Button>
        <Box sx={{ flex: 1, minWidth: 12 }} />
        <FeedbackControl onSubmit={(fb) => onFeedback(msg.id, fb)} />
      </Box>
      {actionError && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5, flex: 1, minWidth: 160 }}>
            {actionError.message || actionError}
          </Typography>
          {actionError.code === 'reply_mailbox_not_in_fleet' && onGoToMailboxes && (
            <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, flexBasis: '100%', maxWidth: 220 }} onClick={onGoToMailboxes} startIcon={<SyncIcon sx={{ fontSize: 15 }} />}>
              Open Mailboxes to re-sync
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

// The reading pane: the whole conversation, oldest first, newest open. Opens in place
// under its list row so the list is never lost.
const ThreadPane = ({ data, loading, error, onRetry, onSetRead, onSent, onGoToMailboxes, actionErrors, onApprove, onReject, onFeedback }) => {
  if (loading) return <Box sx={{ p: 2 }}><LoadingState label="Loading conversation…" /></Box>;
  if (error) return <Box sx={{ p: 2 }}><ErrorState message={error} onRetry={onRetry} /></Box>;
  if (!data) return null;

  // Chronological, the way an email thread reads: oldest at the top, newest at the
  // bottom, composer under it. The endpoint already sorts oldest first, so this renders
  // in the order it arrives. Do NOT reverse it.
  const messages = Array.isArray(data.messages) ? data.messages : [];
  const lastIdx = messages.length - 1; // the newest message, open by default
  // The newest INBOUND message is the one an AI draft would hang off, and the one the
  // human composer answers. Ours are never a reply target.
  const inbound = messages.filter((m) => !m.outbound);
  const newestInbound = inbound.length ? inbound[inbound.length - 1] : null;
  const aiDraft = newestInbound && newestInbound.id != null && newestInbound.draft ? newestInbound : null;

  return (
    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary, wordBreak: 'break-word' }}>
          {data.subject || '(no subject)'}
        </Typography>
        <Badge tone="gray">{`${data.messageCount} in thread`}</Badge>
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <RepliedToMailbox mailbox={data.replyFromMailbox} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {messages.map((m, i) => (
          <ThreadMessage
            key={m.replyId || `${m.receivedAt}-${i}`}
            msg={m}
            defaultOpen={i === lastIdx}
            onSetRead={onSetRead}
          />
        ))}
      </Box>

      {aiDraft && (
        <AiDraftBlock
          msg={aiDraft}
          actionError={actionErrors ? actionErrors[aiDraft.id] : undefined}
          onApprove={onApprove}
          onReject={onReject}
          onFeedback={onFeedback}
          onGoToMailboxes={onGoToMailboxes}
        />
      )}

      <ThreadComposer thread={data} onSent={onSent} onGoToMailboxes={onGoToMailboxes} />
    </Box>
  );
};

// One conversation as a single line: sender, count, subject, snippet, date. Unread is
// weight, not a badge or a coloured bar, so a full inbox stays scannable.
const ThreadListRow = ({ summary, newest, open, onToggle, children }) => {
  const unread = (summary.unreadCount || 0) > 0;
  const who = senderLabel(newest) || (summary.participants && summary.participants[0]) || '(unknown sender)';
  const count = summary.messageCount || 1;
  const noise = !!(newest && newest.isNoise);

  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: `1px solid ${open ? THEME.mintDim : THEME.border}`,
        background: THEME.card,
        opacity: noise ? 0.72 : 1,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          userSelect: 'none',
          minWidth: 0,
          '&:hover': { background: 'rgba(255,255,255,0.03)' },
          [MOBILE_MQ]: { minHeight: 48, flexWrap: 'wrap' },
        }}
      >
        {unread && <UnreadDot />}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: unread ? 700 : 500,
            color: unread ? THEME.textPrimary : THEME.textSecondary,
            flexShrink: 0,
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {who}
          {count > 1 && <Box component="span" sx={{ color: THEME.textMuted, fontWeight: 500 }}>{` (${count})`}</Box>}
        </Typography>
        {newest && <MinedChip minedType={newest.minedType} />}
        <Typography
          sx={{
            fontSize: '13px',
            color: unread ? THEME.textSecondary : THEME.textMuted,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_MQ]: { flexBasis: '100%', flex: 'unset' },
          }}
        >
          {newest && newest.subject ? newest.subject : '(no subject)'}
          <Box component="span" sx={{ color: THEME.textMuted }}>
            {newest ? `  ${replySnippet(newest, 90)}` : ''}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, flexShrink: 0 }}>
          {fmtWhen(summary.lastReceivedAt || (newest && newest.receivedAt))}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${THEME.border}` }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// GTM-REPLY-REFUSAL-COPY-01: what actually went out, after the fact, naming the alias
// the server reported sending from. A null mailboxInFleet is called out on its own: it
// means the fleet could not be read, so the reply left without anyone checking the alias
// is still connected. storedInThread false is NOT a failure, the reply was sent and only
// our local copy of it did not save.
const SentNotice = ({ notice, onDismiss }) => {
  const unverified = notice.inFleet === null || notice.inFleet === undefined;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        borderRadius: '10px',
        background: unverified ? 'rgba(251,191,36,0.06)' : THEME.mintFaint,
        border: `1px solid ${unverified ? 'rgba(251,191,36,0.35)' : THEME.mintDim}`,
      }}
    >
      {unverified
        ? <WarningAmberIcon sx={{ fontSize: 16, color: THEME.amber, flexShrink: 0, mt: '1px' }} />
        : <CheckCircleOutlineIcon sx={{ fontSize: 16, color: THEME.mint, flexShrink: 0, mt: '1px' }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, lineHeight: 1.5, wordBreak: 'break-word' }}>
          {notice.mailbox ? `Replied to ${notice.who} from ${notice.mailbox}.` : `Replied to ${notice.who}.`}
        </Typography>
        {unverified && (
          <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.5, mt: 0.25 }}>
            The mailbox fleet could not be read, so this went out without checking that the alias is still connected in Instantly.
          </Typography>
        )}
        {notice.storedInThread === false && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5, mt: 0.25 }}>
            Sent. It will appear in the conversation shortly.
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={onDismiss} sx={{ color: THEME.textMuted, p: 0.25 }} aria-label="Dismiss">
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

const InboxTab = ({ res, actionErrors, onApprove, onReject, onFeedback, onGoToMailboxes }) => {
  const {
    replies, threads, total, totalMessages, truncated,
    unreadGenuineTotal, counts, loading, error, reload, loadMore,
    unreadOnly, setUnreadOnly, kind, setKind, applyRead, applyReadAll, applyTotals,
  } = res;
  const [readError, setReadError] = useState(null);

  const loadedIds = useMemo(() => replies.map((r) => r.replyId).filter(Boolean), [replies]);
  const readLoaded = replies.filter((r) => r.read).length;
  // In groupBy=thread mode `total` counts CONVERSATIONS and `totalMessages` counts
  // messages. Unread stays message-level in both, so the badge never changes meaning.
  const threadCount = total != null ? total : threads.length;
  const messageCount = totalMessages != null ? totalMessages : replies.length;
  const unreadCount =
    unreadGenuineTotal != null ? unreadGenuineTotal : Math.max(0, messageCount - readLoaded);
  const showingNoise = kind === 'all';
  const noiseCount = counts ? counts.noise : 0;
  const unclassifiedCount = counts ? counts.unclassified : 0;

  // The newest message of each conversation, for the one-line list rows. The list
  // response ships every message of the conversations on this page, newest first, so
  // the first hit per threadId is the newest.
  const newestByThread = useMemo(() => {
    const m = {};
    replies.forEach((r) => {
      const k = r.threadId || r.replyId;
      if (k && !m[k]) m[k] = r;
    });
    return m;
  }, [replies]);

  // Which conversation is open in the reading pane, and its fetched contents.
  const [openThreadId, setOpenThreadId] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const loadThread = useCallback(async (threadId) => {
    setThreadLoading(true);
    setThreadError(null);
    try {
      setThreadData(await fetchReplyThread(threadId));
    } catch (err) {
      setThreadData(null);
      setThreadError(err.message || 'Could not load that conversation.');
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const toggleThread = useCallback((threadId) => {
    setOpenThreadId((cur) => {
      if (cur === threadId) return null;
      setThreadData(null);
      loadThread(threadId);
      return threadId;
    });
  }, [loadThread]);

  const [sentNotices, setSentNotices] = useState([]);

  // Approving an AI draft sends through the contact-keyed path, which reports the alias
  // it used the same way the human composer does, so it gets the same confirmation.
  const approveAiDraft = useCallback(async (id, draft, who) => {
    const out = await onApprove(id, draft);
    setSentNotices((prev) => [
      {
        id: `ai-${id}`,
        who,
        mailbox: (out && out.replyFromMailbox) || '',
        inFleet: out ? out.mailboxInFleet : undefined,
      },
      ...prev.filter((n) => n.id !== `ai-${id}`),
    ].slice(0, 4));
    reload();
    return out;
  }, [onApprove, reload]);

  // A send writes straight into the thread, so refetch rather than wait on a sync. The
  // inbox list is refreshed too, since the conversation's snippet and date just moved.
  const onSent = useCallback((out, who) => {
    setSentNotices((prev) => [
      {
        id: `${out.threadId || ''}-${prev.length}`,
        who,
        mailbox: out.replyFromMailbox || '',
        inFleet: out.mailboxInFleet,
        storedInThread: out.storedInThread,
      },
      ...prev,
    ].slice(0, 4));
    if (out.threadId) loadThread(out.threadId);
    reload();
  }, [loadThread, reload]);

  // The open pane renders from its own fetched copy, so a read mark has to land in both
  // places or the dot clears in the list and stays in the conversation.
  const applyReadInPane = useCallback((ids, nextRead) => {
    const s = new Set(ids);
    setThreadData((prev) => {
      if (!prev || !Array.isArray(prev.messages)) return prev;
      return {
        ...prev,
        messages: prev.messages.map((m) => (s.has(m.replyId) ? { ...m, read: !!nextRead } : m)),
      };
    });
  }, []);

  // A reply is marked read when you OPEN it or say so, never by scrolling past it.
  const setRead = useCallback(async (replyId, nextRead) => {
    if (!replyId) return;
    setReadError(null);
    applyRead([replyId], nextRead);
    applyReadInPane([replyId], nextRead);
    try {
      applyTotals(await setReplyRead(replyId, nextRead));
    } catch (err) {
      applyRead([replyId], !nextRead);
      applyReadInPane([replyId], !nextRead);
      setReadError(err.message || 'Could not change that reply.');
    }
  }, [applyRead, applyReadInPane, applyTotals]);

  // Mark all read covers rows never fetched (all:true), so the badge actually clears.
  // It carries the tab's `kind`, or an all:true sweep would silently clear the 40
  // out-of-office rows the operator cannot even see. Mark all unread deliberately
  // stays on loaded ids only, so it can never resurrect months of handled replies.
  const markAll = useCallback(async (nextRead) => {
    setReadError(null);
    applyReadAll(nextRead);
    try {
      applyTotals(
        nextRead
          ? await bulkReplyRead({ all: true, kind: showingNoise ? undefined : 'genuine', read: true })
          : await bulkReplyRead({ replyIds: loadedIds, read: false })
      );
    } catch (err) {
      setReadError(err.message || 'Could not update the replies.');
      reload();
    }
  }, [applyReadAll, applyTotals, loadedIds, reload, showingNoise]);

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Prospects who replied. The system drafted a response; you approve, edit, or reject. Nothing sends without your click.">
        Replies waiting for you. Review the inbound message and the drafted response, edit if needed, then approve to send.
      </Explainer>

      {/* Read state bar: what is still new, and the bulk controls. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>
          {unreadCount > 0 ? `${unreadCount} unread` : 'All opened'}
          <Box component="span" sx={{ color: THEME.textMuted }}>
            {` of ${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`}
            {` in ${threadCount} ${threadCount === 1 ? 'conversation' : 'conversations'}`}
          </Box>
        </Typography>
        <InfoTip
          size={13}
          text="A reply is marked opened when you open it, or when you mark it yourself. Scrolling past it does nothing. Opened is a viewing aid only: it never approves, rejects, or hides anything. Note that it is currently shared with the rest of the team, so a reply one of you opens is opened for everyone."
        />
        <Box sx={{ flex: 1, minWidth: 8 }} />
        {/* REPLY-NOISE-01: out-of-office and bounces are hidden by default. The count
            rides the label so it reads as filtered, never as replies going missing. */}
        <Tooltip
          arrow
          placement="top"
          {...tipSlotProps}
          title="Out-of-office notices, bounces, and unsubscribe confirmations, judged by the reply classifier. Hidden by default because they are not work; nothing is deleted."
        >
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              size="small"
              variant={showingNoise ? 'contained' : 'outlined'}
              sx={showingNoise ? mintBtnSx : outlinedBtnSx}
              disabled={loading || (!showingNoise && noiseCount === 0)}
              onClick={() => setKind(showingNoise ? 'genuine' : 'all')}
              startIcon={showingNoise ? <MarkEmailReadOutlinedIcon sx={{ fontSize: 15 }} /> : <FilterAltOutlinedIcon sx={{ fontSize: 15 }} />}
            >
              {showingNoise
                ? 'Hide out-of-office & auto-replies'
                : `Show out-of-office & auto-replies${noiseCount ? ` (${noiseCount})` : ''}`}
            </Button>
          </Box>
        </Tooltip>
        <Button
          size="small"
          variant={unreadOnly ? 'contained' : 'outlined'}
          sx={unreadOnly ? mintBtnSx : outlinedBtnSx}
          disabled={loading}
          onClick={() => setUnreadOnly(!unreadOnly)}
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
        >
          {unreadOnly ? 'Showing unread only' : 'Unread only'}
        </Button>
        <Tooltip
          arrow
          placement="top"
          {...tipSlotProps}
          title={
            showingNoise
              ? 'Marks every reply as opened, out-of-office included, whether or not this page has loaded it.'
              : 'Marks every real reply as opened, including ones this page has not loaded yet. Out-of-office rows are left alone.'
          }
        >
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              size="small"
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={unreadCount === 0}
              onClick={() => markAll(true)}
              startIcon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 15 }} />}
            >
              Mark all read
            </Button>
          </Box>
        </Tooltip>
        <Tooltip arrow placement="top" {...tipSlotProps} title="Puts the replies loaded on this page back to unread so you can go through them again. Older replies are left alone.">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              size="small"
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={readLoaded === 0}
              onClick={() => markAll(false)}
              startIcon={<MarkEmailUnreadOutlinedIcon sx={{ fontSize: 15 }} />}
            >
              Mark all unread
            </Button>
          </Box>
        </Tooltip>
        {/* What is being held back, and what has not been judged yet. Both are stated
            rather than left implicit, so a filtered list never reads as a short one. */}
        {(!showingNoise && noiseCount > 0) || unclassifiedCount > 0 ? (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, flexBasis: '100%', mt: -0.5 }}>
            {!showingNoise && noiseCount > 0
              ? `${noiseCount} out-of-office and auto-reply ${noiseCount === 1 ? 'message is' : 'messages are'} hidden.`
              : ''}
            {unclassifiedCount > 0
              ? `${!showingNoise && noiseCount > 0 ? ' ' : ''}${unclassifiedCount} awaiting classification, shown here until judged.`
              : ''}
          </Typography>
        ) : null}
        {readError && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexBasis: '100%' }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: '11px', color: THEME.pink, lineHeight: 1.5 }}>{readError}</Typography>
          </Box>
        )}
      </Box>

      {/* Outside Resource on purpose: answering the last reply empties the list, and the
          confirmation naming the alias must survive that. */}
      {sentNotices.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {sentNotices.map((n) => (
            <SentNotice
              key={n.id}
              notice={n}
              onDismiss={() => setSentNotices((prev) => prev.filter((x) => x.id !== n.id))}
            />
          ))}
        </Box>
      )}

      <Resource
        loading={loading && replies.length === 0}
        error={error}
        empty={replies.length === 0}
        loadingLabel="Loading replies…"
        emptyLabel={
          unreadOnly
            ? 'Nothing unread. Turn off Unread only to see the rest.'
            : !showingNoise && noiseCount > 0
              ? `No real replies waiting. ${noiseCount} out-of-office and auto-reply ${noiseCount === 1 ? 'message is' : 'messages are'} hidden.`
              : 'No replies waiting.'
        }
        onRetry={reload}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {threads.map((t) => {
            const newest = newestByThread[t.threadId];
            const open = openThreadId === t.threadId;
            return (
              <ThreadListRow
                key={t.threadId}
                summary={t}
                newest={newest}
                open={open}
                onToggle={() => toggleThread(t.threadId)}
              >
                <ThreadPane
                  data={threadData}
                  loading={threadLoading}
                  error={threadError}
                  onRetry={() => loadThread(t.threadId)}
                  onSetRead={setRead}
                  onSent={(out) => onSent(out, senderLabel(newest))}
                  onGoToMailboxes={onGoToMailboxes}
                  actionErrors={actionErrors}
                  onApprove={approveAiDraft}
                  onReject={onReject}
                  onFeedback={onFeedback}
                />
              </ThreadListRow>
            );
          })}
          {truncated && (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>
              Some conversations on this page were long enough to be trimmed in the list. Opening one always loads it in full.
            </Typography>
          )}
          {total != null && threads.length < total && (
            <Button variant="outlined" sx={{ ...outlinedBtnSx, mt: 1 }} disabled={loading} onClick={loadMore}>
              {loading
                ? 'Loading…'
                : `Load more (${threads.length} of ${threadCount} ${threadCount === 1 ? 'conversation' : 'conversations'})`}
            </Button>
          )}
        </Box>
      </Resource>
    </Box>
  );
};

// ===== Tab: Links (Chunk C — link-asset library that [link] resolves to) =====

const LINK_TYPE_OPTIONS = ['video', 'pdf', 'image', 'page', 'external'];

// Register an existing URL as a link asset.
const AddLinkForm = ({ onCreated }) => {
  const [form, setForm] = useState({ key: '', label: '', url: '', type: 'external', isDefault: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.key.trim() || !form.label.trim() || !form.url.trim()) {
      setError('Key, label, and URL are all required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createLinkAsset({ key: form.key.trim(), label: form.label.trim(), url: form.url.trim(), type: form.type, isDefault: form.isDefault });
      setForm({ key: '', label: '', url: '', type: 'external', isDefault: false });
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.message || 'Could not add the link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Register a URL</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <TextField size="small" placeholder="key" value={form.key} onChange={set('key')} InputProps={inputProps} sx={{ width: 140 }} />
        <TextField size="small" placeholder="label" value={form.label} onChange={set('label')} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
        <TextField size="small" placeholder="https://…" value={form.url} onChange={set('url')} InputProps={inputProps} sx={{ flex: 1, minWidth: 200 }} />
        <Select size="small" value={form.type} onChange={set('type')} sx={{ fontSize: 13, color: THEME.textPrimary, width: 130, '& fieldset': { borderColor: THEME.border }, '& .MuiSvgIcon-root': { color: THEME.textMuted } }}
          MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, '& .MuiMenuItem-root': { fontSize: 13 } } } }}>
          {LINK_TYPE_OPTIONS.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Checkbox size="small" checked={form.isDefault} onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} sx={{ p: 0.25, color: THEME.border, '&.Mui-checked': { color: THEME.mint } }} />
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>Set as the default asset</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" sx={mintBtnSx} disabled={busy} onClick={submit}>{busy ? 'Adding…' : 'Add link'}</Button>
      </Box>
      {error && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
    </Box>
  );
};

// Upload a file to S3; on success show the branded share URL with a copy button.
const UploadLinkForm = ({ onUploaded }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ label: '', key: '', type: 'video', isDefault: false });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { url }
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!file) { setError('Choose a file first.'); return; }
    if (!form.label.trim()) { setError('A label is required.'); return; }
    setBusy(true); setError(null); setResult(null); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', form.label.trim());
      if (form.key.trim()) fd.append('key', form.key.trim());
      fd.append('type', form.type);
      if (form.isDefault) fd.append('isDefault', 'true');
      const res = await uploadLinkAsset(fd, setProgress);
      setResult(res.linkAsset || null);
      setFile(null);
      setForm({ label: '', key: '', type: 'video', isDefault: false });
      if (onUploaded) onUploaded();
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    if (result && result.url && navigator.clipboard) {
      navigator.clipboard.writeText(result.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
    }
  };

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Upload a file</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button component="label" size="small" variant="outlined" sx={outlinedBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}>
          {file ? 'Change file' : 'Choose file'}
          <input hidden type="file" onChange={(e) => { setFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </Button>
        {file && <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{file.name}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        <TextField size="small" placeholder="label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
        <TextField size="small" placeholder="key (optional)" value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} InputProps={inputProps} sx={{ width: 160 }} />
        <Select size="small" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} sx={{ fontSize: 13, color: THEME.textPrimary, width: 130, '& fieldset': { borderColor: THEME.border }, '& .MuiSvgIcon-root': { color: THEME.textMuted } }}
          MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, '& .MuiMenuItem-root': { fontSize: 13 } } } }}>
          {LINK_TYPE_OPTIONS.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Checkbox size="small" checked={form.isDefault} onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} sx={{ p: 0.25, color: THEME.border, '&.Mui-checked': { color: THEME.mint } }} />
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>Set as the default asset</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" sx={mintBtnSx} disabled={busy} onClick={submit} startIcon={busy ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}>
          {busy ? `Uploading ${progress}%` : 'Upload'}
        </Button>
      </Box>
      {busy && (
        <Box sx={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', mt: 1, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${progress}%`, background: THEME.mint, transition: 'width 120ms' }} />
        </Box>
      )}
      {error && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
      {result && result.url && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, borderRadius: '8px', background: THEME.mintFaint, border: `1px solid ${THEME.mintDim}` }}>
          <Link href={result.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '12px', color: THEME.mint, flex: 1, minWidth: 0, wordBreak: 'break-all', textDecoration: 'none' }}>{result.url}</Link>
          <Button size="small" variant="outlined" sx={outlinedBtnSx} startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />} onClick={copyUrl}>{copied ? 'Copied' : 'Copy'}</Button>
        </Box>
      )}
    </Box>
  );
};

// GTM-LINK-ASSET-REPLACE-UI-01: a file can be overwritten in place when we host it, either
// uploaded through the panel or a plain URL on the branded share domain. Other sites keep
// the edit-URL path.
const SHARE_HOST_PREFIX = 'https://share.joinunited.ai/';
const canReplaceLinkFile = (la) =>
  !!la && (la.source === 'upload' || (typeof la.url === 'string' && la.url.startsWith(SHARE_HOST_PREFIX)));

function fmtBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return 'unknown size';
  const b = Number(n);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// MIME without parameters ("video/mp4; codecs=..." -> "video/mp4"), lowercased.
const bareMime = (t) => String(t || '').split(';')[0].trim().toLowerCase();

// Confirm + run a file replace. `target` is { la, file }; closes itself on success and
// hands the server result to onReplaced. Errors (link_asset_not_hosted, ...) stay inline.
const ReplaceLinkFileDialog = ({ target, onClose, onReplaced }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => { setBusy(false); setProgress(0); setError(null); }, [target]);

  const la = target && target.la;
  const file = target && target.file;
  const curType = la ? bareMime(la.contentType) : '';
  const newType = file ? bareMime(file.type) : '';
  const typeChanged = !!(curType && newType && curType !== newType);

  const submit = async () => {
    setBusy(true); setError(null); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await replaceLinkAssetFile(la._id, fd, setProgress);
      onReplaced(la, res || {});
      onClose();
    } catch (err) {
      setError({ code: err.code || '', message: err.message || 'Replace failed.' });
      setBusy(false);
    }
  };

  const labelSx = { fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const valSx = { fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-all' };

  return (
    <Dialog
      open={!!target}
      onClose={busy ? undefined : onClose}
      PaperProps={{
        sx: {
          background: THEME.card,
          color: THEME.textPrimary,
          border: `1px solid ${THEME.borderStrong}`,
          borderRadius: '12px',
          maxWidth: 480,
          fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
          ...sheetPaperSx,
        },
      }}
    >
      {la && file && (
        <>
          <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary, pb: 1 }}>Replace file</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, wordBreak: 'break-all' }}>
              Replace the file at {la.url}? Everyone who already has this link will see the new file.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1.5, p: 1.5, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}` }}>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={labelSx}>Current</Typography>
                <Typography sx={valSx}>{la.contentType || 'unknown type'}</Typography>
                <Typography sx={valSx}>{fmtBytes(la.bytes)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={labelSx}>New</Typography>
                <Typography sx={{ ...valSx, color: THEME.textPrimary }}>{file.name}</Typography>
                <Typography sx={valSx}>{file.type || 'unknown type'}</Typography>
                <Typography sx={valSx}>{fmtBytes(file.size)}</Typography>
              </Box>
            </Box>
            {typeChanged && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5 }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: THEME.amber, mt: '1px' }} />
                <Typography sx={{ fontSize: '12px', color: THEME.amber, lineHeight: 1.5 }}>
                  The file type is changing from {curType} to {newType}. People opening this link will get a different kind of file.
                </Typography>
              </Box>
            )}
            {busy && (
              <Box sx={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', mt: 1.5, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${progress}%`, background: THEME.mint, transition: 'width 120ms' }} />
              </Box>
            )}
            {error && (
              <Typography sx={{ fontSize: '12px', color: THEME.pink, mt: 1.5, wordBreak: 'break-word' }}>
                {error.message}{error.code && error.code !== error.message ? ` (${error.code})` : ''}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button variant="outlined" sx={outlinedBtnSx} disabled={busy} onClick={onClose}>Cancel</Button>
            <Button variant="contained" sx={mintBtnSx} disabled={busy} onClick={submit} startIcon={busy ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}>
              {busy ? `Uploading ${progress}%` : 'Replace file'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// One link-asset row: label, type, url (opens), default badge, referencing sequences.
const LinkAssetRow = ({ la, referencing, onSetDefault, onDelete, onReplaceFile }) => (
  <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'stretch' } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>{la.label}</Typography>
          <Badge tone="gray">{la.type}</Badge>
          {la.isDefault && <Badge tone="mint">Default</Badge>}
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>({la.key})</Typography>
        </Box>
        <Link href={la.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '12px', color: THEME.mint, textDecoration: 'none', wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}>{la.url}</Link>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25 }}>
          {referencing.length ? `Used by: ${referencing.map((s) => s.name || s.key).join(', ')}` : 'Not referenced by any sequence'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {!la.isDefault && <Button size="small" variant="outlined" sx={outlinedBtnSx} onClick={() => onSetDefault(la)}>Set default</Button>}
        {onReplaceFile && canReplaceLinkFile(la) && (
          <Button component="label" size="small" variant="outlined" sx={outlinedBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}>
            Replace file
            <input hidden type="file" onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onReplaceFile(la, f); }} />
          </Button>
        )}
        <IconButton size="small" onClick={() => onDelete(la)} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.pink } }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  </Box>
);

// ===== Library sub-tab: Prompts (DB-backed, versioned GTM system prompts) =====
// The live system prompts (compose_opener, fit_check, ...) each edit to a NEW version with
// full history and rollback. Mirrors the Sequences editor idiom: open a card, edit the body,
// save (bumps the version), preview the effect, roll back. Rule 7: copy carries no dashes.

// Format a timestamp for the "updated by / at" lines. Null-safe.
function fmtWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

// The one prompt whose output the "Preview with this prompt" test-target renders.
const PREVIEWABLE_PROMPT_KEY = 'compose_opener';

// GTM-PROMPTS-CLARITY-01: presentation-only display names + plain descriptions, keyed by the
// underlying gtm_prompts key. The raw keys (compose_opener, ...) are NEVER renamed — code and
// seeds depend on them — so this maps key -> operator-facing copy and the raw key still shows in
// muted mono beneath each card.
const PROMPT_DISPLAY = {
  compose_opener: {
    name: 'Email 1: the whole message',
    description: 'Writes the personalized opening, the closing ask, and the subject line for the first email in your own sequences. Governs voice, length, banned words, and which client proof or capability gets cited.',
  },
  compose_followup: {
    name: 'Emails 2-5: follow-ups',
    description: 'Writes each follow-up in your own sequences: acknowledges the thread, takes a new angle every touch, gets progressively shorter, ends with a graceful breakup.',
  },
  fit_check: {
    name: 'Right-person check (gate)',
    description: 'Decides whether this contact is actually the right recipient before anything is written or sent. A mismatch holds the contact for your review.',
  },
  sequence_routing: {
    name: 'Which sequence to use',
    description: 'Reads the evidence about a prospect and picks which of your sequences they belong in when a campaign is set to auto-route.',
  },
  capability_translation: {
    name: 'Turning our capabilities into prospect language',
    description: "Converts a matched United capability into the plain wording that appears in an email, grounded only in the capability's real spec.",
  },
  hook_research: {
    name: 'Brad-style pipes: find the hook',
    description: 'Used ONLY for campaigns linked to an Instantly campaign that owns its own copy. Researches one verifiable recent event about the prospect and returns FOUND: YES/NO plus the hook.',
  },
  hook_opener: {
    name: 'Brad-style pipes: write the personalization line',
    description: "Used ONLY for Instantly-owned-copy campaigns. Turns the hook into the single opening line that fills {{personalization}} in Brad's template, or the warm fallback line when no hook was found.",
  },
  hook_close: {
    name: 'Brad-style pipes: write the closing ask',
    description: 'Used ONLY for Instantly-owned-copy campaigns. Writes the closing line that fills {{close}} in the template.',
  },
  persona_context_travel: {
    name: 'Persona definition: Travel',
    description: 'Describes who the travel persona is and what counts as a valid hook for them. Injected into the three Brad-style prompts above. Copy this pattern to add other personas.',
  },
};
// Resolve the operator-facing name + description for a prompt, falling back to the backend's
// own name/description, then the raw key, so an unmapped prompt still renders.
const promptDisplay = (p) => {
  const d = (p && PROMPT_DISPLAY[p.key]) || {};
  return {
    name: d.name || (p && p.name) || (p && p.key) || '',
    description: d.description || (p && p.description) || '',
  };
};

// Ordered visual grouping of the prompt list. Any prompt key not listed falls into a trailing
// "Other prompts" group so nothing disappears if the backend adds a key.
const PROMPT_GROUPS = [
  { label: 'Your sequences', keys: ['compose_opener', 'compose_followup', 'capability_translation'] },
  { label: 'Gates and routing', keys: ['fit_check', 'sequence_routing'] },
  { label: 'Instantly-owned-copy campaigns', keys: ['hook_research', 'hook_opener', 'hook_close', 'persona_context_travel'] },
];

// One prompt as a bordered card: name, what it controls, version, status, last updated.
const PromptCard = ({ prompt, onOpen }) => {
  const st = SEQ_STATUS_META[prompt.status] || { label: prompt.status || 'unknown', tone: 'muted' };
  const when = fmtWhen(prompt.updatedAt);
  const display = promptDisplay(prompt);
  return (
    <Box
      onClick={onOpen}
      sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.5, cursor: 'pointer', '&:hover': { borderColor: THEME.borderStrong }, [MOBILE_MQ]: { py: 1.75 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>{display.name}</Typography>
            <Badge tone="gray">v{prompt.version}</Badge>
            <Badge tone={st.tone}>{st.label}</Badge>
            {prompt.key === PREVIEWABLE_PROMPT_KEY && <Badge tone="mint">Previewable</Badge>}
          </Box>
          {display.description && (
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mt: 0.25 }}>{display.description}</Typography>
          )}
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25, wordBreak: 'break-word' }}>
            {prompt.key}
            {when ? ` · updated ${when}` : ''}
            {prompt.updatedBy ? ` by ${prompt.updatedBy}` : ''}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, flexShrink: 0, [MOBILE_MQ]: { minHeight: 44 } }}>Open</Button>
      </Box>
    </Box>
  );
};

// The prompt editor: a large monospace body textarea (flexes to fill), a version indicator,
// Save (PATCH, bumps version), a version-history panel with Restore (rollback behind a
// ConfirmModal), and, for compose_opener, a "Preview with this prompt" action that reuses the
// existing TestTargetModal to render the assembled email against a sample contact.
const PromptEditor = ({ prompt, onClose, onSaved }) => {
  const [body, setBody] = useState(prompt.body || '');
  const [baseline, setBaseline] = useState(prompt.body || '');
  const [version, setVersion] = useState(prompt.version || 1);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type:'success'|'error', message } | null
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isPreviewable = prompt.key === PREVIEWABLE_PROMPT_KEY;
  const dirty = body !== baseline;
  const clearStatus = () => setStatus((s) => (s ? null : s));
  const st = SEQ_STATUS_META[prompt.status] || { label: prompt.status || 'unknown', tone: 'muted' };

  const loadVersions = useCallback(async () => {
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const v = await fetchPromptVersions(prompt.key);
      setVersions(Array.isArray(v) ? v : []);
    } catch (err) {
      setVersionsError((err && err.message) || 'Could not load version history.');
      setVersions(null);
    } finally {
      setVersionsLoading(false);
    }
  }, [prompt.key]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const save = async () => {
    if (!body.trim()) {
      setStatus({ type: 'error', message: 'The prompt body cannot be empty.' });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const updated = await savePrompt(prompt.key, { body });
      const v = (updated && updated.version) || version + 1;
      setVersion(v);
      setBaseline(body);
      setStatus({ type: 'success', message: `Saved as version ${v}.` });
      if (onSaved) onSaved(updated);
      loadVersions();
    } catch (err) {
      setStatus({ type: 'error', message: (err && err.message) || 'Could not save this prompt.' });
    } finally {
      setSaving(false);
    }
  };

  const doRollback = async (v) => {
    setStatus(null);
    try {
      const updated = await rollbackPrompt(prompt.key, v);
      const nv = (updated && updated.version) || version;
      const nbody = updated && typeof updated.body === 'string' ? updated.body : body;
      setBody(nbody);
      setBaseline(nbody);
      setVersion(nv);
      setStatus({ type: 'success', message: `Restored version ${v} as new version ${nv}.` });
      if (onSaved) onSaved(updated);
      loadVersions();
    } catch (err) {
      setStatus({ type: 'error', message: (err && err.message) || 'Could not restore that version.' });
    }
  };

  const askRollback = (v) =>
    setConfirm({
      title: `Restore version ${v}?`,
      body: 'The current version is kept in history. Restoring saves the chosen version as a new live version.',
      confirmLabel: 'Restore',
      tone: 'mint',
      onConfirm: () => doRollback(v),
    });

  return (
    <Dialog
      open
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', height: '85vh', maxHeight: '85vh', ...sheetPaperSx } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>Edit prompt: {promptDisplay(prompt).name}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        <Badge tone="gray">v{version}</Badge>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'hidden' }}>
        {(promptDisplay(prompt).description || prompt.key) && (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
            {promptDisplay(prompt).description}
            {promptDisplay(prompt).description ? ' · ' : ''}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{prompt.key}</Box>
          </Typography>
        )}

        {/* version indicator + preview action */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Editing version {version}
          </Typography>
          {isPreviewable && (
            <Button
              size="small"
              variant="outlined"
              sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
              startIcon={<ScienceOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => setPreviewOpen(true)}
            >
              Preview with this prompt
            </Button>
          )}
        </Box>
        {isPreviewable && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
            Preview runs the saved prompt against one sample contact and shows the assembled email. Save first to preview your latest edit.
          </Typography>
        )}

        {/* monospace body, flexes to fill the sheet */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <FieldLabel tip="The full instruction text the pipeline reads for this prompt.">Prompt body</FieldLabel>
          <TextField
            multiline
            fullWidth
            minRows={8}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              clearStatus();
            }}
            InputProps={{ sx: { fontFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, color: THEME.textPrimary, lineHeight: 1.6, alignItems: 'flex-start', '& fieldset': { borderColor: THEME.border } } }}
            sx={{ flex: 1, minHeight: 0, '& .MuiInputBase-root': { height: '100%' }, '& textarea': { height: '100% !important', overflowY: 'auto !important', ...scrollSx } }}
          />
          {status && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
              {status.type === 'success' ? (
                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: THEME.mint }} />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 15, color: THEME.pink, mt: '1px' }} />
              )}
              <Typography sx={{ fontSize: '12px', color: status.type === 'success' ? THEME.mint : THEME.pink, lineHeight: 1.5 }}>{status.message}</Typography>
            </Box>
          )}
        </Box>

        {/* version history + restore */}
        <Box sx={{ borderTop: `1px solid ${THEME.border}`, pt: 1 }}>
          <Box
            onClick={() => setHistoryOpen((o) => !o)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', minHeight: 36 }}
          >
            <Typography sx={{ flex: 1, fontSize: '12px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Version history{versions ? ` (${versions.length})` : ''}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 18, color: THEME.textMuted, transform: historyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
          </Box>
          <Collapse in={historyOpen} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              <Resource
                loading={versionsLoading && versions == null}
                error={versionsError}
                empty={!versionsLoading && versions != null && versions.length === 0}
                loadingLabel="Loading version history…"
                emptyLabel="No prior versions yet."
                onRetry={loadVersions}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 220, overflowY: 'auto', ...scrollSx }}>
                  {(versions || []).map((v) => {
                    const isCurrent = Number(v.version) === Number(version);
                    return (
                      <Box key={v.version} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)', flexWrap: 'wrap' }}>
                        <Badge tone={isCurrent ? 'mint' : 'gray'}>v{v.version}</Badge>
                        <Typography sx={{ flex: 1, minWidth: 120, fontSize: '11px', color: THEME.textMuted }}>
                          {fmtWhen(v.savedAt)}
                          {v.savedBy ? ` · ${v.savedBy}` : ''}
                        </Typography>
                        {isCurrent ? (
                          <Typography sx={{ fontSize: '11px', color: THEME.mint, fontWeight: 600 }}>Current</Typography>
                        ) : (
                          <Button size="small" variant="outlined" sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }} onClick={() => askRollback(v.version)}>
                            Restore
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Resource>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }} disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
          disabled={saving || !dirty}
          onClick={save}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
        >
          {saving ? 'Saving…' : 'Save new version'}
        </Button>
      </DialogActions>

      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
      {previewOpen && <TestTargetModal onClose={() => setPreviewOpen(false)} />}
    </Dialog>
  );
};

// The Prompts library sub-tab: a card per live prompt; open one to edit / preview / roll back.
const PromptsTab = () => {
  const promptRes = useGtmResource('/api/gtm/prompts', (d) => (d && d.prompts) || []);
  const [editing, setEditing] = useState(null);
  const prompts = promptRes.data || [];

  // Group the prompts for display; unmapped keys fall into a trailing "Other prompts" group.
  const byKey = {};
  prompts.forEach((p) => { if (p && p.key) byKey[p.key] = p; });
  const knownKeys = new Set(PROMPT_GROUPS.flatMap((g) => g.keys));
  const groups = PROMPT_GROUPS
    .map((g) => ({ label: g.label, items: g.keys.map((k) => byKey[k]).filter(Boolean) }))
    .filter((g) => g.items.length > 0);
  const others = prompts.filter((p) => p && p.key && !knownKeys.has(p.key));
  if (others.length) groups.push({ label: 'Other prompts', items: others });

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The live system prompts the GTM pipeline reads (compose_opener, fit check, routing, and more). Editing saves a new version; every prior version is kept and can be restored.">
        System prompts. Open one to edit its body, save a new version, review history, and roll back.
      </Explainer>
      <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mb: 2, lineHeight: 1.6, maxWidth: 640 }}>
        These control what the AI writes at each stage. Your own sequences use the first five; campaigns linked to an Instantly campaign that owns its copy use the three hook prompts.
      </Typography>
      <Resource
        loading={promptRes.loading}
        error={promptRes.error}
        empty={prompts.length === 0}
        loadingLabel="Loading prompts…"
        emptyLabel="No prompts configured yet."
        onRetry={promptRes.reload}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {groups.map((g) => (
            <Box key={g.label}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>{g.label}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {g.items.map((p) => (
                  <PromptCard key={p.key || p._id} prompt={p} onOpen={() => setEditing(p)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Resource>
      {editing && <PromptEditor prompt={editing} onClose={() => setEditing(null)} onSaved={() => promptRes.reload()} />}
    </Box>
  );
};

const LinksTab = () => {
  const linkRes = useGtmResource('/api/gtm/link-assets', (d) => (d && d.linkAssets) || []);
  const seqRes = useGtmResource('/api/gtm/sequences', (d) => (Array.isArray(d) ? d : (d && d.sequences) || []));
  const [confirm, setConfirm] = useState(null);
  const [replacing, setReplacing] = useState(null); // { la, file }
  const [replaced, setReplaced] = useState({}); // _id -> linkAsset from a replace response
  const [toast, setToast] = useState(null); // { severity, text }
  const links = (linkRes.data || []).map((la) => (replaced[la._id] ? { ...la, ...replaced[la._id] } : la));
  const sequences = seqRes.data || [];

  // The URL stays the same; bytes / contentType / updatedAt come back on linkAsset.
  const onReplaced = (la, res) => {
    if (res.linkAsset) setReplaced((m) => ({ ...m, [la._id]: res.linkAsset }));
    const cache = res.cache || {};
    setToast(cache.invalidated
      ? { severity: 'success', text: 'File replaced, live in about a minute' }
      : { severity: 'info', text: `File replaced. May take time to appear (cache: ${cache.reason || 'not refreshed'})` });
  };

  const referencingFor = (la) =>
    sequences.filter((s) => s.linkAssetKey === la.key || (la.isDefault && !s.linkAssetKey));

  const setDefault = async (la) => {
    try {
      await updateLinkAsset(la._id, { isDefault: true });
      linkRes.reload();
    } catch (err) {
      setConfirm({ title: 'Could not set default', body: err.message || 'Update failed.', confirmLabel: 'OK', tone: 'mint', onConfirm: () => {} });
    }
  };
  const requestDelete = (la) =>
    setConfirm({
      title: 'Delete this link asset?',
      body: `Removes "${la.label}" (${la.key}). Sequences using it fall back to the default asset.`,
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try { await deleteLinkAsset(la._id); linkRes.reload(); } catch (e) { /* surfaced by list */ }
      },
    });

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The library of link targets that [link] resolves to in outbound copy. Exactly one asset is the default; sequences can point [link] at a specific asset by key.">
        Link assets. Register a URL or upload a file, set the default, and see which sequences use each key.
      </Explainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        <AddLinkForm onCreated={linkRes.reload} />
        <UploadLinkForm onUploaded={linkRes.reload} />
      </Box>
      <Resource loading={linkRes.loading} error={linkRes.error} empty={links.length === 0} loadingLabel="Loading link assets…" emptyLabel="No link assets yet." onRetry={linkRes.reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {links.map((la) => (
            <LinkAssetRow key={la._id} la={la} referencing={referencingFor(la)} onSetDefault={setDefault} onDelete={requestDelete} onReplaceFile={(row, file) => setReplacing({ la: row, file })} />
          ))}
        </Box>
      </Resource>
      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
      <ReplaceLinkFileDialog target={replacing} onClose={() => setReplacing(null)} onReplaced={onReplaced} />
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={(toast && toast.severity) || 'success'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast && toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ===== Tabs: Mismatch + Deferred (Chunks F/G — read-only held filters) =====

// Fetch /review (held_sensitive) + /held and merge unique by id. Both serialize
// via serializeReviewContact, so heldReason / fitCheck / deferred / companyGovernance
// are present on every row.
function useHeldReview() {
  const reviewRes = useGtmResource('/api/gtm/review', (d) => (d && d.review) || []);
  const heldRes = useGtmResource('/api/gtm/held', (d) => (d && d.held) || []);
  const byId = {};
  [...(reviewRes.data || []), ...(heldRes.data || [])].forEach((c) => { byId[c.id] = c; });
  return {
    contacts: Object.values(byId),
    loading: reviewRes.loading || heldRes.loading,
    error: reviewRes.error || heldRes.error,
    reload: () => { reviewRes.reload(); heldRes.reload(); },
  };
}

const HeldContactCard = ({ contact, children }) => {
  const sub = [contact.title, contact.companyName].filter(Boolean).join(' · ');
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary }}>{contact.name || contact.email}</Typography>
        {contact.persona && <Badge tone="gray">{contact.persona}</Badge>}
        <LaneBadges contact={contact} />
      </Box>
      {sub && <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 1 }}>{sub}</Typography>}
      {children}
    </Box>
  );
};

// Reasoning text that clamps to 2 lines under 768px with tap-to-expand; on desktop
// it always renders in full. Used by the Mismatch / Deferred / Suppressions blocks.
const ClampText = ({ children, sx = {} }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Typography
      onClick={() => setExpanded((e) => !e)}
      sx={{
        ...sx,
        [MOBILE_MQ]: {
          cursor: 'pointer',
          ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        },
      }}
    >
      {children}
    </Typography>
  );
};

const LabeledBlock = ({ label, text }) =>
  text ? (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      <ClampText sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text}</ClampText>
    </Box>
  ) : null;

const MismatchTab = ({ held }) => {
  const { contacts, loading, error, reload } = held;
  const rows = contacts.filter((c) => c.heldReason === 'fit_mismatch');
  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Contacts the fit check held because the person does not match the sequence's audience. Read-only; the fit reasoning and the evidence it judged are shown.">
        Fit mismatches. The fit check held these because they do not match the audience.
      </Explainer>
      <Resource loading={loading} error={error} empty={rows.length === 0} loadingLabel="Loading held contacts…" emptyLabel="No fit mismatches." onRetry={reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((c) => (
            <HeldContactCard key={c.id} contact={c}>
              <LabeledBlock label="Fit reasoning" text={c.fitCheck && c.fitCheck.reasoning} />
              <LabeledBlock label="Evidence judged" text={c.fitCheck && c.fitCheck.evidence} />
            </HeldContactCard>
          ))}
        </Box>
      </Resource>
    </Box>
  );
};

// Held with reason 'competitor_blocked' (a full-block competitor row matched the contact's
// company). Mirrors the Mismatch tab: renders the competitor check reasoning + the withheld
// capabilities. Reads competitorCheck defensively (fail-soft until the held serializer emits it).
const CompetitorBlockedTab = ({ held }) => {
  const { contacts, loading, error, reload } = held;
  const rows = contacts.filter((c) => c.heldReason === 'competitor_blocked');
  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Contacts held because their company matched a full-block competitor row. Read-only; the competitor check reasoning and the capabilities it withheld are shown.">
        Competitor blocks. These were held because their company is on the full-block competitor list.
      </Explainer>
      <Resource loading={loading} error={error} empty={rows.length === 0} loadingLabel="Loading held contacts…" emptyLabel="No competitor blocks." onRetry={reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((c) => (
            <HeldContactCard key={c.id} contact={c}>
              <LabeledBlock label="Competitor reasoning" text={c.competitorCheck && c.competitorCheck.reasoning} />
              <LabeledBlock
                label="Capabilities withheld"
                text={c.competitorCheck && Array.isArray(c.competitorCheck.competingCapabilities) ? c.competitorCheck.competingCapabilities.join(', ') : ''}
              />
            </HeldContactCard>
          ))}
        </Box>
      </Resource>
    </Box>
  );
};

// Map a deferral reason to a readable label. Known codes (holiday_break, ...) get a
// friendly name; an unmapped snake_case code is humanized; a plain sentence is left
// as-is. Fixes a raw "holiday_break" leaking into the UI (Item 5).
const DEFERRAL_REASON_META = {
  holiday_break: 'Holiday window',
  holiday: 'Holiday window',
  company_active: 'Company already active',
  company_governance: 'Company governance',
};
function deferralReasonLabel(code) {
  if (!code) return 'Deferred';
  const key = String(code).toLowerCase().trim();
  if (DEFERRAL_REASON_META[key]) return DEFERRAL_REASON_META[key];
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(key)) {
    return key.replace(/_/g, ' ').replace(/^./, (ch) => ch.toUpperCase());
  }
  return String(code);
}

const DeferredTab = ({ held }) => {
  const { contacts, loading, error, reload } = held;
  const rows = contacts.filter((c) => c.deferred === true);
  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Contacts the system set aside for later: a holiday window, or another person at the same company already in an active sequence. Read-only; each shows why and, when set, until when.">
        Deferred for later. Each one shows why it was held and, when known, when it can resume.
      </Explainer>
      <Resource loading={loading} error={error} empty={rows.length === 0} loadingLabel="Loading deferred contacts…" emptyLabel="Nothing deferred." onRetry={reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {rows.map((c) => {
            const until = c.deferredUntil ? (fmtWhen(c.deferredUntil) || String(c.deferredUntil)) : '';
            // Oracle reasoning behind the deferral, read defensively across the shapes
            // the held serializer may emit; falls back to the governance reasoning.
            const oracleReasoning =
              (c.deferralOracle && c.deferralOracle.reasoning) ||
              (c.oracle && c.oracle.reasoning) ||
              c.deferredReasoning ||
              '';
            const govReasoning = (c.companyGovernance && c.companyGovernance.reasoning) || '';
            return (
              <HeldContactCard key={c.id} contact={c}>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Badge tone="gray">{deferralReasonLabel(c.deferredReason)}</Badge>
                  {until && <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Deferred until {until}</Typography>}
                </Box>
                <LabeledBlock label="Reasoning" text={oracleReasoning} />
                {govReasoning && govReasoning !== oracleReasoning && (
                  <LabeledBlock label="Governance reasoning" text={govReasoning} />
                )}
              </HeldContactCard>
            );
          })}
        </Box>
      </Resource>
    </Box>
  );
};

// ===== Library sub-tab: Competitors (manual competitor-protection backstop) =====
// Mirrors the Suppressions idiom (hand-curated, domain-keyed, add + delete). A row says a
// company competes with one or more of our capability families: block_capability strips only
// those capabilities from what the composer may cite; block_entirely holds the contact
// 'competitor_blocked' before it reaches the composer.

// One-line, operator-facing explanation of each mode. Reused verbatim in three places: the
// mode-picker help lines, the badge tooltip on each row, and (implicitly) the review queue copy.
const COMPETITOR_MODE_META = {
  block_capability: {
    label: 'Capability block',
    tone: 'amber',
    explain: 'We still email people at this company. We just never mention the capabilities they compete with.',
  },
  block_entirely: {
    label: 'Full block',
    tone: 'pink',
    explain: 'Nobody at this domain is ever contacted. Contacts are held with the reason visible in your review queue.',
  },
};

// The three-layer explanation of how competitor protection works, verbatim. Rendered in the
// collapsible panel below the header using the same idiom as "How outbound capacity works":
// a mint ordinal marker supplies the "1./2./3." so the layer text itself carries no ordinal.
const COMPETITOR_PROTECTION_LAYERS = [
  'The AI checks every prospect automatically. Before writing any email, the AI reads the prospect\'s company description and judges whether their company competes with any capability it was about to mention. If it competes, or the AI is even unsure, that capability is stripped from the email. This happens for every prospect, even companies not on this list.',
  'This list is the guaranteed backstop. Anything you add here is enforced deterministically by domain match, on top of the AI\'s judgment. A match here always wins, even if the AI concluded otherwise.',
  'Blocked contacts stay visible. Anyone held by a Full block appears in Review & approve with the reasoning attached, so you can always see who was protected and why. Nothing silently disappears.',
];

// Collapsible "How competitor protection works" panel. Same idiom as CapacityHowItWorks:
// an expand toggle, a Collapse, and mint ordinal markers on each numbered layer. Collapsed
// by default. Presentation only, no data.
const CompetitorsHowItWorks = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          color: THEME.textSecondary,
          '&:hover': { color: THEME.textPrimary },
          [MOBILE_MQ]: { minHeight: 44 },
        }}
      >
        <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>How competitor protection works</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {COMPETITOR_PROTECTION_LAYERS.map((text, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.mint, flexShrink: 0 }}>{i + 1}.</Typography>
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{text}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// GTM-COMPETITORS-PICKER-01: normalize GET /api/gtm/capabilities defensively into a usable
// registry. `usable` is false when the endpoint is absent, errored, degraded, or empty, which
// makes the competesWith field fall back to the plain free-text input and rows render plain
// chips. A few likely field names are read so a name/description pair is found whatever the
// exact shape (array of strings, or objects keyed name/key/label with description/desc/summary).
function useCapabilities() {
  // The bare /api/gtm/capabilities path is the platform-capabilities knowledge base; the
  // competitor directory is mounted at /registry, returning { capabilities:[{ name, description,
  // source }], degraded, cached }. degraded:true or a non-200 (surfaced as res.error) is treated
  // as unusable, which keeps the free-text fallback.
  const res = useGtmResource('/api/gtm/capabilities/registry', (d) => d || {});
  const data = res.data || {};
  const raw = Array.isArray(data) ? data : (Array.isArray(data.capabilities) ? data.capabilities : []);
  const names = [];
  const descByName = new Map();
  raw.forEach((c) => {
    const name = String(typeof c === 'string' ? c : ((c && (c.name || c.key || c.label || c.id)) || '')).trim();
    if (!name || descByName.has(name)) return;
    names.push(name);
    const desc = typeof c === 'object' && c ? (c.description || c.desc || c.summary || '') : '';
    descByName.set(name, String(desc).trim());
  });
  const usable = !res.error && data.degraded !== true && names.length > 0;
  return { usable, names, descByName };
}

// A single capability chip. Registry matches render solid with an "exact match" tooltip; custom
// (off-registry) entries render with a dashed border and the custom-entry tooltip. Used both in
// the picker's selected tags and, read-only, on existing competitor rows.
const CapabilityChip = ({ name, inRegistry, onDelete }) => (
  <Tooltip
    arrow
    title={inRegistry
      ? 'Matches this capability exactly.'
      : 'Custom entry: not in the capability registry yet. Matching is by this exact wording.'}
  >
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.4, m: '2px',
        px: '8px', py: '3px', borderRadius: '6px', fontSize: 11, lineHeight: 1.4,
        color: inRegistry ? THEME.textSecondary : THEME.textPrimary,
        background: inRegistry ? 'rgba(255,255,255,0.06)' : 'transparent',
        border: inRegistry ? `1px solid ${THEME.border}` : `1px dashed ${THEME.borderStrong}`,
      }}
    >
      {name}
      {onDelete && (
        <Box
          component="button" type="button" onClick={onDelete} aria-label={`Remove ${name}`}
          sx={{ display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', p: 0, ml: '1px', cursor: 'pointer', color: 'inherit', fontSize: 13, lineHeight: 1, '&:hover': { color: THEME.pink } }}
        >
          ×
        </Box>
      )}
    </Box>
  </Tooltip>
);

// GTM-COMPETITORS-PICKER-01: the competesWith editor. With a usable capability registry it is a
// chip multi-select: type to filter, click or Enter to add, x to remove, and each option shows
// its one-line description in the dropdown. Typing an off-registry value and pressing Enter adds
// it as a custom chip (dashed). When the registry is absent/degraded/empty it falls back to the
// prior free-text input with its original placeholder, so there is no new failure path and no
// layout shift. Either way the value is a plain string array of chip names, submitted unchanged.
const splitCsv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const CapabilityPicker = ({ caps, value, onChange }) => {
  const [text, setText] = useState(value.join(', '));
  // When the parent clears the selection (after a successful add), clear the fallback text too.
  useEffect(() => { if (value.length === 0) setText(''); }, [value.length]);
  if (!caps.usable) {
    return (
      <TextField
        size="small"
        placeholder="capabilities they compete with, e.g. travel, gifting, ai agents"
        value={text}
        onChange={(e) => { setText(e.target.value); onChange(splitCsv(e.target.value)); }}
        InputProps={inputProps}
        sx={{ flex: 1, minWidth: 220 }}
      />
    );
  }
  return (
    <Autocomplete
      multiple
      freeSolo
      disableCloseOnSelect
      filterSelectedOptions
      options={caps.names}
      value={value}
      isOptionEqualToValue={(o, v) => o === v}
      onChange={(e, v) => {
        const cleaned = [];
        v.forEach((item) => { const s = String(item).trim(); if (s && !cleaned.includes(s)) cleaned.push(s); });
        onChange(cleaned);
      }}
      size="small"
      sx={{
        flex: 1, minWidth: 220,
        '& .MuiOutlinedInput-root': { color: THEME.textPrimary },
        '& fieldset': { borderColor: THEME.border },
        '& .MuiSvgIcon-root': { color: THEME.textMuted },
        '& .MuiInputBase-input::placeholder': { color: THEME.textMuted, opacity: 1 },
      }}
      componentsProps={{
        paper: {
          sx: {
            background: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`,
            '& .MuiAutocomplete-option': {
              '&[aria-selected="true"]': { background: THEME.mintFaint },
              '&.Mui-focused': { background: THEME.mintFaint },
            },
            '& .MuiAutocomplete-noOptions': { color: THEME.textMuted, fontSize: 13 },
          },
        },
      }}
      renderTags={(vals, getTagProps) =>
        vals.map((name, index) => {
          const { onDelete } = getTagProps({ index });
          return <CapabilityChip key={name} name={name} inRegistry={caps.descByName.has(name)} onDelete={onDelete} />;
        })
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const desc = caps.descByName.get(option);
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'flex !important', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.2, py: 0.6 }}>
            <Typography sx={{ fontSize: 13, color: THEME.textPrimary }}>{option}</Typography>
            {desc && <Typography sx={{ fontSize: 11, color: THEME.textMuted, lineHeight: 1.4 }}>{desc}</Typography>}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={value.length === 0 ? 'type to filter capabilities, or add your own' : ''} />
      )}
    />
  );
};

// One competitor as a card: company, domain, competesWith chips, mode badge, note, added
// by/at, and a delete control. Stacks under 768px.
const CompetitorRow = ({ row, onDelete, registry }) => {
  const meta = COMPETITOR_MODE_META[row.mode] || { label: row.mode || 'unknown', tone: 'muted' };
  const when = fmtWhen(row.createdAt);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, border: `1px solid ${THEME.border}`, borderRadius: '8px', background: THEME.card, px: 1.5, py: 1, [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'flex-start' } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-word' }}>
            {row.company || row.domain || '(unnamed)'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Badge tone={meta.tone}>{meta.label}</Badge>
            {meta.explain && <InfoTooltip title={meta.label}>{meta.explain}</InfoTooltip>}
          </Box>
        </Box>
        {row.domain && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, wordBreak: 'break-all' }}>{row.domain}</Typography>}
        {row.competesWith && row.competesWith.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>Competes with:</Typography>
            {row.competesWith.map((f) => (
              registry
                ? <CapabilityChip key={f} name={f} inRegistry={registry.has(f)} />
                : <Badge key={f} tone="gray">{f}</Badge>
            ))}
          </Box>
        )}
        {row.note && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>{row.note}</Typography>}
        {(when || row.addedBy) && (
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>
            {when ? `added ${when}` : 'added'}
            {row.addedBy ? ` by ${row.addedBy}` : ''}
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={() => onDelete(row)} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.pink }, [MOBILE_MQ]: { minHeight: 44, minWidth: 44 } }}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const CompetitorsTab = () => {
  const compRes = useGtmResource('/api/gtm/competitors', (d) => (d && d.competitors) || []);
  const caps = useCapabilities();
  const [company, setCompany] = useState('');
  const [domain, setDomain] = useState('');
  const [families, setFamilies] = useState([]);
  const [mode, setMode] = useState('block_capability');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const rows = compRes.data || [];

  const add = async () => {
    const d = domain.trim().toLowerCase();
    if (!d) { setError('Enter a company domain (the match key).'); return; }
    if (mode === 'block_capability' && families.length === 0) {
      setError('A capability block needs at least one competing capability family.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await createCompetitor({ company: company.trim(), domain: d, competesWith: families, mode, note: note.trim() });
      setCompany(''); setDomain(''); setFamilies([]); setNote(''); setMode('block_capability');
      compRes.reload();
    } catch (err) {
      setError((err && err.message) || 'Could not add the competitor.');
    } finally {
      setBusy(false);
    }
  };

  const requestDelete = (row) =>
    setConfirm({
      title: 'Remove this competitor?',
      body: `${row.company || row.domain} will no longer be competitor-protected, and its capabilities may be cited again.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: async () => {
        try { await deleteCompetitor(row.id); compRes.reload(); } catch (e) { /* surfaced by list */ }
      },
    });

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The manual backstop for competitor protection (the primary defense is the agentic per-contact check). Keyed by company domain. Capability block strips only the competing capabilities; full block holds the contact 'competitor_blocked' before the composer.">
        Protect your roadmap. When a prospect's company competes with something United does, their emails never pitch that capability.
      </Explainer>
      <CompetitorsHowItWorks />
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField size="small" placeholder="company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
          <TextField size="small" placeholder="domain, e.g. acme.com" value={domain} onChange={(e) => setDomain(e.target.value)} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
          <CapabilityPicker caps={caps} value={families} onChange={setFamilies} />
          <TextField size="small" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
        </Box>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5, lineHeight: 1.5 }}>
          Pick from United's capabilities so matching is exact. These are the topics that get stripped from emails to this company. Leave blank with Full block.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Mode</Typography>
          <SelectChip label="Capability block" selected={mode === 'block_capability'} onClick={() => setMode('block_capability')} />
          <SelectChip label="Full block" selected={mode === 'block_entirely'} onClick={() => setMode('block_entirely')} />
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44 } }} disabled={busy} onClick={add}>
            {busy ? 'Adding…' : 'Add competitor'}
          </Button>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600, color: THEME.amber }}>Capability block:</Box>{' '}
            {COMPETITOR_MODE_META.block_capability.explain}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600, color: THEME.pink }}>Full block:</Box>{' '}
            {COMPETITOR_MODE_META.block_entirely.explain}
          </Typography>
        </Box>
        {error && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
      </Box>
      <Resource loading={compRes.loading} error={compRes.error} empty={rows.length === 0} loadingLabel="Loading competitors…" emptyLabel="No competitors listed yet. The AI still checks every prospect automatically. Add a company here when you want a guaranteed, domain-level rule on top of that." onRetry={compRes.reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row) => (
            <CompetitorRow key={row.id} row={row} onDelete={requestDelete} registry={caps.usable ? caps.descByName : null} />
          ))}
        </Box>
      </Resource>
      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

// ===== Library sub-tab: Mailboxes (Instantly-owned sending mailboxes + capacity) =====
// Read-only mirror of the sending infrastructure. Instantly is the source of truth: no
// manual add here — mailboxes appear after a sync. The live /capacity shape gives each
// mailbox only { email, warmupStatus, dailyLimit } — domain is derived from the email, and
// there is no per-row sync time (only the summary-level time from a manual sync).
// The summary rolls the fleet up into the daily send/enrich capacity the pipeline plans against.

// Warmup state -> badge tone. warmupStatus arrives as a numeric string ("1" = warmed-active);
// the word aliases are kept for readability, and an unknown state degrades to a neutral badge.
const WARMUP_META = {
  '1': { label: 'Active', tone: 'mint' },
  warming: { label: 'Warming', tone: 'amber' },
  'warmed-active': { label: 'Active', tone: 'mint' },
  warmed: { label: 'Active', tone: 'mint' },
  active: { label: 'Active', tone: 'mint' },
  'paused-error': { label: 'Paused', tone: 'pink' },
  paused: { label: 'Paused', tone: 'pink' },
  error: { label: 'Error', tone: 'pink' },
};
// Single primary state per mailbox, so "which ones are not ready" reads at a glance:
// amber (Warming up) is the ONLY not-ready state; everything else is sending-eligible.
//   ready            -> "Ready" (green): warmed and counted.
//   warming          -> "Warming up" (amber): still warming, shows score + est. ready.
//   ready_unverified -> "Ready (unverified)" (grey-green): counted ready on the status
//                       flag alone (no warmup score from Instantly yet). Still sends.
//   disabled         -> "Disabled" (muted grey): genuinely not active per Instantly.
const WARMUP_STATE_META = {
  ready: { label: 'Ready', tone: 'mint' },
  warming: { label: 'Warming up', tone: 'amber' },
  ready_unverified: { label: 'Ready (unverified)', tone: 'sage' },
  disabled: { label: 'Disabled', tone: 'muted' },
};
function mailboxWarmState(m) {
  if (!m) return 'ready_unverified';
  // readinessBasis is the exact backend field; keep the earlier assumed names as fallbacks.
  const basis = String(m.readinessBasis || m.warmupBasis || m.derivationBasis || m.basis || '').toLowerCase().trim();
  if (basis === 'flag_only') return 'ready_unverified'; // counted ready by flag alone, no score yet
  // The backend states each counted mailbox with a `ready` boolean; trust it first.
  if (m.ready === true) return 'ready';
  if (m.ready === false) return 'warming';
  // Fallbacks for shapes without an explicit ready flag: derive from warmupStatus.
  const key = String(m.warmupStatus == null ? '' : m.warmupStatus).toLowerCase().trim();
  const meta = WARMUP_META[key];
  if (meta && meta.tone === 'mint') return 'ready';
  if (key === 'warming' || (meta && meta.tone === 'amber')) return 'warming';
  if (meta && meta.tone === 'pink') return 'disabled'; // paused / error per Instantly
  // In-list membership implies the backend judged it active + warmed, so an unrecognized
  // signal is still sending-eligible, just unconfirmed.
  return 'ready_unverified';
}

const READY_UNVERIFIED_INFO =
  'The warmup score for this mailbox has not arrived from Instantly yet, so it is counted as ready on its status flag alone. It can send now; the exact warmup confidence will fill in on the next sync.';

// Short date for "est. ready" lines. Null-safe: returns '' when the value is missing
// or unparseable, so nothing renders rather than "Invalid Date".
const fmtDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Integer capacity formatter (null-safe). Renders a placeholder rather than "NaN/day".
const fmtCount = (n) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('en-US');

// Shared numeral styling for the large figures: even-width tabular digits, slightly
// tighter tracking, and a clean 600 weight instead of a heavy chunky weight. THEME
// defines no display or mono font, so this relies on the system tabular numerics.
const numeralSx = {
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.02em',
  fontWeight: 600,
};

// One summary stat: an uppercase label over a large value, with an optional unit
// suffix, an optional info node (an InfoTooltip) after the label, an optional muted
// secondary line under the value, and an optional row of small badges below it.
const CapacityStat = ({ label, value, unit, info, secondary, badges }) => (
  <Box sx={{ minWidth: 120 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      {info}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography component="div" sx={{ fontSize: '18px', color: THEME.textPrimary, ...numeralSx }}>{value}</Typography>
      {unit && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{unit}</Typography>}
    </Box>
    {secondary && <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>{secondary}</Typography>}
    {badges && <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>{badges}</Box>}
  </Box>
);

// ─── Deliverability health (GET /api/gtm/health) ─────────────────────────────
// Presentation over the health snapshot. Everything here fails soft: no health data
// means nothing new renders and the tab looks exactly as it did before. Audience is a
// non-engineer operator, so copy is plain and every acronym gets a hover explanation.

// The three fleet states map to the same three colors used everywhere else.
const HEALTH_TONE = { green: THEME.mint, amber: THEME.amber, red: THEME.pink };

// A small filled status dot.
const HealthDot = ({ color, size = 8 }) => (
  <Box component="span" sx={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
);

// Plain-English explanations for the only acronyms that can appear (on the domain
// header). No acronym is ever shown without one of these on hover.
const ACRONYM_INFO = {
  SPF: 'SPF is a setting on your domain that lets inbox providers confirm an email really came from you. Without it, more of your messages land in spam.',
  DMARC: 'DMARC is a setting on your domain that tells inbox providers how to handle messages that fail security checks. Without it, more of your messages land in spam.',
};

// Plain description of the three fleet buckets, shown from one info icon on the strip.
const FLEET_HEALTH_INFO =
  'Healthy means the mailbox is sending normally. Watch means it is still sending, but something is worth keeping an eye on. Protected means it was paused automatically to guard your sending reputation, and it resumes on its own once healthy.';

// The fleet health strip: three colored dots with counts, plus a reassuring banner when
// any mailbox is auto-paused. Renders nothing when there is no health data at all.
const FleetHealthStrip = ({ fleet }) => {
  if (!fleet) return null;
  const green = Number(fleet.green) || 0;
  const amber = Number(fleet.amber) || 0;
  const red = Number(fleet.red) || 0;
  if (green + amber + red === 0) return null;
  const items = [
    { color: THEME.mint, label: 'Healthy', count: green },
    { color: THEME.amber, label: 'Watch', count: amber },
    { color: THEME.pink, label: 'Protected', count: red },
  ];
  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${THEME.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Mailbox health
        </Typography>
        {items.map((it) => (
          <Box key={it.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <HealthDot color={it.color} />
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{it.label}</Typography>
            <Typography component="span" sx={{ fontSize: '12px', color: THEME.textPrimary, ...numeralSx }}>{fmtCount(it.count)}</Typography>
          </Box>
        ))}
        <InfoTooltip title="What these mean" size={13}>{FLEET_HEALTH_INFO}</InfoTooltip>
      </Box>
      {red > 0 && (
        <Box sx={{ mt: 1.25, px: 1.25, py: 1, borderRadius: '8px', background: 'rgba(198,61,87,0.10)', border: '1px solid rgba(198,61,87,0.30)' }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>
            {fmtCount(red)} {red === 1 ? 'mailbox is' : 'mailboxes are'} paused automatically to protect your sending reputation. Nothing is lost, they resume when healthy.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// One mailbox's health line under its warm state. Green shows a fixed reassurance;
// amber and red prepend a plain lead-in to the plainLanguage sentence, which comes
// VERBATIM from the API (never composed here). Returns null unless there is a real state.
const MailboxHealthLine = ({ health }) => {
  const state = health && health.state;
  if (state !== 'green' && state !== 'amber' && state !== 'red') return null;
  const color = HEALTH_TONE[state];
  const plain = String(health.plainLanguage || '').trim();
  let text;
  if (state === 'green') text = 'Sending normally';
  else if (state === 'amber') text = plain ? `Worth watching: ${plain}` : 'Worth watching';
  else text = plain ? `Paused to protect you: ${plain}` : 'Paused to protect you';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
      <HealthDot color={color} size={7} />
      <Typography sx={{ fontSize: '11px', color: state === 'green' ? THEME.textMuted : color, lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  );
};

// Format an API rate fraction (e.g. 0.012) as a percent string (e.g. "1.2%").
const fmtPct = (frac) => {
  const v = Number(frac);
  if (!Number.isFinite(v)) return '—';
  const p = Math.round(v * 1000) / 10;
  return `${p.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
};

// STATS-PATH-FIX-01: one plain-language line of the workspace's pre-GTM sending history
// (real Instantly totals summed across all campaigns, never a per-mailbox split). Uses the
// 30-day window, the widest Instantly provides, with rates computed from those same totals.
// Renders nothing unless fleet backfill with real sends exists.
const FleetStatsBackfillLine = ({ statsBackfill }) => {
  const sb = statsBackfill;
  const sent = sb ? Number(sb.sent30d) || 0 : 0;
  if (!sb || sent <= 0) return null;
  const bounced = Number(sb.bounced30d) || 0;
  const replies = Number(sb.replies30d) || 0;
  const bounceRate = bounced / sent;
  const replyRate = replies / sent;
  return (
    <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1.5, lineHeight: 1.6 }}>
      Before United took over: {fmtCount(sent)} emails sent, {fmtCount(bounced)} bounces ({fmtPct(bounceRate)}), {fmtCount(replies)} replies ({fmtPct(replyRate)}) across all mailboxes (from Instantly).
    </Typography>
  );
};

// Numbers-on-demand: bounce and reply rates with one-line, plain explanations of what
// each means and what good looks like. The backend stores a 7-day bounce rate and a
// 30-day reply rate (not every window), so each number is labeled with its own window
// and nothing is invented; raw send counts cover both 7 and 30 days.
const MailboxRates = ({ rates, historyNote }) => {
  const r = rates || {};
  // STATS-PATH-FIX-01: when Instantly gives no per-mailbox history, the per-mailbox numbers
  // would read as misleading zeros. Show the backend's neutral note where those numbers would
  // be instead, and point to the fleet totals in the rollup.
  if (historyNote) {
    return (
      <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${THEME.border}` }}>
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, lineHeight: 1.5 }}>{historyNote}</Typography>
      </Box>
    );
  }
  const rows = [
    {
      label: 'Bounces, last 7 days',
      value: fmtPct(r.bounceRate7d),
      detail: `${fmtCount(r.bounces7d)} of ${fmtCount(r.sends7d)} emails. A bounce is an email that could not be delivered. Under 2% is healthy.`,
    },
    {
      label: 'Replies, last 30 days',
      value: fmtPct(r.replyRate30d),
      detail: `${fmtCount(r.replies30d)} of ${fmtCount(r.sends30d)} emails. How often people wrote back. Even a few percent is normal for cold outbound.`,
    },
    {
      label: 'Emails sent',
      value: `${fmtCount(r.sends7d)} / ${fmtCount(r.sends30d)}`,
      detail: 'This week / last 30 days.',
    },
  ];
  return (
    <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary }}>{row.label}</Typography>
            <Typography sx={{ fontSize: '11px', color: THEME.textPrimary, ...numeralSx }}>{row.value}</Typography>
          </Box>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, lineHeight: 1.5 }}>{row.detail}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// GTM-AUTOPILOT-01: the last autopilot action for one mailbox, as one plain sentence.
// Prefers a backend-composed sentence VERBATIM (summary / text / plainLanguage); only when
// none is present does it build one from the discrete fields it has (verb + new daily limit +
// reason), e.g. "Lowered to 24/day: bounce rate crept above 1.5%". Returns '' when there is
// nothing real to say, so the caller renders no line at all (never an empty or invented action).
const AUTOPILOT_VERB = {
  raised: 'Raised', increased: 'Raised', lowered: 'Lowered', decreased: 'Lowered',
  paused: 'Paused', resumed: 'Resumed', held: 'Held steady', unchanged: 'Held steady',
};
function mailboxActionText(a) {
  if (!a || typeof a !== 'object') return '';
  const verbatim = String(a.summary || a.text || a.plainLanguage || '').trim();
  if (verbatim) return verbatim;
  const verb = AUTOPILOT_VERB[String(a.action || a.type || '').toLowerCase()];
  if (!verb) return '';
  const limit = firstFiniteNumber(a.toLimit, a.dailyLimit, a.limit);
  const reason = String(a.reason || '').trim();
  let head;
  if (verb === 'Paused') head = 'Paused sending';
  else if (verb === 'Held steady') head = 'Held steady';
  else if (limit != null) head = `${verb} to ${fmtCount(limit)}/day`;
  else head = verb;
  return reason ? `${head}: ${reason}` : head;
}

// GTM-AUTOPILOT-01: one mailbox's autopilot health block, from GET /api/gtm/mailboxes/health
// (keyed by email). A small metrics line (warmup score, bounce % last 3 days, replies last 7
// days) plus the last autopilot action with its plain-language reason and a humanized time.
// Entirely additive and fails soft: numbers are read defensively across a couple of likely
// field names, a metric that is absent is skipped (never a misleading zero), and if there is
// nothing real to show the whole block returns null so the card is exactly as before.
const MailboxAutopilotHealth = ({ ap }) => {
  if (!ap || typeof ap !== 'object') return null;
  // Warmup score: accept a 0..1 fraction or a 0..100 score; clamp for display.
  const rawScore = firstFiniteNumber(ap.warmupScore, ap.warmup_score);
  const scorePct = rawScore == null ? null : Math.min(100, Math.max(0, Math.round(rawScore <= 1 ? rawScore * 100 : rawScore)));
  const bounce3d = firstFiniteNumber(ap.bounceRate3d, ap.bounce_rate_3d);
  const replies7d = firstFiniteNumber(ap.replies7d, ap.replies_7d);
  const bits = [];
  if (scorePct != null) bits.push(`Warmup score ${scorePct}`);
  if (bounce3d != null) bits.push(`${fmtPct(bounce3d)} bounces (3d)`);
  if (replies7d != null) bits.push(`${fmtCount(replies7d)} ${replies7d === 1 ? 'reply' : 'replies'} (7d)`);
  const lastAction = ap.lastAction || ap.last_action || null;
  const action = mailboxActionText(lastAction);
  const actionAt = (lastAction && (lastAction.at || lastAction.changedAt || lastAction.timestamp)) || ap.lastActionAt || null;
  if (!bits.length && !action) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {bits.length > 0 && (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5, ...numeralSx }}>
          {bits.join(' · ')}
        </Typography>
      )}
      {action && (
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.5 }}>
          {action}
          {actionAt ? <Box component="span" sx={{ color: THEME.textMuted }}> · {fmtWhen(actionAt)}</Box> : null}
        </Typography>
      )}
    </Box>
  );
};

// Reduce a domain-health record to a single header view. Mirrors the backend's red
// precedence (blacklist, then SPF, then DMARC), amber for anything not yet confirmed,
// green when the setup checks out. `text` is composed here from the pass/fail flags
// because the /health domain payload carries flags only, no sentence.
const domainHealthView = (dom) => {
  if (!dom || !dom.checks) return null;
  const c = dom.checks;
  if (c.blacklist === 'listed') {
    return { tone: 'red', ok: false, acronym: null, text: 'This domain is on an email blacklist that inbox providers check. Emails from it are much more likely to land in spam.' };
  }
  if (c.spf === 'fail') {
    return { tone: 'red', ok: false, acronym: 'SPF', text: 'This domain is missing a setting (SPF) that inbox providers check. Emails from it are more likely to land in spam.' };
  }
  if (c.dmarc === 'fail') {
    return { tone: 'red', ok: false, acronym: 'DMARC', text: 'This domain is missing a setting (DMARC) that inbox providers check. Emails from it are more likely to land in spam.' };
  }
  const anyUnknown = [c.spf, c.dkim, c.dmarc, c.blacklist].some((v) => v === 'unknown' || v == null || v === '');
  if (anyUnknown) {
    return { tone: 'amber', ok: false, acronym: null, text: 'Still confirming this domain\'s email setup. Nothing is wrong yet; we are re-checking.' };
  }
  return { tone: 'green', ok: true, acronym: null, text: 'Domain set up correctly' };
};

// The inline domain-health note appended to a domain group header. Green shows a check;
// red/amber show a plain sentence in the state color, with an acronym explainer on hover.
const DomainHealthNote = ({ view }) => {
  if (!view) return null;
  if (view.ok) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: THEME.mint }} />
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{view.text}</Typography>
      </Box>
    );
  }
  const color = HEALTH_TONE[view.tone] || THEME.textMuted;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <HealthDot color={color} size={7} />
      <Typography sx={{ fontSize: '11px', color, lineHeight: 1.5 }}>{view.text}</Typography>
      {view.acronym && <InfoTooltip title={view.acronym} size={13}>{ACRONYM_INFO[view.acronym]}</InfoTooltip>}
    </Box>
  );
};

// Minimum warming days before a mailbox sends (backend GTM_WARMUP_MIN_DAYS, default 14).
// The capacity payload does not carry this value, so we use the shared default here,
// overridable at build time via REACT_APP_GTM_WARMUP_MIN_DAYS if the backend env differs.
const WARMUP_MIN_DAYS = Number(process.env.REACT_APP_GTM_WARMUP_MIN_DAYS) > 0
  ? Number(process.env.REACT_APP_GTM_WARMUP_MIN_DAYS)
  : 14;

// One mailbox as a card: email, exactly ONE primary state chip (Ready / Warming up /
// Ready (unverified) / Disabled), and daily send cap. Warming shows the mailbox's warmup
// HEALTH score (how well warmup emails are landing, from Instantly) and an "Est. ready
// {date}" line, which is the actual readiness/progress indicator; the score bar is not
// completion. Ready (unverified) carries a tooltip about the missing score.
const MailboxCard = ({ mailbox, health, ap, onPin, warmupMinDays }) => {
  const [showNumbers, setShowNumbers] = useState(false);
  // GTM-AUTOPILOT-01: per-box autopilot state. The whole autopilot block (health line +
  // last action + pin control) is gated on `hasAp`, so a mailbox with no health record renders
  // exactly as before, no errors and no layout shift (Rule 4). Pin is optimistic with a
  // revert-and-surface on failure, mirroring the master switch, so it never shows a false pin.
  const hasAp = ap && typeof ap === 'object';
  const [pinOptimistic, setPinOptimistic] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErr, setPinErr] = useState(null);
  const serverPinned = !!(hasAp && (ap.pinned === true || ap.manualControl === true));
  const pinned = pinOptimistic == null ? serverPinned : pinOptimistic;
  const canPin = !!onPin && !!mailbox.email;
  const togglePin = async () => {
    if (!canPin || pinBusy) return;
    const next = !pinned;
    setPinBusy(true); setPinErr(null); setPinOptimistic(next);
    try {
      await onPin(mailbox.email, next);
      setPinOptimistic(null); // reconcile to the freshly-read server value
    } catch (e) {
      setPinErr((e && e.message) || 'Could not change manual control.');
      setPinOptimistic(null); // revert to the server value
    } finally {
      setPinBusy(false);
    }
  };
  // Prefer the backend's live warmupMinDays (from the capacity payload); the WARMUP_MIN_DAYS
  // constant is only a fallback when the payload does not carry it.
  const minDays = Number(warmupMinDays) > 0 ? Number(warmupMinDays) : WARMUP_MIN_DAYS;
  // State comes from the provenance tag set when the two backend arrays were combined
  // (warmingMailboxes -> warming; counted -> ready / ready_unverified). Only fall back to
  // per-row inference for a row with no tag, so a warming row can never reach Ready here.
  const state = mailbox._warmState || mailboxWarmState(mailbox);
  const meta = WARMUP_STATE_META[state] || WARMUP_STATE_META.ready_unverified;
  // warmupScore is the exact backend field; keep the earlier assumed name as a fallback.
  const rawScore = Number(mailbox.warmupScore != null ? mailbox.warmupScore : mailbox.stat_warmup_score);
  const scorePct = Number.isFinite(rawScore) ? Math.round(rawScore <= 1 ? rawScore * 100 : rawScore) : null;
  const clampedPct = scorePct == null ? null : Math.min(100, Math.max(0, scorePct));
  const estReady = fmtDate(mailbox.estimatedReadyAt);
  // The score bar is Instantly's warmup DELIVERABILITY HEALTH (are warmup emails landing in
  // inbox), NOT completion progress. Say so plainly; the est-ready date is the real progress.
  const warmupHealthInfo = `How well this mailbox's warmup emails are landing (from Instantly). A high score is good, but the mailbox still needs ${minDays} days of warming before it sends.${estReady ? ` Ready ${estReady}.` : ''}`;
  // Health is additive and fails soft: absent -> no health line, no numbers toggle.
  const hasHealth = health && (health.state === 'green' || health.state === 'amber' || health.state === 'red');
  const rates = hasHealth && health.rates ? health.rates : null;
  // GTM-FLEET-SCALER-01: purchased (done-for-you) mailboxes carry a small DFY badge. Absent
  // sourceType leaves the card exactly as before.
  const isDfy = String(mailbox.sourceType || '').toLowerCase() === 'dfy';
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-all' }}>
          {mailbox.email || '(no address)'}
        </Typography>
        {isDfy && <Badge tone="blue">DFY</Badge>}
        {pinned && <Badge tone="muted">pinned</Badge>}
        {state === 'ready_unverified' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Badge tone={meta.tone}>{meta.label}</Badge>
            <InfoTooltip title="Ready, warmup score pending">{READY_UNVERIFIED_INFO}</InfoTooltip>
          </Box>
        ) : (
          <Badge tone={meta.tone}>{meta.label}</Badge>
        )}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textSecondary }}>
        {fmtCount(mailbox.dailyLimit)} <Box component="span" sx={{ color: THEME.textMuted }}>send/day</Box>
      </Typography>
      {state === 'warming' && (
        <Box>
          {clampedPct != null && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>Warmup health</Typography>
                  <InfoTooltip title="Warmup health" size={12}>{warmupHealthInfo}</InfoTooltip>
                </Box>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>{clampedPct}%</Typography>
              </Box>
              <Box sx={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <Box sx={{ width: `${clampedPct}%`, height: '100%', background: THEME.amber }} />
              </Box>
            </>
          )}
          {estReady && (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5 }}>
              Est. ready {estReady}
            </Typography>
          )}
        </Box>
      )}
      {hasHealth && <MailboxHealthLine health={health} />}
      {rates && (
        <Box>
          <Box
            component="button"
            type="button"
            onClick={() => setShowNumbers((v) => !v)}
            sx={{
              alignSelf: 'flex-start', background: 'none', border: 'none', p: 0, cursor: 'pointer',
              fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em',
              '&:hover': { color: THEME.mint },
            }}
          >
            {showNumbers ? 'Hide numbers' : 'Show numbers'}
          </Box>
          {showNumbers && <MailboxRates rates={rates} historyNote={hasHealth ? health.historyNote : ''} />}
        </Box>
      )}
      {hasAp && (
        <Box sx={{ mt: 0.25, pt: 0.75, borderTop: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          <MailboxAutopilotHealth ap={ap} />
          <Box
            component="button"
            type="button"
            onClick={togglePin}
            disabled={!canPin || pinBusy}
            sx={{
              alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.4,
              background: 'none', border: 'none', p: 0, cursor: canPin && !pinBusy ? 'pointer' : 'default',
              fontSize: '10px', color: pinned ? THEME.amber : THEME.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.06em',
              '&:hover': canPin ? { color: pinned ? THEME.amber : THEME.textSecondary } : {},
            }}
          >
            {pinBusy
              ? <CircularProgress size={10} sx={{ color: THEME.textMuted }} />
              : <PushPinOutlinedIcon sx={{ fontSize: 12 }} />}
            {pinned ? 'Manual control on' : 'Manual control'}
          </Box>
          {pinned && (
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted, lineHeight: 1.5 }}>
              Autopilot won't touch this mailbox.
            </Typography>
          )}
          {pinErr && <Typography sx={{ fontSize: '10px', color: THEME.pink }}>{pinErr}</Typography>}
        </Box>
      )}
    </Box>
  );
};

// Group mailboxes by domain, preserving first-seen order, so cards cluster under a domain
// header. The live shape has no domain field, so it is always derived from the email host.
function groupByDomain(mailboxes) {
  const order = [];
  const byDomain = {};
  (mailboxes || []).forEach((m) => {
    const domain = ((m.email || '').split('@')[1] || '(no domain)').toLowerCase();
    if (!byDomain[domain]) { byDomain[domain] = []; order.push(domain); }
    byDomain[domain].push(m);
  });
  return order.map((domain) => ({ domain, mailboxes: byDomain[domain] }));
}

// Self-explaining copy for the three capacity metrics (rendered inside InfoTooltip).
// Kept verbatim; no em dashes anywhere.
const MAILBOXES_INFO =
  'The email accounts that actually send your outbound. Each mailbox is capped at 30 sends per day to protect deliverability. More mailboxes means more total daily send without pushing any single account hard enough to get flagged. Example: 40 mailboxes x 30 per day = 1,200 emails per day.';
const SEND_CAPACITY_INFO =
  'The number of emails your mailboxes can send in a day right now, counting only the ones that are done warming up. Mailboxes still warming up do not add to this number yet, so it climbs as they finish. This is the real limit on how fast you can work through a contact list.';
const ENRICH_BUDGET_INFO =
  'A daily speed limit on how many new contact emails the system unlocks per day, so enrichment never runs ahead of what your mailboxes can actually send. It is calculated purely from your fleet: send capacity times a 1.5 runway buffer. Example: 1,200 sends per day x 1.5 = 1,800. This number costs nothing by itself. Your actual enrichment cost is separate and comes from unlocking and researching each contact. See the estimated enrichment cost next to this metric for a rough dollar figure.';
// Appended to both enrichment tooltips: the freshness pass is planned, not built, so
// it reads zero here and adds nothing to today's estimate.
const FRESHNESS_PASS_NOTE =
  ' A planned freshness re-check before each follow-up (catch job changes, awards, news) is not yet built and is set to zero here. When shipped it adds about 1.8 cents per contact, not a full re-enrichment.';
const ENRICHMENT_COST_INFO =
  'A rate-card ESTIMATE of daily enrichment spend, not metered. Range because saved-list contacts skip the Apollo reveal and net-new ones do not. Same per-contact basis as the Monthly Cost panel below. Real spend appears once provider cost tracking is turned on.' + FRESHNESS_PASS_NOTE;
const READY_INVENTORY_INFO =
  'How many enriched, approved contacts are ready to send right now, and how many days of sending that covers at current volume. Deferring contacts for PTO is safe while this buffer stays deep, the sender simply flows to the next ready contact. If this drops toward 1 to 2 days, deferral could dip send volume and the enrich pace should rise.';

// Collapsible walkthrough tying the three numbers together. Collapsed by default;
// matches the app's chevron + Collapse disclosure idiom.
const CapacityHowItWorks = () => {
  const [open, setOpen] = useState(false);
  const steps = [
    'You find people (free).',
    'You enrich them to unlock their email (Enrich Budget paces this; Apollo credits pay for net-new ones).',
    'Your mailboxes send to them (Send Capacity is the daily ceiling).',
  ];
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          color: THEME.textSecondary,
          '&:hover': { color: THEME.textPrimary },
          [MOBILE_MQ]: { minHeight: 44 },
        }}
      >
        <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>How outbound capacity works</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {steps.map((text, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.mint, flexShrink: 0 }}>{i + 1}.</Typography>
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{text}</Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textPrimary, lineHeight: 1.6, mt: 0.5 }}>
            Bottleneck order: enough enriched contacts, then enough send capacity, then enough Apollo credits for net-new people.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

// ===== Monthly Cost & Scaling (below the capacity metrics) =====
// Two clearly separated cost worlds: sending infrastructure (fixed, confirmed from
// provider billing) and enrichment (variable, a rate-card ESTIMATE, not metered). A
// projection control recomputes both as you model adding mailboxes, without touching
// the real synced mailbox count.

// Confirmed provider costs, kept together and clearly labeled so they are easy to
// edit as billing changes. Dollars per month unless the comment says otherwise.
const COST = {
  workspacePerMailboxMo: 8.40,   // Google Workspace Business Starter, Flexible
  domainPerMo: 0.92,             // ~$11/yr Porkbun, per domain
  mailboxesPerDomain: 2,         // current structure: 2 mailboxes/domain
  // enrichment per-contact (ESTIMATE, rate-card, not metered):
  enrichPerContactSavedList: 0.03,  // free Apollo reveal + Exa+BrightData+verify+LLM+Voyage
  enrichPerContactNetNew: 0.055,    // adds Apollo reveal ~$0.025
  freshnessPassPerContact: 0,   // planned re-check delta before follow-ups
                                // (verify + serper delta + conditional rewrite);
                                // set to 0.018 ONLY when the freshness pass ships
  sendsPerMailboxPerDay: 30,
  businessDaysPerMo: 22,
  emailsPerContact: 3,           // sequence touches, for contacts/mo math
};

// Instantly plans, priced by MONTHLY EMAIL VOLUME, which is what the plan actually meters.
// The old model held $97 flat at any size and used a 170-mailbox threshold that only
// annotated a row, so the panel showed "Instantly $97" next to "next step at 170
// mailboxes" at 223 mailboxes. Verified: Hypergrowth $97 = 125k emails/mo, Light Speed
// $358 = 500k emails/mo. Ordered cheapest first; the first plan whose ceiling covers the
// projected send volume wins.
const INSTANTLY_TIERS = [
  { name: 'Hypergrowth', monthlyUsd: 97, emailsPerMo: 125000 },
  { name: 'Light Speed', monthlyUsd: 358, emailsPerMo: 500000 },
];

// The plan for a projected monthly send volume. Above the top published plan there is no
// public price, so it returns the top plan flagged aboveTop rather than inventing one.
function instantlyTierFor(emailsPerMo) {
  const v = Number(emailsPerMo) || 0;
  const fit = INSTANTLY_TIERS.find((t) => v <= t.emailsPerMo);
  if (fit) return { ...fit, aboveTop: false };
  const top = INSTANTLY_TIERS[INSTANTLY_TIERS.length - 1];
  return { ...top, aboveTop: true };
}

const SENDING_INFRA_INFO =
  'The recurring cost to run outbound, independent of how many contacts you process. Google Workspace mailboxes at 8.40 each per month, domains at about 0.92 each per month (2 mailboxes per domain), plus Instantly at 97 flat until roughly 170 mailboxes. This is what scales as you add mailboxes.';
const ENRICHMENT_SECTION_INFO =
  'An ESTIMATE of per-contact cost to unlock and research contacts: Apollo reveal about 2.5 cents on net-new only (saved-list contacts are free), plus Exa, Bright Data, Serper, email verify, drafting, and embeddings. Real per-contact cost appears once provider cost tracking is turned on. Shown as a range because saved-list contacts skip the Apollo reveal.' + FRESHNESS_PASS_NOTE;

// Copy for the leads-target projection mode tooltip. Every input is LIVE (your fleet's mean
// per-mailbox daily limit, your business-days input, and the weighted emails-per-lead across
// active campaigns), so this recomputes as the fleet and campaigns change. It is a STEADY-STATE
// number: it assumes new mailboxes are warmed, so the real ramp lags by the 2 to 3 week warm-up.
const LEADS_TARGET_INFO =
  "Back-solves the mailboxes needed for a monthly unique-lead target from your fleet's real per-mailbox daily average, your business-days input, and the weighted emails per lead. Steady state: it assumes the whole fleet is warmed and running at capacity, so the real ramp to any target lags by the 2 to 3 week per-mailbox warm-up.";

// Whole-dollar per-month formatter.
const money0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

// A "~$X to $Y" figure with the numbers in tabular numerals and the "~" / "to"
// qualifiers rendered smaller and muted so the digits read cleanly. numberSize sets
// the digit size; qualifiers scale from it. Display only, values are unchanged.
const RangeFigure = ({ low, high, numberSize = '20px', color = THEME.textPrimary }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, fontSize: numberSize }}>
    <Box component="span" sx={{ fontSize: '0.6em', color: THEME.textMuted, fontWeight: 600 }}>~</Box>
    <Box component="span" sx={{ ...numeralSx, color }}>{money0(low)}</Box>
    <Box component="span" sx={{ fontSize: '0.6em', color: THEME.textMuted, fontWeight: 600 }}>to</Box>
    <Box component="span" sx={{ ...numeralSx, color }}>{money0(high)}</Box>
  </Box>
);

// One-decimal rounder for the on-screen derivations.
const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
// ECONOMICS-DISPLAY-01: a per-contact rate string from a payload number (dollars). Up to four
// decimals so a fraction-of-a-cent rate is not rounded away. Returns null for a non-number so the
// caller hides the row rather than rendering "$NaN/contact".
const perContactRate = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `~$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}/contact`;
};

// GTM-CAPACITY-MATH-01 (1a): the SHARED fleet-capacity derivation used by BOTH the capacity
// stat and the cost/scaling calculator, so the two surfaces can never disagree.
//   todayCapacity        = ready-only send capacity (backend dailySendCapacity = sum of ready
//                          mailbox dailyLimits minus health-red).
//   afterWarmingCapacity = every synced mailbox at its dailyLimit (backend theoreticalCapacity,
//                          else summed from the synced dailyLimit values).
//   perMailboxAvg        = the fleet's mean configured dailyLimit (real per-box average, never 30).
function deriveFleetCapacity(cap, mailboxes) {
  const c = cap || {};
  const list = Array.isArray(mailboxes) ? mailboxes : [];
  const mailboxCount = list.length;
  const sumLimits = list.reduce((s, m) => s + (Number(m.dailyLimit) || 0), 0);
  const today = Number(c.dailySendCapacity);
  const todayCapacity = Number.isFinite(today) ? today : sumLimits;
  const theoreticalRaw =
    c.theoreticalCapacity != null ? c.theoreticalCapacity
      : c.theoreticalSendCapacity != null ? c.theoreticalSendCapacity
        : c.totalSendCapacity != null ? c.totalSendCapacity
          : c.fullSendCapacity;
  const afterWarmingCapacity = Number.isFinite(Number(theoreticalRaw)) ? Number(theoreticalRaw) : sumLimits;
  const perMailboxAvg = mailboxCount > 0 ? afterWarmingCapacity / mailboxCount : 0;
  const showAfterWarming = Number.isFinite(todayCapacity) && Number.isFinite(afterWarmingCapacity) && afterWarmingCapacity > todayCapacity;
  return { mailboxCount, todayCapacity, afterWarmingCapacity, perMailboxAvg, sumLimits, showAfterWarming };
}

// GTM-CAPACITY-MATH-01 (1b): the weighted average emails-per-lead across ACTIVE campaigns.
// Per campaign: an external_personalization linked pipe uses its pipe step count; otherwise the
// routed GTM sequence's live step count. Weighted by each campaign's share of in-flight contacts
// (the Home pendingByCampaign derivation). With no flow, falls back to the flat mean of LIVE
// sequences' step counts, flagged so the UI labels it. Returns { value, derivation, isFallback, ok }.
// An external_personalization pipe uses its real stepCount from the directory (now exposed by GET
// /api/gtm/instantly/campaigns); it falls back to the routed sequence's steps only when stepCount
// is absent (campaign never synced).
function deriveEmailsPerLead(campaigns, sequences, directory, pendingByCampaign) {
  const seqs = Array.isArray(sequences) ? sequences : [];
  const stepsOf = (s) => (Array.isArray(s && s.steps) ? s.steps.length : 0);
  const seqById = {};
  seqs.forEach((s) => { const id = s && (s._id || s.id); if (id) seqById[String(id)] = stepsOf(s); });
  const dirById = {};
  (Array.isArray(directory) ? directory : []).forEach((d) => { const id = d && (d.instantlyCampaignId || d.id); if (id) dirById[String(id)] = d; });
  const pending = pendingByCampaign || {};

  const liveSteps = seqs.filter((s) => s && s.status === 'live').map(stepsOf).filter((n) => n > 0);
  const liveMean = liveSteps.length ? liveSteps.reduce((a, b) => a + b, 0) / liveSteps.length : 0;

  const active = (Array.isArray(campaigns) ? campaigns : []).filter(
    (c) => c && (c.status === 'active' || c.status === 'processing' || c.status === 'pending_approval')
  );
  let weightSum = 0;
  let weighted = 0;
  let counted = 0;
  active.forEach((c) => {
    const dir = c.instantlyCampaignId ? dirById[String(c.instantlyCampaignId)] : null;
    // For an external_personalization pipe, use the pipe's real stepCount from the directory when
    // present (omitted until first synced); fall back to the routed GTM sequence's steps only when
    // stepCount is absent. Non-external campaigns use the routed sequence's steps.
    let steps;
    if (dir && dir.templateMode === 'external_personalization' && Number(dir.stepCount) > 0) {
      steps = Number(dir.stepCount);
    } else {
      steps = seqById[String(c.sequenceId)] || liveMean;
    }
    if (!steps) return;
    const w = Number(pending[c.id]) || 0;
    if (w > 0) { weightSum += w; weighted += steps * w; counted += 1; }
  });

  if (weightSum > 0) {
    const value = weighted / weightSum;
    return { value, isFallback: false, ok: true, derivation: `${round1(value)}-email weighted average across ${counted} active campaign${counted === 1 ? '' : 's'}` };
  }
  if (liveMean > 0) {
    return { value: liveMean, isFallback: true, ok: true, derivation: `${round1(liveMean)}-email average across ${liveSteps.length} live sequence${liveSteps.length === 1 ? '' : 's'} (no campaign flow yet)` };
  }
  return { value: 0, isFallback: true, ok: false, derivation: 'no live sequences to derive emails per lead' };
}

// Derive every monthly figure (STEP 2). Pure, called for both the live and projected counts so
// the delta is a subtraction. Every rate is LIVE: perMailboxAvg (fleet mean dailyLimit),
// businessDays (input), emailsPerLead (weighted), and the domain count (from the real synced
// mailboxes-per-domain ratio) — nothing here is a hardcoded send/day, business-day, or domain constant.
function deriveCosts(mailboxes, opts) {
  const o = opts || {};
  const perMailboxAvg = Number(o.perMailboxAvg) > 0 ? Number(o.perMailboxAvg) : COST.sendsPerMailboxPerDay;
  const businessDays = Number(o.businessDays) > 0 ? Number(o.businessDays) : COST.businessDaysPerMo;
  const emailsPerLead = Number(o.emailsPerLead) > 0 ? Number(o.emailsPerLead) : COST.emailsPerContact;
  const curMailboxes = Number(o.currentMailboxes) || 0;
  const curDomains = Number(o.currentDomains) || 0;
  const n = Math.max(0, Math.round(Number(mailboxes) || 0));
  // Domains from the REAL synced mailboxes-per-domain ratio (the page groups by domain), not a
  // hardcoded ratio; for n === currentMailboxes this reproduces the live domain count exactly.
  const perDomain = (curMailboxes > 0 && curDomains > 0) ? curMailboxes / curDomains : COST.mailboxesPerDomain;
  const domains = Math.max(0, Math.ceil(n / perDomain));
  const workspaceMo = n * COST.workspacePerMailboxMo;
  const domainsMo = domains * COST.domainPerMo;
  // Send volume first: the Instantly plan is priced on emails per month, so the tier has
  // to be chosen from capacity, not from a mailbox-count threshold.
  const monthlySendCapacity = n * perMailboxAvg * businessDays;
  const instantlyTier = instantlyTierFor(monthlySendCapacity);
  const instantlyMo = instantlyTier.monthlyUsd;
  const instantlyStep = instantlyTier.aboveTop;
  const sendingInfraMo = workspaceMo + domainsMo + instantlyMo;
  const contactsPerMo = monthlySendCapacity / emailsPerLead; // = leads/mo (one enrichment per lead)
  // Freshness pass is additive and defaults to 0, so it never inflates today's number.
  const freshnessMo = contactsPerMo * COST.freshnessPassPerContact;
  const enrichMoSavedList = contactsPerMo * COST.enrichPerContactSavedList + freshnessMo;
  const enrichMoNetNew = contactsPerMo * COST.enrichPerContactNetNew + freshnessMo;
  const totalBurnLow = sendingInfraMo + enrichMoSavedList;
  const totalBurnHigh = sendingInfraMo + enrichMoNetNew;
  return {
    n, domains, workspaceMo, domainsMo, instantlyStep, instantlyMo, instantlyTier, sendingInfraMo,
    monthlySendCapacity, contactsPerMo, enrichMoSavedList, enrichMoNetNew, totalBurnLow, totalBurnHigh,
  };
}

// One cost line: label left, value (plus optional basis) right.
const CostLine = ({ label, value, basis, strong }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, py: 0.65 }}>
    <Typography sx={{ fontSize: '12px', color: strong ? THEME.textPrimary : THEME.textSecondary, fontWeight: strong ? 700 : 400 }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{value}</Typography>
      {basis && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{basis}</Typography>}
    </Box>
  </Box>
);

// A labeled cost section (A and B) with an InfoTooltip in the header.
const CostSection = ({ title, tooltipTitle, tooltipBody, children }) => (
  <Box sx={{ flex: '1 1 300px', minWidth: 260, border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 1.75, [MOBILE_MQ]: { flexBasis: '100%' } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>{title}</Typography>
      <InfoTooltip title={tooltipTitle}>{tooltipBody}</InfoTooltip>
    </Box>
    {children}
  </Box>
);

// Preset mailbox-target button. Mirrors SelectChip, with a 44px mobile touch target.
const PresetButton = ({ label, selected, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      border: `1px solid ${selected ? THEME.mint : THEME.borderStrong}`,
      background: selected ? THEME.mintFaint : 'transparent',
      color: selected ? THEME.mint : THEME.textSecondary,
      borderRadius: '8px',
      px: 1.5,
      py: 0.6,
      fontSize: '12px',
      fontWeight: 600,
      '&:hover': { borderColor: THEME.mint },
      [MOBILE_MQ]: { minHeight: 44, flex: 1 },
    }}
  >
    {label}
  </Box>
);

// STEP 3 + 4: the panel. A/B/C recompute off the projected mailbox count; the real
// synced number is never mutated. Projection only scales up from the live count
// (slider min = current); reset returns projection to the live count.
// GTM-SEQUENCE-LEVER-01. Real sends per contact, from send history.
//
// CONFIGURED is how many emails a sequence is set up to send. ACTUAL is how many it
// really sends, which is lower because a reply ends a sequence early. The panel used
// only configured, which silently assumes nobody ever replies and therefore understates
// how many people the fleet can reach.
//
// Actual has to come from the server: the only collection that can answer it is
// gtm_send_events (one doc per message, type 'sent', recipient in toEmail), counted
// over distinct recipients. gtm_instantly_history CANNOT answer it, it holds one row
// per email address with no send count. Today NO endpoint aggregates send events by
// recipient (gtmHealthService groups them by mailbox only), so this reads the field
// defensively and renders the honest empty state until it is served. It never derives
// an actual from mismatched totals, which would be a fabricated ratio.
function readActualEmailsPerLead(economics) {
  const e = economics && typeof economics === 'object' ? economics : null;
  const blk = e && (e.sendsPerContact || e.sequenceActuals || (e.sending && e.sending.sendsPerContact));
  if (!blk || typeof blk !== 'object') return null;
  const value = firstFiniteNumber(blk.actual, blk.perContact, blk.value);
  if (value == null || !(value > 0)) return null;
  const sample = blk.sample && typeof blk.sample === 'object' ? blk.sample : {};
  return {
    value,
    sends: firstFiniteNumber(sample.sends, blk.sends),
    contacts: firstFiniteNumber(sample.contacts, blk.contacts),
    windowDays: firstFiniteNumber(sample.windowDays, blk.windowDays),
  };
}

const MonthlyCostPanel = ({ currentMailboxes = 0, fleet, emailsPerLead, domainCount = 0, inputsFailed = false, economics = null, onNavigate = null }) => {
  const [projected, setProjected] = useState(currentMailboxes);
  const [touched, setTouched] = useState(false);
  // Projection input mode: drive by mailbox count, or by a monthly unique-lead target
  // that back-solves the mailbox count. Both share the one projectedMailboxes state.
  const [mode, setMode] = useState('mailboxes'); // 'mailboxes' | 'leads'
  const [targetLeads, setTargetLeads] = useState(0);
  // businessDays (1c): editable, default 22. The only operator-set input; everything else is live.
  const [businessDays, setBusinessDays] = useState(22);

  // Live inputs (never constants). perMailboxAvg is the fleet's real mean daily limit; eplValue
  // is the weighted emails-per-lead. Both come from synced state and recompute when it changes.
  const perMailboxAvg = fleet && Number(fleet.perMailboxAvg) > 0 ? Number(fleet.perMailboxAvg) : 0;
  // Configured is what the sequences are set up to send. Actual is what they really
  // send once replies cut sequences short. The math uses actual whenever it exists,
  // because assuming nobody replies understates the fleet's real reach.
  const configuredEpl = emailsPerLead && Number(emailsPerLead.value) > 0 ? Number(emailsPerLead.value) : 0;
  const actualEpl = readActualEmailsPerLead(economics);
  const usingActual = !!(actualEpl && actualEpl.value > 0);
  const eplValue = usingActual ? actualEpl.value : configuredEpl;
  const bd = Number(businessDays) > 0 ? Number(businessDays) : 22;
  const todayCap = fleet ? Number(fleet.todayCapacity) : NaN;
  const afterCap = fleet ? Number(fleet.afterWarmingCapacity) : NaN;
  const costOpts = { perMailboxAvg, businessDays: bd, emailsPerLead: eplValue, currentMailboxes, currentDomains: domainCount };
  const mathReady = perMailboxAvg > 0 && eplValue > 0;

  // Monthly sends the CURRENT fleet can make. Both lever lines below are computed off
  // this, so they move with the real fleet rather than a fixed example.
  const fleetSendsPerMo = perMailboxAvg > 0 ? currentMailboxes * perMailboxAvg * bd : 0;
  const contactsAt = (epl) => (fleetSendsPerMo > 0 && epl > 0 ? Math.round(fleetSendsPerMo / epl) : null);
  // Capacity that sequences ending early gives back, in contacts per month. Stated only
  // when BOTH numbers are real; never inferred from one.
  const freedContactsPerMo =
    usingActual && configuredEpl > 0 && actualEpl.value < configuredEpl && fleetSendsPerMo > 0
      ? Math.max(0, Math.round(fleetSendsPerMo / actualEpl.value) - Math.round(fleetSendsPerMo / configuredEpl))
      : 0;

  // Track the live synced count until the operator projects a different number.
  useEffect(() => {
    if (!touched) setProjected(currentMailboxes);
  }, [currentMailboxes, touched]);

  // Forward and inverse, ALL from live inputs (2). Forward uses n mailboxes at the fleet's mean
  // per-box average; inverse back-solves required mailboxes off the same average, never 30.
  const leadsForMailboxes = (m) =>
    (mathReady ? Math.round(m * perMailboxAvg * bd / eplValue) : 0);
  const requiredMailboxesForLeads = (leads) =>
    (mathReady ? Math.ceil((Number(leads || 0) * eplValue / bd) / perMailboxAvg) : 0);

  // Forward leads/mo shown to the operator: today = ready-only capacity now; after warming =
  // every synced mailbox at its limit. Both / weighted emails-per-lead.
  const leadsToday = mathReady && Number.isFinite(todayCap) ? Math.round(todayCap * bd / eplValue) : null;
  const leadsAfterWarming = mathReady && Number.isFinite(afterCap) ? Math.round(afterCap * bd / eplValue) : null;

  const sliderMin = Math.max(1, currentMailboxes);
  const sliderMax = Math.max(200, currentMailboxes, projected);
  const clamp = (n) => Math.min(sliderMax, Math.max(sliderMin, Math.round(Number(n) || 0)));
  const setProjection = (n) => { setTouched(true); setProjected(clamp(n)); };

  // Leads mode: back-solve the required mailboxes and drive the SAME engine off it.
  const applyTargetLeads = (leads) => {
    setTouched(true);
    const t = Math.max(0, Math.round(Number(leads) || 0));
    setTargetLeads(t);
    setProjected(Math.max(1, requiredMailboxesForLeads(t)));
  };
  // Switch modes without losing the value: entering leads mode seeds the target from
  // the mailboxes currently projected (the forward formula).
  const switchMode = (m) => {
    if (m === mode) return;
    if (m === 'leads') setTargetLeads(leadsForMailboxes(projected));
    setMode(m);
  };
  const reset = () => { setTouched(false); setProjected(currentMailboxes); setTargetLeads(leadsForMailboxes(currentMailboxes)); };

  const isProjected = projected !== currentMailboxes;
  const d = deriveCosts(projected, costOpts);
  const cur = deriveCosts(currentMailboxes, costOpts);
  const deltaLow = d.totalBurnLow - cur.totalBurnLow;
  const deltaHigh = d.totalBurnHigh - cur.totalBurnHigh;

  // ECONOMICS-DISPLAY-01: itemized variable/usage costs, every figure read from the T6 economics
  // payload (no hardcoded rates). Each row is read defensively and rendered only when it has a real
  // number, so a missing field hides its row rather than showing zero or NaN.
  const econ = economics && typeof economics === 'object' ? economics : null;
  const econObj = (v) => (v && typeof v === 'object' ? v : null);
  const enrichmentEc = econ && econObj(econ.enrichment);
  const verificationEc = econ && (econObj(econ.emailVerification) || econObj(econ.verificationPrimary) || (econObj(econ.verification) && econObj(econ.verification.primary)));
  const catchAllEc = econ && (econObj(econ.catchAllResolution) || econObj(econ.catchAll) || (econObj(econ.verification) && econObj(econ.verification.secondPass)));
  const purchasesEc = econ && econObj(econ.purchases);
  const mailboxSpend = purchasesEc ? firstFiniteNumber(purchasesEc.mailboxSpendThisMonth, purchasesEc.mailboxesThisMonth, purchasesEc.thisMonth) : null;
  const instEc = econ && econObj(econ.instantly);
  // economics.instantly.tier is an OBJECT { name, monthlyUsd, mailboxCount }, not a string.
  // Interpolating it produced the literal "Instantly $97 [object Object]" in that row.
  // Read the name off it, still tolerating a plain string from an older payload.
  const instTierRaw = instEc ? (instEc.tier || instEc.currentTier || null) : null;
  const instTierName = typeof instTierRaw === 'string'
    ? instTierRaw
    : (instTierRaw && typeof instTierRaw === 'object' ? String(instTierRaw.name || '') : '');
  // The projected plan comes from projected SEND VOLUME. The live payload's tier reflects
  // today's fleet, so it is only quoted when nothing is being projected; otherwise quoting
  // it beside a projected cost would put two disagreeing tiers on one row.
  const projTier = d.instantlyTier || null;
  const instantlyBasis = isProjected
    ? `${projTier ? projTier.name : 'plan'} at ${fmtCount(Math.round(d.monthlySendCapacity))} emails/mo${d.instantlyStep ? ', above the top published plan' : ''}`
    : (instTierName
      ? `${instTierName}${d.instantlyStep ? ', above the top published plan' : ''}`
      : (projTier ? projTier.name : 'flat'));
  // A named variable-cost row: prefers the monthly projection as the figure and the per-contact
  // rate as the basis. Returns null when neither number is present.
  const econRow = (label, obj) => {
    if (!obj) return null;
    const per = firstFiniteNumber(obj.perContact, obj.rate);
    const mo = firstFiniteNumber(obj.monthly, obj.monthlyProjection, obj.projection);
    if (per == null && mo == null) return null;
    const perStr = perContactRate(per);
    return (
      <CostLine
        label={label}
        value={mo != null ? money0(mo) : perStr}
        basis={mo != null ? (perStr || undefined) : 'per contact'}
      />
    );
  };
  const enrichmentRow = econRow('Enrichment', enrichmentEc);
  const verificationRow = econRow('Email verification', verificationEc);
  const catchAllRow = econRow('Catch-all resolution', catchAllEc);
  // Catch-all coverage line: what share of contacts reach the second pass, and whether that share
  // is measured or assumed.
  const caPctRaw = catchAllEc ? firstFiniteNumber(catchAllEc.pctOfContacts, catchAllEc.pct, catchAllEc.percentOfContacts) : null;
  const caPct = caPctRaw == null ? null : Math.round(caPctRaw <= 1 ? caPctRaw * 100 : caPctRaw);
  const caBasis = catchAllEc ? String(catchAllEc.pctBasis || catchAllEc.basis || '').toLowerCase() : '';
  const caBasisNote = caBasis === 'measured' ? 'measured' : caBasis === 'assumed' ? 'assumed' : '';
  const anyEconRow = !!(enrichmentRow || verificationRow || catchAllRow || mailboxSpend != null);

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: THEME.textPrimary }}>Monthly Cost &amp; Scaling</Typography>
        {isProjected && <Badge tone="amber">Projected at {projected} mailboxes</Badge>}
      </Box>

      {/* Live inputs (nothing here is a constant): the fleet's real per-mailbox average, the
          weighted emails-per-lead with its derivation on screen, and an editable business-days
          input. Below them, the forward leads/mo today and once warming completes. */}
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.5, mb: 2 }}>
        {inputsFailed && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: THEME.amber, mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: THEME.amber, lineHeight: 1.4 }}>
              Some live inputs failed to load; showing the last known values where possible. Sync to retry.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2.5, rowGap: 1.25, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Box sx={{ minWidth: 130 }}>
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Per-mailbox avg</Typography>
            <Typography component="div" sx={{ fontSize: '16px', color: THEME.textPrimary, ...numeralSx }}>{perMailboxAvg > 0 ? round1(perMailboxAvg) : '--'} <Box component="span" sx={{ fontSize: 11, color: THEME.textMuted }}>/day</Box></Typography>
          </Box>
          {/* Configured beside actual. The gap between them IS the story, so they sit on
              one line with an arrow rather than in two separate stats. */}
          <Box sx={{ minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Emails per lead</Typography>
              <InfoTip
                text="Configured is how many emails your sequences are set up to send. Actual is how many they really send, which is lower because a reply ends a sequence early. Configured is the emails-per-lead weighted by each active campaign's share of in-flight contacts; actual is real sends divided by the people they went to."
                size={13}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography component="div" sx={{ fontSize: '16px', color: THEME.textPrimary, ...numeralSx }}>
                {configuredEpl > 0 ? round1(configuredEpl) : '--'}
                <Box component="span" sx={{ fontSize: 11, color: THEME.textMuted, ml: 0.5 }}>configured</Box>
              </Typography>
              {usingActual && (
                <>
                  <Typography component="span" sx={{ fontSize: 12, color: THEME.textMuted }}>to</Typography>
                  <Typography component="div" sx={{ fontSize: '16px', color: THEME.mint, ...numeralSx }}>
                    {round1(actualEpl.value)}
                    <Box component="span" sx={{ fontSize: 11, color: THEME.textMuted, ml: 0.5 }}>actual</Box>
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>Business days / mo</Typography>
            <TextField
              type="number"
              size="small"
              value={businessDays}
              onChange={(e) => setBusinessDays(e.target.value)}
              InputProps={inputProps}
              inputProps={{ min: 1, max: 31, 'aria-label': 'Business days per month' }}
              sx={{ width: 90 }}
            />
          </Box>
        </Box>
        {/* Plain language first: what the two numbers mean and which one the money and
            capacity figures below are actually using. The weighted-average wording stays
            in the tooltip above, where it belongs. */}
        {usingActual ? (
          <Typography sx={{ fontSize: 11, color: THEME.textSecondary, mt: 0.75, lineHeight: 1.5 }}>
            Replies end sequences early.
            {freedContactsPerMo > 0
              ? ` That frees capacity for about ${fmtCount(freedContactsPerMo)} more contacts a month.`
              : ''}
            <Box component="span" sx={{ color: THEME.textMuted }}>
              {` Everything below uses the actual ${round1(actualEpl.value)} emails per lead`}
              {actualEpl.sends != null && actualEpl.contacts != null
                ? `, from ${fmtCount(actualEpl.sends)} sends to ${fmtCount(actualEpl.contacts)} people`
                : ''}
              {actualEpl.windowDays != null ? ` over the last ${fmtCount(actualEpl.windowDays)} days` : ''}.
            </Box>
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 0.75, lineHeight: 1.5 }}>
            No send history yet, so actual emails per lead is not known. Everything below uses the configured
            {configuredEpl > 0 ? ` ${round1(configuredEpl)} ` : ' '}
            emails per lead, which assumes no one replies. The actual number appears once sending starts, and it will be lower.
          </Typography>
        )}
        {emailsPerLead && emailsPerLead.isFallback && emailsPerLead.derivation && (
          <Typography sx={{ fontSize: 11, color: THEME.amber, mt: 0.5 }}>
            Configured is an estimate: {emailsPerLead.derivation}.
          </Typography>
        )}
        {mathReady ? (
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1.25, pt: 1.25, borderTop: `1px solid ${THEME.border}` }}>
            <Box>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Leads / mo today</Typography>
              <Typography component="div" sx={{ fontSize: '18px', color: THEME.mint, ...numeralSx }}>{leadsToday != null ? fmtCount(leadsToday) : '--'}</Typography>
              <Typography sx={{ fontSize: 10, color: THEME.textMuted }}>{Number.isFinite(todayCap) ? `${fmtCount(todayCap)}/day ready` : ''}</Typography>
            </Box>
            {fleet && fleet.showAfterWarming && (
              <Box>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Once warming completes</Typography>
                <Typography component="div" sx={{ fontSize: '18px', color: THEME.textPrimary, ...numeralSx }}>{leadsAfterWarming != null ? fmtCount(leadsAfterWarming) : '--'}</Typography>
                <Typography sx={{ fontSize: 10, color: THEME.textMuted }}>{Number.isFinite(afterCap) ? `${fmtCount(afterCap)}/day full` : ''}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 11, color: THEME.textMuted, mt: 1 }}>
            Leads/mo appears once mailbox capacity and emails-per-lead are known.
          </Typography>
        )}
      </Box>

      {/* Projection control: pick the input mode, then drive the one projectedMailboxes
          state either directly (mailboxes) or by back-solving a monthly lead target. */}
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>
            Project by
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <PresetButton label="By mailboxes" selected={mode === 'mailboxes'} onClick={() => switchMode('mailboxes')} />
            <PresetButton label="By target leads/mo" selected={mode === 'leads'} onClick={() => switchMode('leads')} />
          </Box>
          {mode === 'leads' && <InfoTooltip title="Target Leads Mode (steady-state estimate)">{LEADS_TARGET_INFO}</InfoTooltip>}
        </Box>

        {mode === 'mailboxes' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'stretch', gap: 1.5 } }}>
            <Box sx={{ flex: 1, minWidth: 200, px: 1, [MOBILE_MQ]: { width: '100%', px: 0 } }}>
              <Slider
                value={clamp(projected)}
                min={sliderMin}
                max={sliderMax}
                onChange={(e, v) => setProjection(v)}
                valueLabelDisplay="auto"
                sx={{ color: THEME.mint, [MOBILE_MQ]: { py: 1.5 } }}
              />
            </Box>
            <TextField
              type="number"
              size="small"
              value={projected}
              onChange={(e) => setProjection(e.target.value)}
              InputProps={inputProps}
              inputProps={{ min: sliderMin, max: sliderMax, 'aria-label': 'Target mailboxes' }}
              placeholder="Target mailboxes"
              sx={{ width: 140, [MOBILE_MQ]: { width: '100%' }, '& .MuiInputBase-root': { [MOBILE_MQ]: { minHeight: 44 } } }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', [MOBILE_MQ]: { width: '100%' } }}>
              {[60, 90, 120].map((p) => (
                <PresetButton key={p} label={p} selected={projected === p} onClick={() => setProjection(p)} />
              ))}
            </Box>
            {isProjected && (
              <Box
                component="button"
                type="button"
                onClick={reset}
                sx={{ cursor: 'pointer', background: 'transparent', border: 'none', color: THEME.mint, fontSize: '12px', fontWeight: 600, px: 1, py: 0.5, '&:hover': { textDecoration: 'underline' }, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }}
              >
                Reset to {currentMailboxes}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'stretch', gap: 1.5 } }}>
            <TextField
              type="number"
              size="small"
              value={targetLeads}
              onChange={(e) => applyTargetLeads(e.target.value)}
              InputProps={inputProps}
              inputProps={{ min: 0, step: 1000, 'aria-label': 'Target unique leads per month' }}
              placeholder="Target unique leads / mo"
              sx={{ width: 200, [MOBILE_MQ]: { width: '100%' }, '& .MuiInputBase-root': { [MOBILE_MQ]: { minHeight: 44 } } }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', [MOBILE_MQ]: { width: '100%' } }}>
              {[25000, 50000, 100000].map((p) => (
                <PresetButton key={p} label={fmtCount(p)} selected={targetLeads === p} onClick={() => applyTargetLeads(p)} />
              ))}
            </Box>
            {isProjected && (
              <Box
                component="button"
                type="button"
                onClick={reset}
                sx={{ cursor: 'pointer', background: 'transparent', border: 'none', color: THEME.mint, fontSize: '12px', fontWeight: 600, px: 1, py: 0.5, '&:hover': { textDecoration: 'underline' }, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }}
              >
                Reset to {currentMailboxes}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Leads mode: the back-solved mailbox count, prominent, above the cost blocks. The
          summary cites the LIVE numbers used (fleet per-mailbox average and emails per lead). */}
      {mode === 'leads' && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: THEME.textPrimary }}>
            To reach {fmtCount(targetLeads)} leads/mo at your fleet's {round1(perMailboxAvg)}/day average and {round1(eplValue)} emails per lead, you need ~{fmtCount(projected)} mailboxes
          </Typography>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25 }}>
            Steady state at {round1(perMailboxAvg)} sends per mailbox per day (fleet average), {bd} business days, {round1(eplValue)} emails per lead. {emailsPerLead && emailsPerLead.isFallback ? '(emails per lead is a fallback estimate)' : ''}
          </Typography>
        </Box>
      )}

      {/* The lever, stated once in plain words. Sequence length is the single biggest
          control on reach, and nothing on this panel said so. Both figures come off the
          live fleet, so they move with it. */}
      {contactsAt(3) != null && contactsAt(6) != null && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${THEME.mintDim}`, background: THEME.mintFaint }}>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
            At 3 emails per lead you would reach about <Box component="span" sx={{ color: THEME.textPrimary, fontWeight: 700 }}>{fmtCount(contactsAt(3))} contacts a month</Box> with this fleet.
            At 6 you reach about <Box component="span" sx={{ color: THEME.textPrimary, fontWeight: 700 }}>{fmtCount(contactsAt(6))}</Box>. Same sends, half the people.
          </Typography>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.5, lineHeight: 1.5 }}>
            Sequence length is the biggest lever you have on how many people you reach.
            {onNavigate && (
              <Button
                size="small"
                variant="text"
                onClick={() => onNavigate({ group: 'emails', sub: 'sequences' })}
                sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700, minWidth: 0, px: 0.75, ml: 0.25, color: THEME.mint, '&:hover': { background: 'rgba(255,255,255,0.04)' } }}
              >
                Change emails per lead
              </Button>
            )}
          </Typography>
        </Box>
      )}

      {/* A + B, side by side on desktop, stacked on mobile. */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <CostSection title="Sending infrastructure (fixed)" tooltipTitle="Sending Infrastructure (fixed monthly)" tooltipBody={SENDING_INFRA_INFO}>
          <CostLine label="Workspace" value={money0(d.workspaceMo)} basis={`${fmtCount(d.n)} mailboxes`} />
          <CostLine label="Domains" value={money0(d.domainsMo)} basis={`${fmtCount(d.domains)} domains`} />
          <CostLine label="Instantly" value={money0(d.instantlyMo)} basis={instantlyBasis} />
          <Box sx={{ borderTop: `1px solid ${THEME.border}`, mt: 0.5, pt: 0.25 }}>
            <CostLine label="Sending subtotal" value={money0(d.sendingInfraMo)} strong />
          </Box>
        </CostSection>

        {/* ECONOMICS-DISPLAY-01: itemized variable and usage costs, each figure from the T6
            economics payload. A missing field hides its row; if the whole payload is absent the
            section stays honest with a single muted line instead of hardcoded estimates. */}
        <CostSection title="Per-contact and usage costs" tooltipTitle="Per-contact and usage costs (from live rates)" tooltipBody={ENRICHMENT_SECTION_INFO}>
          {anyEconRow ? (
            <>
              {enrichmentRow}
              {verificationRow}
              {catchAllRow}
              {catchAllRow && caPct != null && (
                <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25, mb: 0.5, lineHeight: 1.5 }}>
                  Only addresses our primary verifier cannot resolve, currently about {caPct}% of contacts{caBasisNote ? ` (${caBasisNote})` : ''}.
                </Typography>
              )}
              {mailboxSpend != null && (
                <Box sx={{ borderTop: `1px solid ${THEME.border}`, mt: 0.5, pt: 0.25 }}>
                  <CostLine label="Purchases (mailboxes)" value={money0(mailboxSpend)} basis="this month" />
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6 }}>
              Per-contact and usage costs appear once live rates are available.
            </Typography>
          )}
        </CostSection>
      </Box>

      {/* C total monthly burn, prominent. The ONE figure allowed to be green, with a
          thin mint left-border accent and the largest numerals on the panel. */}
      <Box sx={{ border: `1px solid ${THEME.mintDim}`, borderLeft: `3px solid ${THEME.mint}`, borderRadius: '10px', background: THEME.mintFaint, p: 1.75 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 0.5 }}>
          Total monthly burn
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <RangeFigure low={d.totalBurnLow} high={d.totalBurnHigh} numberSize="28px" color={THEME.mint} />
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>/mo</Typography>
        </Box>
        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, mt: 0.5, lineHeight: 1.6 }}>
          Low end assumes mostly saved-list contacts (no Apollo reveal). High end assumes all net-new contacts.
        </Typography>
        {isProjected && (
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.amber, mt: 0.75 }}>
            +{money0(deltaLow)} to {money0(deltaHigh)} /mo vs current {currentMailboxes}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// GTM-AUTOPILOT-01: the mailbox-autopilot master switch, at the top of the Mailboxes tab.
// Reuses the Sending master control idiom exactly: a state pill (colored dot + label) beside a
// single contained/outlined action button, an InfoTip, a provenance line, and an error line.
// One plain sentence explains what autopilot does. When the backend env kill switch has it off,
// the control is disabled and says "Enabled in config first" (the same infra-lock pattern the
// Sending switch uses), so the UI never implies autopilot is running when config has it off.
const AUTOPILOT_BLURB =
  "United watches each mailbox's health every hour and gently raises, lowers, or pauses its sending. Hard safety limits always apply.";
const AutopilotMasterControl = ({ enabled, configDisabled, changedBy, changedAt, busy, error, onTurnOn, onTurnOff }) => {
  const pill = (on) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: '5px', borderRadius: '6px', border: `1px solid ${on ? THEME.mintDim : THEME.borderStrong}`, background: on ? THEME.mintFaint : 'rgba(255,255,255,0.04)' }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: on ? THEME.mint : THEME.textMuted, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: on ? THEME.mint : THEME.textSecondary }}>{on ? 'Autopilot on' : 'Autopilot off'}</Typography>
    </Box>
  );
  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, px: 2, py: 1.75, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: THEME.textPrimary }}>Mailbox autopilot</Typography>
          <InfoTip text="When on, United reviews each mailbox's deliverability every hour and adjusts its daily send within fixed safety limits. Mailboxes on manual control are never touched." />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {configDisabled ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: '5px', borderRadius: '6px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.04)' }}>
              <LockOutlinedIcon sx={{ fontSize: 14, color: THEME.textMuted }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: THEME.textMuted }}>Enabled in config first</Typography>
            </Box>
          ) : enabled ? (
            <>
              {pill(true)}
              <Button variant="outlined" size="small" sx={outlinedBtnSx} disabled={busy} onClick={onTurnOff}
                startIcon={busy ? <CircularProgress size={12} sx={{ color: THEME.mint }} /> : null}>
                {busy ? 'Working…' : 'Turn off'}
              </Button>
            </>
          ) : (
            <>
              {pill(false)}
              <Button variant="contained" size="small" sx={mintBtnSx} disabled={busy} onClick={onTurnOn}
                startIcon={busy ? <CircularProgress size={12} sx={{ color: '#06281f' }} /> : null}>
                {busy ? 'Working…' : 'Turn on'}
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12, color: THEME.textSecondary, lineHeight: 1.6, mt: 0.75, maxWidth: 620 }}>
        {AUTOPILOT_BLURB}
      </Typography>
      {!configDisabled && (changedBy || changedAt) && (
        <Typography sx={{ fontSize: 10, color: THEME.textMuted, mt: 0.5 }}>
          {enabled ? 'On' : 'Off'}{changedBy ? ` by ${changedBy}` : ''}{changedAt ? ` · ${fmtWhen(changedAt)}` : ''}
        </Typography>
      )}
      {error && <Typography sx={{ fontSize: 10, color: THEME.pink, mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
};

// ===== Find people › Verify a list (VERIFY-LANES-01) =====
// A dedicated list-verification surface: upload a CSV/XLSX, see the detected column mapping and a
// pre-flight of what will actually be checked and what it costs, spend on confirm, then a results
// funnel that treats catch-alls as PENDING (not failures) with their expected ready date. Reuses
// the import-wizard upload idiom, the FleetScalerCard idiom for the credits card, and ConfirmModal
// for anything that spends. Every backend endpoint is assumed until the verify backend ships; each
// card fails soft (a missing endpoint hides its card, never a crash).
const VERIFY_MODES = [
  { key: 'all', label: 'Verify everything', desc: 'Check every address in this file. Most resolve instantly; catch-all addresses take up to three days.' },
  { key: 'catchall', label: 'Catch-alls only', desc: 'This file is already just catch-all addresses. Skip straight to deep verification.' },
];
const VERIFY_RUNNING_STATES = ['running', 'processing', 'queued', 'pending', 'in_progress', 'started'];
const VERIFY_DONE_STATES = ['complete', 'completed', 'done', 'finished', 'ready'];
const isRunningStatus = (s) => VERIFY_RUNNING_STATES.includes(String(s || '').toLowerCase());
const isDoneStatus = (s) => VERIFY_DONE_STATES.includes(String(s || '').toLowerCase());

// One labeled count row in the pre-flight breakdown. Renders only when the value is a real number.
const PreflightLine = ({ label, value, tone }) => {
  if (value == null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, py: 0.35 }}>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: '12px', color: tone || THEME.textPrimary, ...numeralSx }}>{fmtCount(value)}</Typography>
    </Box>
  );
};

const VerifyListTab = () => {
  const creditsRes = useGtmResource('/api/gtm/verify/credits', (d) => d || {});
  const jobsRes = useGtmResource('/api/gtm/verify/jobs', (d) => (Array.isArray(d) ? d : (d && d.jobs) || []));

  const [mode, setMode] = useState('all');
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [analysis, setAnalysis] = useState(null); // { jobId, columns, columnMapping, emailColumn, preflight }
  const [emailColumn, setEmailColumn] = useState('');
  const [remapping, setRemapping] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [job, setJob] = useState(null);

  const [promoting, setPromoting] = useState(false);
  const [resultsError, setResultsError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const credits = creditsRes.data || {};
  const creditsUsable = !creditsRes.loading && !creditsRes.error;
  const balance = firstFiniteNumber(credits.balance, credits.credits);
  const asOf = credits.asOf || credits.asOfAt || credits.updatedAt || null;
  const asOfMs = asOf ? Date.parse(asOf) : NaN;
  const creditsStale = Number.isFinite(asOfMs) ? (Date.now() - asOfMs > 24 * 60 * 60 * 1000) : false;
  const deepChecks = firstFiniteNumber(credits.estimatedDeepChecks, credits.deepChecksRemaining);
  const topUpUrl = credits.topUpUrl || 'https://scrubby.io/';

  const jobs = jobsRes.data || [];

  // Poll a running job until it reaches a terminal state.
  useEffect(() => {
    if (!job || !job.jobId || !isRunningStatus(job.status)) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const fresh = await fetchVerifyJob(job.jobId);
        if (!cancelled && fresh) setJob(fresh);
      } catch (e) { /* leave as-is; a transient poll failure retries on the next tick */ }
    }, 3000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [job]);

  const reset = () => {
    setFile(null); setAnalysis(null); setAnalyzeError(null); setEmailColumn('');
    setJob(null); setStartError(null); setResultsError(null);
  };

  const analyzeFile = async (f) => {
    if (!f) return;
    setFile(f); setAnalysis(null); setJob(null); setAnalyzeError(null); setStartError(null); setAnalyzing(true);
    try {
      const form = new FormData();
      form.append('file', f);
      form.append('mode', mode);
      const data = await verifyUpload(form);
      setAnalysis(data || {});
      setEmailColumn((data && (data.emailColumn || (data.columnMapping && data.columnMapping.email))) || '');
    } catch (err) {
      setAnalyzeError((err && err.message) || 'Could not read this file.');
    } finally {
      setAnalyzing(false);
    }
  };

  const onBrowse = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    analyzeFile(f);
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) analyzeFile(f);
  };

  const changeEmailColumn = async (col) => {
    setEmailColumn(col);
    if (!analysis || !analysis.jobId) return;
    setRemapping(true); setStartError(null);
    try {
      const data = await verifyRemapEmail(analysis.jobId, col);
      setAnalysis((a) => ({ ...a, ...(data || {}), emailColumn: (data && data.emailColumn) || col }));
    } catch (err) {
      setStartError((err && err.message) || 'Could not re-map the email column.');
    } finally {
      setRemapping(false);
    }
  };

  const pre = (analysis && analysis.preflight) || {};
  const needChecking = firstFiniteNumber(pre.needChecking, pre.toCheck);
  const estCredits = firstFiniteNumber(pre.estimatedCredits, pre.credits);
  const columns = (analysis && Array.isArray(analysis.columns) && analysis.columns) || [];

  const doStart = async () => {
    if (!analysis || !analysis.jobId) return;
    setStarting(true); setStartError(null);
    try {
      const j = await startVerification(analysis.jobId);
      setJob(j || { jobId: analysis.jobId, status: 'running' });
    } catch (err) {
      setStartError((err && err.message) || 'Could not start verification.');
    } finally {
      setStarting(false);
    }
  };

  const confirmStart = () => setConfirm({
    title: 'Start verification?',
    body: `This checks ${needChecking != null ? fmtCount(needChecking) : 'the'} addresses and spends ${estCredits != null ? `about ${fmtCount(estCredits)} credits` : 'credits'} from your Scrubby balance. Catch-all addresses can take up to three days.`,
    confirmLabel: 'Start verification',
    onConfirm: doStart,
  });

  const results = (job && job.results) || {};
  const validCount = firstFiniteNumber(results.valid, results.validCount);
  const invalidCount = firstFiniteNumber(results.invalid, results.invalidCount);
  const pendingArr = Array.isArray(results.pending) ? results.pending : null;
  const pendingCount = pendingArr ? pendingArr.length : firstFiniteNumber(results.pending, results.pendingCount);
  const expectedReady = fmtDate(job && (job.expectedReadyAt || results.expectedReadyAt));
  const progress = firstFiniteNumber(job && (job.progress), job && job.percent);
  const running = job && isRunningStatus(job.status);
  const done = job && isDoneStatus(job.status);

  const doPromote = async () => {
    if (!job || !job.jobId) return;
    setPromoting(true); setResultsError(null);
    try {
      await promoteVerifiedContacts(job.jobId);
      jobsRes.reload();
    } catch (err) {
      setResultsError((err && err.message) || 'Could not add contacts to the pipeline.');
    } finally {
      setPromoting(false);
    }
  };
  const confirmPromote = () => setConfirm({
    title: 'Add valid contacts to the pipeline?',
    body: `${validCount != null ? fmtCount(validCount) : 'These'} valid contacts enter the normal approval flow and never send without your review. Pending catch-all addresses promote later automatically when their verdict lands.`,
    confirmLabel: 'Add to pipeline',
    onConfirm: doPromote,
  });

  const doDownload = async () => {
    if (!job || !job.jobId) return;
    setResultsError(null);
    try {
      if (job.cleanedCsvUrl) { window.open(job.cleanedCsvUrl, '_blank', 'noopener'); return; }
      const blob = await downloadVerifyCsv(job.jobId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(file && file.name ? file.name.replace(/\.[^.]+$/, '') : 'verified')}-cleaned.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setResultsError((err && err.message) || 'Could not download the cleaned file.');
    }
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshVerifyCredits();
      creditsRes.reload();
    } catch (e) { /* balance simply stays last-known; no destructive failure */ }
    finally { setRefreshing(false); }
  };
  const confirmRefresh = () => setConfirm({
    title: 'Refresh the balance?',
    body: 'Re-reading your Scrubby balance costs 1 credit. The number shown is always last-known, never live.',
    confirmLabel: 'Refresh for 1 credit',
    onConfirm: doRefresh,
  });

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="Verify a list of email addresses through Scrubby before they enter outreach. Standard addresses resolve instantly; catch-all addresses go to a slower deep-verification lane and come back as valid or invalid later, never as failures.">
        Verify a list. Upload a file, see exactly what will be checked and what it costs, then clean it.
      </Explainer>

      {/* Credits card (FleetScalerCard idiom). Hidden entirely if the endpoint is absent. */}
      {creditsUsable && (
        <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.75 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: THEME.textPrimary }}>Verification credits</Typography>
            <InfoTip text="Your Scrubby credit balance. It is last-known, not live: refreshing it costs 1 credit. Top-ups happen on Scrubby; automatic purchase is not available from their API." />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, rowGap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last-known balance</Typography>
              <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: THEME.textPrimary, ...numeralSx }}>{balance != null ? fmtCount(balance) : '--'}</Typography>
              {asOf && (
                <Typography sx={{ fontSize: 10, color: creditsStale ? THEME.amber : THEME.textMuted }}>
                  as of {fmtWhen(asOf)}{creditsStale ? ' · stale, over 24h old' : ''}
                </Typography>
              )}
            </Box>
            {deepChecks != null && (
              <Box>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Est. deep checks left</Typography>
                <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: THEME.textPrimary, ...numeralSx }}>{fmtCount(deepChecks)}</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" sx={outlinedBtnSx} disabled={refreshing} onClick={confirmRefresh}
                startIcon={refreshing ? <CircularProgress size={12} sx={{ color: THEME.mint }} /> : null}>
                {refreshing ? 'Refreshing…' : 'Refresh balance (1 credit)'}
              </Button>
              <Button variant="outlined" size="small" sx={outlinedBtnSx} component="a" href={topUpUrl} target="_blank" rel="noopener noreferrer"
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}>
                Top up on Scrubby
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Main flow: mode + upload -> mapping + pre-flight -> progress -> results. */}
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
        {!job && (
          <>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 1 }}>What to check</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {VERIFY_MODES.map((m) => {
                const selected = mode === m.key;
                return (
                  <Box
                    key={m.key}
                    onClick={() => { if (!analysis) setMode(m.key); }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !analysis) { e.preventDefault(); setMode(m.key); } }}
                    sx={{
                      px: 1.75, py: 1.25, borderRadius: '10px',
                      cursor: analysis ? 'default' : 'pointer',
                      opacity: analysis && !selected ? 0.5 : 1,
                      border: `1px solid ${selected ? THEME.mint : THEME.border}`,
                      background: selected ? THEME.mintFaint : 'transparent',
                      '&:hover': analysis ? {} : { borderColor: THEME.mint },
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: selected ? THEME.mint : THEME.textPrimary }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.25, lineHeight: 1.5 }}>{m.desc}</Typography>
                  </Box>
                );
              })}
            </Box>

            {!analysis && (
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                sx={{
                  border: `1px dashed ${dragOver ? THEME.mint : THEME.borderStrong}`,
                  background: dragOver ? THEME.mintFaint : 'rgba(255,255,255,0.02)',
                  borderRadius: '10px', px: 2, py: 3, textAlign: 'center',
                }}
              >
                <UploadFileOutlinedIcon sx={{ fontSize: 26, color: THEME.textMuted }} />
                <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mt: 0.5 }}>
                  Drag a CSV or XLSX here, or
                </Typography>
                <Button component="label" variant="outlined" size="small" sx={{ ...outlinedBtnSx, mt: 1 }} disabled={analyzing}
                  startIcon={analyzing ? <CircularProgress size={13} sx={{ color: THEME.mint }} /> : <UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}>
                  {analyzing ? 'Reading…' : 'Browse for a file'}
                  <input hidden type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onBrowse} />
                </Button>
                {analyzeError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{analyzeError}</Typography>}
              </Box>
            )}
          </>
        )}

        {/* Mapping + pre-flight (no spend yet). */}
        {analysis && !job && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-all' }}>{file ? file.name : 'Uploaded file'}</Typography>
              <Badge tone="muted">{VERIFY_MODES.find((m) => m.key === mode) ? VERIFY_MODES.find((m) => m.key === mode).label : mode}</Badge>
              <Box sx={{ flex: 1 }} />
              <Box component="button" type="button" onClick={reset}
                sx={{ cursor: 'pointer', background: 'transparent', border: 'none', color: THEME.mint, fontSize: '12px', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                Verify a different file
              </Box>
            </Box>

            {/* Detected column mapping, with the email column correctable before any spend. */}
            <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Detected columns</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
              {Object.entries((analysis.columnMapping) || {}).map(([field, col]) => (
                <Box key={field} component="span" sx={{ fontSize: '11px', px: '8px', py: '3px', borderRadius: '4px', border: `1px solid ${THEME.border}`, background: THEME.mintFaint, color: THEME.mint }}>
                  {field} = {String(col)}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>Email column</Typography>
              {columns.length > 0 ? (
                <Select
                  size="small"
                  value={columns.includes(emailColumn) ? emailColumn : ''}
                  onChange={(e) => changeEmailColumn(e.target.value)}
                  disabled={remapping}
                  displayEmpty
                  sx={{ minWidth: 180, fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border }, '& .MuiSvgIcon-root': { color: THEME.textMuted } }}
                  MenuProps={{ PaperProps: { sx: { background: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, '& .MuiMenuItem-root': { fontSize: 13 } } } }}
                >
                  {columns.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              ) : (
                <TextField size="small" value={emailColumn} onChange={(e) => setEmailColumn(e.target.value)} onBlur={(e) => changeEmailColumn(e.target.value)} InputProps={inputProps} sx={{ minWidth: 180 }} />
              )}
              {remapping && <CircularProgress size={13} sx={{ color: THEME.mint }} />}
            </Box>

            {/* Pre-flight: what will actually be checked and what it costs. */}
            <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.5 }}>
              <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>Before you spend</Typography>
              <PreflightLine label="Rows found" value={firstFiniteNumber(pre.rowsFound, pre.rows, pre.total)} />
              <PreflightLine label="Removed as duplicates" value={firstFiniteNumber(pre.duplicatesRemoved, pre.duplicates)} tone={THEME.textMuted} />
              <PreflightLine label="Already known" value={firstFiniteNumber(pre.alreadyKnown, pre.known)} tone={THEME.textMuted} />
              <PreflightLine label="Suppressed" value={firstFiniteNumber(pre.suppressed)} tone={THEME.textMuted} />
              <PreflightLine label="Competitor-blocked" value={firstFiniteNumber(pre.competitorBlocked, pre.competitorHeld)} tone={THEME.textMuted} />
              <Box sx={{ borderTop: `1px solid ${THEME.border}`, mt: 0.75, pt: 0.75 }}>
                <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, lineHeight: 1.6 }}>
                  {needChecking != null ? fmtCount(needChecking) : 'Some'} addresses need checking{estCredits != null ? `, about ${fmtCount(estCredits)} credits` : ''}
                  {balance != null ? `, you have ${fmtCount(balance)}${asOf ? ` (as of ${fmtWhen(asOf)})` : ''}` : ''}.
                </Typography>
                <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>Balance is last-known, not live.</Typography>
              </Box>
              <Button variant="contained" size="small" sx={{ ...mintBtnSx, mt: 1.25, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }} disabled={starting || remapping} onClick={confirmStart}
                startIcon={starting ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}>
                {starting ? 'Starting…' : 'Start verification'}
              </Button>
              {startError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{startError}</Typography>}
            </Box>
          </Box>
        )}

        {/* Progress. */}
        {running && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={16} sx={{ color: THEME.mint }} />
              <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>
                Verifying{progress != null ? ` · ${Math.min(100, Math.max(0, Math.round(progress)))}%` : '…'}
              </Typography>
            </Box>
            {progress != null && (
              <Box sx={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <Box sx={{ width: `${Math.min(100, Math.max(0, Math.round(progress)))}%`, height: '100%', background: THEME.mint }} />
              </Box>
            )}
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1 }}>
              Standard addresses resolve now. Catch-all addresses go to deep verification and come back later, never as failures.
            </Typography>
          </Box>
        )}

        {/* Results funnel. */}
        {done && (
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary, mb: 1 }}>Results</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {validCount != null && (
                <Box sx={{ flex: '1 1 120px', border: `1px solid ${THEME.mintDim}`, borderRadius: '8px', background: THEME.mintFaint, p: 1.25 }}>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Valid</Typography>
                  <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: THEME.mint, ...numeralSx }}>{fmtCount(validCount)}</Typography>
                </Box>
              )}
              {invalidCount != null && (
                <Box sx={{ flex: '1 1 120px', border: `1px solid ${THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.25 }}>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Invalid</Typography>
                  <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: THEME.textSecondary, ...numeralSx }}>{fmtCount(invalidCount)}</Typography>
                </Box>
              )}
              {pendingCount != null && pendingCount > 0 && (
                <Box sx={{ flex: '1 1 120px', border: `1px solid ${THEME.border}`, borderRadius: '8px', background: 'rgba(255,255,255,0.02)', p: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Badge tone="amber">PENDING</Badge>
                  </Box>
                  <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: THEME.amber, ...numeralSx, mt: 0.25 }}>{fmtCount(pendingCount)}</Typography>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>{expectedReady ? `ready by ${expectedReady}` : 'deep verification in progress'}</Typography>
                </Box>
              )}
            </Box>
            {pendingCount != null && pendingCount > 0 && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mb: 1.5, lineHeight: 1.6 }}>
                Pending catch-all addresses are not failures. They finish deep verification{expectedReady ? ` by ${expectedReady}` : ' in up to three days'} and promote themselves automatically when their verdict lands.
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" sx={outlinedBtnSx} onClick={doDownload}>Download cleaned CSV</Button>
              <Button variant="contained" size="small" sx={mintBtnSx} disabled={promoting || !(validCount > 0)} onClick={confirmPromote}
                startIcon={promoting ? <CircularProgress size={12} sx={{ color: '#06281f' }} /> : null}>
                {promoting ? 'Adding…' : 'Add valid contacts to the pipeline'}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Box component="button" type="button" onClick={reset}
                sx={{ cursor: 'pointer', background: 'transparent', border: 'none', color: THEME.mint, fontSize: '12px', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                Verify another list
              </Box>
            </Box>
            {resultsError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{resultsError}</Typography>}
          </Box>
        )}
      </Box>

      {/* Jobs history. Hidden entirely if the endpoint is absent. */}
      {!jobsRes.error && jobs.length > 0 && (
        <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 1 }}>Verification history</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {jobs.map((jb, i) => {
              const rows = firstFiniteNumber(jb.rowsFound, jb.rows, jb.total);
              const spent = firstFiniteNumber(jb.creditsSpent, jb.credits);
              const by = jb.by || jb.createdBy || jb.uploadedBy || '';
              const when = fmtWhen(jb.createdAt || jb.at || jb.startedAt);
              const jbMode = VERIFY_MODES.find((m) => m.key === jb.mode);
              const jbReady = fmtDate(jb.expectedReadyAt);
              const jbPending = isRunningStatus(jb.status) || (firstFiniteNumber(jb.pending, jb.pendingCount) || 0) > 0;
              return (
                <Box key={jb.jobId || jb.id || i} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '8px', px: 1.25, py: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-all' }}>{jb.filename || jb.fileName || jb.label || '(file)'}</Typography>
                    <Badge tone={isDoneStatus(jb.status) ? 'mint' : isRunningStatus(jb.status) ? 'amber' : 'gray'}>{jb.status || 'unknown'}</Badge>
                    {jbMode && <Badge tone="muted">{jbMode.label}</Badge>}
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>{by ? `by ${by}` : ''}{by && when ? ' · ' : ''}{when || ''}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>
                    {rows != null ? `${fmtCount(rows)} rows` : ''}{rows != null && spent != null ? ' · ' : ''}{spent != null ? `${fmtCount(spent)} credits` : ''}
                    {jbPending && jbReady ? ` · pending, ready by ${jbReady}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

// GTM-FLEET-SCALER-01: why the effective daily ceiling matters. Plain-language explanation of
// the failure it guards against (a mailbox fleet planned above what the linked campaigns actually
// permit per day, which once caused a large overshoot and a deliverability hit).
const CAMPAIGN_CAPS_INFO =
  'Your true sending ceiling is the lower of two limits: what your mailboxes can send per day, and what each linked Instantly campaign allows per day. Until a campaign is linked we cannot see the campaign side, so we show only the mailbox ceiling and say so. Planning around the mailbox number alone can push far more mail than a campaign actually permits, which once caused a large overshoot and a deliverability hit. This line keeps that honest.';

// Map a coded order refusal to a plain-language sentence. Falls back to the server message, then
// a generic line, so the operator always sees a real reason rather than a raw code.
function fleetRefusalText(e) {
  const code = (e && e.code) || '';
  const msg = (e && e.message) || '';
  const map = {
    ceiling_zero: 'Purchasing is capped at zero right now. Ask an admin to raise the fleet purchase ceiling, then try again.',
    price_moved: 'The price changed since this quote was built. Rebuild the plan for a fresh quote, then confirm.',
    price_changed: 'The price changed since this quote was built. Rebuild the plan for a fresh quote, then confirm.',
    plan_expired: 'This quote expired. Rebuild the plan for a fresh quote, then confirm.',
    plan_scope: 'This plan is outside what can be purchased right now. Rebuild with a smaller target.',
    scope_exceeded: 'This plan is outside what can be purchased right now. Rebuild with a smaller target.',
  };
  return map[code] || msg || 'The order was refused. Rebuild the plan and try again.';
}

// Tone for an order-status badge. Confirmed/charged reads mint, pending amber, failure pink.
const orderStatusTone = (status) => {
  const s = String(status || '').toLowerCase();
  if (['confirmed', 'paid', 'charged', 'complete', 'completed', 'fulfilled'].includes(s)) return 'mint';
  if (['pending', 'processing', 'quoted', 'awaiting'].includes(s)) return 'amber';
  if (['failed', 'refused', 'cancelled', 'canceled', 'error'].includes(s)) return 'pink';
  return 'gray';
};

// GTM-FLEET-SCALER-01: "Grow the fleet" card. Presence-gated on GET /api/gtm/orders: if that
// endpoint is absent (errored) or still loading, the whole card and orders list render nothing,
// so a missing backend shows zero errors and no broken card. A target (mailboxes or leads/mo,
// same "Project by" toggle idiom as the cost panel) posts to /api/gtm/fleet/plan and renders the
// returned plan with its LIVE simulated one-time quote and the new monthly total including any
// Instantly tier step. "Review order" opens a typed-CONFIRM dialog that posts
// /api/gtm/orders/:id/confirm; a structured refusal renders in plain language.
const FleetScalerCard = ({ currentMailboxes = 0 }) => {
  const ordersRes = useGtmResource('/api/gtm/orders', (d) => (Array.isArray(d) ? d : (d && d.orders) || []));
  const [mode, setMode] = useState('mailboxes'); // 'mailboxes' | 'leads'
  const [target, setTarget] = useState('');
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState(null);

  // Presence gate: hide entirely until the orders endpoint has resolved without error.
  if (ordersRes.loading || ordersRes.error) return null;
  const orders = ordersRes.data || [];

  const runPlan = async () => {
    const t = Math.max(0, Math.round(Number(target) || 0));
    if (t <= 0) { setPlanError(mode === 'leads' ? 'Enter a monthly lead target.' : 'Enter a target mailbox count.'); return; }
    setPlanBusy(true); setPlanError(null); setPlan(null);
    try {
      const p = await fetchFleetPlan(t, mode);
      setPlan(p || null);
    } catch (e) {
      setPlanError((e && e.message) || 'Could not build a plan.');
    } finally {
      setPlanBusy(false);
    }
  };

  // Read the plan defensively across a few likely field names.
  const quote = (plan && plan.quote) || plan || {};
  const domains = plan ? firstFiniteNumber(plan.domains, plan.domainCount) : null;
  const boxes = plan ? firstFiniteNumber(plan.boxes, plan.mailboxes, plan.mailboxCount) : null;
  const preWarmed = plan ? firstFiniteNumber(plan.preWarmed, plan.prewarmed, plan.warmCount) : null;
  const regular = plan ? firstFiniteNumber(plan.regular, plan.regularCount, plan.coldCount) : null;
  const oneTime = plan ? firstFiniteNumber(quote.oneTimePrice, quote.price, quote.total, plan.oneTimePrice) : null;
  const lineItems = Array.isArray(quote.lineItems) ? quote.lineItems : (Array.isArray(plan && plan.lineItems) ? plan.lineItems : []);
  const monthly = (plan && plan.monthly) || {};
  const newMonthly = plan ? firstFiniteNumber(monthly.newMonthlyTotal, monthly.total, plan.newMonthlyTotal) : null;
  const step = (monthly && monthly.instantlyStep) || (plan && plan.instantlyStep) || null;
  const stepDelta = step ? firstFiniteNumber(step.deltaMo, step.delta) : null;
  const stepCrossed = !!step && (step.crossed === true || step.stepped === true || stepDelta != null);
  const stepText = stepCrossed
    ? (step.message || step.note
        ? String(step.message || step.note)
        : `This takes you past ${fmtCount(firstFiniteNumber(step.threshold, step.at) || 170)} mailboxes: Instantly moves to ${step.tierName || step.tier || 'the next tier'}, +${money0(stepDelta || 0)}/mo`)
    : '';
  const orderId = plan && (plan.orderId || quote.orderId || plan.id);
  const canReview = oneTime != null && !!orderId;

  const openReview = () => { setConfirmText(''); setBuyError(null); setDialogOpen(true); };
  const closeDialog = () => { if (!buying) setDialogOpen(false); };
  const confirmReady = confirmText.trim().toUpperCase() === 'CONFIRM';

  const confirmPurchase = async () => {
    if (!orderId) { setBuyError('This plan has no order to confirm. Rebuild the plan and try again.'); return; }
    setBuying(true); setBuyError(null);
    try {
      await confirmFleetOrder(orderId);
      setDialogOpen(false);
      setPlan(null); setTarget(''); setConfirmText('');
      ordersRes.reload();
    } catch (e) {
      setBuyError(fleetRefusalText(e));
    } finally {
      setBuying(false);
    }
  };

  const stat = (label, value) => (
    <Box sx={{ minWidth: 90 }}>
      <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: '16px', color: THEME.textPrimary, ...numeralSx }}>{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: THEME.textPrimary }}>Grow the fleet</Typography>
        <InfoTip text="Buy pre-warmed mailboxes to add send capacity without waiting out a warmup. We size the domains and boxes for your target and quote a live one-time price; nothing is charged until you confirm." />
      </Box>
      <Typography sx={{ fontSize: 12, color: THEME.textSecondary, lineHeight: 1.6, mb: 1.5, maxWidth: 620 }}>
        Set a target and we plan the domains and pre-warmed mailboxes to reach it, with a live one-time price and the new monthly total.
      </Typography>

      {/* Project-by toggle + target input, mirroring the Monthly Cost panel idiom. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>
          Project by
        </Typography>
        <PresetButton label="By mailboxes" selected={mode === 'mailboxes'} onClick={() => { setMode('mailboxes'); setPlan(null); }} />
        <PresetButton label="By target leads/mo" selected={mode === 'leads'} onClick={() => { setMode('leads'); setPlan(null); }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          type="number"
          size="small"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          InputProps={inputProps}
          inputProps={{ min: 0, step: mode === 'leads' ? 1000 : 1, 'aria-label': mode === 'leads' ? 'Target unique leads per month' : 'Target mailbox count' }}
          placeholder={mode === 'leads' ? 'Target unique leads / mo' : `Target mailboxes (now ${currentMailboxes})`}
          sx={{ width: 220, [MOBILE_MQ]: { width: '100%' } }}
        />
        <Button
          variant="contained"
          size="small"
          sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
          disabled={planBusy}
          onClick={runPlan}
          startIcon={planBusy ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
        >
          {planBusy ? 'Planning…' : 'Build plan'}
        </Button>
      </Box>
      {planError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{planError}</Typography>}

      {plan && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
          <Box sx={{ display: 'flex', gap: 2.5, rowGap: 1.25, flexWrap: 'wrap', mb: 1.25 }}>
            {domains != null && stat('Domains', fmtCount(domains))}
            {boxes != null && stat('Mailboxes', fmtCount(boxes))}
            {preWarmed != null && stat('Pre-warmed', fmtCount(preWarmed))}
            {regular != null && stat('Regular', fmtCount(regular))}
          </Box>
          {oneTime != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>One-time order</Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: THEME.textPrimary, ...numeralSx }}>{money0(oneTime)}</Typography>
            </Box>
          )}
          {newMonthly != null && (
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, mt: 0.75 }}>
              New monthly total: <Box component="span" sx={{ ...numeralSx, fontWeight: 700, color: THEME.mint }}>{money0(newMonthly)}</Box>/mo
            </Typography>
          )}
          {stepText && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: THEME.amber, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '11px', color: THEME.amber, lineHeight: 1.5 }}>{stepText}</Typography>
            </Box>
          )}
          {canReview ? (
            <Button variant="contained" size="small" sx={{ ...mintBtnSx, mt: 1.25, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }} onClick={openReview}>
              Review order
            </Button>
          ) : (
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1 }}>
              This plan cannot be ordered right now. Try a different target.
            </Typography>
          )}
        </Box>
      )}

      {orders.length > 0 && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${THEME.border}` }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 1 }}>
            Past orders
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {orders.map((o, i) => {
              const price = firstFiniteNumber(o.quotedPrice, o.price, o.oneTimePrice, o.total);
              const by = o.by || o.createdBy || o.orderedBy || o.confirmedBy || '';
              const when = fmtWhen(o.confirmedAt || o.createdAt || o.at || o.orderedAt);
              return (
                <Box key={o.id || o.orderId || i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', border: `1px solid ${THEME.border}`, borderRadius: '8px', px: 1.25, py: 0.75 }}>
                  <Badge tone={orderStatusTone(o.status)}>{o.status || 'unknown'}</Badge>
                  {price != null && <Typography sx={{ fontSize: '12px', color: THEME.textPrimary, ...numeralSx }}>{money0(price)}</Typography>}
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: '10px', color: THEME.textMuted }}>
                    {by ? `by ${by}` : ''}{by && when ? ' · ' : ''}{when || ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', maxWidth: 460, fontFamily: '"IBM Plex Sans", -apple-system, sans-serif', ...sheetPaperSx } }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary, pb: 1 }}>Review order</DialogTitle>
        <DialogContent>
          {oneTime != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: THEME.textPrimary, ...numeralSx }}>{money0(oneTime)}</Typography>
              <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>one-time</Typography>
            </Box>
          )}
          {lineItems.length > 0 && (
            <Box sx={{ mb: 1, p: 1.25, borderRadius: '8px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.02)' }}>
              {lineItems.map((li, i) => (
                <CostLine
                  key={i}
                  label={li.label || li.name || `Item ${i + 1}`}
                  value={money0(firstFiniteNumber(li.amount, li.price, li.total) || 0)}
                  basis={firstFiniteNumber(li.qty, li.quantity) != null ? `x${fmtCount(firstFiniteNumber(li.qty, li.quantity))}` : undefined}
                />
              ))}
            </Box>
          )}
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>
            Your card on file with Instantly will be charged. This cannot be undone from United.
          </Typography>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1.25, mb: 0.5 }}>
            Type CONFIRM to enable the purchase.
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="CONFIRM"
            InputProps={inputProps}
            inputProps={{ 'aria-label': 'Type CONFIRM to enable the purchase' }}
          />
          {buyError && <Typography sx={{ fontSize: '12px', color: THEME.pink, mt: 1 }}>{buyError}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" sx={outlinedBtnSx} disabled={buying} onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            sx={mintBtnSx}
            disabled={!confirmReady || buying}
            onClick={confirmPurchase}
            startIcon={buying ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : null}
          >
            {buying ? 'Charging…' : oneTime != null ? `Buy for ${money0(oneTime)}` : 'Buy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MailboxesTab = ({ campaigns, pendingByCampaign, onNavigate }) => {
  const capRes = useGtmResource('/api/gtm/capacity', (d) => d || {});
  // Live inputs for the cost/scaling calculator below (GTM-CAPACITY-MATH-01): sequences (step
  // counts) and the Instantly directory (templateMode). Campaigns + per-campaign flow are threaded
  // from the page root (the same Home pendingByCampaign derivation). All read-only, no new endpoint.
  const seqRes = useGtmResource('/api/gtm/sequences', (d) => (Array.isArray(d) ? d : (d && d.sequences) || []));
  const [directory, setDirectory] = useState([]);
  const [dirError, setDirError] = useState(false);
  const loadDirectory = useCallback(async () => {
    try { const list = await fetchInstantlyCampaigns(); setDirectory(Array.isArray(list) ? list : []); setDirError(false); }
    catch (e) { setDirError(true); }
  }, []);
  useEffect(() => { loadDirectory(); }, [loadDirectory]);
  // Deliverability health, fetched alongside capacity. Entirely additive: if this 404s
  // or errors, `health` stays {} and nothing health-related renders (the tab is unchanged).
  const healthRes = useGtmResource('/api/gtm/health', (d) => d || {});
  // ECONOMICS-DISPLAY-01: live per-contact and usage rates for the Monthly Cost panel. Additive
  // and fails soft: absent -> the panel's itemized rows hide and it says so, never a hardcoded rate.
  const econRes = useGtmResource('/api/gtm/economics', (d) => d || {});
  const health = healthRes.data || {};
  const healthByEmail = {};
  (Array.isArray(health.mailboxes) ? health.mailboxes : []).forEach((h) => {
    if (h && h.email) healthByEmail[String(h.email).toLowerCase()] = h;
  });
  const healthByDomain = {};
  (Array.isArray(health.domains) ? health.domains : []).forEach((d) => {
    if (d && d.domain) healthByDomain[String(d.domain).toLowerCase()] = d;
  });
  const fleet = health.fleet && typeof health.fleet === 'object' ? health.fleet : null;
  // GTM-AUTOPILOT-01: mailbox-autopilot state + per-box health, fetched alongside capacity from
  // GET /api/gtm/mailboxes/health. Entirely additive and fails soft: if this 404s or errors,
  // `apData` stays {} so `apByEmail` is empty (cards render exactly as today, Rule 4) and the
  // master switch reads Off/enabled with no provenance. The env kill switch is read defensively
  // across a few likely field names; only an explicit false (or a locked flag) disables it.
  const autopilotRes = useGtmResource('/api/gtm/mailboxes/health', (d) => d || {});
  const apData = autopilotRes.data || {};
  const apInfo = apData.autopilot && typeof apData.autopilot === 'object' ? apData.autopilot : {};
  const apByEmail = {};
  (Array.isArray(apData.mailboxes) ? apData.mailboxes : []).forEach((h) => {
    if (h && h.email) apByEmail[String(h.email).toLowerCase()] = h;
  });
  const serverAutopilot = apInfo.enabled === true;
  const autopilotConfigDisabled =
    apInfo.configEnabled === false || apInfo.envEnabled === false || apInfo.lockedByEnv === true ||
    apData.autopilotConfigEnabled === false || apData.autopilotEnvEnabled === false;
  const autopilotChangedBy = apInfo.changedBy || apInfo.lastChangedBy || '';
  const autopilotChangedAt = apInfo.changedAt || apInfo.lastChangedAt || null;
  const [autopilotOptimistic, setAutopilotOptimistic] = useState(null); // null = mirror server
  const [autopilotBusy, setAutopilotBusy] = useState(false);
  const [autopilotError, setAutopilotError] = useState(null);
  const autopilotEnabled = autopilotOptimistic == null ? serverAutopilot : autopilotOptimistic;
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // Summary-level sync time. There is no per-row lastSyncedAt in the /capacity shape, so
  // this is only known after a manual sync in this session; null until then.
  const [lastSynced, setLastSynced] = useState(null);
  const cap = capRes.data || {};
  // The backend splits the fleet into `mailboxes` (COUNTED: warmup-ready and not health-red)
  // and `warmingMailboxes` (still warming, carrying warmupScore + estimatedReadyAt). PROVENANCE
  // is the source of truth for a card's state: a row from warmingMailboxes[] is Warming, full
  // stop; a row from the counted mailboxes[] is Ready (or Ready-unverified on a flag_only basis).
  // We tag `_warmState` HERE when combining and never re-derive it from per-row fields afterward,
  // so a warming row can never fall through to Ready via warmupStatus or a missing `ready` field.
  const readyList = Array.isArray(cap.mailboxes) ? cap.mailboxes : [];
  const warmingList = Array.isArray(cap.warmingMailboxes) ? cap.warmingMailboxes : [];
  const seenMailboxEmails = new Set();
  const mailboxes = [];
  const pushMailbox = (m, provenance, i) => {
    const key = (m && m.email) || `${provenance}-${i}`;
    if (seenMailboxEmails.has(key)) return;
    seenMailboxEmails.add(key);
    const warmState = provenance === 'warming'
      ? 'warming'
      : (String((m && m.readinessBasis) || '').toLowerCase() === 'flag_only' ? 'ready_unverified' : 'ready');
    mailboxes.push({ ...m, _warmState: warmState, _provenance: provenance });
  };
  readyList.forEach((m, i) => pushMailbox(m, 'counted', i));
  warmingList.forEach((m, i) => pushMailbox(m, 'warming', i));
  const groups = groupByDomain(mailboxes);

  // GTM-FLEET-SCALER-01: owned vs purchased (DFY) mix and the campaign-aware ceiling. Both prefer
  // backend fields and fall back to a per-row derivation; each surfaces only when it has something
  // real to say, so an older capacity payload leaves the panel exactly as before.
  const dfyCount = mailboxes.filter((m) => String(m.sourceType || '').toLowerCase() === 'dfy').length;
  const purchasedCount = firstFiniteNumber(cap.purchasedCount, cap.dfyCount);
  const purchased = purchasedCount != null ? purchasedCount : dfyCount;
  const ownedCount = firstFiniteNumber(cap.ownedCount);
  const owned = ownedCount != null ? ownedCount : (mailboxes.length - dfyCount);
  const capsKnown = typeof cap.campaignCapsKnown === 'boolean' ? cap.campaignCapsKnown : null;
  const effectiveCeiling = firstFiniteNumber(cap.effectiveDailyCeiling);
  const hasCeilingInfo = capsKnown !== null || effectiveCeiling != null;

  // Est. daily enrichment cost, from the SINGLE per-contact rate source (the COST
  // object the Monthly Cost panel below also uses). Enrichment fires ONCE per contact
  // at day zero (all sequence touches compose from that snapshot), so this counts
  // contacts, not sends: send capacity divided by the emails per lead. A range because
  // saved-list contacts skip the Apollo reveal and net-new ones do not. Rate-card
  // ESTIMATE, not metered spend, hence the mandatory "~" and "est." markers.
  // Shared capacity derivation (GTM-CAPACITY-MATH-01): the SAME numbers the stat above and the
  // cost calculator below use, plus the weighted emails-per-lead. Nothing is a constant.
  const fleetCap = deriveFleetCapacity(cap, mailboxes);
  const emailsPerLead = deriveEmailsPerLead(campaigns, seqRes.data, directory, pendingByCampaign);
  const inputsFailed = !!seqRes.error || dirError;
  const eplForDay = emailsPerLead && emailsPerLead.value > 0 ? emailsPerLead.value : COST.emailsPerContact;
  // Daily enrich volume from LIVE today capacity (ready-only) / emails per lead, not 30 x boxes / 3.
  const enrichContactsPerDay = Number.isFinite(fleetCap.todayCapacity) ? fleetCap.todayCapacity / eplForDay : 0;
  // Freshness pass is additive and defaults to 0, so it never inflates today's number.
  const freshnessPerDay = enrichContactsPerDay * COST.freshnessPassPerContact;
  const estEnrichLow = enrichContactsPerDay * COST.enrichPerContactSavedList + freshnessPerDay;
  const estEnrichHigh = enrichContactsPerDay * COST.enrichPerContactNetNew + freshnessPerDay;

  // Ready inventory: enriched, approved contacts queued to send (the /capacity
  // sendReadyBuffer field), and how many days of sending that covers at current
  // consumption. dailyContactsConsumed is the same daily contact throughput as above.
  // Fails soft when the field is absent (not yet deployed).
  const sendReadyBuffer = Number(cap.sendReadyBuffer);
  const hasReady = Number.isFinite(sendReadyBuffer);
  const dailyContactsConsumed = enrichContactsPerDay; // mailboxes * sendsPerMailboxPerDay / emailsPerContact
  const daysOfReady = hasReady && dailyContactsConsumed > 0 ? sendReadyBuffer / dailyContactsConsumed : null;
  const daysReadyStr = daysOfReady == null ? null : daysOfReady.toLocaleString('en-US', { maximumFractionDigits: 1 });

  // Card-derived counts come straight from the provenance tag (never re-inferred): counted
  // rows are ready / ready_unverified, warming rows are warming.
  const stateCounts = mailboxes.reduce(
    (acc, m) => { const s = m._warmState; acc[s] = (acc[s] || 0) + 1; return acc; },
    { ready: 0, warming: 0, ready_unverified: 0, disabled: 0 }
  );
  const cardReadyCount = stateCounts.ready + stateCounts.ready_unverified;
  const cardWarmingCount = stateCounts.warming;
  // The rollup BADGE reads the backend fleet numbers directly (readyCount / warmingCount),
  // exactly like the Home tab, never re-derived from cards. The fleet number is the source of
  // truth: readyCount is warmup-ready fleet-wide (it may include a health-red mailbox that has
  // no card), so it can legitimately exceed the card-derived count. When they disagree the
  // fleet number wins and we warn so the drift is visible in dev.
  const backendReadyCount = Number.isFinite(Number(cap.readyCount)) ? Number(cap.readyCount) : null;
  const backendWarmingCount = Number.isFinite(Number(cap.warmingCount)) ? Number(cap.warmingCount) : null;
  const readyBadgeCount = backendReadyCount != null ? backendReadyCount : cardReadyCount;
  const warmingCount = backendWarmingCount != null ? backendWarmingCount : cardWarmingCount;
  useEffect(() => {
    if (backendReadyCount == null && backendWarmingCount == null) return;
    if ((backendReadyCount != null && backendReadyCount !== cardReadyCount) ||
        (backendWarmingCount != null && backendWarmingCount !== cardWarmingCount)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[WARMSTATE-SOURCE-OF-TRUTH] rollup badge/card count mismatch: fleet ready=${backendReadyCount} warming=${backendWarmingCount}, cards ready=${cardReadyCount} warming=${cardWarmingCount}. Fleet counts win the badge.`
      );
    }
  }, [backendReadyCount, backendWarmingCount, cardReadyCount, cardWarmingCount]);
  // The capacity stat reads the SHARED derivation (fleetCap), the exact same function the cost
  // calculator uses, so the two surfaces can never disagree.
  const realCapacity = fleetCap.todayCapacity;
  const theoretical = fleetCap.afterWarmingCapacity;
  const showTheoretical = fleetCap.showAfterWarming;

  const sync = async () => {
    setSyncing(true); setError(null);
    try {
      const res = await syncMailboxesNow();
      const total = (res && res.total) || 0;
      const upserted = (res && res.upserted) || 0;
      setToast(`Synced ${fmtCount(total)} from Instantly, ${fmtCount(upserted)} upserted.`);
      setLastSynced(new Date());
      capRes.reload();
      // Recompute the calculator's live inputs off the fresh sync (no page reload).
      seqRes.reload();
      loadDirectory();
    } catch (err) {
      setError((err && err.message) || 'Could not sync from Instantly.');
    } finally {
      setSyncing(false);
    }
  };

  // GTM-AUTOPILOT-01: flip the master autopilot switch. Optimistic with a revert-and-surface on
  // failure so it never shows a false success; reconciles to the freshly-read server value.
  const writeAutopilot = async (next) => {
    setAutopilotBusy(true); setAutopilotError(null); setAutopilotOptimistic(next);
    try {
      await setMailboxAutopilot(next);
      autopilotRes.reload();
      setAutopilotOptimistic(null);
    } catch (err) {
      setAutopilotError((err && err.message) || 'Could not change autopilot.');
      setAutopilotOptimistic(null);
    } finally {
      setAutopilotBusy(false);
    }
  };
  // Pin ("manual control") one mailbox. Throws on failure so the card reverts and surfaces the
  // error itself; on success the health resource reloads so the pinned badge/copy reconcile.
  const pinMailbox = async (email, pinned) => {
    await setMailboxPin(email, pinned);
    autopilotRes.reload();
  };

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The sending mailboxes behind every campaign, and the daily send/enrich capacity they add up to. Instantly is the source of truth: add mailboxes in Instantly, they appear here on the next sync.">
        Add mailboxes in Instantly, buy pre-warmed ones here, or let autopilot manage what you have.
      </Explainer>
      <AutopilotMasterControl
        enabled={autopilotEnabled}
        configDisabled={autopilotConfigDisabled}
        changedBy={autopilotChangedBy}
        changedAt={autopilotChangedAt}
        busy={autopilotBusy}
        error={autopilotError}
        onTurnOn={() => writeAutopilot(true)}
        onTurnOff={() => writeAutopilot(false)}
      />
      <FleetScalerCard currentMailboxes={mailboxes.length} />
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
        <CapacityHowItWorks />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 5, rowGap: 2.5, flexWrap: 'wrap', flex: 1, [MOBILE_MQ]: { gap: 2.5 } }}>
            {/* Send capacity as a compact stat in the row: ready-only number as the primary,
                the warmed-up ceiling as muted secondary only when it differs, and the
                ready / warming split as small inline badges. */}
            <CapacityStat
              label="Send capacity"
              value={fmtCount(realCapacity)}
              unit="/day"
              secondary={showTheoretical ? `Up to ${fmtCount(theoretical)} once warming completes` : undefined}
              badges={mailboxes.length > 0 ? (
                <>
                  <Badge tone="mint">{fmtCount(readyBadgeCount)} ready</Badge>
                  {warmingCount > 0 && <Badge tone="amber">{fmtCount(warmingCount)} warming</Badge>}
                </>
              ) : undefined}
              info={<InfoTooltip title="Send capacity right now">{SEND_CAPACITY_INFO}</InfoTooltip>}
            />
            <CapacityStat
              label="Mailboxes"
              value={fmtCount(mailboxes.length)}
              info={<InfoTooltip title="Mailboxes">{MAILBOXES_INFO}</InfoTooltip>}
            />
            <CapacityStat
              label="Enrich budget"
              value={fmtCount(cap.dailyEnrichBudget)}
              unit="enrich/day"
              info={<InfoTooltip title="Enrich Pace (this is NOT a dollar cost)">{ENRICH_BUDGET_INFO}</InfoTooltip>}
            />
            <CapacityStat
              label="Est. enrich cost"
              value={mailboxes.length > 0 ? <RangeFigure low={estEnrichLow} high={estEnrichHigh} numberSize="18px" color={THEME.textPrimary} /> : '~$-- to $--'}
              unit="/day est."
              info={<InfoTooltip title="Estimated Enrichment Cost (per day, rate-card estimate)">{ENRICHMENT_COST_INFO}</InfoTooltip>}
            />
            <CapacityStat
              label="Ready inventory"
              value={hasReady ? <Box component="span" sx={{ color: THEME.blue }}>{fmtCount(sendReadyBuffer)}</Box> : '--'}
              unit={hasReady ? (daysReadyStr != null ? `~${daysReadyStr} days` : 'ready') : ''}
              info={<InfoTooltip title="Ready Inventory (deferral safety buffer)">{READY_INVENTORY_INFO}</InfoTooltip>}
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            sx={{ ...mintBtnSx, [MOBILE_MQ]: { minHeight: 44, width: '100%' } }}
            disabled={syncing}
            onClick={sync}
            startIcon={syncing ? <CircularProgress size={13} sx={{ color: '#06281f' }} /> : <SyncIcon sx={{ fontSize: 16 }} />}
          >
            {syncing ? 'Syncing…' : 'Sync from Instantly'}
          </Button>
        </Box>
        {(purchased > 0 || hasCeilingInfo) && (
          <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {purchased > 0 && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                {fmtCount(owned)} owned, {fmtCount(purchased)} purchased
              </Typography>
            )}
            {hasCeilingInfo && (capsKnown === false ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: THEME.amber, lineHeight: 1.5 }}>
                  Campaign caps unknown: no campaigns linked yet.
                </Typography>
                <InfoTooltip title="Why this matters">{CAMPAIGN_CAPS_INFO}</InfoTooltip>
              </Box>
            ) : effectiveCeiling != null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: THEME.textSecondary }}>
                  Effective daily ceiling: <Box component="span" sx={{ ...numeralSx, color: THEME.textPrimary }}>{fmtCount(effectiveCeiling)}</Box>/day
                </Typography>
                <InfoTooltip title="Effective daily ceiling">{CAMPAIGN_CAPS_INFO}</InfoTooltip>
              </Box>
            ) : null)}
          </Box>
        )}
        <FleetHealthStrip fleet={fleet} />
        <FleetStatsBackfillLine statsBackfill={fleet ? fleet.statsBackfill : null} />
        {(cap.capacitySource || lastSynced) && (
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 1.25, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {cap.capacitySource ? `Capacity source: ${cap.capacitySource}` : ''}
            {cap.capacitySource && lastSynced ? ' · ' : ''}
            {lastSynced ? `Synced ${fmtWhen(lastSynced)}` : ''}
          </Typography>
        )}
        {error && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
      </Box>
      {mailboxes.length > 0 && (
        <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${THEME.border}` }}>
          <MonthlyCostPanel
            currentMailboxes={mailboxes.length}
            fleet={fleetCap}
            emailsPerLead={emailsPerLead}
            domainCount={groups.length}
            inputsFailed={inputsFailed}
            economics={econRes.data}
            onNavigate={onNavigate}
          />
        </Box>
      )}
      <Resource loading={capRes.loading} error={capRes.error} empty={mailboxes.length === 0} loadingLabel="Loading mailboxes…" emptyLabel="No mailboxes yet. Add them in Instantly, then sync." onRetry={capRes.reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map((g) => {
            const domView = domainHealthView(healthByDomain[g.domain]);
            const domRed = domView && domView.tone === 'red';
            return (
              <Box key={g.domain}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                  <LanguageIcon sx={{ fontSize: 14, color: domRed ? THEME.pink : THEME.textMuted }} />
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: domRed ? THEME.pink : THEME.textSecondary, wordBreak: 'break-all' }}>{g.domain}</Typography>
                  <Badge tone="gray">{g.mailboxes.length}</Badge>
                  <DomainHealthNote view={domView} />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    [MOBILE_MQ]: { gridTemplateColumns: '1fr' },
                  }}
                >
                  {g.mailboxes.map((m, i) => (
                    <MailboxCard
                      key={m.id || m._id || m.email || i}
                      mailbox={m}
                      health={healthByEmail[String(m.email || '').toLowerCase()]}
                      ap={apByEmail[String(m.email || '').toLowerCase()]}
                      onPin={pinMailbox}
                      warmupMinDays={cap.warmupMinDays}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Resource>
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ===== Tab: Suppressions (Chunk H — the "we know them" send-suppression list) =====

// One suppression row. Renders the agent reasoning + sourceRef when present, and
// for a row missing its domain, a 'needs domain' badge with an inline action that
// PATCHes the company domain onto this row in place.
const SuppressionRow = ({ row, onDelete, onCompleteDomain }) => {
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const needsDomain = !row.domain;

  const complete = async () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) { setErr('Enter a domain.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await onCompleteDomain(d, row);
      setDomainInput('');
    } catch (e) {
      setErr(e.message || 'Could not add the domain.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, border: `1px solid ${THEME.border}`, borderRadius: '8px', background: THEME.card, px: 1.5, py: 1, [MOBILE_MQ]: { flexDirection: 'column', alignItems: 'flex-start' } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-all' }}>{row.email || row.domain}</Typography>
          <Badge tone="gray">{row.email ? 'email' : 'domain'}</Badge>
          {row.source && row.source !== 'manual' && <Badge tone="muted">{row.source}</Badge>}
          {needsDomain && <Badge tone="amber">needs domain</Badge>}
        </Box>
        {row.note && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.25 }}>{row.note}</Typography>}
        {row.reasoning && (
          <ClampText sx={{ fontSize: '12px', color: THEME.textSecondary, fontStyle: 'italic', lineHeight: 1.5, mt: 0.5 }}>{row.reasoning}</ClampText>
        )}
        {row.sourceRef && <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25 }}>ref: {row.sourceRef}</Typography>}
        {needsDomain && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="company domain, e.g. acme.com" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} InputProps={inputProps} sx={{ minWidth: 220 }} />
            <Button size="small" variant="outlined" sx={outlinedBtnSx} disabled={busy} onClick={complete}>{busy ? 'Adding…' : 'Add domain'}</Button>
            {err && <Typography sx={{ fontSize: '11px', color: THEME.pink }}>{err}</Typography>}
          </Box>
        )}
      </Box>
      <IconButton size="small" onClick={() => onDelete(row)} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.pink } }}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const SuppressionsTab = () => {
  const supRes = useGtmResource('/api/gtm/suppressions', (d) => (d && d.suppressions) || []);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const rows = supRes.data || [];

  const add = async () => {
    const v = value.trim().toLowerCase();
    if (!v) { setError('Enter an email or a domain.'); return; }
    // An '@' means an exact email; otherwise treat it as a whole-company domain.
    const payload = v.includes('@') ? { email: v, note: note.trim() } : { domain: v, note: note.trim() };
    setBusy(true); setError(null);
    try {
      await createSuppression(payload);
      setValue(''); setNote('');
      supRes.reload();
    } catch (err) {
      setError(err.message || 'Could not add the suppression.');
    } finally {
      setBusy(false);
    }
  };

  const requestDelete = (row) =>
    setConfirm({
      title: 'Remove this suppression?',
      body: `${row.email || row.domain} will no longer be suppressed and may be contacted again.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: async () => {
        try { await deleteSuppression(row._id); supRes.reload(); } catch (e) { /* surfaced by list */ }
      },
    });

  // Domain-complete: PATCH the domain onto the ORIGINAL row in place, then reload
  // so its needs-domain badge clears (throws on failure so the row surfaces it).
  const completeDomain = async (domain, row) => {
    await patchSuppression(row._id, { domain, note: row.note || '' });
    supRes.reload();
  };

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Explainer tip="The 'we know them' send-suppression layer in our pipeline. Suppress an exact email or a whole company domain; isSuppressed runs first in enrichment.">
        Suppressions. Add an email or a domain to exclude it from all outreach.
      </Explainer>
      <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField size="small" placeholder="email or domain" value={value} onChange={(e) => setValue(e.target.value)} InputProps={inputProps} sx={{ flex: 1, minWidth: 200 }} />
          <TextField size="small" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} InputProps={inputProps} sx={{ flex: 1, minWidth: 160 }} />
          <Button size="small" variant="contained" sx={mintBtnSx} disabled={busy} onClick={add}>{busy ? 'Adding…' : 'Suppress'}</Button>
        </Box>
        {error && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
      </Box>
      <Resource loading={supRes.loading} error={supRes.error} empty={rows.length === 0} loadingLabel="Loading suppressions…" emptyLabel="No suppressions yet." onRetry={supRes.reload}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row) => (
            <SuppressionRow key={row._id} row={row} onDelete={requestDelete} onCompleteDomain={completeDomain} />
          ))}
        </Box>
      </Resource>
      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

// ===== Page =====

// ===== Home (Pass 1 redesign): the operator's landing surface =====
// Reads only. Surfaces what needs attention now — approvals, fresh replies, and
// sending capacity — each a card that deep-links to where the work is done, with a
// primary "Find new people" CTA. The campaign list (full management) sits below.
// Approval / reply counts are passed down from the page (already-streamed data);
// capacity is its own light read so Home works even if a count is still loading.
const sectionHeadingSx = {
  fontSize: '12px',
  fontWeight: 700,
  color: THEME.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

// One "needs attention" card: an uppercase label, a large number, a plain-language
// hint, and (when there is work) a deep-link CTA. The whole card is the click target.
const AttentionCard = ({ label, value, unit, hint, tone = THEME.mint, loading, error, ctaLabel, onClick }) => (
  <Box
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    sx={{
      flex: '1 1 220px',
      minWidth: 200,
      border: `1px solid ${THEME.border}`,
      borderLeft: `3px solid ${tone}`,
      borderRadius: '12px',
      background: THEME.card,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s, background 0.15s',
      '&:hover': onClick ? { borderColor: tone, background: 'rgba(255,255,255,0.02)' } : undefined,
      [MOBILE_MQ]: { minHeight: 44 },
    }}
  >
    <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>
      {label}
    </Typography>
    {error ? (
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: THEME.pink, lineHeight: 1.2 }}>—</Typography>
    ) : loading ? (
      <CircularProgress size={20} sx={{ color: tone, my: 0.75 }} />
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontSize: '30px', fontWeight: 700, color: THEME.textPrimary, lineHeight: 1.1 }}>{value}</Typography>
        {unit && <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>{unit}</Typography>}
      </Box>
    )}
    <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.5 }}>{hint}</Typography>
    {ctaLabel && onClick && (
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: tone, mt: 0.5 }}>{ctaLabel} →</Typography>
    )}
  </Box>
);

// ===== GTM-REACH-01: how many humans we actually reached =====
//
// Two endpoints, two different questions, never merged into one number:
//   GET /api/gtm/analytics/daily  Instantly's per day series. `contacted` is deduped
//     WITHIN a day, so adding the column across days counts a person once per day they
//     were emailed. It is a shape, not a headcount.
//   GET /api/gtm/analytics/reach  our own count from the witnessed send log, deduped
//     across the whole range. This is the only figure allowed to be called people.
//
// Colour discipline: mint is reserved for the one total reach figure. Everything on the
// daily chart is blue or grey, so the eye does not read a bar as the headline number.

const REACH_BLUE = '#60a5fa';
const REACH_FILL = {
  // Bottom of each stack is the lighter new people portion; the top is everyone else
  // contacted that day. Stack total equals `contacted`, so the bar height is the series.
  newWeekday: 'rgba(96,165,250,0.42)',
  returningWeekday: REACH_BLUE,
  newWeekend: 'rgba(96,165,250,0.16)',
  returningWeekend: 'rgba(96,165,250,0.34)',
  sentWeekday: 'rgba(255,255,255,0.20)',
  sentWeekend: 'rgba(255,255,255,0.09)',
};

// Integer formatter that never emits a dash. fmtCount renders an em dash for null,
// which Rule 7 forbids in any label, so reach copy uses this instead.
const reachInt = (n) => (n == null || Number.isNaN(Number(n)) ? 'no data' : Number(n).toLocaleString('en-US'));

const reachShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const reachIsWeekend = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const day = d.getUTCDay();
  return day === 0 || day === 6;
};

// UTC day strings, to match the ISO dates the daily endpoint keys on.
const reachDayIso = (offsetDays = 0) =>
  new Date(Date.now() - offsetDays * 86400000).toISOString().slice(0, 10);

// "Friday, Aug 21". The hero names the day it is actually reporting on, so a number
// from three days ago can never be mistaken for a number from last night.
const reachLongDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// A missing row is not one fact, it is two, and they must not look the same.
//
//   zero     the date is in the past AND inside the range the sync covered, and no row
//            came back. That means nobody was emailed that day. It is an answer.
//   unknown  the date is today (the sync runs during it), or it falls before our history
//            starts, or the sync has not reached it yet. We do not know what happened.
//
// Instantly returns no row for a zero activity day, so before this split every quiet
// weekend was rendered as missing data, and the hero had nothing to report on a Sunday.
const classifyReachDay = (p, ctx) => {
  if (!p.filled) return 'data';
  if (p.date >= ctx.todayIso) return 'unknown';
  if (!ctx.coverageStart || !ctx.lastSyncedDay) return 'unknown';
  if (p.date < ctx.coverageStart) return 'unknown';
  if (p.date > ctx.lastSyncedDay) return 'unknown';
  return 'zero';
};

const buildReachSeries = (points, meta) => {
  const m = meta || {};
  const ctx = {
    coverageStart: m.coverageStart || null,
    lastSyncedDay: m.lastSyncedAt ? String(m.lastSyncedAt).slice(0, 10) : null,
    todayIso: reachDayIso(0),
  };
  return (points || []).map((p) => {
    const raw = classifyReachDay(p, ctx);
    const contacted = Number(p.contacted) || 0;
    const sent = Number(p.sent) || 0;
    const newPeople = Math.min(Number(p.newContacted) || 0, contacted);
    // A synced row of zeros and a covered day with no row are the same fact, so they
    // render the same way: a flat tick on the baseline, not an empty slot.
    const kind = raw !== 'unknown' && contacted === 0 && sent === 0 ? 'zero' : raw;
    const isData = kind === 'data';
    return {
      date: p.date,
      weekend: reachIsWeekend(p.date),
      kind,
      isToday: p.date >= ctx.todayIso,
      newPeople: isData ? newPeople : null,
      returning: isData ? Math.max(0, contacted - newPeople) : null,
      contacted: kind === 'unknown' ? null : contacted,
      sent: kind === 'unknown' ? null : sent,
      replies: kind === 'unknown' ? null : Number(p.replies) || 0,
      // 0 renders the baseline tick, null renders nothing at all.
      baselineTick: kind === 'zero' ? 0 : null,
    };
  });
};

// Round a max up to a readable gridline. The axis is scaled to the DATA, never to
// capacity: real sending runs near 337 a day against a 1200 ceiling, so scaling to the
// ceiling would squash every bar into the bottom fifth of the chart and hide the shape.
const reachNiceCeil = (n) => {
  if (!Number.isFinite(n) || n <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const step = mag / 2;
  return Math.max(step, Math.ceil(n / step) * step);
};

const ReachTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  if (!d) return null;
  const row = (label, value) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{label}</Typography>
      <Typography sx={{ fontSize: '11px', color: THEME.textPrimary, ...numeralSx, fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        background: THEME.cardRaised,
        border: `1px solid ${THEME.borderStrong}`,
        borderRadius: '8px',
        px: 1.25,
        py: 1,
        minWidth: 180,
        boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
      }}
    >
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textPrimary, mb: 0.5 }}>
        {reachShortDate(d.date)}
        {d.weekend ? ' (weekend)' : ''}
      </Typography>
      {d.kind === 'unknown' ? (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
          {d.isToday
            ? 'Today is still running and the sync has not closed it yet.'
            : 'Nothing synced for this day, so we do not know what happened.'}
        </Typography>
      ) : d.kind === 'zero' ? (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5 }}>
          No sending this day.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {row('People reached', reachInt(d.contacted))}
          {row('New people', reachInt(d.newPeople))}
          {row('Emails sent', reachInt(d.sent))}
          {row('Replies', reachInt(d.replies))}
        </Box>
      )}
    </Box>
  );
};

// A day we know had no sending draws a flat tick sitting on the baseline. An unknown
// day draws nothing at all. Same slot, two different facts, told apart at a glance.
const REACH_ZERO_TICK = 'rgba(255,255,255,0.30)';
const ReachZeroTick = (props) => {
  const { x, y, width, payload } = props;
  if (!payload || payload.kind !== 'zero') return null;
  const w = Math.max(2, Number(width) || 0);
  const top = (Number(y) || 0) - 2;
  return <rect x={x} y={top} width={w} height={2} rx={1} fill={REACH_ZERO_TICK} />;
};

// The chart itself. Presentational: it is handed a built series and draws it.
const ReachChart = ({ series, capacity, showSent, height = 200 }) => {
  const dataMax = series.reduce(
    (m, d) => Math.max(m, d.contacted || 0, showSent ? d.sent || 0 : 0),
    0
  );
  const yMax = reachNiceCeil(dataMax * 1.12);
  const cap = Number(capacity) || 0;
  // Only draw the capacity line when it actually falls inside the plotted range. A line
  // pinned at the top edge to represent a value far above it would be a drawn lie.
  const capacityInRange = cap > 0 && cap <= yMax;
  // With 90 bars every tick cannot be labelled, so thin them to roughly ten.
  const tickInterval = Math.max(0, Math.ceil(series.length / 10) - 1);

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 16, right: 8, bottom: 4, left: 0 }} barCategoryGap="18%">
          <XAxis
            dataKey="date"
            tickFormatter={reachShortDate}
            interval={tickInterval}
            tickLine={false}
            axisLine={{ stroke: THEME.border }}
            tick={{ fill: THEME.textMuted, fontSize: 10 }}
          />
          <YAxis
            domain={[0, yMax]}
            width={44}
            tickLine={false}
            axisLine={false}
            tick={{ fill: THEME.textMuted, fontSize: 10 }}
            tickFormatter={(v) => Number(v).toLocaleString('en-US')}
          />
          <RechartsTooltip
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            content={<ReachTooltip />}
          />
          {capacityInRange && (
            <ReferenceLine
              y={cap}
              stroke={THEME.amber}
              strokeDasharray="4 4"
              label={{
                value: `capacity ${cap.toLocaleString('en-US')} a day`,
                position: 'insideTopRight',
                fill: THEME.amber,
                fontSize: 10,
              }}
            />
          )}
          {/* Shares the people stackId so it occupies no extra column and cannot
              narrow the real bars. */}
          <Bar dataKey="baselineTick" stackId="people" shape={<ReachZeroTick />} isAnimationActive={false} />
          {/* Weekend bars are drawn faint. The gaps in this series are Saturdays and
              Sundays, not outages, and the colour says so without a legend entry. */}
          <Bar dataKey="newPeople" stackId="people" isAnimationActive={false}>
            {series.map((d) => (
              <Cell key={`n-${d.date}`} fill={d.weekend ? REACH_FILL.newWeekend : REACH_FILL.newWeekday} />
            ))}
          </Bar>
          <Bar dataKey="returning" stackId="people" radius={[2, 2, 0, 0]} isAnimationActive={false}>
            {series.map((d) => (
              <Cell key={`r-${d.date}`} fill={d.weekend ? REACH_FILL.returningWeekend : REACH_FILL.returningWeekday} />
            ))}
          </Bar>
          {showSent && (
            <Bar dataKey="sent" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {series.map((d) => (
                <Cell key={`s-${d.date}`} fill={d.weekend ? REACH_FILL.sentWeekend : REACH_FILL.sentWeekday} />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const ReachLegendSwatch = ({ color, label, thin }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
    <Box sx={{ width: 9, height: thin ? 2 : 9, borderRadius: '2px', background: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{label}</Typography>
  </Box>
);

// Skeleton, never a zeroed chart. A chart full of zero height bars reads as
// "we sent nothing", which is a claim, not a loading state.
const ReachSkeleton = ({ height = 200 }) => {
  const bars = [38, 62, 55, 71, 44, 12, 10, 66, 58, 74, 49, 61, 15, 11, 70, 52, 64, 47, 68, 14];
  return (
    <Box sx={{ '@keyframes gtmReachPulse': { '0%, 100%': { opacity: 0.45 }, '50%': { opacity: 0.2 } } }}>
      <Box sx={{ height, display: 'flex', alignItems: 'flex-end', gap: '4px', pl: '44px' }}>
        {bars.map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${h}%`,
              borderRadius: '2px',
              background: 'rgba(255,255,255,0.07)',
              animation: 'gtmReachPulse 1.6s ease-in-out infinite',
              animationDelay: `${(i % 6) * 0.08}s`,
            }}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1 }}>Loading sending history</Typography>
    </Box>
  );
};

// Segmented 7 / 30 / 90 control. Grey, not mint.
const ReachRangeToggle = ({ value, onChange, options = [7, 30, 90] }) => (
  <Box sx={{ display: 'inline-flex', border: `1px solid ${THEME.border}`, borderRadius: '8px', overflow: 'hidden' }}>
    {options.map((n) => {
      const on = n === value;
      return (
        <Box
          key={n}
          component="button"
          onClick={() => onChange(n)}
          sx={{
            border: 'none',
            cursor: 'pointer',
            px: 1.25,
            py: 0.5,
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: 'inherit',
            background: on ? 'rgba(255,255,255,0.08)' : 'transparent',
            color: on ? THEME.textPrimary : THEME.textMuted,
            '&:hover': { background: 'rgba(255,255,255,0.05)' },
            [MOBILE_MQ]: { minHeight: 36 },
          }}
        >
          {n}d
        </Box>
      );
    })}
  </Box>
);

// ----- People reached (GET /api/gtm/analytics/reach) -----

const ReachCampaignRow = ({ name, unnamed, people, sends, strong, divider }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 1.5,
      py: 0.85,
      borderTop: divider ? `1px solid ${THEME.borderStrong}` : 'none',
    }}
  >
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: '12px',
        color: unnamed ? THEME.textMuted : THEME.textSecondary,
        fontWeight: strong ? 600 : 400,
        fontStyle: unnamed ? 'italic' : 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {name}
    </Typography>
    {sends != null && (
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, ...numeralSx, fontWeight: 400, width: 92, textAlign: 'right' }}>
        {reachInt(sends)} emails
      </Typography>
    )}
    <Typography
      sx={{
        fontSize: '13px',
        color: THEME.textPrimary,
        ...numeralSx,
        fontWeight: strong ? 700 : 600,
        width: 76,
        textAlign: 'right',
      }}
    >
      {reachInt(people)}
    </Typography>
  </Box>
);

const PeopleReachedPanel = ({ days = 90 }) => {
  // groupBy stays on campaign. The persona toggle is rendered but disabled, because
  // these sends came out of Instantly directly and carry no persona to group on.
  const res = useGtmResource(`/api/gtm/analytics/reach?days=${days}&groupBy=campaign`);
  const d = res.data || {};
  const ws = d.workspace || {};
  const rows = Array.isArray(d.byCampaign) ? d.byCampaign : [];
  const overlap = d.overlap || {};
  const coverage = d.coverage || {};

  const nameFor = (r) => {
    const n = (r.campaignName || '').trim();
    if (n) return { name: n, unnamed: false };
    const id = String(r.campaignId || '');
    // Never a blank row. An id we cannot resolve is shown shortened and labelled.
    const short = id ? `${id.slice(0, 8)} (unnamed Instantly campaign)` : 'unnamed Instantly campaign';
    return { name: short, unnamed: true };
  };

  if (res.loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1.25 }}>People reached</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Loading the people count</Typography>
      </Box>
    );
  }
  if (res.error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1.25 }}>People reached</Typography>
        <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.6 }}>{res.error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ ...sectionHeadingSx, mb: 1.25 }}>People reached</Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Total reach
          </Typography>
          {/* The one mint figure in this feature. Nothing else earns the colour. */}
          <Typography component="div" sx={{ fontSize: '34px', color: THEME.mint, ...numeralSx, lineHeight: 1.1 }}>
            {reachInt(ws.uniquePeople)}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>unique people, not emails.</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Touches
          </Typography>
          <Typography component="div" sx={{ fontSize: '34px', color: THEME.textPrimary, ...numeralSx, lineHeight: 1.1 }}>
            {ws.sendsPerPerson == null ? 'no data' : Number(ws.sendsPerPerson).toLocaleString('en-US')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>average touches per person.</Typography>
        </Box>
      </Box>

      {/* Group by. Campaign is the only grouping these sends can support. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>Group by</Typography>
        <Box sx={{ display: 'inline-flex', border: `1px solid ${THEME.border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <Box
            component="span"
            sx={{ px: 1.25, py: 0.5, fontSize: '11px', fontWeight: 600, background: 'rgba(255,255,255,0.08)', color: THEME.textPrimary }}
          >
            Campaign
          </Box>
          <Tooltip
            title="Personas appear once GTM starts sending. These sends came from campaigns run directly in Instantly, so there is no persona on file."
            arrow
            placement="top"
            {...tipSlotProps}
          >
            {/* A disabled button swallows hover, so the tooltip anchors to a span. */}
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Box
                component="button"
                disabled
                sx={{
                  border: 'none',
                  px: 1.25,
                  py: 0.5,
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  background: 'transparent',
                  color: THEME.textFaint,
                  cursor: 'not-allowed',
                }}
              >
                Persona
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
          No sends in this range, so there is nobody to count yet.
        </Typography>
      ) : (
        <Box>
          {rows.map((r) => {
            const n = nameFor(r);
            return (
              <ReachCampaignRow
                key={r.campaignId || n.name}
                name={n.name}
                unnamed={n.unnamed}
                people={r.uniquePeople}
                sends={r.sends}
              />
            );
          })}
          <ReachCampaignRow name="These rows added up" people={overlap.sumOfCampaignUniques} strong divider />
          <ReachCampaignRow name="Actual unique people" people={ws.uniquePeople} strong />
          {Number(overlap.difference) > 0 && (
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
              {reachInt(overlap.peopleInMultipleCampaigns)} people appear in more than one campaign, which is why the
              rows above add up to more than the total.
            </Typography>
          )}
        </Box>
      )}

      {/* Coverage is never hidden. If our log does not cover the range, say so. */}
      {coverage.truncated && coverage.plainLanguage && (
        <Typography sx={{ fontSize: '11px', color: THEME.amber, lineHeight: 1.6, mt: 1.5 }}>
          {coverage.plainLanguage}
        </Typography>
      )}
      {d.plainLanguage && (
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
          {d.plainLanguage}
        </Typography>
      )}
    </Box>
  );
};

// ----- Daily reach card (GET /api/gtm/analytics/daily) -----

const DailyReachCard = () => {
  const [days, setDays] = useState(30);
  const [showSent, setShowSent] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  const res = useGtmResource(`/api/gtm/analytics/daily?days=${days}`);
  const d = res.data || {};
  const points = Array.isArray(d.points) ? d.points : [];
  const meta = d.meta || {};
  const range = d.range || {};
  const totals = d.totals || {};
  const averages = d.averages || {};
  const capacity = (d.capacity && d.capacity.dailySendCapacity) || 0;

  // Keyed on res.data, which the hook sets once and is stable between renders. Keying
  // on the derived points/meta would rebuild every render, because both carry a
  // fallback and so get a fresh identity each time.
  const series = useMemo(
    () => buildReachSeries(res.data && res.data.points, res.data && res.data.meta),
    [res.data]
  );

  // Empty means every day is UNKNOWN. A window full of days we know had no sending is
  // not an empty window, it is an answer, and it still gets a chart.
  const allUnknown = series.length > 0 && series.every((r) => r.kind === 'unknown');
  const noHistory = !res.loading && !res.error && (points.length === 0 || allUnknown);

  // The hero reports the most recent day that actually sent, not yesterday. On a Sunday,
  // yesterday is Saturday and nothing sends at the weekend, so anchoring on yesterday
  // left the number reading "no data" when the real answer was sitting two days back.
  const active = series.filter((r) => r.kind === 'data' && (r.contacted || 0) > 0);
  const lastActive = active.length ? active[active.length - 1] : null;

  // Days between the reported day and yesterday. If they are all weekend days, say so
  // plainly. It is an explanation, not a gap and not a warning.
  const yesterdayIso = reachDayIso(1);
  const sinceActive = lastActive
    ? series.filter((r) => r.date > lastActive.date && r.date <= yesterdayIso)
    : [];
  const quietNote = !sinceActive.length
    ? ''
    : sinceActive.every((r) => r.weekend)
    ? 'No sending on Sat or Sun.'
    : 'No sending since then.';

  // The average comes from the payload and names its own denominator. "901 a day over
  // the last 7 days" is ambiguous about whether the quiet weekend is in the divisor.
  const activeDays = Number(totals.daysWithActivity) || 0;
  const perActiveDay = averages.contactedPerActiveDay;
  const averageLine =
    activeDays > 0 && perActiveDay != null
      ? `${reachInt(Math.round(perActiveDay))} a day on average across the ${activeDays} ${
          activeDays === 1 ? 'day' : 'days'
        } with sending.`
      : 'No sending in this range yet.';

  // Open tracking is deliberately off in Instantly for deliverability, so the daily
  // payload carries no opens at all. A permanent zero percent would read as catastrophic
  // performance for a measurement that does not exist. The metric renders only if a real
  // non zero open count ever appears; otherwise it is omitted entirely.
  const openedTotal = points.reduce((s, p) => s + (Number(p.opened) || 0), 0);
  const showOpens = openedTotal > 0;

  // Partial coverage: a PAST day we still know nothing about, or a series that starts
  // later than the window asked for. Today is always unknown and that is expected, so it
  // does not count. The server already writes the sentence; render it verbatim.
  const coverageLate = !!(meta.coverageStart && range.start && meta.coverageStart > range.start);
  const unknownPastDays = series.some((r) => r.kind === 'unknown' && !r.isToday);
  const partial = !allUnknown && (unknownPastDays || coverageLate);

  const runSync = async () => {
    setSyncing(true);
    setSyncError('');
    try {
      await syncDailyStats({ days });
      res.reload();
    } catch (err) {
      setSyncError(
        err && err.httpStatus === 424
          ? 'No Instantly API key is set, so there is nothing to pull history from.'
          : (err && err.message) || 'Could not sync sending history.'
      );
    } finally {
      setSyncing(false);
    }
  };

  const capacityBelowChart = capacity > 0;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...sectionHeadingSx, mb: 1.25 }}>Daily reach</Typography>
      <Box
        sx={{
          border: `1px solid ${THEME.border}`,
          borderRadius: '12px',
          background: THEME.card,
          p: 2.5,
        }}
      >
        {/* header: hero on the left, controls on the right */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>
                {lastActive ? `People contacted ${reachLongDate(lastActive.date)}` : 'People contacted'}
              </Typography>
              <InfoTip text="Unique humans who received at least one email on the most recent day that actually sent. Today is left out because it is still running." />
            </Box>
            {res.loading ? (
              <Box sx={{ width: 120, height: 34, borderRadius: '4px', background: 'rgba(255,255,255,0.06)', my: 0.5 }} />
            ) : (
              <Typography component="div" sx={{ fontSize: '34px', color: THEME.textPrimary, ...numeralSx, lineHeight: 1.1 }}>
                {lastActive ? reachInt(lastActive.contacted) : allUnknown ? 'no data' : '0'}
              </Typography>
            )}
            {quietNote && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mb: 0.25 }}>{quietNote}</Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{averageLine}</Typography>
              <InfoTip text="Only days that actually sent are in this average. A quiet weekend is not counted as a zero in the divisor." />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
            <ReachRangeToggle value={days} onChange={setDays} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', color: showSent ? THEME.textPrimary : THEME.textMuted }}>Emails sent</Typography>
              <Switch
                size="small"
                checked={showSent}
                onChange={(e) => setShowSent(e.target.checked)}
                inputProps={{ 'aria-label': 'Show emails sent' }}
              />
              <InfoTip text="Emails sent counts messages. People reached counts humans." />
            </Box>
          </Box>
        </Box>

        {/* body */}
        {res.loading ? (
          <ReachSkeleton />
        ) : res.error ? (
          <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.6 }}>{res.error}</Typography>
        ) : noHistory ? (
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, mb: 0.5 }}>
              No sending history synced yet
            </Typography>
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6, mb: 1.5, maxWidth: 520 }}>
              Nothing has been pulled from Instantly for this range, so there is nothing to chart. This is not a
              report of zero sends, it is an absence of data.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              disabled={syncing}
              onClick={runSync}
              sx={{ ...outlinedBtnSx, [MOBILE_MQ]: { minHeight: 44 } }}
            >
              {syncing ? 'Syncing' : 'Sync now'}
            </Button>
            {syncError && (
              <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 1, lineHeight: 1.6 }}>{syncError}</Typography>
            )}
          </Box>
        ) : (
          <>
            <ReachChart series={series} capacity={capacity} showSent={showSent} height={200} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
              <ReachLegendSwatch color={REACH_FILL.returningWeekday} label="People reached" />
              <ReachLegendSwatch color={REACH_FILL.newWeekday} label="New people" />
              {showSent && <ReachLegendSwatch color={REACH_FILL.sentWeekday} label="Emails sent" />}
              <ReachLegendSwatch color={REACH_FILL.returningWeekend} label="Weekend" />
              <ReachLegendSwatch color={REACH_ZERO_TICK} label="No sending" thin />
              {showOpens && (
                <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                  {reachInt(openedTotal)} opens recorded
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
              The low days are Saturdays and Sundays, not outages.
              {capacityBelowChart
                ? ` Daily capacity is ${capacity.toLocaleString('en-US')} emails. The chart is scaled to what actually sent, so the capacity line sits above the top of it unless sending gets close.`
                : ''}
            </Typography>

            {partial && meta.plainLanguage && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
                {meta.plainLanguage}
              </Typography>
            )}

            <Box
              component="button"
              onClick={() => setHistoryOpen(true)}
              sx={{
                mt: 1.5,
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '12px',
                fontWeight: 600,
                color: REACH_BLUE,
                '&:hover': { textDecoration: 'underline' },
                [MOBILE_MQ]: { minHeight: 44 },
              }}
            >
              See full history
            </Box>
          </>
        )}
      </Box>

      {/* Mounted only while open, so Home does not fire a 90 day read plus a reach
          read on every page load just to have a dialog ready. */}
      {historyOpen && <ReachHistoryDialog open onClose={() => setHistoryOpen(false)} />}
    </Box>
  );
};

// "See full history": the same chart at 90 days, with the people count underneath.
const ReachHistoryDialog = ({ open, onClose }) => {
  const [showSent, setShowSent] = useState(false);
  const res = useGtmResource('/api/gtm/analytics/daily?days=90');
  const d = res.data || {};
  const points = Array.isArray(d.points) ? d.points : [];
  const meta = d.meta || {};
  const range = d.range || {};
  const capacity = (d.capacity && d.capacity.dailySendCapacity) || 0;
  // Keyed on res.data, which the hook sets once and is stable between renders. Keying
  // on the derived points/meta would rebuild every render, because both carry a
  // fallback and so get a fresh identity each time.
  const series = useMemo(
    () => buildReachSeries(res.data && res.data.points, res.data && res.data.meta),
    [res.data]
  );
  const allUnknown = series.length > 0 && series.every((r) => r.kind === 'unknown');
  const coverageLate = !!(meta.coverageStart && range.start && meta.coverageStart > range.start);
  const unknownPastDays = series.some((r) => r.kind === 'unknown' && !r.isToday);
  const partial = !allUnknown && (unknownPastDays || coverageLate);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: THEME.textPrimary }}>Full sending history</Typography>
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>The last 90 days</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: THEME.textMuted }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: THEME.border }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: showSent ? THEME.textPrimary : THEME.textMuted }}>Emails sent</Typography>
          <Switch
            size="small"
            checked={showSent}
            onChange={(e) => setShowSent(e.target.checked)}
            inputProps={{ 'aria-label': 'Show emails sent' }}
          />
          <InfoTip text="Emails sent counts messages. People reached counts humans." />
        </Box>

        {res.loading ? (
          <ReachSkeleton height={240} />
        ) : res.error ? (
          <Typography sx={{ fontSize: '12px', color: THEME.pink, lineHeight: 1.6 }}>{res.error}</Typography>
        ) : points.length === 0 || allUnknown ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, lineHeight: 1.6 }}>
            No sending history synced yet, so there is nothing to chart for this range.
          </Typography>
        ) : (
          <>
            <ReachChart series={series} capacity={capacity} showSent={showSent} height={240} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
              <ReachLegendSwatch color={REACH_FILL.returningWeekday} label="People reached" />
              <ReachLegendSwatch color={REACH_FILL.newWeekday} label="New people" />
              {showSent && <ReachLegendSwatch color={REACH_FILL.sentWeekday} label="Emails sent" />}
              <ReachLegendSwatch color={REACH_FILL.returningWeekend} label="Weekend" />
              <ReachLegendSwatch color={REACH_ZERO_TICK} label="No sending" thin />
            </Box>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
              The low days are Saturdays and Sundays, not outages.
              {capacity > 0
                ? ` Daily capacity is ${capacity.toLocaleString('en-US')} emails. The chart is scaled to what actually sent, not to the ceiling.`
                : ''}
            </Typography>
            {partial && meta.plainLanguage && (
              <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.6, mt: 1 }}>
                {meta.plainLanguage}
              </Typography>
            )}
          </>
        )}

        {open && <PeopleReachedPanel days={90} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={outlinedBtnSx} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const HomeTab = ({ approvalsCount, replyCount, onNavigate, onFindNewPeople, campaignsProps }) => {
  const capRes = useGtmResource('/api/gtm/capacity', (d) => d || {});
  const cap = capRes.data || {};
  const campaigns = campaignsProps.campaigns || [];
  const pendingByCampaign = campaignsProps.pendingByCampaign || {};

  // Instantly campaign list, fetched once here and shared with the campaign cards below, so the
  // Delivery issues card, the "sending" chip, and each card read the SAME synthesized status
  // (no drift). 424 -> keyMissing.
  const [instantly, setInstantly] = useState({ list: [], loading: true, error: null, keyMissing: false });
  const loadInstantly = useCallback(async () => {
    setInstantly((s) => ({ ...s, loading: true, error: null }));
    try {
      const list = await fetchInstantlyCampaigns();
      setInstantly({ list: Array.isArray(list) ? list : [], loading: false, error: null, keyMissing: false });
    } catch (err) {
      const keyMissing = !!err && (err.httpStatus === 424 || err.code === 'instantly_key_missing');
      setInstantly({ list: [], loading: false, error: keyMissing ? null : (err && err.message) || 'Could not load Instantly campaigns.', keyMissing });
    }
  }, []);
  useEffect(() => { loadInstantly(); }, [loadInstantly]);

  // Synthesize each campaign's single state (same function the campaign rows use), so the
  // "sending" chip and the Delivery issues card can never disagree with the rows.
  const instantlyById = {};
  (instantly.list || []).forEach((x) => { const id = x.id || x._id; if (id) instantlyById[id] = x; });
  const synthOf = (c) => synthesizeCampaignState(c, linkedEntryOf(c, instantlyById), { pendingApprovals: pendingByCampaign[c.id] });
  const sendingCount = campaigns.filter((c) => synthOf(c).key === 'sending').length;
  // Delivery issues: campaigns whose SYNTHESIZED state is a red delivery state. Silence means
  // healthy — the card renders only when > 0.
  const affected = campaigns.filter((c) => {
    const k = synthOf(c).key;
    return k === 'bounce_protect' || k === 'accounts_unhealthy';
  });
  const jumpToFirstAffected = () => {
    const first = affected[0];
    const el = first && typeof document !== 'undefined' && document.getElementById(`gtm-campaign-${first.id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // dailySendCapacity already reflects ready-only capacity from the API; surface how
  // many mailboxes are still warming up (from the API count, else derived) as context.
  const homeMailboxes = Array.isArray(cap.mailboxes) ? cap.mailboxes : [];
  const homeWarming = Number.isFinite(Number(cap.warmingCount))
    ? Number(cap.warmingCount)
    : (Array.isArray(cap.warmingMailboxes) ? cap.warmingMailboxes.length : 0);

  return (
    <Box>
      {/* Hero: greeting + the one primary action */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: THEME.textPrimary, lineHeight: 1.2 }}>Home</Typography>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mt: 0.75, maxWidth: 540, lineHeight: 1.6 }}>
            Everything that needs you, in one place. Approve what's ready, answer replies, and start finding new people to reach.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<TravelExploreIcon />}
          onClick={onFindNewPeople}
          sx={{ ...mintBtnSx, px: 3, py: 1.25, fontSize: '14px', flexShrink: 0, [MOBILE_MQ]: { width: '100%', minHeight: 48 } }}
        >
          Find new people
        </Button>
      </Box>

      {/* One-line pitch rotation status (renders nothing when there is no rotation state) */}
      <HomeRotationLine rotation={cap.rotation} />

      {/* Needs your attention */}
      <Typography sx={{ ...sectionHeadingSx, mb: 1.25 }}>Needs your attention</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
        <AttentionCard
          label="Approvals"
          value={fmtCount(approvalsCount)}
          tone={THEME.amber}
          hint={approvalsCount > 0 ? 'Emails ready for your OK before anything sends.' : "You're all caught up — nothing waiting for approval."}
          ctaLabel={approvalsCount > 0 ? 'Review & approve' : undefined}
          onClick={() => onNavigate({ group: 'review', chip: 'approvals' })}
        />
        <AttentionCard
          label="New replies"
          value={fmtCount(replyCount)}
          tone={THEME.mint}
          hint={replyCount > 0 ? 'Prospects replied; a drafted response is ready to send.' : 'No new replies to handle right now.'}
          ctaLabel={replyCount > 0 ? 'Open replies' : undefined}
          onClick={() => onNavigate({ group: 'replies' })}
        />
        <AttentionCard
          label="Daily send capacity"
          value={fmtCount(cap.dailySendCapacity)}
          unit="/day"
          tone={THEME.mint}
          loading={capRes.loading}
          error={capRes.error}
          hint={`${fmtCount(homeMailboxes.length)} mailboxes · ${fmtCount(cap.dailyEnrichBudget)} enrich/day${homeWarming > 0 ? ` · ${fmtCount(homeWarming)} warming` : ''}`}
          ctaLabel="Manage mailboxes"
          onClick={() => onNavigate({ group: 'settings', sub: 'mailboxes' })}
        />
        {/* Delivery issues: only rendered when a linked Instantly campaign is unhealthy. */}
        {affected.length > 0 && (
          <AttentionCard
            label="Delivery issues"
            value={fmtCount(affected.length)}
            tone={THEME.pink}
            hint={`${affected.length === 1 ? 'One campaign has' : `${affected.length} campaigns have`} a mailbox or bounce problem in Instantly. Open the campaign to see the fix.`}
            ctaLabel="Jump to first"
            onClick={jumpToFirstAffected}
          />
        )}
      </Box>

      {/* Your campaigns — full management relocated from the old Campaigns tab */}
      {/* GTM-REACH-01: how many humans we reached. Sits above the campaign list and
          below the cards that need action. Read only, and it never writes. */}
      <DailyReachCard />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Typography sx={sectionHeadingSx}>Your campaigns</Typography>
        {sendingCount > 0 && <Badge tone="mint">{sendingCount} sending</Badge>}
      </Box>
      <CampaignsTab {...campaignsProps} instantly={instantly} loadInstantly={loadInstantly} onNavigate={onNavigate} />
    </Box>
  );
};

// GTM-MASTER-SWITCH-01: the master "Sending" control in the header. OFF shows a prominent-but-
// calm "Sending is off" pill + "Go live"; ON shows a mint "Sending live" pill + "Turn off".
// Turning ON is gated by a two-step confirm (owned by the parent); turning OFF is instant.
// When the infrastructure (env) breaker is tripped, the control is disabled and says so, so the
// UI never lies about why nothing sends.
const SendingMasterControl = ({ sendingEnabled, infraDisabled, changedBy, changedAt, busy, error, onGoLive, onTurnOff }) => {
  const pill = (on) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: '5px', borderRadius: '6px', border: `1px solid ${on ? THEME.mintDim : THEME.borderStrong}`, background: on ? THEME.mintFaint : 'rgba(255,255,255,0.04)' }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: on ? THEME.mint : THEME.textMuted, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: on ? THEME.mint : THEME.textSecondary }}>{on ? 'Sending live' : 'Sending is off'}</Typography>
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.35, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {infraDisabled ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: '5px', borderRadius: '6px', border: `1px solid ${THEME.border}`, background: 'rgba(255,255,255,0.04)' }}>
            <LockOutlinedIcon sx={{ fontSize: 14, color: THEME.textMuted }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: THEME.textMuted }}>Disabled at the infrastructure level</Typography>
          </Box>
        ) : sendingEnabled ? (
          <>
            {pill(true)}
            <Button variant="outlined" size="small" sx={outlinedBtnSx} disabled={busy} onClick={onTurnOff}
              startIcon={busy ? <CircularProgress size={12} sx={{ color: THEME.mint }} /> : null}>
              {busy ? 'Working…' : 'Turn off'}
            </Button>
          </>
        ) : (
          <>
            {pill(false)}
            <Button variant="contained" size="small" sx={mintBtnSx} disabled={busy} onClick={onGoLive}
              startIcon={busy ? <CircularProgress size={12} sx={{ color: '#06281f' }} /> : null}>
              {busy ? 'Working…' : 'Go live'}
            </Button>
          </>
        )}
        <InfoTip text="The master send switch. When off, no approved contact is pushed to Instantly and nothing sends, whatever the individual campaigns say." />
      </Box>
      {!infraDisabled && (changedBy || changedAt) && (
        <Typography sx={{ fontSize: 10, color: THEME.textMuted }}>
          {sendingEnabled ? 'On' : 'Off'}{changedBy ? ` by ${changedBy}` : ''}{changedAt ? ` · ${fmtWhen(changedAt)}` : ''}
        </Typography>
      )}
      {error && <Typography sx={{ fontSize: 10, color: THEME.pink }}>{error}</Typography>}
    </Box>
  );
};

const OutboundPage = () => {
  // Navigation lives in the URL so a refresh lands on the same view. `group` is one of
  // the six task-verb groups; `sub` selects the sub-tab inside Find/Emails/Settings;
  // `chip` selects the Review & approve surface. Legacy ?group/chip/lib values are
  // rewritten to this scheme by the redirect effect below (old deep-links preserved).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawGroup = searchParams.get('group');
  const rawChip = searchParams.get('chip');
  const rawSub = searchParams.get('sub');
  const group = NAV_GROUP_KEYS.includes(rawGroup) ? rawGroup : 'home';
  const queueChip = QUEUE_KEYS.includes(rawChip) ? rawChip : 'approvals';
  const findSub = FIND_KEYS.includes(rawSub) ? rawSub : 'new';
  const emailsSub = EMAILS_KEYS.includes(rawSub) ? rawSub : 'sequences';
  const settingsSub = SETTINGS_KEYS.includes(rawSub) ? rawSub : 'links';
  const updateNav = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => { if (v == null) next.delete(k); else next.set(k, v); });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Switch top-level group: land on that group's default sub/chip and drop any stale
  // sub/chip/lib params so the URL stays canonical for the new IA.
  const goGroup = useCallback(
    (g) => {
      const next = new URLSearchParams();
      next.set('group', g);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  // Legacy redirect: rewrite pre-redesign ?group/chip/lib deep-links to the new scheme
  // once, in place, so existing bookmarks and in-app links keep landing on the same view.
  useEffect(() => {
    const LEGACY_GROUP = {
      campaigns: 1, new: 1, contacts: 1, queue: 1, sequences: 1, library: 1, inbox: 1,
    };
    const g = searchParams.get('group');
    if (!g || !LEGACY_GROUP[g]) return;
    const lib = searchParams.get('lib');
    const chip = searchParams.get('chip');
    const next = new URLSearchParams();
    if (g === 'campaigns') {
      next.set('group', 'home');
    } else if (g === 'inbox') {
      next.set('group', 'replies');
    } else if (g === 'queue') {
      next.set('group', 'review');
      if (QUEUE_KEYS.includes(chip)) next.set('chip', chip);
    } else if (g === 'new') {
      next.set('group', 'find'); next.set('sub', 'new');
    } else if (g === 'contacts') {
      next.set('group', 'find'); next.set('sub', 'import');
    } else if (g === 'sequences') {
      next.set('group', 'emails'); next.set('sub', 'sequences');
    } else if (g === 'library') {
      if (lib === 'prompts') { next.set('group', 'emails'); next.set('sub', 'prompts'); }
      else { next.set('group', 'settings'); next.set('sub', SETTINGS_KEYS.includes(lib) ? lib : 'links'); }
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Held/review data, fetched once so the Queue chip counts and the Mismatch/Deferred
  // surfaces share it (no extra fetches beyond what those tabs already did).
  const held = useHeldReview();

  // Review & approve: the four "skipped" queues sit behind a disclosure. Open it when
  // a skipped chip is deep-linked so a bookmarked ?chip=mismatch lands expanded.
  const [skippedOpen, setSkippedOpen] = useState(() => QUEUE_KEYS.includes(rawChip) && rawChip !== 'approvals');
  // The Home "Find new people" CTA opens the import flow at the page level.
  const [homeImportOpen, setHomeImportOpen] = useState(false);

  // ----- live reads (READ ONLY) -----
  // Each tab gets its own loading/empty/error state from its own resource.
  const campaignsRes = useGtmResource('/api/gtm/campaigns', (d) => d.campaigns || []);
  const reviewRes = useGtmResource('/api/gtm/review', (d) => d.review || []);
  const controlsRes = useGtmResource('/api/gtm/controls', (d) => d || {});
  // Inbox (Chunk 4): replied contacts with a drafted reply awaiting approval,
  // paginated. Local mutators back the optimistic approve/reject.
  const repliesRes = useReplies();
  const [replyErrors, setReplyErrors] = useState({}); // id -> message

  const campaigns = campaignsRes.data;
  const review = reviewRes.data;

  // ----- Approvals queue (Chunk 3.5) -----
  // The Approvals tab is contact-driven now: contacts arrive live over the SSE
  // stream (keyed by id, superseding in place) and models come from GET /models.
  const { contacts, status: contactsStatus, applyLocal } = useContactsStream();
  const modelsRes = useGtmModels();

  const [defaultModel, setDefaultModel] = useState('');
  const [editedOpeners, setEditedOpeners] = useState({}); // id -> { text, subject } operator edits
  const [dismissed, setDismissed] = useState(() => new Set()); // approved/rejected ids hidden locally
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // bulk-approve selection

  // ----- Auto-approve (Item 1) -----
  // Campaign-level autoApprove, shown as a per-campaign switch and mirrored on the
  // Approvals rows. Overrides hold the optimistic value between a toggle and its
  // reconcile; errors surface per campaign. Effective value = override, else the
  // campaign's own field. autoApproveIds is the set the Approvals badge reads.
  const [autoApproveOverrides, setAutoApproveOverrides] = useState({}); // campaignId -> bool
  const [autoApproveErrors, setAutoApproveErrors] = useState({}); // campaignId -> message

  // In-flight approve/reject ids, so "Approve all pending" disables while any row is
  // mid-request (Item 2) and never fires a second write over an unresolved one.
  const [inFlight, setInFlight] = useState(() => new Set());
  const addInFlight = useCallback((ids) => setInFlight((s) => { const n = new Set(s); ids.forEach((i) => n.add(i)); return n; }), []);
  const removeInFlight = useCallback((ids) => setInFlight((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; }), []);
  const anyBusy = inFlight.size > 0;

  // Seed the engine default from GET /models the first time it resolves.
  useEffect(() => {
    if (modelsRes.defaultModel) setDefaultModel((d) => d || modelsRes.defaultModel);
  }, [modelsRes.defaultModel]);

  // The visible queue: everything streamed, minus locally-decided contacts.
  const queueContacts = useMemo(
    () => contacts.filter((c) => !dismissed.has(c.id)),
    [contacts, dismissed]
  );

  // Effective auto-approve for a campaign: the optimistic override if we have one,
  // else the campaign's own autoApprove field (default off).
  //
  // GTM-AUTOAPPROVE-SHAPE-01: serializeCampaign emits autoApprove as an OBJECT that
  // is always present ({ enabled, requireVerified, maxAutoApprovals, approvedCount }),
  // so a bare truthiness test read every campaign as auto-approving. Read .enabled off
  // the object, and still tolerate the plain boolean the PATCH body uses.
  const autoApproveOf = useCallback(
    (c) => {
      if (!c) return false;
      if (c.id in autoApproveOverrides) return autoApproveOverrides[c.id];
      const aa = c.autoApprove;
      return aa && typeof aa === 'object' ? !!aa.enabled : !!aa;
    },
    [autoApproveOverrides]
  );
  // Set of campaignIds currently auto-approving, for the Approvals rows.
  const autoApproveIds = useMemo(() => {
    const s = new Set();
    (campaigns || []).forEach((c) => { if (autoApproveOf(c)) s.add(c.id); });
    return s;
  }, [campaigns, autoApproveOf]);

  // Toggle a campaign's auto-approve: optimistic override, PATCH, revert + surface on
  // error (Rule 3: never a false success). No refetch on success; the override stands
  // in until the campaigns list next reloads.
  const requestToggleAutoApprove = useCallback(
    async (c, next) => {
      const id = c.id;
      const prev = id in autoApproveOverrides ? autoApproveOverrides[id] : !!c.autoApprove;
      setAutoApproveOverrides((m) => ({ ...m, [id]: next }));
      setAutoApproveErrors((m) => { const n = { ...m }; delete n[id]; return n; });
      try {
        await updateCampaign(id, { autoApprove: next });
      } catch (err) {
        setAutoApproveOverrides((m) => ({ ...m, [id]: prev }));
        setAutoApproveErrors((m) => ({ ...m, [id]: friendlyActionError('save', err) }));
      }
    },
    [autoApproveOverrides]
  );

  // Effective opener = streamed opener with any saved operator edits layered on,
  // so the approved version carries the edits.
  const effectiveOpener = useCallback(
    (c) => {
      const base = (c && c.opener) || { text: '', subject: '', modelUsed: '' };
      const ed = editedOpeners[c.id];
      return ed ? { ...base, ...ed } : base;
    },
    [editedOpeners]
  );

  // Header reflects controls.paused / controls.breaker from the API. These are
  // local mirrors of read data; the toggles below are stubs and never write back.
  const [allPaused, setAllPaused] = useState(false);
  const [breaker, setBreaker] = useState({ tripped: false, reason: '' });
  useEffect(() => {
    const ctrl = controlsRes.data;
    if (!ctrl) return;
    setAllPaused(!!ctrl.paused);
    setBreaker(ctrl.breaker || { tripped: false, reason: '' });
  }, [controlsRes.data]);

  // Confirm modal config: { title, body, confirmLabel, tone, onConfirm } | null.
  const [confirm, setConfirm] = useState(null);
  const openConfirm = (cfg) => setConfirm(cfg);
  const closeConfirm = () => setConfirm(null);

  // GTM-MASTER-SWITCH-01: the master send switch. Reads controls.sendingEnabled (the T6 GTM
  // settings field); writes via POST /api/gtm/controls { sendingEnabled }. Optimistic with a
  // revert-and-surface on failure so it never shows a false success (Rule 3). The env outer
  // breaker (infra-level disable) is read defensively so the control says WHY it is off.
  const ctrl = controlsRes.data || {};
  const serverSending = ctrl.sendingEnabled === true;
  const [sendingOptimistic, setSendingOptimistic] = useState(null); // null = mirror server
  const [sendingBusy, setSendingBusy] = useState(false);
  const [sendingError, setSendingError] = useState(null);
  const sendingEnabled = sendingOptimistic == null ? serverSending : sendingOptimistic;
  const infraDisabled =
    ctrl.sendingLockedByEnv === true || ctrl.sendingInfraDisabled === true ||
    (ctrl.infraBreaker && ctrl.infraBreaker.tripped === true) || ctrl.envSendEnabled === false;
  const sendingChangedBy = ctrl.sendingChangedBy || ctrl.lastChangedBy || '';
  const sendingChangedAt = ctrl.sendingChangedAt || ctrl.lastChangedAt || null;
  // Ready-to-send count for the confirm copy, from the capacity read (enriched, approved, queued).
  const sendReadyRes = useGtmResource('/api/gtm/capacity', (d) => d || {});
  const readyCount = Number(sendReadyRes.data && sendReadyRes.data.sendReadyBuffer);

  const writeSending = useCallback(async (next) => {
    setSendingBusy(true); setSendingError(null); setSendingOptimistic(next);
    try {
      await setGtmControls({ sendingEnabled: next });
      controlsRes.reload();
      setSendingOptimistic(null); // reconcile to the freshly-read server value
    } catch (err) {
      setSendingError((err && err.message) || 'Could not change sending.');
      setSendingOptimistic(null); // revert to the server value
    } finally {
      setSendingBusy(false);
    }
    // controlsRes identity is stable enough for this handler; excluded intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Turning ON is gated by a two-step confirm citing the ready count. Turning OFF is instant.
  const requestGoLive = () => {
    const countStr = Number.isFinite(readyCount)
      ? ` ${readyCount.toLocaleString('en-US')} contacts are currently approved and ready.`
      : '';
    openConfirm({
      title: 'Turn sending on?',
      body: `Approved contacts will be pushed to their linked Instantly campaigns and emails will send.${countStr}`,
      confirmLabel: 'Go live',
      tone: 'mint',
      onConfirm: () => writeSending(true),
    });
  };
  const turnSendingOff = () => writeSending(false);

  // Tab count badges. Approvals is the count of READY (pending_approval) contacts
  // in the live queue; held comes from the (server-filtered) review resource.
  const pendingCount = useMemo(
    () => queueContacts.filter((c) => c.status === 'pending_approval').length,
    [queueContacts]
  );
  // Per-campaign pending-approval counts, derived from the live queue (contacts carry
  // campaignId). Drives the "N emails need your approval." synthesized sentence; a campaign
  // with no entry here degrades to the generic wording.
  const pendingByCampaign = useMemo(() => {
    const m = {};
    queueContacts.forEach((ct) => {
      if (ct.status === 'pending_approval' && ct.campaignId) m[ct.campaignId] = (m[ct.campaignId] || 0) + 1;
    });
    return m;
  }, [queueContacts]);
  const heldCount = review ? review.length : 0;
  // The Replies badge counts what still needs your eyes, not everything ever received.
  // unreadGenuineTotal (REPLY-NOISE-01) is unread AND not confirmed out-of-office /
  // bounce / unsubscribe, counted server-side so it covers replies this page never
  // fetched. It supersedes unreadTotal, which is kept only as a fallback for an older
  // response body: on live data the difference is 53 vs 13.
  const replyCount =
    repliesRes.unreadGenuineTotal != null
      ? repliesRes.unreadGenuineTotal
      : repliesRes.unreadTotal != null
        ? repliesRes.unreadTotal
        : (repliesRes.total != null ? repliesRes.total : repliesRes.replies.length);
  const mismatchCount = held.contacts.filter((c) => c.heldReason === 'fit_mismatch').length;
  const deferredCount = held.contacts.filter((c) => c.deferred === true).length;
  const competitorCount = held.contacts.filter((c) => c.heldReason === 'competitor_blocked').length;
  const queueCounts = { approvals: pendingCount, review: heldCount, mismatch: mismatchCount, deferred: deferredCount, competitor: competitorCount };

  // ----- confirm-modal-driven actions -----
  // READ ONLY page: every confirm is a stub (console.log). Nothing writes back
  // to the API and no fetched data is mutated locally.

  const requestTogglePause = (c) => {
    const pausing = c.status === 'active';
    openConfirm({
      title: pausing ? 'Pause this campaign?' : 'Resume this campaign?',
      body: pausing
        ? `Nothing will source or send for ${c.name} until you resume.`
        : `Sourcing and sending will continue for ${c.name}.`,
      confirmLabel: pausing ? 'Pause campaign' : 'Resume campaign',
      tone: pausing ? 'danger' : 'mint',
      // eslint-disable-next-line no-console
      onConfirm: () => console.log('[GTM] toggle pause (stub)', c.id),
    });
  };

  const requestPauseAll = (next) => {
    openConfirm({
      title: next ? 'Pause all GTM activity?' : 'Resume all GTM activity?',
      body: next
        ? 'Nothing will source or send until you resume.'
        : 'Sourcing and sending will continue.',
      confirmLabel: next ? 'Pause all' : 'Resume all',
      tone: next ? 'danger' : 'mint',
      // eslint-disable-next-line no-console
      onConfirm: () => console.log('[GTM] pause all (stub)', next),
    });
  };

  // ----- per-contact Approvals actions (Chunk 3.5) -----
  // Wired to the Part A write endpoints (approve / reject / edit-opener). Each
  // action updates the local queue OPTIMISTICALLY, reconciles against the server
  // response, and ROLLS BACK on failure so nothing shows a false success (Rule 3).
  // A failed approve/reject surfaces on the card through actionErrors[id]; a 409
  // contact_not_pending_approval gets a specific message rather than being swallowed.
  const [actionErrors, setActionErrors] = useState({}); // id -> message

  const setActionError = useCallback((id, message) => {
    setActionErrors((prev) => ({ ...prev, [id]: message }));
  }, []);
  const clearActionError = useCallback((id) => {
    setActionErrors((prev) => {
      if (!(id in prev)) return prev;
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }, []);

  // Hide a decided contact (optimistic) and drop it from the bulk selection.
  const dismiss = useCallback((ids) => {
    setDismissed((prev) => {
      const n = new Set(prev);
      ids.forEach((i) => n.add(i));
      return n;
    });
    setSelectedIds((prev) => {
      const n = new Set(prev);
      ids.forEach((i) => n.delete(i));
      return n;
    });
  }, []);
  // Roll an optimistic dismissal back into the queue (the call failed).
  const restore = useCallback((ids) => {
    setDismissed((prev) => {
      const n = new Set(prev);
      ids.forEach((i) => n.delete(i));
      return n;
    });
  }, []);

  // Approve: optimistically remove each card, POST with the edit-merged opener so
  // the approved version carries the operator's edits, reconcile to the server
  // status, roll back + surface the error on failure.
  const doApprove = useCallback(
    async (ids) => {
      addInFlight(ids);
      try {
        await Promise.all(
          ids.map(async (id) => {
            const c = contacts.find((x) => x.id === id);
            const openerBody = c ? effectiveOpener(c) : null;
            clearActionError(id);
            dismiss([id]);
            try {
              const res = await approveContact(id, openerBody);
              applyLocal({ id, status: res.status, opener: res.opener || (c && c.opener) });
            } catch (err) {
              restore([id]);
              setActionError(id, friendlyActionError('approve', err));
            }
          })
        );
      } finally {
        removeInFlight(ids);
      }
    },
    [contacts, effectiveOpener, dismiss, restore, applyLocal, clearActionError, setActionError, addInFlight, removeInFlight]
  );

  // Reject: optimistic remove -> POST -> reconcile; roll back + surface on failure.
  const doReject = useCallback(
    async (id) => {
      addInFlight([id]);
      clearActionError(id);
      dismiss([id]);
      try {
        const res = await rejectContact(id);
        applyLocal({ id, status: res.status });
      } catch (err) {
        restore([id]);
        setActionError(id, friendlyActionError('reject', err));
      } finally {
        removeInFlight([id]);
      }
    },
    [dismiss, restore, applyLocal, clearActionError, setActionError, addInFlight, removeInFlight]
  );

  // GTM-QUEUE-REMOVE-01: remove many at once (the "I ran a batch of tests" case).
  // Same terminal reject the single Remove uses; each id fails independently so one
  // 404 can't take the rest of the batch down.
  const doBulkReject = useCallback(
    async (ids) => {
      addInFlight(ids);
      try {
        await Promise.all(
          ids.map(async (id) => {
            clearActionError(id);
            dismiss([id]);
            try {
              const res = await rejectContact(id);
              applyLocal({ id, status: res.status });
            } catch (err) {
              restore([id]);
              setActionError(id, friendlyActionError('remove', err));
            }
          })
        );
      } finally {
        removeInFlight(ids);
        setSelectedIds(new Set());
      }
    },
    [dismiss, restore, applyLocal, clearActionError, setActionError, addInFlight, removeInFlight]
  );

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  // Edit-save: optimistically layer the edit on, PATCH, then reconcile to the
  // server opener (and clear the local override since the stream now carries it).
  // On failure roll the local edit back to its prior value and rethrow so the
  // card keeps its editor open and shows why — never a false success.
  const handleSaveEdit = useCallback(
    async (id, patch) => {
      const prevEdit = editedOpeners[id];
      setEditedOpeners((prev) => ({ ...prev, [id]: patch }));
      clearActionError(id);
      try {
        const res = await editContactOpener(id, patch);
        if (res && res.opener) applyLocal({ id, opener: res.opener });
        setEditedOpeners((prev) => {
          const n = { ...prev };
          delete n[id];
          return n;
        });
      } catch (err) {
        setEditedOpeners((prev) => {
          const n = { ...prev };
          if (prevEdit === undefined) delete n[id];
          else n[id] = prevEdit;
          return n;
        });
        throw new Error(friendlyActionError('save', err));
      }
    },
    [editedOpeners, applyLocal, clearActionError]
  );

  // Regenerate ONE contact's opener with the chosen model, then supersede in
  // place via applyLocal so the card updates even when Redis/SSE is absent. A
  // regenerate intentionally discards any manual edit for that contact.
  const handleRegenerate = useCallback(
    async (id, model) => {
      const opener = await regenerateOpener(id, model); // throws readable msg on failure
      applyLocal({ id, opener });
      setEditedOpeners((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    },
    [applyLocal]
  );

  const handleChangeDefaultModel = useCallback((m) => {
    setDefaultModel(m);
    // No controls endpoint for the draft-model default yet, local until it exists.
    // eslint-disable-next-line no-console
    console.log('[GTM] default drafting model (local, no controls endpoint yet)', m);
  }, []);

  // ----- Inbox reply actions (Chunk 4) -----
  // Optimistic remove, reconcile against the response, roll back on failure so a
  // dropped card never reads as a sent reply (Rule 3). Errors surface on the row.
  // Carries the server's stable code alongside the sentence, so a refusal the operator
  // can actually fix (a stale alias) can offer the fix and the other two cannot.
  const setReplyError = useCallback((id, message, code) => {
    setReplyErrors((prev) => ({ ...prev, [id]: { message, code: code || '' } }));
  }, []);
  const clearReplyError = useCallback((id) => {
    setReplyErrors((prev) => {
      if (!(id in prev)) return prev;
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }, []);

  const approveReplyRow = useCallback(
    async (id, draft) => {
      const snapshot = repliesRes.replies.find((r) => r.id === id);
      clearReplyError(id);
      repliesRes.removeReply(id, !(snapshot && snapshot.read)); // optimistic; keeps the unread badge exact
      try {
        // The response names the alias it actually sent from, so the confirmation can
        // state a fact rather than the pre-send guess the draft box used to show.
        return await approveReply(id, draft);
      } catch (err) {
        repliesRes.restoreReply(snapshot); // roll back
        setReplyError(id, friendlyActionError('send', err), err && err.code);
        throw err;
      }
    },
    [repliesRes, clearReplyError, setReplyError]
  );

  const rejectReplyRow = useCallback(
    async (id) => {
      const snapshot = repliesRes.replies.find((r) => r.id === id);
      clearReplyError(id);
      repliesRes.removeReply(id, !(snapshot && snapshot.read)); // optimistic; keeps the unread badge exact
      try {
        await rejectReply(id);
      } catch (err) {
        repliesRes.restoreReply(snapshot); // roll back
        setReplyError(id, friendlyActionError('reject', err), err && err.code);
        throw err;
      }
    },
    [repliesRes, clearReplyError, setReplyError]
  );

  // Feedback writes to the learning log. Throws a readable message on failure so
  // the row's FeedbackControl shows it rather than reporting a false success.
  const submitFeedbackRow = useCallback(async (id, feedback) => {
    await submitReplyFeedback(id, feedback);
  }, []);

  const requestApproveOne = (id) => {
    const c = contacts.find((x) => x.id === id);
    const campaignId = c && c.campaignId;
    openConfirm({
      title: 'Approve this contact?',
      body: 'The approved email, including any edits, becomes sendable to this person. You can pause anytime.',
      confirmLabel: 'Approve',
      tone: 'mint',
      costLoading: !!campaignId, // show the run cost estimate from the campaign read
      costEstimate: null,
      onConfirm: () => doApprove([id]),
    });
    if (campaignId) {
      fetchCampaign(campaignId)
        .then((res) =>
          setConfirm((prev) => (prev ? { ...prev, costLoading: false, costEstimate: res.costEstimate || { available: false } } : prev))
        )
        .catch(() =>
          setConfirm((prev) => (prev ? { ...prev, costLoading: false, costEstimate: { available: false } } : prev))
        );
    }
  };

  const requestBulkApprove = (ids) =>
    openConfirm({
      title: `Approve ${ids.length} contact${ids.length === 1 ? '' : 's'}?`,
      body: 'Each approved email, including any edits, becomes sendable. You can pause anytime.',
      confirmLabel: `Approve ${ids.length}`,
      tone: 'mint',
      onConfirm: () => doApprove(ids),
    });

  // "Approve all pending" (Item 2): count-confirm every pending row a human still owns
  // (auto-approving campaigns are excluded). Reuses the bulk-approve count-confirm.
  const requestApproveAllPending = () => {
    const ids = queueContacts
      .filter((c) => c.status === 'pending_approval' && !autoApproveIds.has(c.campaignId))
      .map((c) => c.id);
    if (ids.length === 0) return;
    requestBulkApprove(ids);
  };

  // GTM-QUEUE-REMOVE-01: the queue's Remove. It is the same terminal reject the
  // backend has always had (status 'rejected', never claimed by the send loop), now
  // named and iconed for what operators actually use it for: throwing out a test.
  const requestRejectOne = (id) =>
    openConfirm({
      title: 'Remove this person from the queue?',
      body: 'Takes them out of Review and approve for good. Nothing will ever send to them from this draft. Use this for test rows you never meant to mail.',
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: () => doReject(id),
    });

  const requestBulkReject = (ids) =>
    openConfirm({
      title: ids.length === 1 ? 'Remove this person from the queue?' : `Remove ${ids.length} people from the queue?`,
      body: `Takes ${ids.length === 1 ? 'this row' : `these ${ids.length} rows`} out of Review and approve for good. Nothing will ever send to them from these drafts.`,
      confirmLabel: ids.length === 1 ? 'Remove' : `Remove ${ids.length}`,
      tone: 'danger',
      onConfirm: () => doBulkReject(ids),
    });

  const requestDrop = (ct) =>
    openConfirm({
      title: 'Remove this person?',
      body: `Permanently removes ${ct.firstName} ${ct.lastName} from all outreach. They will never be contacted by the system again.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      // eslint-disable-next-line no-console
      onConfirm: () => console.log('[GTM] drop', ct.email),
    });

  const requestHandle = (ct) =>
    openConfirm({
      title: 'Take this one off automation?',
      body: `Removes ${ct.firstName} ${ct.lastName} from the automated sequence so you can reach out yourself, appropriately.`,
      confirmLabel: 'Take off automation',
      tone: 'mint',
      // eslint-disable-next-line no-console
      onConfirm: () => console.log('[GTM] handle personally', ct.email),
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        color: THEME.textPrimary,
        fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
      }}
    >
      {/* header bar — collapses to two rows under 768px (title + toggle, then pills)
          via flex order: title grows, toggle sits after it, the pill wraps below. */}
      <Box
        sx={{
          borderBottom: `1px solid ${THEME.border}`,
          px: 3,
          py: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1.5,
          flexShrink: 0,
          [MOBILE_MQ]: { px: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexGrow: 1, order: 1 }}>
          <OutboxIcon sx={{ color: THEME.mint }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              GTM
            </Typography>
            <Typography variant="caption" sx={{ color: THEME.textMuted }}>
              Find the right people, personalize, and reach out, with a human approving every send.
            </Typography>
          </Box>
        </Box>

        {/* T5 operator-only credit pool. Desktop: order 2 (right of title). Mobile:
            order 3 with a full-width basis so it drops to its own row. */}
        <Box sx={{ display: 'flex', alignItems: 'center', order: 2, [MOBILE_MQ]: { order: 3, flexBasis: '100%' } }}>
          <CreditPoolWidget />
        </Box>

        {/* Master send switch. Desktop: order 3 (left of Pause all). Mobile: its own row. */}
        <Box sx={{ display: 'flex', alignItems: 'center', order: 3, [MOBILE_MQ]: { order: 4, flexBasis: '100%', justifyContent: 'flex-end' } }}>
          <SendingMasterControl
            sendingEnabled={sendingEnabled}
            infraDisabled={infraDisabled}
            changedBy={sendingChangedBy}
            changedAt={sendingChangedAt}
            busy={sendingBusy}
            error={sendingError}
            onGoLive={requestGoLive}
            onTurnOff={turnSendingOff}
          />
        </Box>

        {/* Pause toggle. Desktop: order 4. Mobile: order 2 (stays on the title row). */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, order: 4, [MOBILE_MQ]: { order: 2 } }}>
          <Typography sx={{ fontSize: '13px', color: allPaused ? THEME.amber : THEME.textSecondary, fontWeight: 600 }}>
            Pause all GTM
          </Typography>
          <InfoTip text="A single stop for everything. When on, no list sources new people and no email sends." />
          <Switch
            checked={allPaused}
            onChange={(e) => requestPauseAll(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.amber },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.amber },
              '& .MuiSwitch-switchBase.Mui-checked:hover': { backgroundColor: 'rgba(251,191,36,0.08)' },
            }}
          />
        </Box>
      </Box>

      {/* scroll body — centered max-width on desktop, 16px gutters under 768px */}
      <Box sx={{ flex: 1, overflowY: 'auto', ...scrollSx }}>
        <Box sx={{ maxWidth: 1160, mx: 'auto', px: 3, py: 3, [MOBILE_MQ]: { px: 2 } }}>
          {/* controls failed to load — surface it (e.g. a 401 means no admin token) */}
          {controlsRes.error && (
            <Box sx={{ mb: 2 }}>
              <ErrorState message={controlsRes.error} onRetry={controlsRes.reload} />
            </Box>
          )}

          {/* paused banner */}
          {allPaused && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                mb: 2,
                borderRadius: '10px',
                background: THEME.amberDim,
                border: '1px solid rgba(251,191,36,0.3)',
              }}
            >
              <PauseCircleOutlineIcon sx={{ fontSize: 18, color: THEME.amber }} />
              <Typography sx={{ fontSize: '13px', color: THEME.amber, fontWeight: 600 }}>
                GTM paused. Nothing is sourcing or sending.
              </Typography>
            </Box>
          )}

          {/* circuit-breaker banner — only when tripped */}
          {breaker.tripped && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mb: 2,
                borderRadius: '10px',
                background: 'rgba(198,61,87,0.12)',
                border: `1px solid ${THEME.pink}`,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: THEME.pink, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '13px', color: THEME.pink, fontWeight: 700 }}>
                    Circuit breaker tripped
                  </Typography>
                  <InfoTip text="The system halted everything on its own because something looked risky. It stays stopped until a human resumes it." size={13} />
                </Box>
                <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>
                  {breaker.reason || 'GTM halted automatically.'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={dangerBtnSx}
                onClick={() => setBreaker({ tripped: false, reason: '' })}
              >
                Resume
              </Button>
            </Box>
          )}

          {/* group tabs — sticky, horizontally scrollable, active auto-scrolled in */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 5, background: THEME.bg, mb: 2.5 }}>
            <Tabs
              value={group}
              onChange={(e, v) => goGroup(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 0,
                borderBottom: `1px solid ${THEME.border}`,
                '& .MuiTabs-indicator': { backgroundColor: THEME.mint },
                '& .MuiTabs-scroller': { WebkitOverflowScrolling: 'touch' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: THEME.textMuted,
                  minHeight: 40,
                  py: 1,
                  [MOBILE_MQ]: { minHeight: 44 },
                },
                '& .Mui-selected': { color: `${THEME.textPrimary} !important` },
              }}
            >
              {NAV_GROUPS.map((g) => {
                const count = g.key === 'replies' ? replyCount : g.key === 'review' ? pendingCount : undefined;
                const tone = g.key === 'replies' ? 'mint' : g.key === 'review' ? 'amber' : undefined;
                return (
                  <Tab
                    key={g.key}
                    value={g.key}
                    label={
                      <TabLabel tip={g.tip} count={count} countTone={tone}>
                        {g.label}
                      </TabLabel>
                    }
                  />
                );
              })}
            </Tabs>
          </Box>

          {group === 'home' && (
            <HomeTab
              approvalsCount={pendingCount}
              replyCount={replyCount}
              onNavigate={updateNav}
              onFindNewPeople={() => setHomeImportOpen(true)}
              campaignsProps={{
                campaigns,
                loading: campaignsRes.loading,
                error: campaignsRes.error,
                onRetry: campaignsRes.reload,
                onTogglePause: requestTogglePause,
                autoApproveOf,
                autoApproveErrors,
                onToggleAutoApprove: requestToggleAutoApprove,
                pendingByCampaign,
              }}
            />
          )}

          {group === 'find' && (
            <Box>
              {/* sub-tabs: source new people, import a list, or test one person */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {FIND_SUBS.map((s) => (
                  <NavChip key={s.key} label={s.label} tip={s.tip} selected={findSub === s.key} onClick={() => updateNav({ group: 'find', sub: s.key })} />
                ))}
              </Box>
              {findSub === 'new' && (
                <NewRequestTab
                  onCreated={() => {
                    campaignsRes.reload(); // reflect the new pending_approval campaign
                    updateNav({ group: 'review', chip: 'approvals', sub: null }); // land on the new run
                  }}
                />
              )}
              {findSub === 'import' && <ContactsTab onNavigate={updateNav} />}
              {findSub === 'verify' && <VerifyListTab />}
              {findSub === 'test' && <TestPersonPanel />}
            </Box>
          )}

          {group === 'review' && (
            <Box>
              {/* Approvals is the hero surface; the four "skipped" queues live behind a
                  disclosure so the primary job stays front-and-center. */}
              <Box
                onClick={() => {
                  if (skippedOpen || queueChip !== 'approvals') {
                    setSkippedOpen(false);
                    if (queueChip !== 'approvals') updateNav({ group: 'review', chip: 'approvals' });
                  } else {
                    setSkippedOpen(true);
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  userSelect: 'none',
                  mb: 2,
                  color: THEME.textSecondary,
                  '&:hover': { color: THEME.textPrimary },
                  [MOBILE_MQ]: { minHeight: 44 },
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 18, transition: 'transform 0.15s', transform: (skippedOpen || queueChip !== 'approvals') ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Why some were skipped</Typography>
                {(mismatchCount + deferredCount + competitorCount + heldCount) > 0 && (
                  <CountBadge count={mismatchCount + deferredCount + competitorCount + heldCount} tone="gray" />
                )}
                <InfoTip text="People the system set aside instead of drafting: paused for review, held as a poor fit, deferred by governance, or blocked as a competitor. Nothing here sends." size={13} />
              </Box>
              <Collapse in={skippedOpen || queueChip !== 'approvals'} unmountOnExit>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                  {SKIPPED_CHIPS.map((c) => (
                    <NavChip
                      key={c.key}
                      label={c.label}
                      tip={c.tip}
                      count={queueCounts[c.key]}
                      countTone={c.countTone}
                      selected={queueChip === c.key}
                      onClick={() => { setSkippedOpen(true); updateNav({ group: 'review', chip: c.key }); }}
                    />
                  ))}
                </Box>
              </Collapse>

              {queueChip === 'approvals' && (
                <EditableApprovalsTab
                  contacts={queueContacts}
                  streamStatus={contactsStatus}
                  models={modelsRes.models}
                  modelsLoading={modelsRes.loading}
                  modelsError={modelsRes.error}
                  defaultModel={defaultModel}
                  onChangeDefaultModel={handleChangeDefaultModel}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onBulkApprove={requestBulkApprove}
                  onBulkReject={requestBulkReject}
                  onClearSelection={() => setSelectedIds(new Set())}
                  onApprove={requestApproveOne}
                  onReject={requestRejectOne}
                  onSaveEdit={handleSaveEdit}
                  onRegenerate={handleRegenerate}
                  effectiveOpener={effectiveOpener}
                  actionErrors={actionErrors}
                  autoApproveIds={autoApproveIds}
                  anyBusy={anyBusy}
                  onApproveAllPending={requestApproveAllPending}
                  campaigns={campaigns}
                  autoApproveOf={autoApproveOf}
                  autoApproveErrors={autoApproveErrors}
                  onToggleAutoApprove={requestToggleAutoApprove}
                />
              )}
              {queueChip === 'review' && (
                <ReviewTab
                  contacts={review}
                  loading={reviewRes.loading}
                  error={reviewRes.error}
                  onRetry={reviewRes.reload}
                  onDrop={requestDrop}
                  onHandle={requestHandle}
                  onNavigate={updateNav}
                />
              )}
              {queueChip === 'mismatch' && <MismatchTab held={held} />}
              {queueChip === 'deferred' && <DeferredTab held={held} />}
              {queueChip === 'competitor' && <CompetitorBlockedTab held={held} />}
            </Box>
          )}

          {group === 'emails' && (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {EMAILS_SUBS.map((s) => (
                  <NavChip key={s.key} label={s.label} tip={s.tip} selected={emailsSub === s.key} onClick={() => updateNav({ group: 'emails', sub: s.key })} />
                ))}
              </Box>
              {emailsSub === 'sequences' && <SequencesTab />}
              {emailsSub === 'prompts' && <PromptsTab />}
              {emailsSub === 'proofs' && <ProofsTab />}
            </Box>
          )}

          {group === 'replies' && (
            <InboxTab
              res={repliesRes}
              actionErrors={replyErrors}
              onApprove={approveReplyRow}
              onReject={rejectReplyRow}
              onFeedback={submitFeedbackRow}
              onGoToMailboxes={() => updateNav({ group: 'settings', sub: 'mailboxes' })}
            />
          )}

          {group === 'settings' && (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {SETTINGS_SUBS.map((s) => (
                  <NavChip key={s.key} label={s.label} tip={s.tip} selected={settingsSub === s.key} onClick={() => updateNav({ group: 'settings', sub: s.key })} />
                ))}
              </Box>
              {settingsSub === 'links' && <LinksTab />}
              {settingsSub === 'suppressions' && <SuppressionsTab />}
              {settingsSub === 'competitors' && <CompetitorsTab />}
              {settingsSub === 'mailboxes' && <MailboxesTab campaigns={campaigns} pendingByCampaign={pendingByCampaign} onNavigate={updateNav} />}
            </Box>
          )}
        </Box>
      </Box>

      {homeImportOpen && (
        <ImportContactsFlow
          onClose={() => setHomeImportOpen(false)}
          onImported={() => updateNav({ group: 'find', sub: 'import' })}
        />
      )}
      <ConfirmModal config={confirm} onClose={closeConfirm} />
    </Box>
  );
};

export default OutboundPage;
